// WEB: módulo nativo tirvu-rede → shim (no browser: disponivel=false, redeAtualEhWifi() → false).
import TirvuRede from "@/platform/tirvu-rede";
import { chaveNaturalExecucao, ehLinhaDeMidia } from "@/utils/checklist";
import { documentoVisivelAoColaborador } from "@/utils/documentoVisivel";
import { Environment } from "@/utils/Environment";
import {
  colegaCurto,
  inicialDoSobrenome,
  rotuloSubstituto,
} from "@/utils/trocaPlantao";
import AsyncStorage from "@/platform/async-storage";
import * as FileSystem from "@/platform/file-system";
import { AppState } from "@/platform/app-state";
import Api, { peekApiSessionGen, tenantHashDaSessao } from "./Api";
import {
  ehRevertConfirmado,
  empresaHashAtual,
  gravarUserDataFetchedAt,
  lerLogAssinadosDoTenant,
  lerUserDataFetchedAt,
  marcarDocAssinadoLog,
  marcarDocConfirmadoLog,
  marcarDocEntregueLog,
} from "./docAssinadoLog";
import {
  filaAntigaIlegivel,
  filaRegistrada,
  gravarFila,
  lerFilaParaEscrita,
  lerFilaSoLeitura,
  migrarFilaAntiga,
} from "./FilaOffline";
import { caminhoNoAppAtual, reescreverCaminhosAntigos } from "./CaminhoArquivo";
import { readJSONFile, writeJSONFile } from "./FileStorage";
import { pontoParaTimestamp } from "./PontoJornada";
import {
  type CoerenciaFuso,
  type FonteHora,
  RelogioConfiavel,
  type TipoDivergencia,
} from "./RelogioConfiavel";

// WEB: mesmo nome local do `import * as DadosMoveis from "@/modules/tirvu-rede"` da origem.
const DadosMoveis = TirvuRede;

const DOCS_DIR = `${(FileSystem as any).documentDirectory}documentos/`;

// ── Download de documento (PDF): trava por arquivo, temporário e validação ──────────
// "Às vezes o documento não aparece na assinatura" (09/10/2026): a Home, o Arquivos (todos
// os docs a cada userData) e a tela de assinatura baixavam o MESMO <id>.pdf ao mesmo tempo,
// sem trava. No Android o download grava direto no destino — quem abria lia o PDF pela
// metade. E nada conferia o status HTTP nem o conteúdo: página de erro (404/5xx/WAF) virava
// <id>.pdf e ficava em cache para sempre. Agora: uma fila por arquivo, download em
// `.part-*` com prazo, e só PDF inteiro ('%PDF-' no início, '%%EOF' no fim) vira o <id>.pdf.
const _filaDownloadDoc = new Map<string, Promise<unknown>>();
const comDocDownloadLock = <T,>(
  arquivo: string,
  fn: () => Promise<T>,
): Promise<T> => {
  const anterior = _filaDownloadDoc.get(arquivo) ?? Promise.resolve();
  const execucao = anterior.catch(() => {}).then(fn);
  const cauda = execucao.catch(() => {});
  _filaDownloadDoc.set(arquivo, cauda);
  void cauda.then(() => {
    if (_filaDownloadDoc.get(arquivo) === cauda)
      _filaDownloadDoc.delete(arquivo);
  });
  return execucao;
};
// Arquivos já conferidos como PDF inteiro, por `tamanho:mtime` — não relê a cada chamada.
const _pdfsConferidos = new Map<string, string>();
// Prazos do download: sem receber bytes por 30s, desiste (cancela) — sem isso o download
// ficava pendurado para sempre no Android (queda no meio do corpo). O teto total é só um
// limite de sanidade: arquivo grande em rede fraca precisa de tempo, e a tela de assinatura
// tem o prazo dela (o download segue e serve a próxima abertura).
const DOC_DOWNLOAD_SEM_PROGRESSO_MS = 30 * 1000;
const DOC_DOWNLOAD_TOTAL_MS = 20 * 60 * 1000;
// Endereço que respondeu com erro definitivo (4xx, ou 2xx que não é PDF): não é pedido de
// novo por 10 min — o pré-download roda a cada atualização do perfil e baixaria o mesmo
// lixo de novo a cada vez. Chave: arquivo|endereço.
const _downloadRecusado = new Map<string, number>();
const DOC_DOWNLOAD_RECUSADO_MS = 10 * 60 * 1000;
// Mesmos cabeçalhos do "Compartilhar": o servidor de arquivos devolve HTML para cliente
// que não parece navegador.
// WEB: "User-Agent" é cabeçalho controlado pelo browser — o shim de FS o descarta (o UA já é
// de navegador). Mantido para a superfície ficar igual à origem.
const DOC_DOWNLOAD_HEADERS = {
  "User-Agent":
    "Mozilla/5.0 (Linux; Android 13) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Mobile Safari/537.36",
  Accept: "application/pdf,application/octet-stream,*/*",
};
// Pré-download em segundo plano (Home + Arquivos): no máximo 2 por vez, para o documento que
// o colaborador abriu não esperar na fila atrás do histórico inteiro.
const _filaPrebaixar: {
  docId: number;
  url: string;
  assinado?: string;
  tenant: string;
}[] = [];
let _prebaixando = 0;
const PREBAIXAR_PARALELO = 2;
let _partesLimpas = false;

/** Base64 → texto binário (1 char por byte). Só para conferir poucos bytes do PDF. */
const base64ParaBinario = (b64: string): string => {
  const ALFA =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
  const limpo = String(b64 || "").replace(/[^A-Za-z0-9+/]/g, "");
  let saida = "";
  let buffer = 0;
  let bits = 0;
  for (let i = 0; i < limpo.length; i++) {
    buffer = (buffer << 6) | ALFA.indexOf(limpo[i]);
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      saida += String.fromCharCode((buffer >> bits) & 0xff);
    }
  }
  return saida;
};
const JUSTIFICATIVAS_DIR = `${(FileSystem as any).documentDirectory}justificativas/`;
const OS_PHOTOS_DIR = `${(FileSystem as any).documentDirectory}os_fotos/`;
const PONTO_PHOTOS_DIR = `${(FileSystem as any).documentDirectory}ponto_fotos/`;
// Cópia de segurança de cada batida em ARQUIVO, fora do AsyncStorage — ver _gravarBackupPonto.
const PONTO_BACKUP_DIR = `${(FileSystem as any).documentDirectory}ponto_pendentes/`;
const CHECKLIST_ASSETS_DIR = `${(FileSystem as any).documentDirectory}checklist_assets/`;
const LIVRO_PHOTOS_DIR = `${(FileSystem as any).documentDirectory}livro_fotos/`;
// Quarentena forense dos grupos de ronda sem execução — ver gravarQuarentenaRonda.
const RONDA_ORFAOS_DIR = `${(FileSystem as any).documentDirectory}ronda_orfaos/`;
// A FILA OFFLINE mora em arquivo desde 05/10/2026 (services/FilaOffline.ts): no Android, a
// chave única do AsyncStorage não voltava na leitura acima de ~2 MB e a fila sumia.
let isProcessingQueue = false;
/** Batidas pendentes herdadas (sem cópia em arquivo) já conferidas NESTA abertura do app. */
let copiasDasPendentesConferidas = false;

/**
 * Ações cuja BAIXA falhou (dequeueOfflineAction não conseguiu regravar a fila — ex.: disco
 * cheio). Quase sempre já foram entregues: sem isto, cada dreno (o agendador roda a cada
 * 1–5 min) reenviaria todas elas enquanto a gravação da fila falhar. O dreno não as envia de
 * novo neste processo; só tenta a baixa. Memória de propósito: reabrir o app dá uma nova
 * chance (no máximo um reenvio por abertura, nunca um por ciclo).
 */
const baixasPendentes = new Set<string>();

/**
 * Ações sendo despachadas AGORA fora do dreno (o envio "em primeiro plano" que a tela
 * dispara logo após enfileirar, para o usuário não esperar a rede).
 *
 * ⚠️ Sem isto existe uma corrida que DUPLICA registro: a ação só sai da fila em SUCESSO,
 * então, entre o POST do primeiro plano e a baixa, o dreno pode pegar a mesma linha e
 * postar de novo. No início de plantão os dois vão com
 * `id_adm_cliente_livro_ocorrencia = 0` — o servidor não tem como saber que é o mesmo
 * plantão, e nascem dois livros. Mesma família do incidente que criou a mesma O.S. 8×.
 *
 * A trava é por id de ação e vale nas duas direções: o dreno pula o que está aqui
 * (mantendo na fila, sem contar tentativa), e quem despacha em primeiro plano desiste se
 * o dreno já estiver rodando — nesse caso o dreno envia, que é o caminho durável.
 */
const acoesEmDespachoDireto = new Set<string>();

/**
 * Executa um envio de primeiro plano sob a trava. Devolve `null` quando NÃO deve tentar
 * (dreno rodando ou a mesma ação já em voo) — o chamador trata como "ficou offline", que
 * é a verdade: a ação está na fila e o dreno a envia.
 */
const comDespachoDireto = async <T,>(
  enqueuedId: string | null,
  fn: () => Promise<T>,
): Promise<T | null> => {
  if (!enqueuedId) return fn(); // sem id de fila não há corrida possível
  if (isProcessingQueue || acoesEmDespachoDireto.has(enqueuedId)) return null;
  acoesEmDespachoDireto.add(enqueuedId);
  try {
    return await fn();
  } finally {
    acoesEmDespachoDireto.delete(enqueuedId);
  }
};

// ── Serialização da FILA offline ────────────────────────────────────────────
// TODO read-modify-write da fila (lerFilaParaEscrita → gravarFila) passa por este lock.
// Sem ele, o enqueue de uma batida nova podia rodar getItem→parse→push→setItem
// ENTRELAÇADO com o dequeue/reescrita do dreno ou do reconciliador — o último
// setItem vence (last-write-wins) e apaga a escrita do outro. Era assim que
// pontos "orfanavam" da fila (no mapa, mas nunca enviados).
// Regra: NUNCA chamar uma função que também pega o lock de dentro de comFilaLock
// (deadlock) — as seções críticas abaixo manipulam a fila diretamente.
let filaLock: Promise<unknown> = Promise.resolve();
const comFilaLock = <T,>(fn: () => Promise<T>): Promise<T> => {
  const run = filaLock.then(fn, fn);
  filaLock = run.catch(() => {});
  return run;
};

// ── Serialização do MAPA de pontos (@TirvuMais:local_pontos) ─────────────────
// Mesmo racional do filaLock: setLocalPonto / markLocalPontoSynced / removeLocalPonto /
// cura retroativa fazem getItem→parse→mexer→setItem do mapa INTEIRO — sem serialização,
// uma batida gravada em paralelo (submitPonto) era APAGADA pelo setItem de um snapshot
// velho (last-write-wins). O mapa é a fonte da verdade da recuperação de pontos: perdê-lo
// é perder a batida. ORDEM DE LOCKS: fila → mapa é permitida (reconciliador marca synced
// dentro do comFilaLock); mapa → fila é PROIBIDA (deadlock) — nenhuma seção de mapaLock
// pode chamar enqueue/dequeue/comFilaLock.
let mapaLock: Promise<unknown> = Promise.resolve();
const comMapaLock = <T,>(fn: () => Promise<T>): Promise<T> => {
  const run = mapaLock.then(fn, fn);
  mapaLock = run.catch(() => {});
  return run;
};

// Teto de curas retroativas por batida (ver _reconciliarPontosPendentes) — também é o
// limite de quanto tempo uma batida "enviada e não confirmada" pede dreno extra.
const PONTO_CURA_MAX = 3;

/** Mensagem do submitPonto quando a batida não ficou guardada em lugar nenhum. */
export const ERRO_PONTO_NAO_SALVO =
  "Não foi possível guardar o ponto neste aparelho. O armazenamento do celular pode estar cheio — libere espaço e tente de novo.";

// Recibo do T-ARC nos endpoints de ponto: `{ enfileirado: true, fila: "t-arc.ponto", … }`
// (no master desde 02/05/2026). 2xx sem ele não veio da nossa API — ver _enviarPontoOnline.
const pontoFoiEnfileirado = (done: any): boolean =>
  !!done && typeof done === "object" && done.enfileirado === true;

// GET com timeout de 5 s: true se o host devolveu QUALQUER status HTTP (respondeu),
// false em falha de rede, DNS, TLS ou timeout. Base do apiAlcancavel/checkInternet.
// WEB: no browser o probe é cross-origin e os alvos (raiz do T-ARC, generate_204) não mandam
// Access-Control-Allow-Origin — em modo "cors" o fetch rejeitaria SEMPRE (offline falso e o
// dreno nunca rodaria). Com `mode: "no-cors"` o host que respondeu devolve Response opaca
// (type "opaque", status 0), que conta como "respondeu". `navigator.onLine === false` só
// antecipa o "offline"; nunca prova "online".
const respondeEm = async (url: string): Promise<boolean> => {
  if (typeof navigator !== "undefined" && navigator.onLine === false)
    return false;
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), 5000);
  try {
    const response = await fetch(url, {
      method: "GET",
      mode: "no-cors",
      cache: "no-store",
      credentials: "omit",
      signal: controller.signal,
    });
    return (
      response.type === "opaque" ||
      (response.status >= 200 && response.status < 600)
    );
  } catch {
    return false;
  } finally {
    clearTimeout(id);
  }
};

// Leitura da fila: ver services/FilaOffline.ts. Dentro do comFilaLock, `lerFilaParaEscrita`
// (migra a fila antiga e, SÓ com conteúdo que não é JSON de array, põe em quarentena e
// recomeça vazia — o dreno não pode travar para sempre; os PONTOS voltam pelo
// _reconciliarPontosPendentes). Erro de LEITURA lança: quem ia regravar desiste e nada é
// sobrescrito. Até 05/10/2026 o erro de leitura também virava `[]` gravado por cima — e no
// Android, fila acima de ~2 MB não volta do getItem: ela inteira sumia em silêncio.
// Fora do lock, só `lerFilaSoLeitura`.

/**
 * Quarentena forense de um grupo de ronda que não tem para onde ir (execução inexistente,
 * cadastro inexistente no tenant). Um ARQUIVO por grupo, sem ler nada antes: até 05/10/2026
 * era uma chave do AsyncStorage (`@TirvuMais:ronda_finalizar_orfao`, lida → acrescentada →
 * regravada) que podia passar de 2 MB com as assinaturas dos checklists — aí a leitura
 * falhava, virava `[]`, e a gravação apagava os grupos já guardados. A chave antiga fica
 * como está (forense). Lança se não gravou: o grupo continua na fila.
 */
const gravarQuarentenaRonda = async (
  acoes: OfflineAction[],
  motivo: string,
): Promise<void> => {
  const info = await FileSystem.getInfoAsync(RONDA_ORFAOS_DIR);
  if (!info.exists) {
    await FileSystem.makeDirectoryAsync(RONDA_ORFAOS_DIR, {
      intermediates: true,
    });
  }
  const agora = Date.now();
  const destino = `${RONDA_ORFAOS_DIR}${agora}_${Math.random().toString(36).slice(2, 8)}.json`;
  try {
    await FileSystem.writeAsStringAsync(
      destino,
      JSON.stringify(
        acoes.map((a) => ({
          id: a.id,
          type: a.type,
          timestamp: a.timestamp,
          data: a.data,
          motivo,
          quarentenaEm: agora,
        })),
      ),
    );
  } catch (e) {
    // Disco cheio deixa o arquivo pela metade: apaga (o grupo continua na fila).
    await FileSystem.deleteAsync(destino, { idempotent: true }).catch(() => {});
    throw e;
  }
  // Teto (antes: 20 ações na chave): os 50 grupos mais novos. Nunca falha a quarentena.
  try {
    const nomes = (await FileSystem.readDirectoryAsync(RONDA_ORFAOS_DIR)).sort();
    for (const nome of nomes.slice(0, Math.max(0, nomes.length - 50))) {
      await FileSystem.deleteAsync(`${RONDA_ORFAOS_DIR}${nome}`, {
        idempotent: true,
      }).catch(() => {});
    }
  } catch {}
};
// Último snapshot ÍNTEGRO das rondas finalizadas offline (pré-dreno). Servido enquanto
// isProcessingQueue=true p/ a UI não recomputar de uma fila MEIO-DRENADA (badge "Parcial"
// falso + hora início == fim). Atualizado a cada leitura com a fila em repouso.
let snapshotRondasPendentesPreDrain: Array<{
  key: string;
  id_adm_ronda_cadastro: number | null;
  id_adm_ronda_execucao: number | null;
  dataInicioRaw: string | null;
  inicioTs: number;
  fimTs: number;
  qtdePontosExec: number;
}> | null = null;
const LOCAL_DOC_STATUS_KEY = "@TirvuMais:local_doc_status";
const LOCAL_DOC_CONTESTACAO_KEY = "@TirvuMais:local_doc_contestacao";
const LOCAL_CONTEST_VISTAS_KEY = "@TirvuMais:local_contestacoes_vistas";
const LOCAL_NOTIF_LIDAS_KEY = "@TirvuMais:local_notif_lidas";
const LOCAL_DOC_SIGNED_KEY = "@TirvuMais:local_doc_signed";
// Endereço (path) do último download por doc — re-baixa quando o endereço no banco
// passa a apontar p/ OUTRO arquivo (nome/path diferente, ignorando query/token).
// _v2: descarta baselines da versão anterior (que podiam "cimentar" um endereço por cima
// de conteúdo NÃO baixado) → força uma revalidação online única por doc já cacheado.
const LOCAL_DOC_URL_KEY = "@TirvuMais:local_doc_url_v2";
// Momento (ms) em que a assinatura de cada doc foi ENTREGUE (SIGNATURE drenada com sucesso).
// Depois da entrega damos um grace p/ o consumer gerar o _Ass.pdf; passado o grace sem o
// _Ass.pdf num Start com sucesso, a assinatura é considerada REVERTIDA (volta a PENDENTE).
const LOCAL_DOC_DELIVERED_AT_KEY = "@TirvuMais:local_doc_delivered_at";
// Último posto em que o colaborador BATEU PONTO neste aparelho (por tenant):
// {id_adm_cliente, posto_nome, data_log}. Sobrevive à poda do espelho de pontos — é a
// fonte offline-first de "onde ele estava" para o checklist avulso.
const ULTIMO_POSTO_PONTO_KEY = "@TirvuMais:ultimo_posto_ponto";
// "Ver Depois" da assinatura obrigatória: {dia: "YYYY-MM-DD" local, em: ms}. Uma vez por
// dia (reinicia à meia-noite LOCAL), suspende a cobrança por 30min. Escopado por tenant.
const DOC_VER_DEPOIS_KEY = "@TirvuMais:doc_ver_depois";
const VER_DEPOIS_GRACA_MS = 30 * 60 * 1000;
// Falha de CARGA do documento na tela de assinatura (09/10/2026): {[docId]: {em, motivo, n}}.
// Documento que não aparece não pode prender o colaborador: a cobrança fica suspensa (igual
// ao "Ver Depois", mas sem gastar o uso do dia) por 5 min na 1ª falha, 15 na 2ª seguida e
// 60 da 3ª em diante — documento com link quebrado no servidor não cobra a cada 5 min para
// sempre. Abrir com sucesso apaga a marca. Escopado por tenant.
const DOC_FALHA_CARGA_KEY = "@TirvuMais:doc_falha_carga";
const DOC_FALHA_CARGA_GRACAS_MS = [
  5 * 60 * 1000,
  15 * 60 * 1000,
  60 * 60 * 1000,
];
const gracaFalhaCarga = (n: number): number =>
  DOC_FALHA_CARGA_GRACAS_MS[
    Math.min(Math.max(1, n), DOC_FALHA_CARGA_GRACAS_MS.length) - 1
  ];
const LOCAL_JUSTIFICATIVAS_KEY = "@TirvuMais:local_justificativas";
const LOCAL_PONTOS_KEY = "@TirvuMais:local_pontos";
// Cópia forense de um mapa de pontos que não parseou — ver lerMapaPontosParaEscrita.
const LOCAL_PONTOS_CORRUPT_KEY = "@TirvuMais:local_pontos_corrupt";
const LOCAL_OS_STATUS_KEY = "@TirvuMais:local_os_status";
// Espelho das FOTOS de OS deste aparelho, por id do cronograma (temp negativo antes do
// vínculo). Existe porque foto "enviada" não deixava rastro nenhum: saía da fila, o
// arquivo local era apagado — mas 200 = ENFILEIRADO no Rabbit e a lista de OS tem cache,
// então até o servidor DEVOLVER a foto ela não existia em nenhuma fonte que a tela lê
// (relato: OS pendente com detalhes sem as fotos, ou só com as "antes" ainda na fila).
const LOCAL_OS_FOTOS_KEY = "@TirvuMais:local_os_fotos";
const LOCAL_OS_SOBDEMANDA_KEY = "@TirvuMais:local_os_sobdemanda";
const LOCAL_OS_SOBDEMANDA_IDMAP_KEY = "@TirvuMais:local_os_sobdemanda_idmap";
const LOCAL_RECUSAS_VISTAS_KEY = "@TirvuMais:recusas_vistas";
const LIVRO_FINALIZADOS_KEY = "@TirvuMais:livros_finalizados_local";
// Enquetes RESPONDIDAS localmente (id da execução → quando). Persistente de
// propósito: o merge otimista no userData é volátil e o /iniciar (cache ~3min +
// gravação assíncrona via Rabbit) devolve a enquete como pendente por um tempo —
// sem este registro, o pull-to-refresh REABRIA a enquete já respondida.
const ENQUETES_RESPONDIDAS_KEY = "@TirvuMais:enquetes_respondidas_local";
// Contador de falhas PERMANENTES (rejeição do servidor, não queda de rede) por
// ação da fila. Sem ele, uma ação rejeitada de forma determinística (payload
// inválido, execução inexistente, registro já processado) era reenviada a CADA
// dreno PARA SEMPRE — o padrão "acumula pendência e nunca sincroniza".
const ACOES_FALHAS_KEY = "@TirvuMais:acoes_falhas_permanentes";
// (Não existe quarentena/descarte automático de ação: rejeição do servidor só
// aplica BACKOFF — ver tratarFalhaDespacho no dreno.)
// Checklists de O.S. respondidos localmente (id do cronograma → quando). Mesma
// mecânica/janela do ENQUETES_RESPONDIDAS_KEY — ver getChecklistsOsRespondidosLocais.
const CHECKLISTS_RESPONDIDOS_KEY = "@TirvuMais:checklists_os_respondidos_local";
// Execuções de checklist AVULSAS (gatilho livre / ponto eletrônico) feitas neste
// aparelho. Espelha o mapa de local_pontos e é o que sustenta os 3 selos da tela de
// Checklists: entrada no mapa com offline:true = 1 check; offline:false = 2 checks
// cinza (T-ARC aceitou); ausente do mapa e presente no servidor = 2 checks azul.
// Ronda e O.S. NÃO entram aqui — elas têm o próprio selo, na própria tela.
const LOCAL_CHECKLIST_EXEC_KEY = "@TirvuMais:local_checklist_execucoes";
// Complementos (seção de preenchimento posterior) respondidos neste aparelho para uma
// execução JÁ existente no servidor. Chave = id_inspecao. Vive separado do mapa de
// execuções porque não é uma execução nova — é uma segunda gravação sobre a mesma.
const LOCAL_CHECKLIST_COMPL_KEY = "@TirvuMais:local_checklist_complementos";
// O mesmo mapa guarda o rastro de uma EDIÇÃO ainda na fila (tipo "EDICAO"), que sai pelo
// dreno. Este teto é só a válvula: ação descartada não pode deixar "alteração pendente"
// preso no card para sempre. 7 dias — bem além de qualquer offline real de campo.
const TETO_EDICAO_PENDENTE_MS = 7 * 24 * 60 * 60 * 1000;
/**
 * Complementos respondidos ANTES de o servidor devolver o id da execução.
 *
 * Por que existe: online, a execução é enviada e a ação sai da fila na hora — mas o
 * `Salvar` NÃO devolve id (200 do T-ARC = enfileirado no Rabbit). Quem responde a
 * complementar nesses segundos não tem alvo: não há ação para fundir nem id para mirar.
 * Aqui o complemento espera, e o vínculo acontece quando a execução volta do servidor
 * (`vincularComplementosPendentes`).
 *
 * ⚠️ Chave GLOBAL (sem `chaveTenant`) com carimbo `hash` POR REGISTRO: isto é a ÚNICA
 * cópia de um dado que ainda não foi enviado — não pode ser varrido na troca de empresa
 * como um espelho de UI qualquer. O filtro por tenant é na LEITURA.
 */
const LOCAL_CHECKLIST_COMPL_PENDENTE_KEY =
  "@TirvuMais:local_checklist_compl_pendentes";
/** Reserva (lease) de um pendente em envio: expira, para app morto não travar o registro. */
const LEASE_VINCULO_MS = 2 * 60 * 1000;
/** Guarda de módulo: duas passadas de vínculo enviariam o mesmo complemento duas vezes. */
let vinculandoComplementos = false;
/** Teto do pendente: estourou, vai para quarentena forense — nunca delete puro. */
const TETO_COMPL_PENDENTE_MS = 7 * 24 * 60 * 60 * 1000;
const COMPL_PENDENTE_QUARENTENA_KEY =
  "@TirvuMais:checklist_complemento_quarentena";
const LIVRO_LOCAL_KEY = "@TirvuMais:livro_local_ativo";
const LIVRO_LOCAIS_KEY = "@TirvuMais:livros_locais";
const LIVRO_ID_MAP_KEY = "@TirvuMais:livro_id_map";
// Troca de plantão: espelho das solicitações feitas NESTE aparelho (por data da troca)
// e a última lista de substitutos — as duas por tenant (chaveTenant).
const LOCAL_TROCAS_PLANTAO_KEY = "@TirvuMais:local_trocas_plantao";
const TROCA_SUBSTITUTOS_KEY = "@TirvuMais:troca_plantao_substitutos";
// O espelho é só a ponte até o servidor (o status vem da rota `minhas`): 60 dias.
const TETO_ESPELHO_TROCA_MS = 60 * 24 * 60 * 60 * 1000;
// Status das trocas que EU pedi (rota `minhas`), por data da troca — o histórico mostra
// offline o último status que veio do servidor.
const TROCAS_MINHAS_KEY = "@TirvuMais:troca_plantao_minhas";
// Histórico dos DOIS lados (rota `historico`, 26/10/2026): as trocas que pedi e as que cubro.
const TROCAS_HISTORICO_KEY = "@TirvuMais:troca_plantao_historico";
// Trocas que pediram para EU cobrir (pop-up) e as que respondi NESTE aparelho — a resposta
// pode estar na fila, e o pop-up não pode reabrir a mesma solicitação.
const TROCAS_RECEBIDAS_KEY = "@TirvuMais:troca_plantao_recebidas";
const TROCAS_RESPONDIDAS_KEY = "@TirvuMais:troca_plantao_respondidas";
// Recibos emitidos pelo supervisor (rota `recibos`, 28/10/2026): a última lista, para a tela
// abrir offline (o PDF em si fica guardado pela tela, por link).
const TROCAS_RECIBOS_KEY = "@TirvuMais:troca_plantao_recibos";

export type LocalOsStatus = "iniciada" | "concluida";
type LocalOsStatusMap = Record<
  string,
  { status: LocalOsStatus; data_log: string; offline: boolean }
>;

/** Uma foto de OS registrada neste aparelho (ver LOCAL_OS_FOTOS_KEY). */
export type LocalOsFoto = {
  /** Identidade do asset — a MESMA chave de idempotência do envio. */
  id: string;
  tipo_foto: string; // 'A' (antes) | 'P' (pós)
  /** Cópia persistida em os_fotos/ (miniatura garantida mesmo offline). */
  uri?: string;
  /** URL no storage, quando o upload já aconteceu. */
  url?: string;
  /** true = ainda na fila; false = aceito pelo T-ARC (a caminho do banco). */
  pendente: boolean;
  em: number;
};
type LocalOsFotosMap = Record<string, LocalOsFoto[]>;

type LocalOsSobDemandaItem = {
  temp_cronograma_id: number;
  payload: any;
  created_at: number;
  cliente_label?: string;
  equipe_label?: string;
  id_adm_ordem_servico?: number;
  id_adm_ordem_servico_cronograma?: number;
};

type LocalOsSobDemandaMap = Record<string, LocalOsSobDemandaItem>;
type LocalOsSobDemandaIdMap = Record<
  string,
  { id_adm_ordem_servico?: number; id_adm_ordem_servico_cronograma?: number }
>;

// 'S' = assinado/confirmado pelo backend. 'O' = assinado offline, aguardando sync.
export type LocalDocStatus = "S" | "O";
type LocalDocStatusMap = Record<string, LocalDocStatus>;

// Status local de uma justificativa do dia (chaveada pela data DD/MM/YYYY)
/**
 * `"CANCELADO"` (11/09/2026): cancelada pelo PRÓPRIO colaborador enquanto pendente
 * (`cancelarJustificativa`). O espelho fica com esse status — e `offline:false` — só
 * para a tela de atestado ESCONDER a solicitação enquanto o `/iniciar` em cache ainda
 * a devolve como PENDENTE. Home e Justificativas ignoram (só olham `offline` e PENDENTE).
 */
export type JustificativaStatus =
  "PENDENTE" | "ACEITO" | "RECUSADO" | "CANCELADO";
/**
 * `"atestado"` (10/09/2026): atestado médico, enviado pela tela própria. Vai pela
 * MESMA fila e pelo mesmo endpoint da justificativa — a tabela é a mesma; o que
 * muda são os campos do `AtestadoPayload` e o `tipo_atestado` que marca a linha.
 */
export type JustificativaTipo = "ajuste" | "abono" | "atestado";

export interface LocalJustificativa {
  data: string; // data_competencia DD/MM/YYYY
  status: JustificativaStatus;
  tipo: JustificativaTipo;
  offline: boolean; // true se ainda está aguardando ser sincronizada
  timestamp: number;
  motivo?: string;
  observacao_validacao?: string;
  nome_validador?: string;
  data_validacao?: string; // data/hora em que o fiscal validou (DD/MM/YYYY HH:mm)
  foto?: string; // foto enviada na solicitação (endereco_arquivo)
  horasAtual?: (string | null)[]; // horários do dia antes do ajuste
  horasAjuste?: (string | null)[]; // horários solicitados no ajuste
  visto?: boolean; // true = recusa já marcada como vista no servidor (data_log_visto)
  origem?: "local" | "servidor"; // onde a informação originou
  /** Detalhes do atestado médico (só quando `tipo === "atestado"`). */
  atestado?: AtestadoPayload;
  /** Detalhes da solicitação de abono (só na tela de abono; o abono legado não tem). */
  abono?: AbonoPayload;
}
type LocalJustificativasMap = Record<string, LocalJustificativa>;

// ─── PONTOS ──────────────────────────────────────────────────────────────────

/**
 * Carimbo de data/hora LOCAL (BRT) no formato MySQL "YYYY-MM-DD HH:mm:ss".
 *
 * Use SEMPRE isto para o `data_log` de um evento — NUNCA `toISOString()` (que é UTC,
 * = BRT+3). O backend grava o `data_log` VERBATIM (hora do evento), então tem que ser
 * a hora local em que o evento aconteceu. É o mesmo formato/idioma do ponto
 * (submitPonto). Carimbe no MOMENTO do evento e persista na fila — assim um registro
 * feito offline mantém a hora real, e não a hora da sincronização.
 *
 * ⚠️ O default é o RELÓGIO DO APARELHO, de propósito. Já foi tentado ancorar isto no
 * relógio confiável (hora do servidor + uptime) e um bug na detecção de reboot fez
 * eventos serem carimbados com horas de atraso em produção. O app não é dono dessa
 * decisão: ele carimba o que o aparelho diz e anexa a leitura do relógio confiável em
 * campos separados, para o backend tratar. Ver services/RelogioConfiavel.ts.
 */
export const nowLocalSqlDateTime = (d: Date = new Date()): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

/**
 * O inverso de `nowLocalSqlDateTime`: "YYYY-MM-DD HH:mm:ss" (BRT) → ms locais.
 * `Date.now()` quando vazio/inválido — quem chama quer um instante, não um erro.
 */
export const msDeDataLog = (dataLog?: string | null): number => {
  const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/.exec(
    dataLog ?? "",
  );
  if (!m) return Date.now();
  const [, y, mo, d, h, mi, se] = m.map(Number);
  const dt = new Date(y, mo - 1, d, h, mi, se);
  return Number.isNaN(dt.getTime()) ? Date.now() : dt.getTime();
};

/**
 * HORA DA BATIDA para o envio: SEMPRE a carimbada no instante do toque (relógio do
 * aparelho), nunca a do envio — uma batida offline sobe dias depois com a hora em que foi
 * feita. Se o `data_log` guardado estiver ilegível (versão antiga, cópia danificada), refaz a
 * partir do mesmo instante guardado no `timestamp` ou no próprio id (`${Date.now()}_…`, ver
 * submitPonto). Sem nenhum dos três, devolve "" e a batida NÃO sai (ver _enviarPontoOnline):
 * mandar vazio faria o servidor gravar NOW(), a hora da SINCRONIZAÇÃO (relato de 01/10/2026).
 */
const dataLogDaBatida = (p: {
  data_log?: string;
  timestamp?: number;
  id?: string;
}): string => {
  const s = String(p?.data_log ?? "").trim();
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s)) return s;
  const doId = String(p?.id ?? "").match(/^(\d{13})(?:_|$)/)?.[1];
  for (const ts of [Number(p?.timestamp), Number(doId)]) {
    if (Number.isFinite(ts) && ts > 1_500_000_000_000)
      return nowLocalSqlDateTime(new Date(ts));
  }
  return "";
};

/** Competência ISO da batida; ilegível → o dia do data_log (mesmo critério do T-Consumer). */
const competenciaSqlDaBatida = (p: { data?: string }, dataLog: string): string => {
  const s = String(p?.data ?? "").trim();
  const br = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (br) return `${br[3]}-${br[2]}-${br[1]}`;
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  return dataLog ? dataLog.slice(0, 10) : "";
};

export interface PontoPayload {
  id_adm_colaborador: number;
  id_adm_cliente: number;
  id_adm_empresa: number;
  id_rh_ponto_tipo: number; // 1=ENTRADA 2=SAIDA_PAUSA 3=RETORNO_PAUSA 4=SAIDA
  fotoUri: string | null; // URI local da foto a ser uploadada (null se sem foto)
  latitude: string | null;
  longitude: string | null;
  latitude_ponto: string | null;
  longitude_ponto: string | null;
  latitude_lotacao?: string | null;
  longitude_lotacao?: string | null;
  distancia?: string | null;
  data_competencia: string; // DD/MM/YYYY (formato UI)
  postoNome?: string; // pra exibir na UI local
  expressao?: string | null; // expressão facial classificada da foto do ponto
  registra_almoco?: string | null;
}

/** Ponto persistido localmente — usado pra otimismo + offline. */
export interface LocalPonto {
  id: string; // id local (Date.now + random)
  id_adm_colaborador: number;
  id_adm_cliente: number;
  id_adm_empresa: number;
  id_rh_ponto_tipo: number;
  data: string; // DD/MM/YYYY
  hora: string; // HH:mm
  data_log: string; // YYYY-MM-DD HH:mm:ss
  origem: "M"; // sempre mobile pelo app
  fotoUri?: string | null; // arquivo local persistido
  fotoUrl?: string | null; // URL no servidor após upload
  latitude?: string | null;
  longitude?: string | null;
  latitude_ponto?: string | null;
  longitude_ponto?: string | null;
  latitude_lotacao?: string | null;
  longitude_lotacao?: string | null;
  distancia?: string | null;
  postoNome?: string;
  /** Hash da EMPRESA/tenant da sessão que registrou a batida — os ids numéricos colidem
   *  entre bancos; sem hash igual ao atual, a cura retroativa NUNCA reativa o ponto. */
  hash?: string;
  offline: boolean; // true = aguardando sync com backend
  // ─── AUDITORIA DO RELÓGIO (observação pura) ─────────────────────────────────
  // O `data_log`/`hora` acima saem do relógio do APARELHO. Os campos abaixo registram
  // o que o relógio confiável dizia no mesmo instante, para o backend montar relatório
  // e o administrativo tratar. NADA aqui altera a hora gravada — o app só observa.
  /** Hora do relógio CONFIÁVEL no instante da batida, "YYYY-MM-DD HH:mm:ss". */
  horaConfiavel?: string;
  /** Uptime (ms desde o boot) no instante da batida — insumo do `RelogioConfiavel.idadeMs`. */
  uptimeMs?: number | null;
  /** 'S' = servidor fresco, 'M' = monotônico (offline), 'D' = relógio do aparelho. */
  fonteHora?: FonteHora;
  /** `Date.now()` do aparelho no instante da batida — o que o relógio DELE dizia. */
  horaDispositivo?: number;
  /** Aparelho menos confiável, em segundos. Positivo = relógio adiantado. */
  divergenciaSeg?: number;
  /** Regra da hora cheia: 'fuso' = divergência fecha em hora redonda (±2min);
   *  'relogio' = não fecha (23min, 1h47 — mexido na mão); 'nenhuma' = ruído. */
  tipoDivergencia?: TipoDivergencia;
  /** Fuso do aparelho em minutos a OESTE de UTC (Brasília 180, Manaus 240, Acre 300).
   *  A âncora protege o instante, NÃO o fuso — trocar o fuso ainda desloca a hora
   *  gravada. Fica aqui para o RH conseguir VER a divergência. */
  utcOffsetMin?: number;
  /** O fuso declarado bate com a LONGITUDE da batida? 'incoerente' = o aparelho diz
   *  estar num fuso impossível para o lugar onde a batida ocorreu (fuso estrangeiro).
   *  'sem_posicao' = batida sem GPS. Ver `coerenciaFusoPosicao`. */
  coerenciaFuso?: CoerenciaFuso;
  /** Date.now() de quando markLocalPontoSynced marcou offline:false (grace da cura). */
  syncedEm?: number;
  /** Nº de curas retroativas já aplicadas neste ponto (teto anti-loop). */
  curas?: number;
  /** Date.now() da última cura retroativa (intervalo mínimo entre curas). */
  ultimaCuraEm?: number;
  /** Date.now() de quando o /iniciar devolveu a linha GENUÍNA desta batida — só aí ela
   *  está no MySQL (o `offline:false` é o 200 do T-ARC, que só enfileira no Rabbit). */
  confirmadoEm?: number;
  timestamp: number;
  expressao: string | null; //'com medo', 'com nojo', 'feliz', 'nervoso', 'padrão', 'surpreso', 'triste'
  registra_almoco: string | null; // 'S' | `N`
}

type LocalPontosMap = Record<string, LocalPonto>;

/**
 * Leitura do mapa de pontos para quem vai REGRAVÁ-LO (read-modify-write).
 *
 * O `getLocalPontosMap` devolve `{}` em qualquer erro — certo para quem só exibe, fatal
 * para quem grava: o `setItem` seguinte escrevia o mapa "vazio" com uma batida só e
 * apagava todas as pendentes. Aqui:
 *  - falha de LEITURA (storage indisponível) → lança; quem grava desiste e nada é
 *    sobrescrito;
 *  - valor que não é JSON de objeto → quarentena (cópia forense) e recomeça vazio, para
 *    o mapa não ficar travado para sempre. As batidas daquele valor continuam na fila
 *    (snapshot na ação) e nas cópias em arquivo (`ponto_pendentes/`).
 */
const lerMapaPontosParaEscrita = async (): Promise<LocalPontosMap> => {
  const raw = await AsyncStorage.getItem(LOCAL_PONTOS_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      // Foto de antes de uma atualização (iOS muda a pasta do app) → pasta atual.
      return reescreverCaminhosAntigos(parsed);
    }
  } catch {}
  console.error(
    "[Sync] MAPA DE PONTOS ILEGÍVEL — quarentenando e recomeçando vazio (as batidas seguem na fila e nas cópias em arquivo).",
  );
  try {
    await AsyncStorage.setItem(LOCAL_PONTOS_CORRUPT_KEY, raw.slice(0, 512 * 1024));
  } catch {}
  return {};
};

// ─── Aviso de mudança no mapa de pontos ────────────────────────────────────────────────
// A tela de Registro sobrepõe o mapa ao /iniciar para mostrar a batida NA HORA. Só que o
// envio, a cura e a confirmação mudam o mapa em SEGUNDO PLANO (o syncAll que a câmera dispara,
// o agendador, a tarefa do sistema) — e a tela guardava a leitura de quando a câmera voltou:
// "Aguardando envio" numa batida que já estava no banco, até alguém puxar a tela. Toda
// gravação do mapa passa por `gravarMapaPontos`, que avisa quem observa (observarPontosLocais).
// O aviso é agrupado (200 ms): um dreno grava o mapa várias vezes seguidas.
const ouvintesPontosLocais = new Set<() => void>();
let avisoPontosLocaisAgendado: ReturnType<typeof setTimeout> | null = null;
const avisarPontosLocaisMudaram = () => {
  if (ouvintesPontosLocais.size === 0 || avisoPontosLocaisAgendado) return;
  avisoPontosLocaisAgendado = setTimeout(() => {
    avisoPontosLocaisAgendado = null;
    for (const ouvinte of [...ouvintesPontosLocais]) {
      try {
        ouvinte();
      } catch {}
    }
  }, 200);
};

/** Grava o mapa de pontos e avisa quem desenha a partir dele. ÚNICO jeito de gravar a chave. */
const gravarMapaPontos = async (mapa: object): Promise<void> => {
  await AsyncStorage.setItem(LOCAL_PONTOS_KEY, JSON.stringify(mapa));
  avisarPontosLocaisMudaram();
};

// ─── O que o SERVIDOR comprovadamente tem (cache do /iniciar) ──────────────────────────
// Fonte ÚNICA para o reconciliador (dedup, cura e confirmação) e para a conferência local
// logo após o /iniciar — os dois precisam concordar sobre o que é "batida no servidor".

/** Qualquer data → DIA "DD/MM/YYYY" (aceita BR e ISO, com/sem hora). */
const diaDaBatida = (v: any): string => {
  const s = String(v ?? "").trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const br = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  return br ? `${br[1]}/${br[2]}/${br[3]}` : "";
};
const chaveDaBatida = (dia: string, tipo: any) => `${dia}|${Number(tipo)}`;
/** "DD/MM/YYYY" → 20260711 (número comparável, p/ a janela coberta pelo cache). */
const ordemDoDia = (dia: string): number => {
  const m = dia.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  return m ? Number(`${m[3]}${m[2]}${m[1]}`) : 0;
};
/** Linha de EDIÇÃO do gestor (tab_rh_pontos_edicao) — o /iniciar injeta hora1..4 como
 *  pseudo-pontos editado=1 (e id=0). NÃO é batida em tab_rh_pontos. */
const ehEdicaoDoGestor = (sp: any) =>
  sp?.editado === true ||
  Number(sp?.editado) === 1 ||
  sp?.editado === "1" ||
  sp?.editado === "S";

/**
 * Teto do `ultimosPontos` do /iniciar: `LIMIT 50` em cada parte do UNION e no total
 * (t-credentials `buildUltimosPontosSql`, src/start/startSql.js).
 */
const LIMITE_ULTIMOS_PONTOS = 50;

type BatidasGenuinas = {
  /** DIA-competência + tipo de cada batida GENUÍNA do cache. */
  chaves: Set<string>;
  /** Por chave, o instante (ms) de cada batida genuína — ver servidorTemEstaBatida. */
  horarios: Map<string, number[]>;
  /** Por DIA-competência, o instante EXATO (data_log, ao segundo) de cada batida genuína. */
  instantesPorDia: Map<string, Set<number>>;
  /** Menor competência genuína presente (o cache é LIMIT 50) — 0 = nenhuma. */
  diaMaisAntigo: number;
  /**
   * O /iniciar devolveu TODO o histórico (menos linhas que o LIMIT): nenhum dia veio cortado,
   * nem o mais antigo — ver ehCandidataACura.
   */
  historicoCompleto: boolean;
  /** Colaborador/empresa/cliente da SESSÃO (guarda cross-tenant) — 0 = desconhecido. */
  colab: number;
  empresa: number;
  cliente: number;
};

/**
 * Chaves (DIA-competência + tipo) que o SERVIDOR comprovadamente tem — SÓ batida GENUÍNA
 * (linha real de tab_rh_pontos) conta como confirmação:
 *  - injeções locais de merges anteriores carregam `_local: boolean` (true = pendente,
 *    false = synced-ainda-no-mapa). NENHUMA é prova de persistência: um falso-synced
 *    injetado com _local:false virava "confirmação do servidor" e ENGOLIA toda rebatida
 *    do mesmo dia+tipo para sempre (o buraco negro dos "2 checks fantasma");
 *  - linha de edição do gestor (editado=1): RH preencher hora manualmente NÃO pode
 *    suprimir o envio da batida real (foto/geo/NSR);
 *  - id (id_rh_ponto) > 0 obrigatório (linhas de edição vêm com id=0).
 * Sem cache (ou ilegível) → nada comprovado; quem chama segue pelo lado seguro.
 */
const lerBatidasGenuinasDoCache = async (): Promise<BatidasGenuinas> => {
  const r: BatidasGenuinas = {
    chaves: new Set<string>(),
    horarios: new Map<string, number[]>(),
    instantesPorDia: new Map<string, Set<number>>(),
    diaMaisAntigo: 0,
    historicoCompleto: false,
    colab: 0,
    empresa: 0,
    cliente: 0,
  };
  try {
    const ud = await readJSONFile("userData.json");
    r.colab = Number(ud?.idColaborador ?? ud?.id_adm_colaborador ?? ud?.id) || 0;
    r.empresa = Number(ud?.id_adm_empresa) || 0;
    r.cliente = Number(ud?.id_adm_cliente) || 0;
    const serverPontos: any[] = Array.isArray(ud?.ultimosPontos)
      ? ud.ultimosPontos
      : [];
    // Linhas que vieram do SERVIDOR (batidas e edições; sem as injeções locais). Abaixo do
    // LIMIT, a consulta não cortou nada.
    r.historicoCompleto =
      serverPontos.filter((sp) => typeof sp?._local !== "boolean").length <
      LIMITE_ULTIMOS_PONTOS;
    for (const sp of serverPontos) {
      if (typeof sp?._local === "boolean") continue; // injeção local (true OU false)
      if (ehEdicaoDoGestor(sp)) continue; // edição do gestor ≠ batida real
      if (!(Number(sp?.id ?? sp?.id_rh_ponto) > 0)) continue; // sem id real
      const dia = diaDaBatida(sp?.data_competencia) || diaDaBatida(sp?.data);
      if (!dia) continue;
      const chave = chaveDaBatida(dia, sp?.id_rh_ponto_tipo);
      r.chaves.add(chave);
      const ts = pontoParaTimestamp(sp);
      if (Number.isFinite(ts)) {
        const lista = r.horarios.get(chave);
        if (lista) lista.push(ts);
        else r.horarios.set(chave, [ts]);
      }
      // Só o data_log (com segundos) — o fallback data + hora para no minuto.
      const exato = pontoParaTimestamp({ data_log: sp?.data_log });
      if (Number.isFinite(exato)) {
        const doDia = r.instantesPorDia.get(dia);
        if (doDia) doDia.add(exato);
        else r.instantesPorDia.set(dia, new Set([exato]));
      }
      const ord = ordemDoDia(dia);
      if (ord > 0 && (r.diaMaisAntigo === 0 || ord < r.diaMaisAntigo))
        r.diaMaisAntigo = ord;
    }
  } catch {
    // sem cache → segue só com o que é local (o backend é a rede de segurança do dedup)
  }
  return r;
};

/**
 * Tolerância para dizer que a linha do servidor É ESTA batida — e não outra com o mesmo
 * dia de competência e tipo. O app manda o `data_log` e o servidor grava VERBATIM: a mesma
 * batida volta com a MESMA hora; a mesma batida lógica feita em outro aparelho volta
 * minutos depois. Horas de diferença = OUTRA batida (competência errada, batida de outro
 * dia com a mesma chave).
 */
const MESMA_BATIDA_TOLERANCIA_MS = 30 * 60 * 1000;

/**
 * O SERVIDOR tem ESTA batida? Mesmo dia de competência + tipo + hora próxima.
 *
 * Só a chave (dia + tipo) não basta, e foi assim que batida offline sumia: com a
 * competência errada (calculada antes do toque, num dia anterior) ou com outra batida de
 * mesma chave no banco, o "servidor já tem" era FALSO e a batida pendente era dada como
 * enviada SEM ser enviada — e o servidor, que guarda a 1ª batida de cada chave, não tinha
 * nenhum rastro dela. Relato de 01/10/2026: "dias offline, na volta só a batida online sobe".
 * Sem hora para comparar (linha ou batida sem `data_log` legível), vale a regra antiga.
 */
const servidorTemEstaBatida = (
  lp: Pick<LocalPonto, "data" | "data_log" | "id_rh_ponto_tipo"> | null | undefined,
  cache: BatidasGenuinas,
): boolean => {
  const dia = diaDaBatida(lp?.data);
  if (!lp || !dia) return false;
  const lpTs = pontoParaTimestamp({ data_log: lp.data_log });
  // Mesmo dia e MESMO SEGUNDO em qualquer tipo = esta batida, com o tipo trocado pelo gestor
  // (a reordenação mantém o data_log). Duas batidas diferentes no mesmo segundo não existem.
  if (Number.isFinite(lpTs) && cache.instantesPorDia.get(dia)?.has(lpTs))
    return true;
  const chave = chaveDaBatida(dia, lp.id_rh_ponto_tipo);
  if (!cache.chaves.has(chave)) return false;
  const horas = cache.horarios.get(chave) ?? [];
  if (!Number.isFinite(lpTs) || horas.length === 0) return true;
  return horas.some((ts) => Math.abs(ts - lpTs) <= MESMA_BATIDA_TOLERANCIA_MS);
};

// CURA RETROATIVA dos "2 checks fantasma" (ver _reconciliarPontosPendentes): ponto marcado
// sincronizado (offline:false) SEM batida genuína correspondente no servidor.
// Guardas anti-falso-positivo/anti-loop:
//  - só com cache FRESCO (um /iniciar REAL há < 5min — carimbo por tenant);
//  - só com lista genuína não-vazia e competência DENTRO da janela coberta
//    (fora do LIMIT 50 o servidor pode ter o ponto sem ele aparecer no cache) — com o
//    histórico COMPLETO (menos de 50 linhas), a janela é tudo, inclusive o dia mais antigo;
//  - grace de 10min pós-markLocalPontoSynced (propagação Rabbit→MySQL→cache);
//  - no máximo 3 curas por ponto, com 6h entre curas.
const CURA_CACHE_FRESCO_MS = 5 * 60 * 1000;
const CURA_GRACE_POS_SYNC_MS = 10 * 60 * 1000;
const CURA_INTERVALO_MS = 6 * 60 * 60 * 1000;
// Teto POR CICLO: um aparelho com muitos fantasmas acumulados (ex.: backlog histórico)
// não pode despejar tudo num único dreno — o tráfego do ponto é altíssimo e cada reenvio
// custa Rabbit+ODKU no servidor. O restante cura nos próximos drenos.
const CURA_MAX_POR_CICLO = 8;

/** A cura pode agir agora? Cache fresco, tenant da sessão conhecido, lista genuína não vazia. */
const curaPodeAgir = (
  cache: BatidasGenuinas,
  hashSessao: string,
  fetchedAtMs: number | null | undefined,
  agora: number,
): boolean =>
  fetchedAtMs != null &&
  agora - fetchedAtMs < CURA_CACHE_FRESCO_MS &&
  !!hashSessao &&
  cache.chaves.size > 0 &&
  cache.diaMaisAntigo > 0;

/** Batida "enviada" que a cura reverteria para pendente AGORA (critério único). */
/**
 * Hash de tenant como a SESSÃO o tem (`tenantHashSessaoAtual`: sem espaços, MAIÚSCULO).
 * As comparações sobre o MAPA de pontos usavam o carimbo cru: batida carimbada em
 * minúsculas (legado) contava como pendente no "Sair" (que normaliza), mas a reconciliação
 * a tratava como de OUTRA empresa e nunca a reenfileirava — "Sair" bloqueado para sempre
 * com a fila vazia.
 */
const hashNormalizado = (h: unknown): string =>
  String(h ?? "")
    .trim()
    .toUpperCase();

const ehCandidataACura = (
  lp: LocalPonto | null | undefined,
  cache: BatidasGenuinas,
  hashSessao: string,
  agora: number,
): boolean => {
  if (!lp || lp.offline !== false) return false;
  // CONFIRMADA = o /iniciar já devolveu a linha genuína: ela esteve no MySQL. Se sumiu do
  // servidor depois, alguém apagou — reenviar ressuscitaria a batida apagada.
  if (lp.confirmadoEm) return false;
  // Guarda CROSS-TENANT (FAIL-CLOSED): os ids numéricos (colaborador/empresa/cliente) são
  // auto-increment POR BANCO e COLIDEM entre empresas. Só cura ponto CARIMBADO com o hash
  // do tenant ATUAL — sobra de outra empresa (ou ponto legado sem hash) jamais é
  // reativada: reenviá-la iria pro banco errado com o Bearer da sessão atual.
  if (!lp.hash || hashNormalizado(lp.hash) !== hashSessao) return false;
  if (cache.colab > 0 && Number(lp.id_adm_colaborador) !== cache.colab)
    return false;
  const dia = diaDaBatida(lp.data);
  if (!dia) return false;
  // ESTRITAMENTE dentro da janela: o dia-borda (mais antigo do cache) quase nunca vem
  // completo — o LIMIT 50 corta no meio do dia — e curá-lo geraria reenvio indevido.
  // EXCEÇÃO: histórico completo (o /iniciar trouxe menos linhas que o LIMIT) — nada foi
  // cortado, e a batida ausente é ausente mesmo. Sem isto, quem não tinha batida antes dos
  // dias offline nunca recuperava uma batida do 1º dia (o dia-borda), nem de dia anterior a
  // tudo o que o servidor tem.
  if (!cache.historicoCompleto && ordemDoDia(dia) <= cache.diaMaisAntigo)
    return false;
  if (servidorTemEstaBatida(lp, cache)) return false; // está lá
  // Chave ocupada por OUTRA batida (outra hora): o servidor recusa e manda à .dead.final
  // (BatidaEmConflitoException). Uma cura basta para tirar do silêncio uma batida que o
  // servidor antigo descartou; repetir só gera carga. Se a chave ficar livre (o gestor
  // apagou ou reordenou), as curas seguem até o teto.
  if (
    cache.chaves.has(chaveDaBatida(dia, lp.id_rh_ponto_tipo)) &&
    (lp.curas ?? 0) >= 1
  )
    return false;
  if (lp.syncedEm != null && agora - lp.syncedEm < CURA_GRACE_POS_SYNC_MS)
    return false;
  if ((lp.curas ?? 0) >= PONTO_CURA_MAX) return false;
  if (lp.ultimaCuraEm != null && agora - lp.ultimaCuraEm < CURA_INTERVALO_MS)
    return false;
  return true;
};

/** Batida enviada cuja linha GENUÍNA voltou no cache → está no MySQL, ganha `confirmadoEm`. */
const podeConfirmarBatida = (
  lp: LocalPonto | null | undefined,
  cache: BatidasGenuinas,
  hashSessao: string,
): boolean => {
  if (!lp || lp.offline !== false || lp.confirmadoEm) return false;
  if (lp.hash && hashSessao && hashNormalizado(lp.hash) !== hashSessao) return false;
  if (cache.colab > 0 && Number(lp.id_adm_colaborador) !== cache.colab)
    return false;
  return servidorTemEstaBatida(lp, cache);
};

// ─── Espera entre tentativas de ação RECUSADA pelo servidor ─────────────────────────────
// Recusa = a NOSSA API respondeu com erro de negócio (400/401/403/409/422): repetir na hora
// dá o mesmo erro. Rede, timeout e 5xx NÃO contam (`offline: true`) — ali o servidor nem
// recebeu ou está fora, e a cadência é a dos gatilhos.
const ESPERAS_ACAO_RECUSADA_MS = [5 * 60_000, 30 * 60_000, 6 * 60 * 60_000];
// Batida espera MENOS (é o registro mais crítico), mas espera: sem isso, com o agendador e
// a tarefa em segundo plano, uma batida recusada era repetida a cada poucos minutos, 24 h
// por dia, subindo a foto de novo a cada vez (análise de carga de 30/09/2026).
const ESPERAS_PONTO_RECUSADO_MS = [
  2 * 60_000,
  10 * 60_000,
  30 * 60_000,
  2 * 60 * 60_000,
];

/** Ação → `{ n, proximaEm, erro, tipo }` das recusas. Valor ilegível = nenhuma espera. */
const lerEsperasDeAcoes = async (): Promise<Record<string, any>> => {
  try {
    const raw = await AsyncStorage.getItem(ACOES_FALHAS_KEY);
    const p = raw ? JSON.parse(raw) : {};
    return p && typeof p === "object" && !Array.isArray(p) ? p : {};
  } catch {
    return {};
  }
};

const acaoEmEspera = (
  esperas: Record<string, any>,
  id: string,
  agora: number,
): boolean => (Number(esperas?.[id]?.proximaEm) || 0) > agora;

/** Conta mais uma recusa da ação e devolve a espera aplicada. Só o dreno chama (é único). */
const registrarRecusaDaAcao = async (
  action: OfflineAction,
  erro: any,
  esperas: number[],
): Promise<{ n: number; espera: number }> => {
  const mapa = await lerEsperasDeAcoes();
  const n = (Number(mapa[action.id]?.n) || 0) + 1;
  const espera = esperas[Math.min(n - 1, esperas.length - 1)];
  mapa[action.id] = {
    n,
    proximaEm: Date.now() + espera,
    erro: String(erro ?? "").slice(0, 200),
    tipo: action.type,
  };
  const chaves = Object.keys(mapa);
  if (chaves.length > 200) {
    for (const k of chaves.slice(0, chaves.length - 200)) delete mapa[k];
  }
  await AsyncStorage.setItem(ACOES_FALHAS_KEY, JSON.stringify(mapa));
  return { n, espera };
};

/**
 * Execução de checklist avulsa registrada neste aparelho. `offline` segue a MESMA
 * convenção do LocalPonto: true = ainda na fila; false = o T-ARC aceitou o Salvar.
 * A entrada some do mapa quando a execução volta confirmada do servidor.
 */
export interface LocalChecklistExecucao {
  id: string;
  /** Hash do tenant dono da execução — ids são auto-increment por banco e colidem. */
  hash?: string;
  checklistId: number;
  checklistNome: string;
  colaboradorId: number;
  /** ISO enviado como TempoInicio — é a chave que casa com o tempo_inicio do servidor. */
  tempoInicio: string;
  /** De onde veio: menu (livre) ou batida de ponto (com o tipo). */
  origem: "LIVRE" | "PONTO";
  tipoBatida?: string | null;
  latitude?: string | null;
  longitude?: string | null;
  /** Respostas em formato de exibição, p/ reabrir em leitura mesmo sem rede. */
  respostas: {
    perguntaId: number;
    resposta: string | null;
    foto?: string | null;
  }[];
  offline: boolean;
  /** Date.now() de quando virou offline:false (grace de propagação até o servidor). */
  syncedEm?: number;
  timestamp: number;
}

type LocalChecklistExecucoesMap = Record<string, LocalChecklistExecucao>;

/**
 * Complemento de uma execução existente. `offline` segue a mesma convenção dos demais
 * espelhos locais: true = na fila; false = o T-ARC aceitou.
 */
export interface LocalChecklistComplemento {
  idExecucao: number;
  hash?: string;
  /** Perguntas respondidas no complemento — é por elas que sabemos que ele chegou lá. */
  perguntaIds: number[];
  /**
   * Para que serve o rastro. COMPLEMENTO (padrão, e o que registros antigos são) alimenta
   * o selo "Complementar respondida"; EDICAO é a MESMA rota de upsert usada para editar
   * uma execução — sem essa marca, editar um checklist acendia o selo de complementar
   * mesmo num checklist que não tem seção complementar nenhuma.
   */
  tipo?: "COMPLEMENTO" | "EDICAO";
  /**
   * As respostas enviadas, para a tela mostrar o valor NOVO enquanto o servidor ainda
   * devolve o antigo (200 do T-ARC = enfileirado, não gravado). Só as textuais têm uso
   * aqui — foto vira linha no servidor depois do upload.
   */
  respostas?: { PerguntaId: number; Resposta?: string; Foto?: string }[];
  offline: boolean;
  syncedEm?: number;
  timestamp: number;
}

type LocalChecklistComplementosMap = Record<string, LocalChecklistComplemento>;

/**
 * Complemento respondido antes de existir alvo (ver LOCAL_CHECKLIST_COMPL_PENDENTE_KEY).
 * É AUTOSSUFICIENTE de propósito: a execução local some do espelho assim que o servidor
 * a confirma, então tudo que o vínculo precisa mora aqui.
 */
export type LocalChecklistComplPendente = {
  localExecId: string;
  /** Chave natural (checklistId|tempoInicio) — é por ela que o servidor é encontrado. */
  chaveNatural: string;
  checklistId: number;
  tempoInicio: string;
  /** Do espelho LOCAL: o servidor pode não devolver `responsavelId` (backend antigo). */
  responsavelId: number;
  respostas: { PerguntaId: number; Resposta?: string; Foto?: string }[];
  assets: any[];
  perguntaIds: number[];
  latitude?: string;
  longitude?: string;
  hash?: string;
  criadoEm: number;
  /** Reserva de envio em curso (lease com TTL) — evita duas passadas enviarem juntas. */
  enviandoDesde?: number;
  tentativas?: number;
};

/**
 * Campos exclusivos do ATESTADO MÉDICO. Presentes ⇒ a solicitação é um atestado, e
 * a `data_competencia` do payload é o 1º dia dele.
 *
 * Tudo string, vazio quando não se aplica — é o contrato do T-ARC/T-Consumer, cujos
 * eventos são todos `string?` (um número JSON num `string?` estoura o desserializador
 * e manda a mensagem inteira para o parking-lot).
 */
export interface AtestadoPayload {
  /** 'C' comparecimento (usa hora_inicio/hora_fim) · 'I' integral (usa qtd_dias/data_fim). */
  tipo_atestado: "C" | "I";
  qtd_dias: string; // "3" — só integral
  data_fim: string; // "YYYY-MM-DD" — só integral (calculada no app, gravada para o RH não recalcular)
  cid: string; // "M54.5" — pode ficar vazio
  hora_inicio: string; // "HH:MM" — só comparecimento
  hora_fim: string; // "HH:MM" — só comparecimento
}

/** Códigos do `tipo_abono` no banco (ENUM). Rótulos em `ROTULOS_TIPO_ABONO`. */
export type TipoAbono = "CH" | "LM" | "LG" | "LP" | "LN" | "OU";
export const ROTULOS_TIPO_ABONO: Record<TipoAbono, string> = {
  CH: "Compensação de horas",
  LM: "Licença maternidade",
  LG: "Licença gala",
  LP: "Licença paternidade",
  LN: "Licença nojo",
  OU: "Outros",
};

/**
 * Solicitação de ABONO (12/09/2026) — o terceiro tipo na mesma tabela e no mesmo
 * caminho da justificativa/atestado. `tipo_abono` preenchido é o que marca a linha.
 * Mesmo contrato de strings do `AtestadoPayload` (vazio quando não se aplica).
 */
export interface AbonoPayload {
  tipo_abono: TipoAbono;
  /** 'I' dia todo (usa data_fim/qtd_dias) · 'P' parcial (usa hora_inicio/hora_fim). */
  periodo_abono: "I" | "P";
  /** Texto livre quando `tipo_abono === "OU"`; vazio nos demais. */
  descricao_abono: string;
  qtd_dias: string; // "3" — só dia todo (fim − início + 1)
  data_fim: string; // "YYYY-MM-DD" — só dia todo
  hora_inicio: string; // "HH:MM" — só parcial
  hora_fim: string; // "HH:MM" — só parcial
}

export interface JustificativaPayload {
  data_competencia: string; // DD/MM/YYYY
  id_adm_colaborador: number;
  punchesAtual: (string | null)[]; // até 8 — horários atuais (em HH:MM)
  punchesAjuste: (string | null)[]; // até 8 — horários ajustados (em HH:MM)
  motivo: string;
  files: { uri: string; name: string; mimeType?: string }[];
  data_log?: string; // hora da SUBMISSÃO (BRT, "YYYY-MM-DD HH:mm:ss") — carimbada no envio e persistida na fila
  /** Só no atestado médico. Ausente = correção/abono como sempre. */
  atestado?: AtestadoPayload;
  /** Só na solicitação de abono (tela própria). */
  abono?: AbonoPayload;
}

/**
 * Solicitação de TROCA DE PLANTÃO (22/10/2026) — só escala 12x36/24x72
 * (`jornada.is1236`/`jornada.is2472` = 'S'). O substituto cobre o plantão de
 * `data_troca`; o solicitante devolve em `data_pagamento`. Vai para aprovação como
 * a correção de ponto.
 */
export interface TrocaPlantaoPayload {
  id_adm_colaborador: number;
  id_adm_colaborador_substituto: number;
  data_troca: string; // "YYYY-MM-DD"
  data_pagamento: string; // "YYYY-MM-DD"
  /** Na FILA: caminho do PNG (ver desidratarFotosLivro). No ENVIO: data URI base64. */
  assinatura: string;
  data_log?: string; // hora da SOLICITAÇÃO (BRT) — carimbada no toque, preservada na fila
  hash?: string;
}

/** Substituto possível, já minimizado pelo servidor (LGPD). */
export interface TrocaPlantaoSubstituto {
  id_adm_colaborador: number;
  matricula: string;
  primeiro_nome: string;
  /** Só a 1ª letra do sobrenome (LGPD) — "" quando o nome tem uma palavra só. */
  inicial_sobrenome: string;
}

/** Espelho local de uma troca solicitada neste aparelho (chave = data_troca). */
export interface LocalTrocaPlantao {
  data_troca: string; // "YYYY-MM-DD"
  data_pagamento: string; // "YYYY-MM-DD"
  /** Rótulo já mascarado ("1234 - JOSÉ S*****"), o mesmo que a pessoa viu ao escolher. */
  substituto: string;
  /** true = ainda na fila; false = o servidor recebeu. */
  offline: boolean;
  timestamp: number;
}

/**
 * Troca que EU pedi, com o status das duas etapas (rota `minhas`): primeiro o colega
 * (`aceito_substituto`), depois o administrador (`aceito`) — 'P' pendente, 'S' aceita,
 * 'N' recusada.
 */
export interface TrocaPlantaoMinha {
  id_rh_troca_plantao_app: number;
  data_troca: string; // "YYYY-MM-DD"
  data_pagamento: string; // "YYYY-MM-DD"
  /** Rótulo mascarado do colega ("1234 - JOSÉ S*****"). */
  substituto: string;
  aceito_substituto: string;
  observacao_substituto: string;
  aceito: string;
  /** Administrador que decidiu ("" enquanto pendente). */
  nome_validador: string;
  observacao_validacao: string;
  /** Horas de cada etapa ("YYYY-MM-DD HH:mm:ss", "" = ainda não) — a linha do tempo da troca. */
  data_log?: string;
  data_log_substituto?: string;
  data_log_validacao?: string;
}

/**
 * Troca no histórico, dos DOIS lados (rota `historico`): `papel` 'S' = eu pedi (o colega cobre o
 * meu plantão em `data_troca` e eu cubro o dele em `data_pagamento`); 'C' = eu cubro (o contrário).
 * Mesmo status da `TrocaPlantaoMinha`: colega, depois administrador.
 */
export interface TrocaPlantaoHistorico {
  id_rh_troca_plantao_app: number;
  papel: "S" | "C";
  data_troca: string; // "YYYY-MM-DD"
  data_pagamento: string; // "YYYY-MM-DD"
  /** O OUTRO lado, curto e minimizado ("LUIZ G."). */
  colega: string;
  aceito_substituto: string;
  observacao_substituto: string;
  aceito: string;
  nome_validador: string;
  observacao_validacao: string;
  /** "YYYY-MM-DD HH:mm:ss" do pedido — a linha do tempo da tela "Troca de Plantão". */
  data_log?: string;
  /** "YYYY-MM-DD HH:mm:ss" de quando o colega aceitou ou recusou ("" enquanto não respondeu). */
  data_log_substituto?: string;
  /** "YYYY-MM-DD HH:mm:ss" da decisão do supervisor ("" enquanto não decidiu). */
  data_log_validacao?: string;
}

/**
 * Recibo de troca ACEITA que o supervisor emitiu no TirvuAdm (rota `recibos`): o PDF fica no
 * storage e aqui vem o link. Os dois lados da troca veem o mesmo recibo.
 */
export interface TrocaPlantaoRecibo {
  id_rh_troca_plantao_app: number;
  papel: "S" | "C";
  data_troca: string; // "YYYY-MM-DD"
  data_pagamento: string; // "YYYY-MM-DD"
  /** O OUTRO lado, curto e minimizado ("LUIZ G."). */
  colega: string;
  /** Link público do PDF. Emitir de novo gera outro link. */
  endereco_recibo: string;
  /** "YYYY-MM-DD HH:mm:ss" da emissão. */
  data_log_recibo: string;
}

/** Troca que pediram para EU cobrir e que espera a minha resposta (pop-up). */
export interface TrocaPlantaoRecebida {
  id_rh_troca_plantao_app: number;
  data_troca: string; // "YYYY-MM-DD"
  data_pagamento: string; // "YYYY-MM-DD"
  /** Rótulo mascarado de quem pediu ("1234 - JOSÉ S*****"). */
  solicitante: string;
}

/** Resposta do colega à troca: aceita (com assinatura) ou recusa. */
export interface TrocaPlantaoRespostaPayload {
  id_rh_troca_plantao_app: number;
  aceito: "S" | "N";
  /** Só ao aceitar. Na FILA: caminho do PNG. No ENVIO: data URI base64. */
  assinatura: string;
  data_log?: string; // hora do TOQUE (BRT), preservada na fila
  hash?: string;
}

/**
 * Cancelamento de uma solicitação PENDENTE pelo próprio colaborador (11/09/2026).
 * No servidor é só `aceito 'P' → 'C'` (+ data/observação de validação) na linha que
 * casar — nada é apagado. `data_log` identifica a solicitação (é a chave natural que o
 * T-Consumer já usa); vazio = "a pendente daquele dia e tipo".
 */
export interface JustificativaCancelarPayload {
  id_adm_colaborador: number;
  data_competencia: string; // DD/MM/YYYY
  /** `data_log` da solicitação a cancelar ("YYYY-MM-DD HH:mm:ss"), ou "" se desconhecido. */
  data_log: string;
  /** 'C'/'I' quando é atestado, "" quando é correção — escopo do cancelamento (como no INSERT). */
  tipo_atestado: string;
  /** Código do abono ('CH'…) quando é solicitação de abono, "" senão — o outro eixo do escopo. */
  tipo_abono?: string;
  /** Instante do cancelamento (BRT) — carimbado no toque e preservado na fila. */
  data_cancelamento?: string;
  hash?: string;
}

export interface OfflineAction {
  id: string;
  /** Hash do TENANT dono da ação (carimbado no enqueue a partir do claim c_hash
   *  do token). O dreno SÓ despacha a ação quando a sessão atual é do mesmo
   *  tenant — divergiu (trocarEmpresa no meio), a ação fica retida na fila até
   *  a sessão certa voltar. Ausente = ação legada (fail-open, envia como antes). */
  tenantHash?: string;
  type:
    | "SIGNATURE"
    | "PONTO"
    | "JUSTIFICATIVA"
    | "JUSTIFICATIVA_CANCELAR"
    | "DADOS_COLABORADOR"
    | "OS_FOTO_SAVE"
    | "OS_FOTO_DELETE"
    | "OS_ANOTACAO_SAVE"
    | "OS_INICIAR"
    | "OS_CONCLUIR"
    | "OS_SOBDEMANDA_SALVAR"
    | "CHECKLIST_SALVAR"
    | "ENQUETE_SALVAR"
    | "RONDA_INICIAR"
    | "RONDA_SALVAR_PONTO"
    | "RONDA_FINALIZAR"
    | "RONDA_CANCELAR"
    | "TREINAMENTO_LOG_ASSISTIDO"
    | "TREINAMENTO_LOG_ASSISTIDO_PARCIAL"
    | "TREINAMENTO_AVALIACAO"
    | "ESTOQUE_PEDIDO_SALVAR"
    | "ESTOQUE_PEDIDO_EDITAR"
    | "ESTOQUE_PEDIDO_EXCLUIR"
    | "LIVRO_INICIAR"
    | "LIVRO_OCORRENCIA"
    | "LIVRO_FINALIZAR"
    | "DOCUMENTO_CONTESTAR"
    | "CONTESTACAO_VISTA"
    | "NOTIFICACOES_LIDAS"
    | "TROCA_PLANTAO"
    | "TROCA_PLANTAO_RESPOSTA";
  data: any;
  timestamp: number;
  /** Quando a ação entrou na fila pela 1ª vez — só existe se ela já foi SUBSTITUÍDA
   *  (mesmo id), o que renova `timestamp`. Usado na idade da pendência (aviso de sync). */
  criadoEm?: number;
}

const _cleanOsSobDemandaPayload = (data: any) => {
  if (!data || typeof data !== "object") return data;
  const {
    __temp_cronograma_id,
    __cliente_label,
    __equipe_label,
    __local,
    ...rest
  } = data as any;
  return rest;
};

const _isTempCronogramaId = (value: any) => {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) && n < 0;
};

export interface DadosColaboradorPayload {
  codigoColaborador: string;
  tipoColaborador: string;
  contato: {
    celular: string;
    email: string;
  };
  dadosBancarios: {
    hasPix: boolean;
    tipo_chave_pix?: string;
    chave_pix?: string;
    banco?: string;
    bancoId?: any;
    agencia?: string;
    conta?: string;
    titular?: string;
    documento?: string;
  };
}

export interface SalvarDadosResult {
  success: boolean;
  offline: boolean;
  erros?: string[];
}

/** Normaliza qualquer formato de data para DD/MM/YYYY (usado internamente no Sync) */
const _normDate = (raw: string): string => {
  if (!raw) return "";
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return raw.slice(0, 10);
};

const _parseBrDateTimeToMs = (raw: any): number | null => {
  if (raw == null) return null;
  if (raw instanceof Date) {
    const ms = raw.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof raw !== "string") return null;

  const s = raw.trim();
  if (!s) return null;

  const m = s.match(
    /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?/,
  );
  if (m) {
    const day = Number(m[1]);
    const month = Number(m[2]);
    const year = Number(m[3]);
    const hour = Number(m[4] ?? 0);
    const minute = Number(m[5] ?? 0);
    const second = Number(m[6] ?? 0);
    const ms = new Date(
      year,
      month - 1,
      day,
      hour,
      minute,
      second,
      0,
    ).getTime();
    return Number.isFinite(ms) ? ms : null;
  }

  const ms = new Date(s).getTime();
  return Number.isFinite(ms) ? ms : null;
};

/**
 * Tenant DONO da sessão atual (guarda cross-tenant): claim `c_hash` do JWT —
 * o MESMO valor que o T-ARC usa para rotear a escrita, então nunca defasa em
 * relação ao destino real. Fallback: cache do /iniciar (empresaHashAtual) para
 * sessão sem token legível. Sempre normalizado (trim+UPPERCASE).
 * Contexto (incidente 21-23/07): colaborador com vínculo em DUAS empresas
 * bateu ponto offline na empresa A, trocou de empresa no app, e o dreno
 * despachou a fila com o token da empresa B → registro (e foto!) gravados no
 * tenant errado, em cima de OUTRA pessoa com o mesmo id numérico (ids
 * auto-increment COLIDEM entre bancos).
 */
const tenantHashSessaoAtual = async (): Promise<string> => {
  const doToken = await tenantHashDaSessao().catch(() => "");
  if (doToken) return doToken;
  const doCache = await empresaHashAtual().catch(() => "");
  return String(doCache ?? "")
    .trim()
    .toUpperCase();
};

/**
 * Carimba o hash do TENANT dono no PRÓPRIO objeto de payload (in-place), na
 * FUNÇÃO DE ORIGEM de cada fluxo — assim o hash viaja no envio IMEDIATO e no
 * enfileirado/dreno (spreads preservam). Regra do produto (30/07): "para
 * sincronização, sempre enviar o hash/tenant pertinente ao registro". Campo
 * extra é ignorado pelo model binder do T-ARC hoje; habilita a validação 409
 * (padrão do PONTO) domínio a domínio no futuro. Não sobrescreve hash já
 * presente (ex.: replay do dreno com o carimbo do dono).
 */
const carimbarHashTenant = async (obj: any): Promise<void> => {
  try {
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) return;
    if (String(obj.hash ?? "").trim() !== "") return;
    const h = await tenantHashSessaoAtual().catch(() => "");
    if (h) obj.hash = h;
  } catch {}
};

// ── Chave de storage ESCOPADA por TENANT ─────────────────────────────────────
// `base#HASH` — isolamento mecânico dos ESPELHOS locais de UI entre empresas
// (ids numéricos colidem entre bancos; trocar de empresa exibia justificativa/
// livro/OS/doc da outra). A FILA offline e o MAPA de pontos NÃO usam isto: são
// globais com carimbo POR REGISTRO (é o que permite reter ação de outro tenant
// e entregá-la quando a empresa dona voltar). Sem hash de sessão → chave crua
// (fail-open = comportamento antigo).
// MIGRAÇÃO única por chave: na 1ª leitura escopada, valor na chave CRUA (legado
// pré-namespace) é ADOTADO pelo tenant atual e a crua é removida — o legado já
// era misto de qualquer forma; adotar preserva o que o usuário via.
const _chavesMigradas = new Set<string>();

// Chaves de CORRELAÇÃO local→real (livro / OS sob demanda): as chaves internas
// são timestamps/temp-ids gerados NO APARELHO (>1e11) e NÃO colidem entre
// bancos. São COPIADAS na migração (nunca movidas): mover levaria os
// mapeamentos do tenant A para o primeiro tenant que lesse a chave, e as ações
// retidas de A ficariam sem id-base para sempre (o dreno as devolveria à fila
// eternamente = registro nunca entregue + pendência travando o logout).
const _CHAVES_MIGRACAO_COPIA = new Set<string>([
  "@TirvuMais:livro_id_map",
  "@TirvuMais:local_os_sobdemanda_idmap",
]);

// Última resolução de cada base + a GERAÇÃO de sessão do momento — é o que
// permite ao setItemEscopado detectar que a leitura do RMW aconteceu numa
// sessão e a escrita está acontecendo em outra (trocarEmpresa no meio).
const _ultimaResolucao = new Map<string, { gen: any; chave: string }>();

const chaveTenant = async (base: string): Promise<string> => {
  const h = await tenantHashSessaoAtual().catch(() => "");
  if (!h) {
    _ultimaResolucao.set(base, { gen: peekApiSessionGen(), chave: base });
    return base;
  }
  const escopada = `${base}#${h}`;
  _ultimaResolucao.set(base, { gen: peekApiSessionGen(), chave: escopada });
  if (!_chavesMigradas.has(escopada)) {
    try {
      const legado = await AsyncStorage.getItem(base);
      if (legado != null) {
        const jaTem = await AsyncStorage.getItem(escopada);
        if (jaTem == null) {
          await AsyncStorage.setItem(escopada, legado);
          console.warn(
            `[Sync] 🔒 Migração de espelho local para o tenant atual: ${base}`,
          );
        }
        if (!_CHAVES_MIGRACAO_COPIA.has(base)) {
          await AsyncStorage.removeItem(base);
        }
      }
      // Marca só APÓS o sucesso: se o storage falhar, a próxima chamada tenta de
      // novo (antes o catch vazio deixava o legado órfão para sempre).
      _chavesMigradas.add(escopada);
    } catch {}
  }
  return escopada;
};

/**
 * setItem ESCOPADO com guarda de sessão — usado por TODA escrita de espelho.
 * O read-modify-write resolve a chave duas vezes (getter e setter); se um
 * trocarEmpresa acontecer entre as duas, o espelho INTEIRO do tenant A seria
 * gravado na chave do tenant B (contaminação + clobber do que B já tinha).
 * Aqui comparamos a geração de sessão da ÚLTIMA resolução desta base (a da
 * leitura) com a atual: divergiu → aborta a gravação.
 */
const setItemEscopado = async (
  base: string,
  valor: string,
  chaveCapturada?: string,
): Promise<void> => {
  // Caminho CORRETO: o chamador capturou a chave junto com a LEITURA e grava
  // nela — o dado é do tenant que foi lido, então é lá que ele pertence, mesmo
  // que a sessão tenha trocado no meio.
  if (chaveCapturada) {
    await AsyncStorage.setItem(chaveCapturada, valor);
    return;
  }
  // Sem chave capturada (escrita solta): guarda por geração de sessão. É uma
  // rede parcial — se um fluxo da sessão NOVA resolver a mesma base no meio, as
  // gerações coincidem e a gravação passa. Prefira mutarEspelhoTenant ou
  // capture a chave na leitura.
  const anterior = _ultimaResolucao.get(base);
  const genAtual = peekApiSessionGen();
  if (anterior && anterior.gen !== genAtual) {
    console.warn(
      `[Sync] 🔒 Sessão trocou entre a leitura e a escrita de ${base} — gravação abortada (não contaminar o tenant novo).`,
    );
    _ultimaResolucao.delete(base);
    return;
  }
  await AsyncStorage.setItem(await chaveTenant(base), valor);
};

/**
 * READ-MODIFY-WRITE de espelho, ATÔMICO quanto ao tenant: resolve a chave UMA
 * vez, lê e grava NA MESMA chave. É a única forma correta — o `setItemEscopado`
 * (que re-resolve no fim) pode gravar o espelho do tenant A dentro da chave do
 * B se um trocarEmpresa acontecer no meio, e a guarda por geração de sessão não
 * cobre: o registro de resolução é global por base e uma LEITURA da sessão nova
 * o sobrescreve, fazendo as gerações coincidirem.
 * Serializado por base (fila de promessas) para dois RMW concorrentes da mesma
 * chave não se sobrescreverem. Mutador SÍNCRONO; devolver `undefined` aborta.
 */
const _filaEspelho = new Map<string, Promise<void>>();
const mutarEspelhoTenant = async (
  base: string,
  mutar: (atual: any) => any,
): Promise<void> => {
  const anterior = _filaEspelho.get(base) ?? Promise.resolve();
  const execucao = anterior
    .catch(() => {})
    .then(async () => {
      const chave = await chaveTenant(base);
      let atual: any = null;
      try {
        const raw = await AsyncStorage.getItem(chave);
        atual = raw ? JSON.parse(raw) : null;
      } catch {}
      const proximo = mutar(atual);
      if (proximo === undefined) return;
      await AsyncStorage.setItem(chave, JSON.stringify(proximo));
    });
  _filaEspelho.set(base, execucao);
  try {
    await execucao;
  } finally {
    if (_filaEspelho.get(base) === execucao) _filaEspelho.delete(base);
  }
};

// Memo curto (30s) do POST /ronda/carregar usado pelo resolvedor de grupos órfãos
// do dreno: a resposta é por-JWT (sem body), então UMA chamada cobre todos os
// grupos do mesmo dreno. Invalidado por troca de sessão (gen) ou por idade.
let _rondaCarregarMemo: { gen: any; ts: number; done: any } = {
  gen: null,
  ts: 0,
  done: null,
};

/**
 * POST /ronda/carregar com memo de 30s + guarda de sessão (gen). Devolve o done
 * ({ ronda, rondaHistorico }) APENAS quando a resposta é REAL e da sessão atual;
 * null em falha/troca de sessão no meio (o chamador trata como transitório).
 */
const lerRondaCarregarComMemo = async (): Promise<any | null> => {
  try {
    const gen = peekApiSessionGen();
    if (
      _rondaCarregarMemo.done &&
      _rondaCarregarMemo.gen === gen &&
      Date.now() - _rondaCarregarMemo.ts < 30000
    ) {
      return _rondaCarregarMemo.done;
    }
    const resp = await Api.tcredentials.rondaCarregar();
    if (
      peekApiSessionGen() === gen &&
      resp?.success &&
      Array.isArray(resp?.done?.rondaHistorico)
    ) {
      _rondaCarregarMemo = { gen, ts: Date.now(), done: resp.done };
      return resp.done;
    }
    return null;
  } catch {
    return null;
  }
};

export const SyncService = {
  _isEnfileiradoResponse: (response: any): boolean => {
    if (!response?.success) return false;
    const done = response?.done;

    if (done == null) return true;

    if (typeof done === "boolean") return done;

    if (typeof done === "string") {
      const s = done.trim().toLowerCase();
      if (!s) return true;
      return s === "true" || s === "ok" || s.includes("enfile");
    }

    if (Array.isArray(done)) {
      return done.length > 0;
    }

    if (typeof done === "object") {
      const payloadSuccess =
        (done as any).sucesso ?? (done as any).success ?? (done as any).Sucesso;
      if (payloadSuccess === false) return false;

      const payloadErrors =
        (done as any).erros ?? (done as any).errors ?? (done as any).Erros;
      if (Array.isArray(payloadErrors) && payloadErrors.length > 0)
        return false;

      const enfileirado =
        (done as any).enfileirado ??
        (done as any).Enfileirado ??
        (done as any).done?.enfileirado ??
        (done as any).done?.Enfileirado;

      if (typeof enfileirado === "boolean") return enfileirado;
      if (typeof enfileirado === "string") {
        const s = enfileirado.trim().toLowerCase();
        if (!s) return true;
        return s === "true" || s === "s" || s.includes("true");
      }
      if (enfileirado == null) return true;
      return Boolean(enfileirado);
    }

    return true;
  },

  // ─── Livro de Ocorrência (enfileira SEMPRE primeiro; tenta online e remove da fila) ──
  /**
   * Só ENFILEIRA o início — sem tocar na rede. É o passo que torna o registro DURÁVEL,
   * e é rápido: a tela pode liberar o usuário (mensagem + card) assim que ele volta.
   */
  livroIniciarEnfileirar: async (
    payload: any,
  ): Promise<{ enqueuedId: string | null; localId: number }> => {
    await carimbarHashTenant(payload);
    const localId = Number(payload?._localId ?? 0);
    // Enfileira SEMPRE (com o _localId para correlação posterior).
    // As fotos vão para a fila como CAMINHO — o arquivo é gravado AGORA, antes do
    // enqueue: a linha de livro é escrita uma única vez e nunca reescrita, então não
    // existe segunda chance de consertar uma referência quebrada.
    // `desidratarFotosLivro` devolve objeto novo e preserva o `hash` do carimbo de tenant,
    // que o `enqueueOfflineAction` lê do próprio payload.
    const paraFila = await SyncService.desidratarFotosLivro(payload);
    const enqueuedId = await SyncService.enqueueOfflineAction(
      "LIVRO_INICIAR",
      paraFila,
    );
    return { enqueuedId, localId };
  },

  /** Inicia o plantão/livro. Enfileira na hora; tenta enviar online e remove da fila em sucesso. */
  livroIniciar: async (
    payload: any,
  ): Promise<{
    success: boolean;
    offline: boolean;
    id?: number;
  }> => {
    const { enqueuedId, localId } =
      await SyncService.livroIniciarEnfileirar(payload);
    return SyncService.livroIniciarEnviar(payload, enqueuedId, localId);
  },

  /**
   * Tentativa ONLINE do início, já enfileirado. Separada do enfileiramento porque a tela
   * precisa liberar o usuário assim que o registro está DURÁVEL (na fila) — esperar a
   * rede para só então mostrar "plantão iniciado" fazia a mensagem e o card demorarem
   * segundos, mesmo com tudo já salvo no aparelho.
   *
   * Falhar aqui não é problema: a ação continua na fila e o dreno reenvia.
   */
  livroIniciarEnviar: async (
    payload: any,
    enqueuedId: string | null,
    localId: number,
  ): Promise<{ success: boolean; offline: boolean; id?: number }> => {
    // Dreno rodando (ou esta ação já em voo) → não tenta: a linha ainda está na fila e
    // enviar aqui duplicaria o plantão. Ver `comDespachoDireto`.
    const r = await comDespachoDireto(enqueuedId, async () => {
      try {
        const isConnected = await SyncService.checkInternet();

        // Prepara o payload para envio: resolve id local→real (edições) e remove campos
        // internos. `hidratarFotosLivro` é idempotente — base64 passa direto —, então serve
        // tanto para o payload da tela quanto para um que já venha com caminho de arquivo.
        const enviar: any = await SyncService.hidratarFotosLivro(payload);
        delete enviar._localId;
        if (Number(enviar.id_adm_cliente_livro_ocorrencia) > 0) {
          enviar.id_adm_cliente_livro_ocorrencia =
            await SyncService.resolveLivroId(
              enviar.id_adm_cliente_livro_ocorrencia,
            );
        }
        const aindaLocal = SyncService._isLocalLivroId(
          Number(enviar.id_adm_cliente_livro_ocorrencia),
        );

        if (isConnected && !aindaLocal) {
          const response = await Api.livroOcorrencia.salvarInicioLivro(enviar);
          if (response?.success) {
            const realId = SyncService._extractLivroId(response);
            if (realId > 0 && localId > 0)
              await SyncService.mapLivroIdLocalParaReal(localId, realId);
            if (enqueuedId) await SyncService.dequeueOfflineAction(enqueuedId);
            return { success: true, offline: false, id: realId };
          }
        }
      } catch (error) {
        console.warn(
          "[Sync] livroIniciar: online falhou (já enfileirado):",
          error,
        );
      }
      return { success: true, offline: true };
    });
    // Sem lastro na fila E sem confirmação do servidor = NÃO está salvo. Anunciar
    // sucesso aqui é a pior mentira possível num app offline-first: o usuário vai embora
    // achando que registrou. (`enqueuedId` nulo = a escrita na fila falhou.)
    if (!enqueuedId && (!r || r.offline))
      return { success: false, offline: true };
    return r ?? { success: true, offline: true };
  },

  /** Salva uma ocorrência no livro ativo. Enfileira na hora; tenta online e remove da fila em sucesso. */
  livroSalvarOcorrencia: async (
    payload: any,
  ): Promise<{
    success: boolean;
    offline: boolean;
  }> => {
    await carimbarHashTenant(payload);
    // Fotos para a fila como caminho de arquivo — ver `desidratarFotosLivro`.
    const paraFila = await SyncService.desidratarFotosLivro(payload);
    const enqueuedId = await SyncService.enqueueOfflineAction(
      "LIVRO_OCORRENCIA",
      paraFila,
    );
    // Mesma trava do início: o dreno não pode postar a mesma ocorrência em paralelo.
    const r = await comDespachoDireto(enqueuedId, async () => {
      try {
        const isConnected = await SyncService.checkInternet();
        const resolvedId = await SyncService.resolveLivroId(
          payload?.id_adm_cliente_livro_ocorrencia,
        );
        // Só envia online quando o livro-base já existe no servidor (id real)
        if (isConnected && !SyncService._isLocalLivroId(resolvedId)) {
          const enviar = await SyncService.hidratarFotosLivro(payload);
          const response = await Api.livroOcorrencia.salvarOcorrencia({
            ...enviar,
            id_adm_cliente_livro_ocorrencia: resolvedId,
          });
          if (response?.success) {
            if (enqueuedId) await SyncService.dequeueOfflineAction(enqueuedId);
            return { success: true, offline: false };
          }
        }
      } catch (error) {
        console.warn(
          "[Sync] livroSalvarOcorrencia: online falhou (já enfileirado):",
          error,
        );
      }
      return { success: true, offline: true };
    });
    // Sem lastro na fila E sem confirmação do servidor = NÃO está salvo. Anunciar
    // sucesso aqui é a pior mentira possível num app offline-first: o usuário vai embora
    // achando que registrou. (`enqueuedId` nulo = a escrita na fila falhou.)
    if (!enqueuedId && (!r || r.offline))
      return { success: false, offline: true };
    return r ?? { success: true, offline: true };
  },

  /** Finaliza/encerra o plantão. Enfileira na hora; tenta online e remove da fila em sucesso. */
  livroFinalizar: async (
    payload: any,
  ): Promise<{
    success: boolean;
    offline: boolean;
  }> => {
    await carimbarHashTenant(payload);
    const livroId = Number(payload?.id_adm_cliente_livro_ocorrencia ?? 0);
    console.log("[LIVRO-DBG] FINALIZAR start livroId=", livroId);
    if (livroId) await SyncService.markLivroFinalizadoLocal(livroId);
    // Assinatura para a fila como caminho de arquivo — ver `desidratarFotosLivro`.
    const paraFila = await SyncService.desidratarFotosLivro(payload);
    const enqueuedId = await SyncService.enqueueOfflineAction(
      "LIVRO_FINALIZAR",
      paraFila,
    );
    // Mesma trava do início: finalização não pode sair em duplicata pelo dreno.
    const r = await comDespachoDireto(enqueuedId, async () => {
      try {
        const isConnected = await SyncService.checkInternet();
        const resolvedId = await SyncService.resolveLivroId(livroId);
        const finIsLocal = SyncService._isLocalLivroId(resolvedId);
        console.log(
          "[LIVRO-DBG] FINALIZAR isConnected=",
          isConnected,
          "resolvedId=",
          resolvedId,
          "isLocal=",
          finIsLocal,
        );
        if (isConnected && !finIsLocal) {
          console.log("[LIVRO-DBG] FINALIZAR chamando finalizarLivro...");
          const enviar = await SyncService.hidratarFotosLivro(payload);
          const response = await Api.livroOcorrencia.finalizarLivro({
            ...enviar,
            id_adm_cliente_livro_ocorrencia: resolvedId,
          });
          console.log(
            "[LIVRO-DBG] FINALIZAR resposta success=",
            response?.success,
            "offline=",
            (response as any)?.offline,
            "erros=",
            response?.erros,
            "done=",
            response?.done,
          );
          if (response?.success) {
            if (resolvedId !== livroId)
              await SyncService.markLivroFinalizadoLocal(resolvedId);
            if (enqueuedId) await SyncService.dequeueOfflineAction(enqueuedId);
            console.log("[LIVRO-DBG] FINALIZAR dequeued OK (online)");
            return { success: true, offline: false };
          }
        }
      } catch (error) {
        console.warn(
          "[Sync] livroFinalizar: online falhou (já enfileirado):",
          error,
        );
      }
      console.log("[LIVRO-DBG] FINALIZAR ficou OFFLINE (mantido na fila)");
      return { success: true, offline: true };
    });
    // Sem lastro na fila E sem confirmação do servidor = NÃO está salvo. Anunciar
    // sucesso aqui é a pior mentira possível num app offline-first: o usuário vai embora
    // achando que registrou. (`enqueuedId` nulo = a escrita na fila falhou.)
    if (!enqueuedId && (!r || r.offline))
      return { success: false, offline: true };
    return r ?? { success: true, offline: true };
  },

  // ── Troca de plantão ───────────────────────────────────────────────────────

  /**
   * Substitutos possíveis da troca: colaboradores ATIVOS da empresa com escala 12x36
   * ou 24x72 (as duas juntas — regra do produto, 22/10/2026).
   *
   * Online, busca e GUARDA no aparelho (por tenant); offline ou com a API fora, devolve
   * a última lista guardada. A solicitação é offline-first, e escolher o substituto
   * faz parte dela — sem a lista em cache, quem está sem sinal no posto não conseguiria
   * pedir a troca.
   */
  trocaPlantaoSubstitutos: async (): Promise<TrocaPlantaoSubstituto[]> => {
    const chave = await chaveTenant(TROCA_SUBSTITUTOS_KEY);
    const lerCache = async (): Promise<TrocaPlantaoSubstituto[]> => {
      try {
        const raw = await AsyncStorage.getItem(chave);
        const v = raw ? JSON.parse(raw) : [];
        return Array.isArray(v) ? v : [];
      } catch {
        return [];
      }
    };
    try {
      if (await SyncService.checkInternet()) {
        const r: any = await Api.trocaPlantao.substitutos();
        if (r?.success && Array.isArray(r?.done)) {
          const lista: TrocaPlantaoSubstituto[] = r.done
            .map((c: any) => {
              // Defesa LGPD: se um dia o servidor mandar o nome inteiro, só o 1º nome e
              // a inicial do sobrenome ficam no aparelho.
              const completo = String(c?.nome ?? c?.primeiro_nome ?? "").trim();
              return {
                id_adm_colaborador: Number(c?.id_adm_colaborador ?? 0),
                matricula: String(c?.matricula ?? "").trim(),
                primeiro_nome: String(c?.primeiro_nome ?? completo)
                  .trim()
                  .split(/\s+/)[0],
                inicial_sobrenome: (
                  String(c?.inicial_sobrenome ?? "").trim() ||
                  inicialDoSobrenome(completo)
                ).charAt(0),
              };
            })
            .filter(
              (c: TrocaPlantaoSubstituto) =>
                c.id_adm_colaborador > 0 && c.primeiro_nome !== "",
            );
          await AsyncStorage.setItem(chave, JSON.stringify(lista)).catch(
            () => {},
          );
          return lista;
        }
      }
    } catch (e) {
      console.warn("[Sync] trocaPlantaoSubstitutos: online falhou, usa o cache", e);
    }
    return lerCache();
  },

  /**
   * Payload da fila → payload da API: assinatura de caminho para base64.
   * `null` = assinatura ilegível AGORA. Sem ela a troca não vale, então quem chama
   * mantém a ação na fila em vez de enviar sem assinatura.
   */
  _trocaPlantaoParaEnvio: async (
    data: TrocaPlantaoPayload,
  ): Promise<TrocaPlantaoPayload | null> => {
    const assinatura = await SyncService.fotoLivroParaBase64(data?.assinatura);
    if (!assinatura) return null;
    return { ...data, assinatura };
  },

  /**
   * Solicita a troca de plantão — fila PRIMEIRO, envio direto depois (mesmo desenho do
   * `livroFinalizar`): o registro está salvo no aparelho antes de qualquer rede, e o
   * dreno reenvia sozinho se a tentativa online não der certo.
   */
  trocaPlantaoSolicitar: async (
    payload: TrocaPlantaoPayload,
    rotuloSubstituto: string,
  ): Promise<{ success: boolean; offline: boolean }> => {
    await carimbarHashTenant(payload);
    // Hora do TOQUE, não a do sync: offline, a troca pode subir dias depois.
    const comLog: TrocaPlantaoPayload = {
      ...payload,
      data_log: payload.data_log || nowLocalSqlDateTime(),
    };
    // Assinatura vira ARQUIVO antes da fila — base64 na fila custa memória em todo
    // enfileirar/baixar (ver desidratarFotosLivro). Sem arquivo, segue o base64.
    const [arquivo] = await SyncService.persistFotosLivroLocal([
      comLog.assinatura,
    ]);
    const paraFila: TrocaPlantaoPayload = {
      ...comLog,
      assinatura: arquivo ?? comLog.assinatura,
    };
    const enqueuedId = await SyncService.enqueueOfflineAction(
      "TROCA_PLANTAO",
      paraFila,
    );
    const r = await comDespachoDireto(enqueuedId, async () => {
      try {
        if (await SyncService.checkInternet()) {
          const enviar = await SyncService._trocaPlantaoParaEnvio(paraFila);
          if (!enviar)
            console.log("[TROCA-DBG] solicitar: assinatura ilegível → fica na fila");
          if (enviar) {
            const response = await Api.trocaPlantao.solicitar(enviar);
            console.log(
              "[TROCA-DBG] solicitar resposta success=",
              response?.success,
              "offline=",
              (response as any)?.offline,
              "erros=",
              response?.erros,
              "done=",
              response?.done,
            );
            if (response?.success) {
              if (enqueuedId) await SyncService.dequeueOfflineAction(enqueuedId);
              return { success: true, offline: false };
            }
          }
        }
      } catch (error) {
        console.warn(
          "[Sync] trocaPlantaoSolicitar: online falhou (já enfileirado):",
          error,
        );
      }
      return { success: true, offline: true };
    });
    // Sem lastro na fila E sem confirmação do servidor = NÃO está salvo.
    if (!enqueuedId && (!r || r.offline))
      return { success: false, offline: true };
    const resultado = r ?? { success: true, offline: true };
    await SyncService.setLocalTrocaPlantao({
      data_troca: paraFila.data_troca,
      data_pagamento: paraFila.data_pagamento,
      substituto: rotuloSubstituto,
      offline: resultado.offline,
      timestamp: msDeDataLog(paraFila.data_log),
    });
    return resultado;
  },

  /** Espelho das trocas solicitadas neste aparelho, por data da troca ("YYYY-MM-DD"). */
  getLocalTrocasPlantao: async (): Promise<Record<string, LocalTrocaPlantao>> => {
    try {
      const raw = await AsyncStorage.getItem(
        await chaveTenant(LOCAL_TROCAS_PLANTAO_KEY),
      );
      const v = raw ? JSON.parse(raw) : {};
      return v && typeof v === "object" && !Array.isArray(v) ? v : {};
    } catch {
      return {};
    }
  },

  setLocalTrocaPlantao: async (t: LocalTrocaPlantao): Promise<void> => {
    try {
      await mutarEspelhoTenant(LOCAL_TROCAS_PLANTAO_KEY, (atual) => {
        const base =
          atual && typeof atual === "object" && !Array.isArray(atual)
            ? atual
            : {};
        const limite = Date.now() - TETO_ESPELHO_TROCA_MS;
        const proximo: Record<string, LocalTrocaPlantao> = {};
        for (const [k, v] of Object.entries(base) as [string, any][]) {
          if (Number(v?.timestamp) >= limite) proximo[k] = v;
        }
        proximo[t.data_troca] = t;
        return proximo;
      });
    } catch (e) {
      console.warn("[Sync] setLocalTrocaPlantao: falhou", e);
    }
  },

  /** O dreno entregou a troca: tira o "aguardando envio" do espelho. */
  marcarTrocaPlantaoEnviada: async (dataTroca: string): Promise<void> => {
    try {
      await mutarEspelhoTenant(LOCAL_TROCAS_PLANTAO_KEY, (atual) => {
        const item = atual?.[dataTroca];
        if (!item) return undefined;
        return { ...atual, [dataTroca]: { ...item, offline: false } };
      });
    } catch {}
  },

  /**
   * Status das trocas que EU pedi, com data da troca no período ("YYYY-MM-DD"), por data.
   * Online busca e GUARDA (por tenant, trocando só as datas do período); offline ou com a
   * API fora, devolve o último status guardado — o histórico é offline-first.
   */
  trocaPlantaoMinhas: async (
    inicio: string,
    fim: string,
  ): Promise<Record<string, TrocaPlantaoMinha>> => {
    const noPeriodo = (d: string) => d >= inicio && d <= fim;
    try {
      if (await SyncService.checkInternet()) {
        const r: any = await Api.trocaPlantao.minhas(inicio, fim);
        console.log(
          "[TROCA-DBG] minhas",
          inicio,
          fim,
          "success=",
          r?.success,
          "itens=",
          Array.isArray(r?.done) ? r.done.length : r?.done,
          "erros=",
          r?.erros,
        );
        if (r?.success && Array.isArray(r?.done)) {
          const doPeriodo: Record<string, TrocaPlantaoMinha> = {};
          for (const t of r.done) {
            const data = String(t?.data_troca ?? "");
            if (!data) continue;
            const s = t?.substituto ?? {};
            doPeriodo[data] = {
              id_rh_troca_plantao_app: Number(t?.id_rh_troca_plantao_app ?? 0),
              data_troca: data,
              data_pagamento: String(t?.data_pagamento ?? ""),
              substituto: rotuloSubstituto({
                id_adm_colaborador: Number(s?.id_adm_colaborador ?? 0),
                matricula: String(s?.matricula ?? ""),
                primeiro_nome: String(s?.primeiro_nome ?? ""),
                inicial_sobrenome: String(s?.inicial_sobrenome ?? ""),
              }),
              aceito_substituto: String(t?.aceito_substituto ?? "P").toUpperCase(),
              observacao_substituto: String(t?.observacao_substituto ?? ""),
              aceito: String(t?.aceito ?? "P").toUpperCase(),
              nome_validador: String(t?.nome_validador ?? "").trim(),
              observacao_validacao: String(t?.observacao_validacao ?? ""),
              data_log: String(t?.data_log ?? ""),
              data_log_substituto: String(t?.data_log_substituto ?? ""),
              data_log_validacao: String(t?.data_log_validacao ?? ""),
            };
          }
          await mutarEspelhoTenant(TROCAS_MINHAS_KEY, (atual) => {
            const base =
              atual && typeof atual === "object" && !Array.isArray(atual)
                ? atual
                : {};
            const fora: Record<string, TrocaPlantaoMinha> = {};
            for (const [k, v] of Object.entries(base) as [string, any][]) {
              if (!noPeriodo(k)) fora[k] = v;
            }
            return { ...fora, ...doPeriodo };
          }).catch(() => {});
          return doPeriodo;
        }
      }
    } catch (e) {
      console.warn("[Sync] trocaPlantaoMinhas: online falhou, usa o cache", e);
    }
    try {
      const raw = await AsyncStorage.getItem(
        await chaveTenant(TROCAS_MINHAS_KEY),
      );
      const v = raw ? JSON.parse(raw) : {};
      const tudo = v && typeof v === "object" && !Array.isArray(v) ? v : {};
      return Object.fromEntries(
        Object.entries(tudo).filter(([k]) => noPeriodo(k)),
      ) as Record<string, TrocaPlantaoMinha>;
    } catch {
      return {};
    }
  },

  /**
   * Histórico das trocas dos DOIS lados (as que pedi e as que cubro) com a data da troca OU do
   * pagamento no período ("YYYY-MM-DD"). Online busca e GUARDA (por tenant, trocando só o que cai
   * no período); offline ou com a API fora, devolve o último guardado — o histórico é offline-first.
   *
   * T-ARC que ainda não tem a rota `historico` (publicação atrasada): cai na `minhas`, que só traz
   * as que eu pedi — o lado de quem cobre aparece assim que a rota nova estiver no ar.
   */
  trocaPlantaoHistorico: async (
    inicio: string,
    fim: string,
  ): Promise<TrocaPlantaoHistorico[]> => {
    const noPeriodo = (t: TrocaPlantaoHistorico) =>
      (t.data_troca >= inicio && t.data_troca <= fim) ||
      (t.data_pagamento >= inicio && t.data_pagamento <= fim);
    const curto = (c: any) => {
      const primeiro = String(c?.primeiro_nome ?? "").trim().toUpperCase();
      const inicial = String(c?.inicial_sobrenome ?? "").trim().charAt(0).toUpperCase();
      return inicial ? `${primeiro} ${inicial}.` : primeiro;
    };
    const guardar = async (doPeriodo: TrocaPlantaoHistorico[]) => {
      await mutarEspelhoTenant(TROCAS_HISTORICO_KEY, (atual) => {
        const base = Array.isArray(atual) ? atual : [];
        return [...base.filter((t: any) => !noPeriodo(t)), ...doPeriodo];
      }).catch(() => {});
    };
    try {
      if (await SyncService.checkInternet()) {
        const r: any = await Api.trocaPlantao.historico(inicio, fim);
        if (r?.success && Array.isArray(r?.done)) {
          const lista: TrocaPlantaoHistorico[] = r.done
            .map((t: any) => ({
              id_rh_troca_plantao_app: Number(t?.id_rh_troca_plantao_app ?? 0),
              papel: String(t?.papel ?? "S").toUpperCase() === "C" ? "C" : "S",
              data_troca: String(t?.data_troca ?? ""),
              data_pagamento: String(t?.data_pagamento ?? ""),
              colega: curto(t?.colega),
              aceito_substituto: String(t?.aceito_substituto ?? "P").toUpperCase(),
              observacao_substituto: String(t?.observacao_substituto ?? ""),
              aceito: String(t?.aceito ?? "P").toUpperCase(),
              nome_validador: String(t?.nome_validador ?? "").trim(),
              observacao_validacao: String(t?.observacao_validacao ?? ""),
              data_log: String(t?.data_log ?? ""),
              data_log_substituto: String(t?.data_log_substituto ?? ""),
              data_log_validacao: String(t?.data_log_validacao ?? ""),
            }))
            .filter((t: TrocaPlantaoHistorico) => t.id_rh_troca_plantao_app > 0);
          await guardar(lista);
          return lista;
        }
      }
    } catch (e) {
      console.warn("[Sync] trocaPlantaoHistorico: online falhou, usa o cache", e);
    }
    // Sem a rota nova: o último histórico guardado (offline / falha passageira)…
    try {
      const raw = await AsyncStorage.getItem(
        await chaveTenant(TROCAS_HISTORICO_KEY),
      );
      const v = raw ? JSON.parse(raw) : [];
      const guardado = (Array.isArray(v) ? v : []).filter(noPeriodo);
      if (guardado.length > 0) return guardado;
    } catch {}
    // …e, se não há nada guardado, a `minhas` (online ou o cache dela): o lado de quem pediu.
    // ⚠️ T-ARC sem a rota `historico` responde 404 SEM JSON, que o apiRequest lê como
    // `offline: true` — então "falhou" aqui não quer dizer sem internet. Antes este fallback
    // só rodava com offline=false e nunca disparava: o histórico vinha vazio do servidor.
    const minhas = await SyncService.trocaPlantaoMinhas(inicio, fim);
    return Object.values(minhas).map((m) => ({
      id_rh_troca_plantao_app: m.id_rh_troca_plantao_app,
      papel: "S" as const,
      data_troca: m.data_troca,
      data_pagamento: m.data_pagamento,
      colega: colegaCurto(m.substituto),
      aceito_substituto: m.aceito_substituto,
      observacao_substituto: m.observacao_substituto,
      aceito: m.aceito,
      nome_validador: m.nome_validador,
      observacao_validacao: m.observacao_validacao,
      data_log: m.data_log ?? "",
      data_log_substituto: m.data_log_substituto ?? "",
      data_log_validacao: m.data_log_validacao ?? "",
    }));
  },

  /**
   * Trocas que pediram para EU cobrir e que esperam a minha resposta (pop-up da Home).
   * Online busca e guarda; offline usa a última lista. Tira as que já respondi neste
   * aparelho (a resposta pode estar na fila).
   */
  trocaPlantaoRecebidas: async (): Promise<TrocaPlantaoRecebida[]> => {
    const chave = await chaveTenant(TROCAS_RECEBIDAS_KEY);
    let lista: TrocaPlantaoRecebida[] | null = null;
    try {
      if (await SyncService.checkInternet()) {
        const r: any = await Api.trocaPlantao.recebidas();
        console.log(
          "[TROCA-DBG] recebidas servidor success=",
          r?.success,
          "itens=",
          Array.isArray(r?.done) ? r.done.length : r?.done,
          "erros=",
          r?.erros,
        );
        if (r?.success && Array.isArray(r?.done)) {
          lista = r.done
            .map((t: any) => {
              const s = t?.solicitante ?? {};
              return {
                id_rh_troca_plantao_app: Number(t?.id_rh_troca_plantao_app ?? 0),
                data_troca: String(t?.data_troca ?? ""),
                data_pagamento: String(t?.data_pagamento ?? ""),
                solicitante: rotuloSubstituto({
                  id_adm_colaborador: Number(s?.id_adm_colaborador ?? 0),
                  matricula: String(s?.matricula ?? ""),
                  primeiro_nome: String(s?.primeiro_nome ?? ""),
                  inicial_sobrenome: String(s?.inicial_sobrenome ?? ""),
                }),
              };
            })
            .filter((t: TrocaPlantaoRecebida) => t.id_rh_troca_plantao_app > 0);
          await AsyncStorage.setItem(chave, JSON.stringify(lista)).catch(
            () => {},
          );
        }
      }
    } catch (e) {
      console.warn("[Sync] trocaPlantaoRecebidas: online falhou, usa o cache", e);
    }
    if (!lista) {
      try {
        const raw = await AsyncStorage.getItem(chave);
        const v = raw ? JSON.parse(raw) : [];
        lista = Array.isArray(v) ? v : [];
      } catch {
        lista = [];
      }
    }
    const respondidas = await SyncService._trocasRespondidas();
    // Sem filtro de data: troca de dia que já passou também é respondida.
    const paraMostrar = lista.filter(
      (t) => !respondidas[String(t.id_rh_troca_plantao_app)],
    );
    console.log(
      "[TROCA-DBG] recebidas lista=",
      lista.length,
      "pop-up=",
      paraMostrar.length,
      "respondidas aqui=",
      Object.keys(respondidas).length,
    );
    return paraMostrar;
  },

  /**
   * Recibos que o supervisor emitiu (tela "Troca de Plantão" → Recibos). Online busca e guarda por
   * tenant; offline ou com a rota fora (T-ARC ainda sem `recibos`), a última lista guardada.
   */
  trocaPlantaoRecibos: async (): Promise<TrocaPlantaoRecibo[]> => {
    const chave = await chaveTenant(TROCAS_RECIBOS_KEY);
    const curto = (c: any) => {
      const primeiro = String(c?.primeiro_nome ?? "").trim().toUpperCase();
      const inicial = String(c?.inicial_sobrenome ?? "").trim().charAt(0).toUpperCase();
      return inicial ? `${primeiro} ${inicial}.` : primeiro;
    };
    try {
      if (await SyncService.checkInternet()) {
        const r: any = await Api.trocaPlantao.recibos();
        if (r?.success && Array.isArray(r?.done)) {
          const lista: TrocaPlantaoRecibo[] = r.done
            .map((t: any) => ({
              id_rh_troca_plantao_app: Number(t?.id_rh_troca_plantao_app ?? 0),
              papel: String(t?.papel ?? "S").toUpperCase() === "C" ? "C" : "S",
              data_troca: String(t?.data_troca ?? ""),
              data_pagamento: String(t?.data_pagamento ?? ""),
              colega: curto(t?.colega),
              endereco_recibo: String(t?.endereco_recibo ?? "").trim(),
              data_log_recibo: String(t?.data_log_recibo ?? ""),
            }))
            .filter(
              (t: TrocaPlantaoRecibo) =>
                t.id_rh_troca_plantao_app > 0 && /^https?:\/\//i.test(t.endereco_recibo),
            );
          await AsyncStorage.setItem(chave, JSON.stringify(lista)).catch(() => {});
          return lista;
        }
      }
    } catch (e) {
      console.warn("[Sync] trocaPlantaoRecibos: online falhou, usa o cache", e);
    }
    try {
      const raw = await AsyncStorage.getItem(chave);
      const v = raw ? JSON.parse(raw) : [];
      return Array.isArray(v) ? v : [];
    } catch {
      return [];
    }
  },

  /** Respostas dadas NESTE aparelho, por id da troca (guarda anti-reabertura do pop-up). */
  _trocasRespondidas: async (): Promise<
    Record<string, { aceito: string; offline: boolean; timestamp: number }>
  > => {
    try {
      const raw = await AsyncStorage.getItem(
        await chaveTenant(TROCAS_RESPONDIDAS_KEY),
      );
      const v = raw ? JSON.parse(raw) : {};
      return v && typeof v === "object" && !Array.isArray(v) ? v : {};
    } catch {
      return {};
    }
  },

  _marcarTrocaRespondida: async (
    id: number,
    aceito: string,
    offline: boolean,
  ): Promise<void> => {
    try {
      await mutarEspelhoTenant(TROCAS_RESPONDIDAS_KEY, (atual) => {
        const base =
          atual && typeof atual === "object" && !Array.isArray(atual)
            ? atual
            : {};
        const limite = Date.now() - TETO_ESPELHO_TROCA_MS;
        const proximo: Record<string, any> = {};
        for (const [k, v] of Object.entries(base) as [string, any][]) {
          if (Number(v?.timestamp) >= limite) proximo[k] = v;
        }
        proximo[String(id)] = { aceito, offline, timestamp: Date.now() };
        return proximo;
      });
    } catch (e) {
      console.warn("[Sync] _marcarTrocaRespondida: falhou", e);
    }
  },

  /**
   * Payload da fila → payload da API. Recusa vai sem assinatura; aceite converte o PNG
   * para base64 — `null` = assinatura ilegível AGORA, e a resposta segue na fila.
   */
  _trocaRespostaParaEnvio: async (
    data: TrocaPlantaoRespostaPayload,
  ): Promise<TrocaPlantaoRespostaPayload | null> => {
    if (data?.aceito !== "S") return { ...data, assinatura: "" };
    const assinatura = await SyncService.fotoLivroParaBase64(data?.assinatura);
    if (!assinatura) return null;
    return { ...data, assinatura };
  },

  /**
   * Resposta do COLEGA à troca (pop-up) — fila PRIMEIRO, envio direto depois, como o
   * `trocaPlantaoSolicitar`. Marca a troca como respondida neste aparelho para o pop-up
   * não reabrir enquanto a resposta estiver na fila.
   */
  trocaPlantaoResponder: async (
    payload: TrocaPlantaoRespostaPayload,
  ): Promise<{ success: boolean; offline: boolean }> => {
    await carimbarHashTenant(payload);
    const comLog: TrocaPlantaoRespostaPayload = {
      ...payload,
      data_log: payload.data_log || nowLocalSqlDateTime(),
    };
    // Assinatura do aceite vira ARQUIVO antes da fila (ver trocaPlantaoSolicitar).
    let assinatura = "";
    if (comLog.aceito === "S") {
      const [arquivo] = await SyncService.persistFotosLivroLocal([
        comLog.assinatura,
      ]);
      assinatura = arquivo ?? comLog.assinatura;
    }
    const paraFila: TrocaPlantaoRespostaPayload = { ...comLog, assinatura };
    const enqueuedId = await SyncService.enqueueOfflineAction(
      "TROCA_PLANTAO_RESPOSTA",
      paraFila,
    );
    const r = await comDespachoDireto(enqueuedId, async () => {
      try {
        if (await SyncService.checkInternet()) {
          const enviar = await SyncService._trocaRespostaParaEnvio(paraFila);
          if (enviar) {
            const response = await Api.trocaPlantao.responder(enviar);
            console.log(
              "[TROCA-DBG] responder resposta success=",
              response?.success,
              "erros=",
              response?.erros,
              "done=",
              response?.done,
            );
            if (response?.success) {
              if (enqueuedId) await SyncService.dequeueOfflineAction(enqueuedId);
              return { success: true, offline: false };
            }
          }
        }
      } catch (error) {
        console.warn(
          "[Sync] trocaPlantaoResponder: online falhou (já enfileirado):",
          error,
        );
      }
      return { success: true, offline: true };
    });
    // Sem lastro na fila E sem confirmação do servidor = NÃO está salvo.
    if (!enqueuedId && (!r || r.offline))
      return { success: false, offline: true };
    const resultado = r ?? { success: true, offline: true };
    await SyncService._marcarTrocaRespondida(
      paraFila.id_rh_troca_plantao_app,
      paraFila.aceito,
      resultado.offline,
    );
    return resultado;
  },

  /**
   * Marcas de "finalizado neste aparelho", como `{ id: instante }`.
   *
   * ⚠️ A marca é uma PONTE, não um arquivo: ela cobre a janela entre encerrar o plantão
   * aqui e o servidor refletir isso. Guardada eternamente, ela envenena ids futuros — o
   * `id_adm_cliente_livro_ocorrencia` é AUTO_INCREMENT POR TENANT, então uma base
   * recriada devolve o id 1, e um aparelho que já tinha finalizado o "1" antigo passa a
   * enxergar o plantão NOVO como encerrado (some o card de "em andamento"). Daí o
   * instante: `reconciliarFinalizadosLocais` usa a idade para decidir quando o SERVIDOR
   * volta a mandar.
   *
   * Formato antigo (array de ids) é aceito na leitura e vira mapa com instante 0, isto
   * é, "velha o bastante para o servidor mandar" — que é exatamente o que se quer para as
   * marcas herdadas.
   */
  getLivrosFinalizadosLocaisMapa: async (): Promise<Record<string, number>> => {
    try {
      const raw = await AsyncStorage.getItem(
        await chaveTenant(LIVRO_FINALIZADOS_KEY),
      );
      const dado = raw ? JSON.parse(raw) : {};
      if (Array.isArray(dado)) {
        const out: Record<string, number> = {};
        for (const v of dado) {
          const n = Number(v);
          if (Number.isFinite(n) && n > 0) out[String(n)] = 0;
        }
        return out;
      }
      return dado && typeof dado === "object" ? dado : {};
    } catch {
      return {};
    }
  },

  /** Lista de ids de livros finalizados localmente (otimista, enquanto o backend não reflete). */
  getLivrosFinalizadosLocais: async (): Promise<number[]> => {
    const mapa = await SyncService.getLivrosFinalizadosLocaisMapa();
    return Object.keys(mapa)
      .map(Number)
      .filter((n) => Number.isFinite(n) && n > 0);
  },

  markLivroFinalizadoLocal: async (id: number): Promise<void> => {
    try {
      if (!id) return;
      await mutarEspelhoTenant(LIVRO_FINALIZADOS_KEY, (atualRaw) => {
        const mapa: Record<string, number> = Array.isArray(atualRaw)
          ? Object.fromEntries(atualRaw.map((v: any) => [String(Number(v)), 0]))
          : atualRaw && typeof atualRaw === "object"
            ? { ...atualRaw }
            : {};
        mapa[String(Number(id))] = Date.now();
        return mapa;
      });
    } catch (e) {
      console.warn("[Sync] markLivroFinalizadoLocal: erro", e);
    }
  },

  /** Remove marcas de finalização local (usado quando o servidor as desmente). */
  desmarcarLivrosFinalizadosLocais: async (ids: number[]): Promise<void> => {
    try {
      const alvos = new Set((ids || []).map((n) => String(Number(n))));
      if (alvos.size === 0) return;
      await mutarEspelhoTenant(LIVRO_FINALIZADOS_KEY, (atualRaw) => {
        const mapa: Record<string, number> = Array.isArray(atualRaw)
          ? Object.fromEntries(atualRaw.map((v: any) => [String(Number(v)), 0]))
          : atualRaw && typeof atualRaw === "object"
            ? { ...atualRaw }
            : {};
        for (const k of alvos) delete mapa[k];
        return mapa;
      });
    } catch (e) {
      console.warn("[Sync] desmarcarLivrosFinalizadosLocais: erro", e);
    }
  },

  /** Existe uma finalização na fila mirando este livro (direto ou via id local)? */
  temFinalizacaoPendente: async (idReal: number): Promise<boolean> => {
    try {
      const alvo = Number(idReal ?? 0);
      if (!alvo) return false;
      const actions = await lerFilaSoLeitura();
      for (const a of actions) {
        if (a.type !== "LIVRO_FINALIZAR") continue;
        const bruto = Number(
          (a.data as any)?.id_adm_cliente_livro_ocorrencia ?? 0,
        );
        if (bruto === alvo) return true;
        // eslint-disable-next-line no-await-in-loop
        if ((await SyncService.resolveLivroId(bruto)) === alvo) return true;
      }
      return false;
    } catch {
      // Fila ilegível: assume pendência (não desmarca nada por engano).
      return true;
    }
  },

  /**
   * Ids de execução de enquete RESPONDIDAS neste aparelho (guarda anti-reabertura).
   * Poda automática em 7 dias: até lá o servidor já refletiu, e o registro não
   * cresce para sempre. Se o backend ainda insistir em mandar a enquete depois
   * disso, ela reabre — o que é o comportamento correto (algo deu errado no envio).
   */
  getEnquetesRespondidasLocais: async (): Promise<number[]> => {
    try {
      const raw = await AsyncStorage.getItem(
        await chaveTenant(ENQUETES_RESPONDIDAS_KEY),
      );
      const mapa = raw ? JSON.parse(raw) : {};
      if (!mapa || typeof mapa !== "object") return [];
      const limite = Date.now() - 7 * 24 * 60 * 60 * 1000;
      return Object.entries(mapa)
        .filter(([, ts]) => Number(ts) >= limite)
        .map(([id]) => Number(id))
        .filter((n) => Number.isFinite(n) && n > 0);
    } catch {
      return [];
    }
  },

  markEnqueteRespondidaLocal: async (idExecucao: number): Promise<void> => {
    try {
      const id = Number(idExecucao);
      if (!Number.isFinite(id) || id <= 0) return;
      await mutarEspelhoTenant(ENQUETES_RESPONDIDAS_KEY, (atual) => {
        const mapa: Record<string, number> =
          atual && typeof atual === "object" ? atual : {};
        mapa[String(id)] = Date.now();
        // Poda no WRITE (o read já filtra): mantém a chave pequena.
        const limite = Date.now() - 7 * 24 * 60 * 60 * 1000;
        for (const [k, ts] of Object.entries(mapa)) {
          if (Number(ts) < limite) delete mapa[k];
        }
        return mapa;
      });
    } catch (e) {
      console.warn("[Sync] markEnqueteRespondidaLocal: erro", e);
    }
  },

  /**
   * Checklists de O.S. respondidos localmente. Espelha getEnquetesRespondidasLocais —
   * mesma janela de 7 dias e mesma poda — mas com a chave sendo o
   * id_adm_ordem_servico_cronograma (string, como a tela já usa em selectedOrder.id).
   *
   * A janela de 7 dias é de propósito: se em uma semana o envio nunca sincronizou, o
   * checklist volta a aparecer como pendente — que é o comportamento certo, porque aí algo
   * deu errado no envio e o usuário precisa refazer.
   */
  /** Mapa {idCronograma: quando} — tenant da sessão + legado cru (pré-namespace). */
  _lerChecklistsOsRespondidosMapa: async (): Promise<
    Record<string, number>
  > => {
    const ler = async (chave: string): Promise<Record<string, number>> => {
      try {
        const raw = await AsyncStorage.getItem(chave);
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === "object" ? parsed : {};
      } catch {
        return {};
      }
    };
    // Legado primeiro: em empate de id, vale o carimbo do tenant atual.
    return {
      ...(await ler(CHECKLISTS_RESPONDIDOS_KEY)),
      ...(await ler(await chaveTenant(CHECKLISTS_RESPONDIDOS_KEY))),
    };
  },

  getChecklistsOsRespondidosLocais: async (): Promise<string[]> => {
    try {
      const mapa = await SyncService._lerChecklistsOsRespondidosMapa();
      const limite = Date.now() - 7 * 24 * 60 * 60 * 1000;
      return Object.entries(mapa)
        .filter(([, ts]) => Number(ts) >= limite)
        .map(([id]) => String(id))
        .filter((s) => s.trim() !== "");
    } catch {
      return [];
    }
  },

  markChecklistOsRespondidoLocal: async (
    idCronograma: string | number,
  ): Promise<void> => {
    try {
      const id = String(idCronograma ?? "").trim();
      if (!id) return;
      // Escopado por TENANT (ids de cronograma colidem entre bancos). A chave crua
      // antiga segue sendo LIDA (união) até secar pela janela de 7 dias.
      await mutarEspelhoTenant(CHECKLISTS_RESPONDIDOS_KEY, (atual) => {
        const mapa: Record<string, number> =
          atual && typeof atual === "object" ? atual : {};
        mapa[id] = Date.now();
        // Poda no WRITE (o read já filtra): mantém a chave pequena.
        const limite = Date.now() - 7 * 24 * 60 * 60 * 1000;
        for (const [k, ts] of Object.entries(mapa)) {
          if (Number(ts) < limite) delete mapa[k];
        }
        return mapa;
      });
    } catch (e) {
      console.warn("[Sync] markChecklistOsRespondidoLocal: erro", e);
    }
  },

  /**
   * O SERVIDOR desmente marcas de checklist de OS respondido — a mesma cura da marca de
   * finalização do livro: `id_adm_ordem_servico_cronograma` é AUTO_INCREMENT por tenant,
   * então uma base recriada reaproveita ids e a marca de um teste antigo fazia a OS NOVA
   * nascer com "checklist concluído" sem ninguém tê-lo executado.
   *
   * Remove a marca quando as TRÊS valem: a ordem está na lista do servidor SEM respostas
   * de checklist, a marca passou da janela de trânsito, e não há CHECKLIST_SALVAR na fila
   * mirando o cronograma (aí a marca é o rosto de um envio a caminho e fica).
   */
  reconciliarChecklistsOsRespondidos: async (ordens: any[]): Promise<void> => {
    try {
      const lista = Array.isArray(ordens) ? ordens : [];
      if (lista.length === 0) return;
      const mapa = await SyncService._lerChecklistsOsRespondidosMapa();
      const marcados = Object.keys(mapa);
      if (marcados.length === 0) return;

      const JANELA_MS = 10 * 60 * 1000;
      const semRespostas = new Set<string>();
      for (const o of lista) {
        const id = String(o?.id_adm_ordem_servico_cronograma ?? "").trim();
        if (!id) continue;
        const respostas = o?.respostasChecklist ?? o?.respostas_checklist;
        if (!Array.isArray(respostas) || respostas.length === 0)
          semRespostas.add(id);
      }

      const fila = await lerFilaSoLeitura();
      const comEnvioPendente = new Set(
        fila
          .filter((a) => a.type === "CHECKLIST_SALVAR")
          .map((a) => String((a.data as any)?.IdReferenciaVinculo ?? "").trim())
          .filter(Boolean),
      );

      const desmentidos = marcados.filter(
        (id) =>
          semRespostas.has(id) &&
          Date.now() - (Number(mapa[id]) || 0) >= JANELA_MS &&
          !comEnvioPendente.has(id),
      );
      if (desmentidos.length === 0) return;

      console.warn(
        "[Sync] Marcas de checklist de OS desmentidas pelo servidor (ids reaproveitados?):",
        desmentidos,
      );
      const alvos = new Set(desmentidos);
      await mutarEspelhoTenant(CHECKLISTS_RESPONDIDOS_KEY, (atual) => {
        const m: Record<string, number> =
          atual && typeof atual === "object" ? atual : {};
        for (const k of alvos) delete m[k];
        return m;
      });
      // Chave crua legada: mesma poda (é dela que vem o veneno dos ids antigos).
      try {
        const raw = await AsyncStorage.getItem(CHECKLISTS_RESPONDIDOS_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        if (parsed && typeof parsed === "object") {
          let mudou = false;
          for (const k of alvos) {
            if (k in parsed) {
              delete parsed[k];
              mudou = true;
            }
          }
          if (mudou) {
            await AsyncStorage.setItem(
              CHECKLISTS_RESPONDIDOS_KEY,
              JSON.stringify(parsed),
            );
          }
        }
      } catch {}
    } catch (e) {
      console.warn("[Sync] Erro reconciliando checklists de OS:", e);
    }
  },

  /** Plantão (livro) iniciado localmente de forma otimista — mostrado na hora, mesmo offline. */
  getLivroLocalAtivo: async (): Promise<any | null> => {
    try {
      const raw = await AsyncStorage.getItem(
        await chaveTenant(LIVRO_LOCAL_KEY),
      );
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  },

  setLivroLocalAtivo: async (livro: any | null): Promise<void> => {
    try {
      if (livro == null)
        await AsyncStorage.removeItem(await chaveTenant(LIVRO_LOCAL_KEY));
      else await setItemEscopado(LIVRO_LOCAL_KEY, JSON.stringify(livro));
    } catch (e) {
      console.warn("[Sync] setLivroLocalAtivo: erro", e);
    }
  },

  // ── Múltiplos livros locais (vários lotes offline) ─────────────────────────
  getLivrosLocais: async (): Promise<any[]> => {
    try {
      const raw = await AsyncStorage.getItem(
        await chaveTenant(LIVRO_LOCAIS_KEY),
      );
      let arr: any[] = raw ? reescreverCaminhosAntigos(JSON.parse(raw)) : [];
      if (!Array.isArray(arr)) arr = [];

      // Migração do formato antigo (livro local único) para a lista
      try {
        const oldRaw = await AsyncStorage.getItem(
          await chaveTenant(LIVRO_LOCAL_KEY),
        );
        if (oldRaw) {
          const old = reescreverCaminhosAntigos(JSON.parse(oldRaw));
          if (old && typeof old === "object") {
            const oldId = Number(old?.id_adm_cliente_livro_ocorrencia ?? 0);
            if (
              !arr.some(
                (x) =>
                  Number(x?.id_adm_cliente_livro_ocorrencia ?? 0) === oldId,
              )
            ) {
              arr.push(old);
            }
          }
          await AsyncStorage.removeItem(await chaveTenant(LIVRO_LOCAL_KEY));
          await setItemEscopado(LIVRO_LOCAIS_KEY, JSON.stringify(arr));
        }
      } catch {
        /* ignore */
      }

      return arr;
    } catch {
      return [];
    }
  },

  setLivrosLocais: async (arr: any[]): Promise<void> => {
    try {
      await setItemEscopado(
        LIVRO_LOCAIS_KEY,
        JSON.stringify(Array.isArray(arr) ? arr : []),
      );
    } catch (e) {
      console.warn("[Sync] setLivrosLocais: erro", e);
    }
  },

  /** Insere ou atualiza um livro local (por id_adm_cliente_livro_ocorrencia). */
  upsertLivroLocal: async (livro: any): Promise<void> => {
    try {
      const id = Number(livro?.id_adm_cliente_livro_ocorrencia ?? 0);
      const arr = await SyncService.getLivrosLocais();
      const idx = arr.findIndex(
        (x) => Number(x?.id_adm_cliente_livro_ocorrencia ?? 0) === id,
      );
      if (idx >= 0) arr[idx] = livro;
      else arr.push(livro);
      await SyncService.setLivrosLocais(arr);
    } catch (e) {
      console.warn("[Sync] upsertLivroLocal: erro", e);
    }
  },

  removeLivroLocal: async (id: number): Promise<void> => {
    try {
      const arr = await SyncService.getLivrosLocais();
      await SyncService.setLivrosLocais(
        arr.filter(
          (x) => Number(x?.id_adm_cliente_livro_ocorrencia ?? 0) !== Number(id),
        ),
      );
    } catch (e) {
      console.warn("[Sync] removeLivroLocal: erro", e);
    }
  },

  // ── Correlação id local (timestamp) → id real (banco) ──────────────────────
  /** Heurística: ids locais são timestamps (Date.now), muito maiores que ids reais do banco. */
  _isLocalLivroId: (id: any): boolean => Number(id) > 1e11,

  getLivroIdMap: async (): Promise<Record<string, number>> => {
    try {
      const raw = await AsyncStorage.getItem(
        await chaveTenant(LIVRO_ID_MAP_KEY),
      );
      const obj = raw ? JSON.parse(raw) : {};
      return obj && typeof obj === "object" ? obj : {};
    } catch {
      return {};
    }
  },

  /** Resolve um id local para o id real do banco (se já sincronizado); senão devolve o próprio id. */
  resolveLivroId: async (id: any): Promise<number> => {
    const n = Number(id) || 0;
    if (!SyncService._isLocalLivroId(n)) return n;
    const map = await SyncService.getLivroIdMap();
    const real = Number(map[String(n)] ?? 0);
    return real > 0 ? real : n;
  },

  /** Registra o mapeamento local→real e migra o livro local + lista de finalizados. */
  mapLivroIdLocalParaReal: async (
    localId: number,
    realId: number,
  ): Promise<void> => {
    try {
      if (!localId || !realId || Number(localId) === Number(realId)) return;
      await mutarEspelhoTenant(LIVRO_ID_MAP_KEY, (atual) => {
        const map = atual && typeof atual === "object" ? atual : {};
        map[String(localId)] = Number(realId);
        return map;
      });

      // migra o livro local para o id real (mantém os dados otimistas até o servidor refletir)
      try {
        const locais = await SyncService.getLivrosLocais();
        const idx = locais.findIndex(
          (x: any) =>
            Number(x?.id_adm_cliente_livro_ocorrencia ?? 0) === Number(localId),
        );
        if (idx >= 0) {
          locais[idx].id_adm_cliente_livro_ocorrencia = Number(realId);
          await SyncService.setLivrosLocais(locais);
        }
      } catch {
        /* ignore */
      }

      // migra a lista de finalizados locais
      try {
        const fin = await SyncService.getLivrosFinalizadosLocais();
        if (fin.includes(Number(localId)))
          await SyncService.markLivroFinalizadoLocal(Number(realId));
      } catch {
        /* ignore */
      }
    } catch (e) {
      console.warn("[Sync] mapLivroIdLocalParaReal: erro", e);
    }
  },

  /** Extrai o id retornado pela API síncrona do livro (body = { id }). */
  _extractLivroId: (response: any): number => {
    const done = response?.done;
    if (done == null) return 0;
    if (typeof done === "number") return done;
    if (typeof done === "string") {
      const n = Number(done);
      return Number.isFinite(n) ? n : 0;
    }
    if (typeof done === "object") {
      return (
        Number(
          (done as any).id ??
            (done as any).Id ??
            (done as any).ID ??
            (done as any).done?.id ??
            0,
        ) || 0
      );
    }
    return 0;
  },
  /**
   * Verifica se há conectividade com a internet através de um ping simples
   */
  /**
   * A NOSSA API responde? (T-ARC OU t-credentials — qualquer status HTTP conta; só
   * falha de rede/timeout é "não").
   *
   * É o portão do envio. Medir pelo Google não serve: em Wi-Fi de órgão público é comum
   * o Google abrir e os nossos hosts estarem bloqueados — o app se achava online,
   * tentava, falhava, e nunca percebia a hora de tentar de novo.
   */
  apiAlcancavel: async (): Promise<boolean> => {
    try {
      // Online = QUALQUER uma das NOSSAS APIs responde (T-ARC OU t-credentials). Os writes
      // do livro/login vão pro t-credentials; gatear só no T-ARC marcava "offline" quando o
      // T-ARC estava instável/fora mesmo com internet e t-credentials no ar (→ "salvando
      // offline"/"pendente" indevidos). Checa as duas em paralelo (latência = a mais lenta).
      // T-ARC pelo /health/live (anônimo, sem banco, 200 se o serviço está de pé). Antes era
      // GET em "<T-ARC>/api/", rota que não existe: o servidor respondia, mas com 404 — e no
      // web, onde o T-ARC passa pelo proxy do mesmo domínio (/__tarc), o 404 aparecia como
      // erro no console a cada verificação.
      const alvos = [
        Environment.T_ARC_BASE ? `${Environment.T_ARC_BASE.trim()}health/live` : "",
        (Environment.T_CREDENTIALS_BASE || "").trim(),
      ].filter(Boolean);
      if (alvos.length === 0) return false;
      const resultados = await Promise.all(alvos.map((u) => respondeEm(u)));
      const ok = resultados.some(Boolean);
      console.log(
        "[LIVRO-DBG] apiAlcancavel",
        alvos.map((u, i) => `${u}=>${resultados[i]}`).join(" | "),
        "=> online:",
        ok,
      );
      return ok;
    } catch {
      return false;
    }
  },

  /**
   * "Tem internet?" para as TELAS: a nossa API OU, em último caso, o Google (vídeo de
   * treinamento, PDF já hospedado fora etc. funcionam sem os nossos hosts). Para decidir
   * ENVIO use `apiAlcancavel` — com o Google respondendo e a nossa API bloqueada, isto
   * aqui diz "online".
   */
  checkInternet: async (): Promise<boolean> => {
    try {
      if (await SyncService.apiAlcancavel()) return true;
      return await respondeEm("https://clients3.google.com/generate_204");
    } catch (error) {
      return false;
    }
  },

  /**
   * Diretório de documentos ESCOPADO por tenant (`documentos/<HASH>/`): docId é
   * auto-increment POR BANCO e colide entre empresas — com o path compartilhado,
   * o fallback OFFLINE servia o PDF (contracheque!) do doc homônimo de OUTRO
   * tenant. Sem hash de sessão → raiz (legado, fail-open).
   */
  _docsDirDaSessao: async (): Promise<string> => {
    const h = await tenantHashSessaoAtual().catch(() => "");
    return h ? `${DOCS_DIR}${h}/` : DOCS_DIR;
  },

  /**
   * Inicializa o diretório de documentos (base + subpasta do tenant) e faz a
   * FAXINA do legado: PDFs soltos na RAIZ (pré-namespace) são de tenant
   * desconhecido — nunca podem ser servidos; remove para não vazarem nem
   * ocuparem espaço. O doc é re-baixado sob demanda na pasta do tenant certo.
   */
  init: async () => {
    const dirInfo = await FileSystem.getInfoAsync(DOCS_DIR);
    if (!dirInfo.exists) {
      await FileSystem.makeDirectoryAsync(DOCS_DIR, { intermediates: true });
    }
    const dirTenant = await SyncService._docsDirDaSessao();
    if (dirTenant !== DOCS_DIR) {
      const t = await FileSystem.getInfoAsync(dirTenant);
      if (!t.exists) {
        await FileSystem.makeDirectoryAsync(dirTenant, { intermediates: true });
      }
      try {
        const soltos = await FileSystem.readDirectoryAsync(DOCS_DIR);
        for (const nome of soltos) {
          if (nome.toLowerCase().endsWith(".pdf")) {
            await FileSystem.deleteAsync(`${DOCS_DIR}${nome}`, {
              idempotent: true,
            }).catch(() => {});
          }
        }
      } catch {}
    }
    // Downloads interrompidos (app fechado no meio) deixam `<id>.part-<ms>-*`: apaga os com
    // mais de 1h — os recentes podem ser de um download em andamento. Uma vez por processo.
    if (!_partesLimpas) {
      _partesLimpas = true;
      try {
        const nomes = await FileSystem.readDirectoryAsync(dirTenant);
        const limite = Date.now() - 60 * 60 * 1000;
        for (const nome of nomes) {
          const m = nome.match(/\.part-(\d+)-/);
          if (m && Number(m[1]) < limite) {
            await FileSystem.deleteAsync(`${dirTenant}${nome}`, {
              idempotent: true,
            }).catch(() => {});
          }
        }
      } catch {}
    }
  },

  /**
   * Faz o download de um documento para acesso offline. Devolve o caminho local de um PDF
   * INTEIRO, ou null (sem cópia válida — offline sem cache, link quebrado, servidor fora).
   *
   * Uma fila por arquivo (`comDocDownloadLock`): chamadas simultâneas para o mesmo documento
   * esperam a anterior e reavaliam — a 2ª vê o que a 1ª baixou e devolve o cache. O download
   * vai para um `.part-*` com prazo; só vira o `<id>.pdf` (troca atômica) se a resposta for
   * 2xx E o conteúdo for PDF inteiro. Falhou: o arquivo anterior fica (se for válido) e o
   * endereço NÃO é gravado como baixado. Cache inválido já existente (página de erro ou PDF
   * cortado de versões anteriores) é detectado e baixado de novo — os aparelhos se curam.
   */
  downloadDocument: async (
    docId: number,
    url: string,
    assinado?: string,
  ): Promise<string | null> => {
    try {
      await SyncService.init();
      const fileUri = `${await SyncService._docsDirDaSessao()}${docId}.pdf`;
      const normalizedUrl = String(url || "")
        .replace(/`/g, "")
        .trim();
      if (!normalizedUrl) return null;
      return await comDocDownloadLock(fileUri, async () => {
        // Teto absoluto: a fila deste arquivo nunca fica presa, nem se o nativo não responder.
        let tetoTimer: ReturnType<typeof setTimeout> | null = null;
        const teto = new Promise<null>((resolve) => {
          tetoTimer = setTimeout(
            () => resolve(null),
            DOC_DOWNLOAD_TOTAL_MS + 60 * 1000,
          );
        });
        try {
          return await Promise.race([
            SyncService._baixarDocumentoNaFila(
              docId,
              fileUri,
              normalizedUrl,
              assinado,
            ),
            teto,
          ]);
        } finally {
          if (tetoTimer) clearTimeout(tetoTimer);
        }
      });
    } catch (error) {
      console.error(`[Sync] Erro ao baixar documento ${docId}:`, error);
      return null;
    }
  },

  /** O corpo do downloadDocument — só dentro da fila do arquivo. */
  _baixarDocumentoNaFila: async (
    docId: number,
    fileUri: string,
    normalizedUrl: string,
    assinado?: string,
  ): Promise<string | null> => {
    // O documento é baixado UMA única vez. Só rebaixamos quando ele passa a
    // ASSINADO ('S') no servidor — aí o PDF assinado (_ass.pdf) fica disponível
    // e precisamos atualizar a cópia local. NÃO usamos mudança de URL como
    // gatilho: a URL pode variar por token/cache-bust e dispararia download a
    // cada abertura da tela (era o bug de "baixa tudo de novo toda vez").
    let signedMap: Record<string, string> = {};
    try {
      const raw = await AsyncStorage.getItem(
        await chaveTenant(LOCAL_DOC_SIGNED_KEY),
      );
      signedMap = raw ? JSON.parse(raw) : {};
    } catch {
      signedMap = {};
    }

    // Endereço do último download (SEM query string p/ ignorar token/cache-bust). Se o
    // endereço no banco passa a apontar p/ OUTRO arquivo (path/nome diferente), re-baixa
    // — resolve "troquei o endereço no banco e o app não atualizou".
    let urlMap: Record<string, string> = {};
    try {
      const raw = await AsyncStorage.getItem(
        await chaveTenant(LOCAL_DOC_URL_KEY),
      );
      urlMap = raw ? JSON.parse(raw) : {};
    } catch {
      urlMap = {};
    }
    const urlKey = normalizedUrl.split("?")[0];
    const lastUrlKey = urlMap[String(docId)];
    const enderecoMudou = lastUrlKey != null && lastUrlKey !== urlKey;

    const estaAssinado =
      String(assinado || "")
        .trim()
        .toUpperCase() === "S";
    const jaBaixouAssinado =
      String(signedMap[String(docId)] || "").toUpperCase() === "S";
    const fileInfo = await FileSystem.getInfoAsync(fileUri);
    const existe = fileInfo.exists && Number((fileInfo as any).size) > 0;
    // Cache só vale se for PDF inteiro: página de erro gravada como .pdf, ou PDF cortado por
    // download interrompido (versões anteriores), passam a ser baixados de novo. Erro de
    // LEITURA não é conteúdo ruim: a cópia não é apagada por isso.
    const estadoCache = existe
      ? await SyncService._pdfValido(fileUri)
      : "invalido";
    const cacheValido = estadoCache === "valido";
    // PROVENIÊNCIA: a cópia só serve no lugar do endereço pedido se veio DELE. Endereço
    // trocado (documento substituído, ou virou o _Ass assinado) → a cópia é de OUTRO arquivo
    // e não aparece, nem offline nem quando o download novo falha. Cópia sem endereço gravado
    // (o "Sair" limpa o AsyncStorage e o PDF fica) só serve se o pedido não for o assinado —
    // senão poderia ser a versão anterior à assinatura.
    const servivel =
      cacheValido &&
      (lastUrlKey === urlKey ||
        (lastUrlKey == null && !SyncService.isArquivoAssinadoServidor(urlKey)));
    const shouldRedownload =
      !cacheValido ||
      (estaAssinado && !jaBaixouAssinado) ||
      enderecoMudou ||
      // Cache ÓRFÃO: arquivo existe mas SEM baseline de endereço (migração/perda do
      // AsyncStorage). Revalida UMA vez quando online (offline usa o cache abaixo). Sem
      // isso, um doc revertido ANTES do baseline existir ficaria stale pra sempre
      // (enderecoMudou exige lastUrlKey != null).
      lastUrlKey == null;

    if (!shouldRedownload) return fileUri;

    const semCopiaNova = async (motivo: string): Promise<string | null> => {
      console.warn(
        `[Sync] Documento ${docId} NÃO baixado (${motivo}) — ${servivel ? "segue a cópia anterior" : "sem cópia válida deste endereço"}.`,
      );
      if (existe && estadoCache === "invalido") {
        // Cópia inválida (página de erro / PDF cortado): não serve a ninguém.
        await FileSystem.deleteAsync(fileUri, { idempotent: true }).catch(
          () => {},
        );
      }
      return servivel ? fileUri : null;
    };

    const recusado = _downloadRecusado.get(`${fileUri}|${urlKey}`) || 0;
    if (Date.now() - recusado < DOC_DOWNLOAD_RECUSADO_MS)
      return semCopiaNova("endereço recusado há pouco");

    const online = await SyncService.checkInternet();
    if (!online) return servivel ? fileUri : null;

    // Baixa num temporário ÚNICO da mesma pasta. O arquivo bom NÃO é apagado antes: se o
    // download falhar, ele continua servindo (quando é deste endereço).
    const parte = `${fileUri.slice(0, -4)}.part-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;
    // Codifica o que falta (espaço, acento, '%' solto) sem recodificar escapes que já vêm na
    // URL (%23, %2F, %2B...) — `encodeURI(decodeURI(...))` transformava %23 em %2523.
    const urlCodificada = encodeURI(normalizedUrl).replace(
      /%25([0-9A-Fa-f]{2})/g,
      "%$1",
    );
    console.log(`[Sync] Baixando documento ${docId}...`);
    const res = await SyncService._baixarComPrazo(urlCodificada, parte);
    const status = Number(res?.status) || 0;
    const estadoParte =
      res && status >= 200 && status < 300
        ? await SyncService._pdfValido(parte)
        : "invalido";
    if (estadoParte !== "valido") {
      await FileSystem.deleteAsync(parte, { idempotent: true }).catch(() => {});
      // Resposta definitiva (4xx, ou 2xx que não é PDF): não insiste por alguns minutos.
      if ((status >= 400 && status < 500) || (status >= 200 && status < 300))
        _downloadRecusado.set(`${fileUri}|${urlKey}`, Date.now());
      return semCopiaNova(
        `status ${status || "sem resposta"}${
          status >= 200 && status < 300 ? ", conteúdo não é PDF inteiro" : ""
        }`,
      );
    }
    _downloadRecusado.delete(`${fileUri}|${urlKey}`);

    _pdfsConferidos.delete(parte);
    try {
      await FileSystem.moveAsync({ from: parte, to: fileUri });
    } catch (e) {
      console.warn(`[Sync] Documento ${docId}: troca do arquivo falhou:`, e);
      // No iOS o move apaga o destino ANTES de mover: se ele sumiu, tenta de novo — o .part
      // é a única cópia boa agora.
      const destino = await FileSystem.getInfoAsync(fileUri).catch(
        () => ({ exists: true }) as any,
      );
      let movido = false;
      if (!destino.exists) {
        movido = await FileSystem.moveAsync({ from: parte, to: fileUri })
          .then(() => true)
          .catch(() => false);
      }
      if (!movido) {
        if (destino.exists)
          await FileSystem.deleteAsync(parte, { idempotent: true }).catch(
            () => {},
          );
        return destino.exists && servivel ? fileUri : null;
      }
    }
    console.log(`[Sync] Download concluído: ${fileUri}`);
    try {
      // Mutador atômico: o download acima é LONGO (rede) — reescrever o
      // mapa inteiro lido antes dele poderia gravar dados do tenant
      // anterior; aqui a chave é resolvida e lida no MOMENTO da gravação.
      await mutarEspelhoTenant(LOCAL_DOC_SIGNED_KEY, (atual) => {
        const m = atual && typeof atual === "object" ? atual : {};
        m[String(docId)] = estaAssinado ? "S" : "N";
        return m;
      });
      await mutarEspelhoTenant(LOCAL_DOC_URL_KEY, (atual) => {
        const m = atual && typeof atual === "object" ? atual : {};
        m[String(docId)] = urlKey;
        return m;
      });
    } catch {}
    return fileUri;
  },

  /**
   * Download com PRAZO para um arquivo temporário. Sem bytes por 30s (ou o teto de sanidade),
   * cancela e devolve null. Usa o download "resumable", que no Android FALHA quando a rede
   * cai no meio — o comum (`downloadAsync`) ficava pendurado para sempre nesse caso.
   */
  _baixarComPrazo: async (
    url: string,
    destino: string,
  ): Promise<{ status: number } | null> => {
    let ultimoProgresso = Date.now();
    const inicio = Date.now();
    // WEB: o shim faz fetch + ReadableStream (progresso por chunk, cancelAsync = abort);
    // `sessionType` é ignorado. Exige CORS no host dos PDFs.
    const tarefa = FileSystem.createDownloadResumable(
      url,
      destino,
      {
        headers: DOC_DOWNLOAD_HEADERS,
        sessionType: FileSystem.FileSystemSessionType.FOREGROUND,
      },
      () => {
        ultimoProgresso = Date.now();
      },
    );
    let vigia: ReturnType<typeof setInterval> | null = null;
    // Em 2º plano os timers e o progresso param: ao voltar, o vigia não pode tomar o tempo
    // parado como "sem progresso".
    const voltaAoApp = AppState.addEventListener("change", (estado) => {
      if (estado === "active") ultimoProgresso = Date.now();
    });
    const prazo = new Promise<null>((resolve) => {
      vigia = setInterval(() => {
        const agora = Date.now();
        if (
          agora - ultimoProgresso > DOC_DOWNLOAD_SEM_PROGRESSO_MS ||
          agora - inicio > DOC_DOWNLOAD_TOTAL_MS
        ) {
          console.warn(
            "[Sync] Download de documento sem resposta — cancelado.",
          );
          tarefa.cancelAsync().catch(() => {});
          resolve(null);
        }
      }, 2000);
    });
    try {
      const res = await Promise.race([
        tarefa.downloadAsync().catch((e) => {
          console.warn("[Sync] Download de documento falhou:", e);
          return null;
        }),
        prazo,
      ]);
      return res ? { status: Number((res as any).status) || 0 } : null;
    } finally {
      if (vigia) clearInterval(vigia);
      voltaAoApp.remove();
      // O expo só solta o listener de progresso no pause/cancel: sem isto, cada download
      // concluído deixava um listener pendurado.
      (tarefa as any).removeSubscription?.();
    }
  },

  /**
   * O arquivo é um PDF INTEIRO? '%PDF-' no primeiro 1 KB e '%%EOF' nos últimos 4 KB. Página
   * de erro (HTML/XML/JSON), arquivo vazio ou cortado no meio → "invalido". Falha de LEITURA
   * (ex.: iOS com o aparelho bloqueado) → "erro": não diz nada sobre o conteúdo, ninguém
   * apaga a cópia por isso. Lê só esses trechos; "valido" fica guardado por tamanho + data.
   */
  _pdfValido: async (uri: string): Promise<"valido" | "invalido" | "erro"> => {
    try {
      const info = await FileSystem.getInfoAsync(uri);
      const tamanho = Number((info as any).size) || 0;
      if (!info.exists || tamanho < 32) return "invalido";
      const assinatura = `${tamanho}:${Math.floor(Number((info as any).modificationTime) || 0)}`;
      if (_pdfsConferidos.get(uri) === assinatura) return "valido";
      const cabeca = base64ParaBinario(
        await FileSystem.readAsStringAsync(uri, {
          encoding: "base64",
          position: 0,
          length: Math.min(1024, tamanho),
        }),
      );
      if (!cabeca.includes("%PDF-")) return "invalido";
      const tamanhoRabo = Math.min(4096, tamanho);
      const rabo = base64ParaBinario(
        await FileSystem.readAsStringAsync(uri, {
          encoding: "base64",
          position: tamanho - tamanhoRabo,
          length: tamanhoRabo,
        }),
      );
      if (!rabo.includes("%%EOF")) return "invalido";
      _pdfsConferidos.set(uri, assinatura);
      return "valido";
    } catch {
      return "erro";
    }
  },

  /**
   * Pré-download em segundo plano (Home e Arquivos), no máximo 2 por vez. O tenant é
   * conferido na hora de baixar: item enfileirado antes de uma troca de empresa é descartado
   * (os ids colidem entre bancos).
   */
  prebaixarDocumentos: async (
    lista: { docId: number; url: string; assinado?: string }[],
  ): Promise<void> => {
    const tenant = await tenantHashSessaoAtual().catch(() => "");
    for (const d of lista) {
      if (!d?.docId || !d?.url) continue;
      const ja = _filaPrebaixar.find(
        (x) => x.docId === d.docId && x.tenant === tenant,
      );
      if (ja) {
        ja.url = d.url;
        ja.assinado = d.assinado;
      } else {
        _filaPrebaixar.push({ ...d, tenant });
      }
    }
    const proximo = () => {
      while (_prebaixando < PREBAIXAR_PARALELO && _filaPrebaixar.length > 0) {
        const item = _filaPrebaixar.shift()!;
        _prebaixando++;
        void (async () => {
          try {
            const agora = await tenantHashSessaoAtual().catch(() => "");
            if (agora !== item.tenant) return;
            await SyncService.downloadDocument(
              item.docId,
              item.url,
              item.assinado,
            );
          } catch {
          } finally {
            _prebaixando--;
            proximo();
          }
        })();
      }
    };
    proximo();
  },

  /**
   * O visualizador recusou o documento: descarta a FAIXA extraída (pdf-lib, refeita na
   * próxima abertura) e, se a própria cópia estiver inválida, também ela e o endereço gravado
   * — para o próximo acesso baixar de novo. Cópia que passa na conferência fica: apagá-la só
   * faria baixar o mesmo arquivo de novo a cada falha. Só chamar online.
   */
  descartarCacheDocumento: async (docId: number | string): Promise<void> => {
    try {
      const cacheDir: string = (FileSystem as any).cacheDirectory ?? "";
      if (cacheDir) {
        const nomes = await FileSystem.readDirectoryAsync(cacheDir).catch(
          () => [] as string[],
        );
        for (const nome of nomes) {
          if (nome.startsWith(`doc_${docId}_p`))
            await FileSystem.deleteAsync(`${cacheDir}${nome}`, {
              idempotent: true,
            }).catch(() => {});
        }
      }
      const fileUri = `${await SyncService._docsDirDaSessao()}${docId}.pdf`;
      await comDocDownloadLock(fileUri, async () => {
        if ((await SyncService._pdfValido(fileUri)) !== "invalido") return;
        await FileSystem.deleteAsync(fileUri, { idempotent: true }).catch(
          () => {},
        );
        _pdfsConferidos.delete(fileUri);
        await mutarEspelhoTenant(LOCAL_DOC_URL_KEY, (atual) => {
          if (!atual || typeof atual !== "object") return undefined;
          delete atual[String(docId)];
          return atual;
        });
        await mutarEspelhoTenant(LOCAL_DOC_SIGNED_KEY, (atual) => {
          if (!atual || typeof atual !== "object") return undefined;
          delete atual[String(docId)];
          return atual;
        });
      });
    } catch (e) {
      console.warn("[Sync] descartarCacheDocumento: erro", e);
    }
  },

  /**
   * Retorna o caminho local de um documento (na pasta do TENANT da sessão).
   */
  getLocalDocumentUri: async (docId: number): Promise<string> => {
    return `${await SyncService._docsDirDaSessao()}${docId}.pdf`;
  },

  /**
   * Documento ASSINADO DE VERDADE no servidor = o arquivo assinado (`..._Ass.pdf`, também
   * `_assinado.pdf` / `_Ass_Sup.pdf`) foi CRIADO. É o único sinal FIEL de "sincronizado"
   * (2 checks): o campo `assinado` do /iniciar vira 'S' só por existir registro em
   * tab_adm_contra_cheque_assinatura (sem o arquivo) e ainda OSCILA 'S'/'N'. O NOME do
   * arquivo é estável. Casa o sufixo de assinatura antes do `.pdf` (ignora query/hash).
   */
  isArquivoAssinadoServidor: (nomeOuUrl?: string): boolean => {
    const nome = String(nomeOuUrl ?? "")
      .split("?")[0]
      .split("#")[0];
    return /_ass(inado)?(_sup)?\.pdf$/i.test(nome);
  },

  /**
   * Lê o cache local de status de documentos (assinaturas registradas no app
   * que precisam sobreviver a refreshes da API).
   */
  getLocalDocStatusMap: async (): Promise<LocalDocStatusMap> => {
    try {
      const raw = await AsyncStorage.getItem(
        await chaveTenant(LOCAL_DOC_STATUS_KEY),
      );
      return raw ? JSON.parse(raw) : {};
    } catch (error) {
      console.warn("[Sync] Erro lendo local doc status:", error);
      return {};
    }
  },

  /**
   * Marca um documento como assinado localmente (online ou offline).
   */
  setLocalDocStatus: async (docId: number | string, status: LocalDocStatus) => {
    try {
      await mutarEspelhoTenant(LOCAL_DOC_STATUS_KEY, (atual) => {
        const map = atual && typeof atual === "object" ? atual : {};
        map[String(docId)] = status;
        return map;
      });
      // Marca 'O' (assinado localmente) SEMPRE carimba a entrega sob o MESMO docId — assim o
      // applyLocalDocStatus encontra o timestamp do grace EXATAMENTE na chave que ele consulta
      // (id_adm_contra_cheque_colaborador). Sem esse pareamento, um marcador cujo deliveredAt
      // ficou sob outra chave (ou não foi gravado) era lido como "revertido" (entregueMs=0) e o
      // doc voltava a PENDENTE logo após assinar. Se já houver carimbo, a entrega real (drain da
      // fila) o sobrescreve com o horário correto.
      if (String(status).toUpperCase() === "O") {
        await SyncService.setLocalDocDeliveredAt(docId, Date.now());
      }
    } catch (error) {
      console.warn("[Sync] Erro salvando local doc status:", error);
    }
  },

  /**
   * Carimba o momento (ms) em que a assinatura de um doc foi ENTREGUE ao servidor
   * (SIGNATURE drenada com sucesso). Usado pelo applyLocalDocStatus p/ decidir, junto do
   * grace, se um doc SEM _Ass.pdf ainda está aguardando o consumer ou já foi revertido.
   */
  setLocalDocDeliveredAt: async (docId: number | string, ts: number) => {
    try {
      await mutarEspelhoTenant(LOCAL_DOC_DELIVERED_AT_KEY, (atual) => {
        const map = atual && typeof atual === "object" ? atual : {};
        map[String(docId)] = ts;
        return map;
      });
    } catch (error) {
      console.warn("[Sync] Erro salvando local doc deliveredAt:", error);
    }
  },

  /** Mapa docId→ms de entrega da assinatura (usado pelo grace do resolver central). */
  getLocalDocDeliveredAtMap: async (): Promise<Record<string, number>> => {
    try {
      const raw = await AsyncStorage.getItem(
        await chaveTenant(LOCAL_DOC_DELIVERED_AT_KEY),
      );
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  },

  // ── "Ver Depois" da assinatura obrigatória ──────────────────────────────────

  /**
   * Estado do "Ver Depois": `usadoHoje` decide se o BOTÃO aparece (uma vez por dia,
   * reiniciando à meia-noite LOCAL) e `emGraca` decide se a COBRANÇA está suspensa
   * (30min a partir do clique). Fail-safe na leitura: erro = {usadoHoje: true,
   * emGraca: false} — sem botão e sem suspender a cobrança.
   */
  getDocVerDepoisEstado: async (): Promise<{
    usadoHoje: boolean;
    emGraca: boolean;
    graceRestanteMs: number;
  }> => {
    try {
      const raw = await AsyncStorage.getItem(
        await chaveTenant(DOC_VER_DEPOIS_KEY),
      );
      const dado = raw ? JSON.parse(raw) : null;
      const dia = String(dado?.dia ?? "");
      const em = Number(dado?.em) || 0;
      const d = new Date();
      const hoje = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const usadoHoje = dia === hoje && em > 0;
      const restante = usadoHoje ? em + VER_DEPOIS_GRACA_MS - Date.now() : 0;
      return {
        usadoHoje,
        emGraca: restante > 0,
        graceRestanteMs: Math.max(0, restante),
      };
    } catch {
      return { usadoHoje: true, emGraca: false, graceRestanteMs: 0 };
    }
  },

  /** Registra o clique de hoje no "Ver Depois" (inicia os 30min de graça). */
  marcarDocVerDepois: async (): Promise<void> => {
    try {
      const d = new Date();
      const hoje = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      await AsyncStorage.setItem(
        await chaveTenant(DOC_VER_DEPOIS_KEY),
        JSON.stringify({ dia: hoje, em: Date.now() }),
      );
    } catch (e) {
      console.warn("[Sync] marcarDocVerDepois: erro", e);
    }
  },

  // ── Falha de CARGA do documento na assinatura (ver DOC_FALHA_CARGA_KEY) ──────

  /** Marcas de falha de carga do tenant: {[docId]: {em, motivo, n}}. Erro de leitura = {}. */
  getDocFalhaCargaMapa: async (): Promise<
    Record<string, { em: number; motivo: string; n: number }>
  > => {
    try {
      const raw = await AsyncStorage.getItem(
        await chaveTenant(DOC_FALHA_CARGA_KEY),
      );
      const mapa = raw ? JSON.parse(raw) : {};
      return mapa && typeof mapa === "object" ? mapa : {};
    } catch {
      return {};
    }
  },

  /**
   * O documento não apareceu na tela de assinatura: registra a falha e devolve por quanto
   * tempo a cobrança fica suspensa (5 min na 1ª, 15 na 2ª seguida, 60 da 3ª em diante; a
   * contagem recomeça depois de 24h sem falha). Gravado no MOMENTO da falha — fechar o app
   * em seguida não volta a travar a tela.
   */
  marcarDocFalhaCarga: async (
    docId: number | string,
    motivo: string,
  ): Promise<number> => {
    let n = 1;
    try {
      await mutarEspelhoTenant(DOC_FALHA_CARGA_KEY, (atual) => {
        const mapa = atual && typeof atual === "object" ? atual : {};
        const agora = Date.now();
        // Poda: marca com mais de 7 dias não serve para nada.
        for (const [k, v] of Object.entries(mapa)) {
          if (agora - (Number((v as any)?.em) || 0) > 7 * 24 * 60 * 60 * 1000)
            delete mapa[k];
        }
        const anterior = mapa[String(docId)];
        const recente =
          anterior && agora - (Number(anterior.em) || 0) < 24 * 60 * 60 * 1000;
        n = recente ? (Number(anterior.n) || 0) + 1 : 1;
        mapa[String(docId)] = { em: agora, motivo: String(motivo || ""), n };
        return mapa;
      });
    } catch (e) {
      console.warn("[Sync] marcarDocFalhaCarga: erro", e);
    }
    return gracaFalhaCarga(n);
  },

  /** O documento abriu: some a marca de falha (e a contagem do intervalo maior). */
  limparDocFalhaCarga: async (docId: number | string): Promise<void> => {
    try {
      await mutarEspelhoTenant(DOC_FALHA_CARGA_KEY, (atual) => {
        if (!atual || typeof atual !== "object" || !atual[String(docId)])
          return undefined;
        delete atual[String(docId)];
        return atual;
      });
    } catch {}
  },

  /**
   * Estado da COBRANÇA de assinatura: o "Ver Depois" somado à falha de carga. `emGraca` =
   * cobrança suspensa (auto-abrir, bloqueio de voltar e modal da Home esperam) até o fim da
   * janela mais longa. `usadoHoje` é só do "Ver Depois" (decide se o botão aparece).
   * `falhas` = marcas por documento, para quem ordena o próximo a apresentar.
   */
  getDocCobrancaEstado: async (): Promise<{
    usadoHoje: boolean;
    emGraca: boolean;
    graceRestanteMs: number;
    falhas: Record<string, { em: number; motivo: string; n: number }>;
  }> => {
    const verDepois = await SyncService.getDocVerDepoisEstado();
    const falhas = await SyncService.getDocFalhaCargaMapa();
    const agora = Date.now();
    let restanteFalha = 0;
    for (const f of Object.values(falhas)) {
      const em = Number(f?.em) || 0;
      // Marca "no futuro" além de 1 dia = relógio absurdo: ignora. Relógio que voltou um
      // pouco conta como recém-falhado (a janela inteira) — nunca trava antes da hora.
      if (!em || em - agora > 24 * 60 * 60 * 1000) continue;
      const graca = gracaFalhaCarga(Number(f?.n) || 1);
      restanteFalha = Math.max(restanteFalha, graca - Math.max(0, agora - em));
    }
    return {
      usadoHoje: verDepois.usadoHoje,
      emGraca: verDepois.emGraca || restanteFalha > 0,
      graceRestanteMs: Math.max(verDepois.graceRestanteMs, restanteFalha),
      falhas,
    };
  },

  /**
   * Remove a marcação local de um documento (quando o backend já confirmou).
   */
  clearLocalDocStatus: async (docId: number | string) => {
    try {
      await mutarEspelhoTenant(LOCAL_DOC_STATUS_KEY, (atual) => {
        const map = atual && typeof atual === "object" ? atual : {};
        delete map[String(docId)];
        return map;
      });
      // Limpa também o carimbo de entrega (o par do marcador).
      await mutarEspelhoTenant(LOCAL_DOC_DELIVERED_AT_KEY, (atual) => {
        if (!atual || typeof atual !== "object") return undefined;
        delete atual[String(docId)];
        return atual;
      });
    } catch (error) {
      console.warn("[Sync] Erro removendo local doc status:", error);
    }
  },

  // ─── Contestação de documento (offline-first) ──────────────────────────────────
  // Espelha o status_contestacao localmente: ao contestar, o doc deve sair de
  // "obrigatório a assinar" na hora (mesmo offline), antes do backend refletir.
  // `terminal` ('A'/'R'): decisão FINAL do admin já vista uma vez — STICKY local, porque o
  // status_contestacao do servidor OSCILA entre réplicas e a label sumia/regressava.
  getLocalDocContestacaoMap: async (): Promise<
    Record<string, { motivo: string; data_log: string; terminal?: "A" | "R" }>
  > => {
    try {
      const raw = await AsyncStorage.getItem(
        await chaveTenant(LOCAL_DOC_CONTESTACAO_KEY),
      );
      return raw ? JSON.parse(raw) : {};
    } catch (error) {
      console.warn("[Sync] Erro lendo local doc contestação:", error);
      return {};
    }
  },

  setLocalDocContestado: async (
    docId: number | string,
    motivo: string,
    data_log: string,
  ) => {
    try {
      await mutarEspelhoTenant(LOCAL_DOC_CONTESTACAO_KEY, (atual) => {
        const map = atual && typeof atual === "object" ? atual : {};
        map[String(docId)] = { motivo, data_log };
        return map;
      });
    } catch (error) {
      console.warn("[Sync] Erro salvando local doc contestação:", error);
    }
  },

  clearLocalDocContestado: async (docId: number | string) => {
    try {
      await mutarEspelhoTenant(LOCAL_DOC_CONTESTACAO_KEY, (atual) => {
        if (!atual || typeof atual !== "object") return undefined;
        delete atual[String(docId)];
        return atual;
      });
    } catch (error) {
      console.warn("[Sync] Erro removendo local doc contestação:", error);
    }
  },

  /**
   * Mescla os dados retornados pela API com o cache local de status,
   * evitando que assinaturas locais (online ou offline) — e contestações — sejam
   * revertidas enquanto o backend ainda não refletiu a mudança.
   */
  applyLocalDocStatus: async (dados: any): Promise<any> => {
    if (!dados?.documentos?.length) return dados;
    // Nunca exibir arquivos inativos (ativo_inativo='I') nem os da competência de TESTE
    // (12/2222 — utils/documentoVisivel.ts) — vale mesmo sem marcas locais (cobre cache
    // antigo e backend que ainda não filtra). Daqui o userData segue sem eles para todas
    // as telas.
    const ativos = dados.documentos.filter(documentoVisivelAoColaborador);
    // 🔒 Chaves capturadas JUNTO das leituras: esta função tem MUITOS awaits até
    // gravar; se a empresa trocar no meio, o dado lido continua sendo do tenant
    // ANTERIOR e é NELE que deve ser gravado (re-resolver escreveria no novo).
    const kStatus = await chaveTenant(LOCAL_DOC_STATUS_KEY);
    const kDelivered = await chaveTenant(LOCAL_DOC_DELIVERED_AT_KEY);
    const kContestacao = await chaveTenant(LOCAL_DOC_CONTESTACAO_KEY);
    const map = await SyncService.getLocalDocStatusMap();
    const contestacaoMap = await SyncService.getLocalDocContestacaoMap();
    // O log de assinados TAMBÉM conta p/ o early-return: sem isso, doc com marca já
    // limpa mas entrada viva no log pulava o HEAL e a remoção pós-_Ass.pdf.
    let logCheck: Record<string, any> = {};
    try {
      logCheck = await lerLogAssinadosDoTenant();
    } catch {}
    if (
      !Object.keys(map).length &&
      !Object.keys(contestacaoMap).length &&
      !Object.keys(logCheck).length
    ) {
      return { ...dados, documentos: ativos };
    }

    // Sinais p/ decidir a marca local de assinatura SEM depender do `assinado` oscilante:
    //  • SIGNATURE ainda na fila = assinatura NÃO entregue ao servidor (aguardando envio).
    //  • deliveredAt = quando a SIGNATURE foi entregue (drenada). Após a entrega, o consumer
    //    leva ~5s p/ gerar o _Ass.pdf → grace antes de considerar revertido.
    let filaSignatureIds = new Set<string>();
    try {
      const fila = await SyncService.getOfflineQueue();
      filaSignatureIds = new Set(
        (fila || [])
          .filter((a: any) => a?.type === "SIGNATURE")
          .map((a: any) =>
            String(a?.data?.id_adm_contra_cheque_colaborador ?? ""),
          )
          .filter(Boolean),
      );
    } catch {}
    let deliveredMap: Record<string, number> = {};
    try {
      const raw = await AsyncStorage.getItem(
        await chaveTenant(LOCAL_DOC_DELIVERED_AT_KEY),
      );
      deliveredMap = raw ? JSON.parse(raw) : {};
    } catch {}
    // Log persistente de assinados (empresa atual — já carregado no early-return) +
    // carimbo do último /iniciar REAL — a MESMA regra do resolver
    // (docAssinadoLog.ehRevertConfirmado) decide revert aqui.
    const assinadosLog: Record<string, any> = logCheck;
    let fetchedAtMs = 0;
    try {
      fetchedAtMs = await lerUserDataFetchedAt();
    } catch {}
    const agoraMs = Date.now();
    // 3 min de folga p/ o servidor gerar o _Ass.pdf após a entrega. O normal é o _Ass.pdf
    // aparecer e virar 2 checks BEM antes disso; o grace é só a rede de segurança pra NÃO
    // piscar PENDENTE enquanto o servidor gera, e pra reverter um "assinado que nunca gerou o
    // PDF". Revert de doc JÁ assinado (que teve _Ass.pdf) continua INSTANTÂNEO (marca já limpa).
    const GRACE_MS = 180000;

    const contestacoesTerminais: { docId: string; terminal: "A" | "R" }[] = [];
    const idsStatusParaLimpar: string[] = [];
    const idsLogParaConfirmar: string[] = [];
    const documentos = ativos.map((d: any) => {
      const docId = String(d.id_adm_contra_cheque_colaborador || d.id);
      let doc = d;

      // Assinatura local = OTIMISTA e AUTOLIMPÁVEL (não mais "sticky pra sempre").
      // A VERDADE do servidor é o ARQUIVO ASSINADO (_Ass.pdf) TER SIDO CRIADO — e NÃO o
      // campo `assinado`, que o /iniciar computa também via "existe registro em
      // tab_adm_contra_cheque_assinatura" (vira 'S' sem o arquivo) e ainda OSCILA 'S'/'N'.
      // O nome NÃO oscila. Decisão da marca (SEMPRE por id_adm_contra_cheque_colaborador):
      //  • tem _Ass.pdf → LIMPA a marca (servidor confirmou) → 2 checks.
      //  • sem _Ass.pdf + SIGNATURE ainda na fila (não entregue) → mantém 'O' (1 check).
      //  • sem _Ass.pdf + entregue há < GRACE → mantém 'O' (consumer ainda gerando o PDF).
      //  • sem _Ass.pdf + entregue há > GRACE → REVERTIDO (Sync com sucesso e SEM _Ass.pdf)
      //    → LIMPA a marca → volta a PENDENTE (sem selo).
      const assinadoServidor = SyncService.isArquivoAssinadoServidor(
        d?.nome_arquivo || d?.url,
      );
      const localStatus = map[docId];
      const logEntry = assinadosLog[docId];
      if (assinadoServidor) {
        if (localStatus) idsStatusParaLimpar.push(docId);
        // _Ass.pdf VISTO → marca a CONFIRMAÇÃO no log (STICKY — NÃO remove!). O nome
        // OSCILA entre réplicas do t-credentials (cache ~45s): remover aqui fazia o
        // fetch seguinte (réplica com nome antigo) achar o doc sem proteção → PENDENTE
        // + modal → assinado de novo (flip-flop). Também CRIA a entrada p/ doc assinado
        // em outro aparelho/antes do deploy — protege da oscilação do mesmo jeito.
        if (!logEntry?.confirmadoEmMs) idsLogParaConfirmar.push(docId);
      } else if (logEntry) {
        // ── LOG PERSISTENTE: revert só por CONFIRMAÇÃO FRESCA (mesma regra do resolver) ──
        // Nada de relógio cego: só reverte quando um /iniciar REAL (fetchedAtMs), posterior à
        // entrega + janela, ainda veio sem o _Ass.pdf. Cobre inclusive marca 'O' já apagada
        // por versões antigas (o log não depende dela).
        const naFila = filaSignatureIds.has(docId);
        if (!ehRevertConfirmado(logEntry, naFila, fetchedAtMs)) {
          doc = { ...doc, assinado: localStatus || "O" };
        } else if (localStatus) {
          idsStatusParaLimpar.push(docId); // revert confirmado → limpa a marca (log fica; regra é estável)
        }
      } else if (localStatus) {
        // ── LEGADO (sem entrada no log — assinatura anterior a este deploy) ──
        const naFila = filaSignatureIds.has(docId);
        const entregueMs = Number(deliveredMap[docId] || 0);
        const dentroGraca = entregueMs > 0 && agoraMs - entregueMs <= GRACE_MS;
        const semCarimbo = entregueMs === 0;
        // REVERTE (→ PENDENTE) só quando há carimbo de entrega e ele JÁ passou do grace sem
        // _Ass.pdf. Mantém 'O' (1 check) se: ainda na fila (não entregue) OU dentro do grace OU
        // SEM carimbo. Sem carimbo é AMBÍGUO (marca recém-criada) → NÃO reverter na hora; deixa
        // o _Ass.pdf (2 checks) ou um carimbo expirado decidir depois. Com o carimbo pareado no
        // setLocalDocStatus, todo doc recém-assinado cai no grace e permanece assinado.
        if (naFila || dentroGraca || semCarimbo) {
          doc = { ...doc, assinado: localStatus };
        } else {
          idsStatusParaLimpar.push(docId);
        }
      }

      // Contestação local — STICKY, INCLUSIVE o terminal. O status_contestacao do servidor
      // OSCILA entre réplicas (cache ~45s): depois do 'R' (revogada), refreshes podiam trazer
      // 'S' velho ou '' → a label regressava p/ "Em contestação" ou SUMIA. Antes a gente
      // LIMPAVA a marca ao ver A/R (e ficava ao sabor da oscilação); agora GRAVA o terminal
      // na marca local:
      //  • server A/R → registra o terminal (uma vez) — a marca vira a memória da decisão;
      //  • marca com terminal + server oscilando ('S'/'') → overlay do TERMINAL (estável);
      //  • marca sem terminal (contestação recém-feita) → overlay 'S' (comportamento antigo);
      //  • doc SEM marca que chega com A/R → cria a marca terminal (protege contestação
      //    feita em outro aparelho da mesma oscilação).
      const localContest = contestacaoMap[docId];
      const serverContest = String(d.status_contestacao ?? "")
        .trim()
        .toUpperCase();
      if (serverContest === "A" || serverContest === "R") {
        if (localContest?.terminal !== serverContest)
          contestacoesTerminais.push({
            docId,
            terminal: serverContest as "A" | "R",
          });
      } else if (localContest?.terminal) {
        doc = { ...doc, status_contestacao: localContest.terminal };
      } else if (localContest) {
        doc = {
          ...doc,
          status_contestacao: "S",
          motivo_contestacao_colaborador: localContest.motivo,
          data_log_contestacao_colaborador: localContest.data_log,
        };
      }

      return doc;
    });

    // Confirmações no log (_Ass.pdf visto): SEQUENCIAL (cada uma é um read-modify-write
    // do mapa inteiro — em paralelo perderia updates). Uma vez por doc (guard acima).
    for (const idLog of idsLogParaConfirmar) {
      try {
        await marcarDocConfirmadoLog(idLog);
      } catch {}
    }

    if (idsStatusParaLimpar.length) {
      try {
        const novoStatusMap = { ...map };
        const novoDeliveredMap = { ...deliveredMap };
        idsStatusParaLimpar.forEach((k) => {
          delete novoStatusMap[k];
          delete novoDeliveredMap[k];
        });
        await setItemEscopado(
          LOCAL_DOC_STATUS_KEY,
          JSON.stringify(novoStatusMap),
          kStatus,
        );
        await setItemEscopado(
          LOCAL_DOC_DELIVERED_AT_KEY,
          JSON.stringify(novoDeliveredMap),
          kDelivered,
        );
      } catch (error) {
        console.warn("[Sync] Erro limpando local doc status:", error);
      }
    }

    if (contestacoesTerminais.length) {
      try {
        const novoMap: Record<string, any> = { ...contestacaoMap };
        contestacoesTerminais.forEach(({ docId, terminal }) => {
          novoMap[docId] = {
            motivo: novoMap[docId]?.motivo ?? "",
            data_log: novoMap[docId]?.data_log ?? "",
            terminal,
          };
        });
        await setItemEscopado(
          LOCAL_DOC_CONTESTACAO_KEY,
          JSON.stringify(novoMap),
          kContestacao,
        );
      } catch (error) {
        console.warn("[Sync] Erro gravando terminal de contestação:", error);
      }
    }

    return { ...dados, documentos };
  },

  // ─── Notificações (marcar como lido — offline-first, mesmo padrão do doc status) ──
  getLocalNotificacoesLidas: async (): Promise<string[]> => {
    try {
      const raw = await AsyncStorage.getItem(
        await chaveTenant(LOCAL_NOTIF_LIDAS_KEY),
      );
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.map(String) : [];
    } catch {
      return [];
    }
  },

  setLocalNotificacoesLidas: async (ids: (number | string)[]) => {
    try {
      await mutarEspelhoTenant(LOCAL_NOTIF_LIDAS_KEY, (atualRaw) => {
        const set = new Set(
          Array.isArray(atualRaw) ? atualRaw.map(String) : [],
        );
        ids.forEach((id) => {
          const s = String(id ?? "").trim();
          if (s) set.add(s);
        });
        return [...set];
      });
    } catch (error) {
      console.warn("[Sync] Erro salvando notificações lidas locais:", error);
    }
  },

  /**
   * Mescla o cache local: mantém lido=1 nas notificações marcadas no app até o backend
   * confirmar — evita o badge "voltar" num refresh antes da fila sincronizar.
   */
  applyLocalNotificacoesLidas: async (dados: any): Promise<any> => {
    if (!dados?.notificacoes?.length) return dados;
    const ids = await SyncService.getLocalNotificacoesLidas();
    if (!ids.length) return dados;
    const idSet = new Set(ids);
    const idsParaLimpar: string[] = [];
    const notificacoes = dados.notificacoes.map((n: any) => {
      const nid = String(n?.id_notificacao);
      if (!idSet.has(nid)) return n;
      if (Number(n?.lido) === 1) {
        idsParaLimpar.push(nid);
        return n;
      } // backend já confirmou
      return { ...n, lido: 1 };
    });
    if (idsParaLimpar.length) {
      try {
        await mutarEspelhoTenant(LOCAL_NOTIF_LIDAS_KEY, (atualRaw) => {
          const atuais = Array.isArray(atualRaw) ? atualRaw.map(String) : [];
          return atuais.filter((id) => !idsParaLimpar.includes(id));
        });
      } catch {
        /* best-effort */
      }
    }
    return { ...dados, notificacoes };
  },

  /**
   * Marca notificações como lidas — offline-first: registra local na hora, tenta online
   * e ENFILEIRA no sync se estiver sem internet (ou a API falhar). Ação idempotente.
   */
  marcarNotificacoesLidas: async (ids: (number | string)[] = []) => {
    await SyncService.setLocalNotificacoesLidas(ids);
    try {
      const isConnected = await SyncService.checkInternet();
      if (isConnected) {
        const response = await Api.colaboradores.marcarNotificacoesLidas();
        if (response?.success) return { success: true, offline: false };
        console.warn(
          "[Sync] Falha ao marcar notificações via API, enfileirando...",
        );
      } else {
        console.log(
          "[Sync] Sem internet, enfileirando marcação de notificações...",
        );
      }
    } catch (error) {
      console.warn(
        "[Sync] Erro ao marcar notificações lidas, enfileirando:",
        error,
      );
    }
    // Mantém apenas 1 ação POR TENANT na fila (idempotente). O id inclui o hash
    // da empresa: com id fixo, o dequeue abaixo apagava a marcação RETIDA de
    // OUTRA empresa (guarda cross-tenant) — o endpoint é por-JWT, só a sessão
    // da empresa dona entrega.
    const hashNotif = await tenantHashSessaoAtual().catch(() => "");
    const idNotif = hashNotif
      ? `NOTIFICACOES_LIDAS:${hashNotif}`
      : "NOTIFICACOES_LIDAS";
    await SyncService.dequeueOfflineAction(idNotif);
    // migração: remove também a ação legada de id fixo (era da sessão corrente)
    if (idNotif !== "NOTIFICACOES_LIDAS")
      await SyncService.dequeueOfflineAction("NOTIFICACOES_LIDAS");
    await SyncService.enqueueOfflineAction("NOTIFICACOES_LIDAS", {}, idNotif);
    return { success: true, offline: true };
  },

  /**
   * Adiciona uma ação para ser executada offline ou envia imediatamente se houver internet
   */
  signDocument: async (payload: any) => {
    await carimbarHashTenant(payload);
    const docId = payload?.id_adm_contra_cheque_colaborador;
    // 🔒 Tenant/gen do EVENTO capturados JÁ: a assinatura pertence à empresa da
    // sessão em que foi colhida. trocarEmpresa durante os awaits → envio imediato
    // abortado (fica na fila carimbada com o tenant CERTO; o dreno guarda).
    const genEvento = peekApiSessionGen();
    const tenantEvento = await tenantHashSessaoAtual().catch(() => "");
    try {
      const isConnected = await SyncService.checkInternet();

      if (isConnected && peekApiSessionGen() === genEvento) {
        console.log(
          "[Sync] Internet disponível, enviando assinatura imediatamente...",
        );
        const response = await Api.arquivo.assinar(payload);
        if (response.success) {
          console.log("[Sync] Assinatura enviada com sucesso via API");
          // 'O' (assinado AGUARDANDO sync), NÃO 'S'. O endpoint /Arquivo/assinar só
          // PUBLICA no RabbitMQ (retorna enfileirado=true) — "sucesso" = aceito, não
          // persistido. O 'assinado' do /iniciar só vira 'S' depois que o consumer
          // reescreve o arquivo (_ass.pdf). Marcar 'S' aqui afirma falsamente que o
          // backend já confirmou; quem promove/limpa é o applyLocalDocStatus quando o
          // SERVIDOR realmente retorna assinado==='S'. Com 'O' o doc fica ASSINADO no
          // app e SOBREVIVE ao pull-refresh até o backend refletir.
          if (docId != null) {
            await SyncService.setLocalDocStatus(docId, "O");
            // ENTREGUE direto (a API aceitou/publicou no Rabbit): inicia o grace até o
            // consumer gerar o _Ass.pdf. Sem este carimbo, o applyLocalDocStatus via a marca
            // "entregue" (sem SIGNATURE na fila) SEM timestamp e a considerava revertida na
            // hora → o doc PISCAVA PENDENTE logo após assinar, até o _Ass.pdf aparecer.
            await SyncService.setLocalDocDeliveredAt(docId, Date.now());
            // LOG PERSISTENTE (docAssinadoLog): sobrevive à limpeza da marca/troca de
            // empresa; base do "revert só por confirmação fresca" no resolver.
            await marcarDocAssinadoLog(docId, { entregue: true });
          }
          return { success: true, offline: false };
        } else {
          console.warn(
            "[Sync] Falha ao enviar via API, enfileirando para depois...",
          );
          await SyncService.enqueueOfflineAction(
            "SIGNATURE",
            payload,
            undefined,
            tenantEvento,
          );
          if (docId != null) {
            await SyncService.setLocalDocStatus(docId, "O");
            await marcarDocAssinadoLog(docId);
          }
          return { success: true, offline: true };
        }
      } else {
        console.log("[Sync] Sem internet, enfileirando assinatura...");
        await SyncService.enqueueOfflineAction(
          "SIGNATURE",
          payload,
          undefined,
          tenantEvento,
        );
        if (docId != null) {
          await SyncService.setLocalDocStatus(docId, "O");
          await marcarDocAssinadoLog(docId);
        }
        return { success: true, offline: true };
      }
    } catch (error) {
      console.error("[Sync] Erro no fluxo de assinatura:", error);
      // Em caso de erro crítico, tenta pelo menos salvar offline
      await SyncService.enqueueOfflineAction(
        "SIGNATURE",
        payload,
        undefined,
        tenantEvento,
      );
      if (docId != null) {
        await SyncService.setLocalDocStatus(docId, "O");
        await marcarDocAssinadoLog(docId);
      }
      return { success: true, offline: true };
    }
  },

  /**
   * Contestação de documento (colaborador registra discordância; NÃO assina).
   * Online: POST Arquivo/contestar. Se a API estiver inalcançável (offline/gateway),
   * enfileira p/ retry. Em erro de negócio (4xx — ex.: endpoint ainda não publicado)
   * reporta falha SEM enfileirar, evitando retry infinito contra um endpoint inexistente.
   */
  contestarDocumento: async (payload: any, actionId?: string) => {
    await carimbarHashTenant(payload);
    // Marca localmente JÁ (offline-first): o doc sai de "obrigatório assinar" na hora,
    // antes do backend refletir. applyLocalDocStatus limpa quando o servidor confirmar.
    const docId = payload?.id_adm_contra_cheque_colaborador;
    if (docId != null) {
      await SyncService.setLocalDocContestado(
        docId,
        String(payload?.motivo ?? ""),
        String(payload?.data_log ?? ""),
      );
    }
    try {
      const isConnected = await SyncService.checkInternet();
      if (isConnected) {
        const response = await Api.arquivo.contestar(payload);
        if (response.success) {
          return { success: true, offline: false };
        }
        if (!response.offline) {
          return { success: false, offline: false };
        }
      }
      await SyncService.enqueueOfflineAction(
        "DOCUMENTO_CONTESTAR",
        payload,
        actionId,
      );
      return { success: true, offline: true };
    } catch (error) {
      console.error("[Sync] Erro ao contestar documento:", error);
      await SyncService.enqueueOfflineAction(
        "DOCUMENTO_CONTESTAR",
        payload,
        actionId,
      );
      return { success: true, offline: true };
    }
  },

  /**
   * Marca que o colaborador VIU a resposta do adm à contestação (aceita/revogada).
   * Online → POST Arquivo/contestacaoVista; offline → fila CONTESTACAO_VISTA.
   */
  marcarContestacaoVista: async (
    docId: number,
    idColaborador: number,
    actionId?: string,
  ) => {
    const payload = {
      id_adm_contra_cheque_colaborador: docId,
      id_adm_colaborador: idColaborador,
    };
    await carimbarHashTenant(payload);
    try {
      const isConnected = await SyncService.checkInternet();
      if (isConnected) {
        const response = await Api.arquivo.contestacaoVista(payload);
        if (response.success) {
          return { success: true, offline: false };
        }
        if (!response.offline) {
          return { success: false, offline: false };
        }
      }
      await SyncService.enqueueOfflineAction(
        "CONTESTACAO_VISTA",
        payload,
        actionId,
      );
      return { success: true, offline: true };
    } catch (error) {
      console.error("[Sync] Erro ao marcar contestação vista:", error);
      await SyncService.enqueueOfflineAction(
        "CONTESTACAO_VISTA",
        payload,
        actionId,
      );
      return { success: true, offline: true };
    }
  },

  // ─── Contestações respondidas já VISTAS na Home (offline-first, padrão das recusas) ──
  getContestacoesVistas: async (): Promise<string[]> => {
    try {
      const raw = await AsyncStorage.getItem(
        await chaveTenant(LOCAL_CONTEST_VISTAS_KEY),
      );
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.map(String) : [];
    } catch (error) {
      console.warn("[Sync] Erro lendo contestações vistas:", error);
      return [];
    }
  },

  markContestacoesVistas: async (
    docIds: Array<number | string>,
  ): Promise<void> => {
    try {
      if (!docIds?.length) return;
      await mutarEspelhoTenant(LOCAL_CONTEST_VISTAS_KEY, (atualRaw) => {
        const atuais = Array.isArray(atualRaw) ? atualRaw.map(String) : [];
        return Array.from(new Set([...atuais, ...docIds.map(String)]));
      });
    } catch (error) {
      console.warn("[Sync] Erro salvando contestações vistas:", error);
    }
  },

  /**
   * Adiciona uma ação para ser executada offline
   */
  enqueueOfflineAction: async (
    type: OfflineAction["type"],
    data: any,
    customId?: string,
    tenantHashOverride?: string,
  ) => {
    try {
      // Carimbo de TENANT do envelope (guarda cross-tenant do dreno): prioridade
      // para o hash já carimbado no PAYLOAD (ex.: ponto re-enfileirado pelo
      // reconciliador — o dono é o tenant da batida, não o da sessão que está
      // re-enfileirando), depois o override do CHAMADOR (fluxos que enfileiram
      // DEPOIS de awaits longos capturam o tenant na hora do EVENTO — um
      // trocarEmpresa no meio não pode carimbar a ação com a empresa nova),
      // senão o tenant da sessão atual (claim do token).
      // Calculado FORA do lock (não segurar a fila por uma leitura de storage).
      const tenantHash =
        String(data?.ponto?.hash ?? "")
          .trim()
          .toUpperCase() ||
        // hash carimbado no PAYLOAD pela função de origem (tenant do EVENTO —
        // imune a trocarEmpresa durante os awaits até o enqueue)
        String(data?.hash ?? "")
          .trim()
          .toUpperCase() ||
        String(tenantHashOverride ?? "")
          .trim()
          .toUpperCase() ||
        (await tenantHashSessaoAtual().catch(() => ""));
      // Lock: o enqueue não pode entrelaçar com o dequeue/reescrita do dreno
      // (last-write-wins apagava um dos lados). Leitura com quarentena: fila
      // corrompida não bloqueia batidas novas (antes, parse-fail → return null
      // e o valor ruim nunca era sobrescrito).
      return await comFilaLock(async () => {
        const actions = await lerFilaParaEscrita();

        const newAction: OfflineAction = {
          id: customId || Math.random().toString(36).substr(2, 9),
          ...(tenantHash ? { tenantHash } : {}),
          type,
          data,
          timestamp: Date.now(),
        };

        // 🔁 customId = SUBSTITUIÇÃO (não acúmulo). Vários fluxos passam o id da
        // ação como customId justamente para "atualizar" a ação existente — mas
        // o push cego criava uma CÓPIA. No replay isso é explosivo: o dreno de
        // OS_FOTO_SAVE chama o wrapper de origem, que em falha RE-ENFILEIRA;
        // como o dreno também mantém a original, cada ciclo DOBRAVA a ação
        // (causa direta de "inúmeras OS com checklist/foto pendentes").
        if (customId) {
          for (let i = actions.length - 1; i >= 0; i--) {
            if (actions[i].id === customId) {
              // A substituição renova o `timestamp`; a idade da pendência (aviso de sync)
              // segue contando de quando a ação entrou na fila pela 1ª vez.
              const desde = Number(actions[i].criadoEm ?? actions[i].timestamp);
              if (Number.isFinite(desde) && desde > 0 && desde < newAction.timestamp)
                newAction.criadoEm = Math.min(desde, newAction.criadoEm ?? desde);
              actions.splice(i, 1);
            }
          }
        }

        // Dedup de assinatura: nunca empilha duas SIGNATURE do mesmo documento/página na
        // fila. Sem isto, erros transitórios/reenvios acumulavam várias cópias e cada uma
        // virava uma linha no banco (o INSERT do backend não é idempotente). Mantém apenas
        // a mais recente (a que está sendo enfileirada agora).
        // ⚠️ A chave INCLUI o tenant: ids de contracheque colidem entre bancos, e uma
        // SIGNATURE RETIDA de outra empresa (guarda cross-tenant) seria apagada por
        // uma assinatura homônima da empresa atual — perda permanente do blob.
        if (type === "SIGNATURE") {
          const sigKey = (t: any, d: any) =>
            `${String(t ?? "")
              .trim()
              .toUpperCase()}|${d?.id_adm_contra_cheque_colaborador}:${d?.pagina ?? ""}`;
          const novoK = sigKey(tenantHash, data);
          for (let i = actions.length - 1; i >= 0; i--) {
            if (
              actions[i].type === "SIGNATURE" &&
              sigKey((actions[i] as any).tenantHash, actions[i].data) === novoK
            ) {
              actions.splice(i, 1);
            }
          }
        }

        // Dedup por CONTEÚDO das ações de OS — mesma proteção da SIGNATURE, com chave
        // própria por tipo. Além de barrar duplicata nova, é o que COLAPSA filas
        // infladas pelos APKs antigos (o bug do dreno que dobrava OS_FOTO_SAVE a cada
        // ciclo): a próxima entrada varre as cópias herdadas. Fail-safe: só dedupa
        // quando a chave está COMPLETA — na dúvida, preserva (regra de ouro).
        const chaveOsConteudo = (t: string, th: any, d: any): string | null => {
          const tenant = String(th ?? "")
            .trim()
            .toUpperCase();
          if (t === "OS_SOBDEMANDA_SALVAR") {
            if (d?.chave_cliente) return `${t}|${tenant}|${d.chave_cliente}`;
            const cliente = Number(d?.id_adm_cliente ?? 0);
            const equipe = Number(d?.id_adm_ordem_servico_equipe ?? 0);
            const dataExec = String(d?.data_execucao ?? "").trim();
            if (!cliente || !equipe || !dataExec) return null;
            const obs = String(d?.observacao ?? "")
              .trim()
              .toUpperCase();
            return `${t}|${tenant}|${cliente}|${equipe}|${dataExec}|${obs}`;
          }
          if (t === "OS_FOTO_SAVE") {
            if (d?.chave_cliente) return `${t}|${tenant}|${d.chave_cliente}`;
            const crono = Number(d?.id_adm_ordem_servico_cronograma ?? 0);
            const tipo = String(d?.tipo_foto ?? "").trim();
            const dl = String(d?.data_log ?? "").trim();
            const uri = String(d?.uri ?? "").trim();
            if (!crono || !tipo || !dl || !uri) return null;
            return `${t}|${tenant}|${crono}|${tipo}|${dl}|${uri}`;
          }
          return null;
        };
        const novaChaveOs = chaveOsConteudo(type, tenantHash, data);
        if (novaChaveOs) {
          for (let i = actions.length - 1; i >= 0; i--) {
            if (
              actions[i].type === type &&
              chaveOsConteudo(
                actions[i].type,
                (actions[i] as any).tenantHash,
                actions[i].data,
              ) === novaChaveOs
            ) {
              actions.splice(i, 1);
            }
          }
        }

        actions.push(newAction);
        await gravarFila(actions);
        // O id agora é de uma ação NOVA, ainda não entregue (customId reaproveitado).
        baixasPendentes.delete(newAction.id);
        console.log(
          `[Sync] Ação offline enfileirada: ${type} (ID: ${newAction.id})`,
        );
        return newAction.id;
      });
    } catch (error) {
      console.error("[Sync] Erro ao enfileirar ação offline:", error);
      return null;
    }
  },

  /**
   * Remove uma ação específica da fila offline
   */
  dequeueOfflineAction: async (actionId: string) => {
    try {
      await comFilaLock(async () => {
        const actions = await lerFilaParaEscrita();
        const filteredActions = actions.filter((a) => a.id !== actionId);

        if (actions.length !== filteredActions.length) {
          await gravarFila(filteredActions);
          console.log(`[Sync] Ação offline removida da fila: ${actionId}`);
        }
      });
      baixasPendentes.delete(actionId);
    } catch (error) {
      // A ação fica na fila; o dreno não a reenvia neste processo (ver baixasPendentes).
      baixasPendentes.add(actionId);
      console.error("[Sync] Erro ao remover ação offline:", error);
    }
  },

  updateOfflineActionData: async (actionId: string, data: any) => {
    try {
      await comFilaLock(async () => {
        const actions = await lerFilaParaEscrita();
        let changed = false;

        const updated = actions.map((a) => {
          if (a.id !== actionId) return a;
          changed = true;
          return { ...a, data };
        });

        if (changed) {
          await gravarFila(updated);
        }
      });
    } catch (error) {
      console.error("[Sync] Erro ao atualizar ação offline:", error);
    }
  },

  /**
   * Conta o número de ações de ronda pendentes na fila offline
   */
  countPendingRondaActions: async (): Promise<number> => {
    try {
      const actions = await lerFilaSoLeitura();
      const rondaActions = actions.filter(
        (a) =>
          a.type === "RONDA_INICIAR" ||
          a.type === "RONDA_SALVAR_PONTO" ||
          a.type === "RONDA_FINALIZAR" ||
          a.type === "CHECKLIST_SALVAR" ||
          a.type === "RONDA_CANCELAR",
      );

      return rondaActions.length;
    } catch (error) {
      console.error("[Sync] Erro ao contar ações de ronda pendentes:", error);
      return 0;
    }
  },

  /**
   * Rondas FINALIZADAS em modo offline (parciais ou completas) que ainda não sincronizaram.
   * Agrupa a fila offline igual ao processador (RONDA_INICIAR abre um grupo; RONDA_FINALIZAR/
   * RONDA_CANCELAR fecham) e devolve um resumo por grupo que terminou em RONDA_FINALIZAR — para
   * a tela "Rondas Realizadas" listá-las com o selo "pendente de sincronização".
   */
  getPendingFinishedRondas: async (): Promise<
    Array<{
      key: string;
      id_adm_ronda_cadastro: number | null;
      id_adm_ronda_execucao: number | null;
      dataInicioRaw: string | null;
      inicioTs: number;
      fimTs: number;
      qtdePontosExec: number;
    }>
  > => {
    try {
      // Durante o DRENO da fila, o estado dela é MEIO-DRENADO: o RONDA_INICIAR pode já
      // ter sido entregue (→ inicioTs cairia no timestamp do FINALIZAR e a hora de início
      // ficava IGUAL à de fim) e os RONDA_SALVAR_PONTO somem um a um (→ o badge da ronda
      // COMPLETA regredia para "Parcial" por alguns segundos, até o refresh pós-sync).
      // Congela: enquanto o processOfflineQueue roda, devolve o último snapshot ÍNTEGRO.
      if (isProcessingQueue && snapshotRondasPendentesPreDrain) {
        return snapshotRondasPendentesPreDrain;
      }
      const actions = await lerFilaSoLeitura();

      const rondaActions = actions
        .filter(
          (a) =>
            a.type === "RONDA_INICIAR" ||
            a.type === "RONDA_SALVAR_PONTO" ||
            a.type === "RONDA_FINALIZAR" ||
            a.type === "RONDA_CANCELAR",
        )
        .sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));

      // Mesmo agrupamento do processador da fila (Sync.tsx ~5052).
      const groups: OfflineAction[][] = [];
      let cur: OfflineAction[] = [];
      for (const a of rondaActions) {
        if (a.type === "RONDA_INICIAR") {
          if (cur.length) groups.push(cur);
          cur = [a];
        } else if (
          a.type === "RONDA_FINALIZAR" ||
          a.type === "RONDA_CANCELAR"
        ) {
          cur.push(a);
          groups.push(cur);
          cur = [];
        } else {
          cur.push(a);
        }
      }
      if (cur.length) groups.push(cur);

      const num = (v: any): number | null => {
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? n : null;
      };

      const out = [];
      for (const g of groups) {
        const finalizar = g.find((x) => x.type === "RONDA_FINALIZAR");
        if (!finalizar) continue; // só FINALIZADAS (canceladas/em-andamento ficam de fora)
        const iniciar = g.find((x) => x.type === "RONDA_INICIAR");
        const pontosActions = g.filter((x) => x.type === "RONDA_SALVAR_PONTO");

        const iniData: any = iniciar?.data ?? {};
        const finData: any = finalizar.data ?? {};
        const finPontos: any[] = Array.isArray(finData.pontos)
          ? finData.pontos
          : [];

        out.push({
          key: String(finalizar.id),
          id_adm_ronda_cadastro:
            num(iniData.id_adm_ronda_cadastro) ??
            num(iniData.codigoRonda) ??
            num(finData.id_adm_ronda_cadastro) ??
            num(finPontos[0]?.id_adm_ronda_cadastro),
          id_adm_ronda_execucao:
            num(iniData.id_adm_ronda_execucao) ??
            num(finData.id_adm_ronda_execucao) ??
            num(finPontos[0]?.id_adm_ronda_execucao),
          dataInicioRaw:
            typeof iniData.dataInicio === "string" ? iniData.dataInicio : null,
          inicioTs: iniciar?.timestamp ?? finalizar.timestamp ?? 0,
          fimTs: finalizar.timestamp ?? 0,
          qtdePontosExec: Math.max(pontosActions.length, finPontos.length),
        });
      }
      snapshotRondasPendentesPreDrain = out;
      return out;
    } catch (error) {
      console.error("[Sync] getPendingFinishedRondas erro:", error);
      // erro transitório NÃO derruba o snapshot íntegro
      return snapshotRondasPendentesPreDrain ?? [];
    }
  },

  /**
   * Conta o número de ações do Livro de Ocorrência pendentes na fila offline
   * (iniciar/itens-pessoal, ocorrências e finalizações ainda não sincronizadas).
   */
  countPendingLivroActions: async (): Promise<number> => {
    try {
      const actions = await lerFilaSoLeitura();
      return actions.filter(
        (a) =>
          a.type === "LIVRO_INICIAR" ||
          a.type === "LIVRO_OCORRENCIA" ||
          a.type === "LIVRO_FINALIZAR",
      ).length;
    } catch (error) {
      console.error("[Sync] Erro ao contar ações de livro pendentes:", error);
      return 0;
    }
  },

  /**
   * Conta pendências de sincronização por categoria — usado para BLOQUEAR o logout manual
   * (que faz AsyncStorage.clear() e apagaria TUDO que ainda não subiu ao servidor) e para
   * ADIAR o logoff remoto. Ponto = itens ainda `offline` no mapa local; assinatura/ronda/
   * livro = ações na fila offline; "outras" = QUALQUER outra ação da fila (justificativa,
   * OS, estoque, treinamento, enquete, contestação, dados do colaborador…) — categoria
   * residual de propósito, para tipo novo de ação nunca nascer invisível aos gates.
   *
   * Lê sem engolir erro (não usa getOfflineQueue/getLocalPontosMap, que devolvem vazio —
   * falha de leitura viraria "zero pendências" e liberaria os gates): erro de
   * leitura/parse vira `indeterminado: true`, com contagens zeradas e NÃO confiáveis.
   * Também é `indeterminado` (com as contagens do resto, mais 1 em `outras`) quando a fila
   * antiga do AsyncStorage ainda não pôde ser lida (services/FilaOffline.ts). O logoff
   * remoto ADIA no indeterminado; o "Sair" manual BLOQUEIA (desde 06/10/2026 — antes
   * liberava, e o clear() apagava o que não tinha sido lido).
   *
   * Fora da fila também contam: as batidas (mapa + cópias em arquivo) e os complementos de
   * checklist esperando o id da execução (LOCAL_CHECKLIST_COMPL_PENDENTE_KEY — única cópia
   * de resposta ainda não enviada; os da sessão vão em `outras`).
   */
  contarPendenciasSync: async (): Promise<{
    pontos: number;
    assinaturas: number;
    rondas: number;
    livros: number;
    outras: number;
    total: number;
    /** Itens de OUTRO tenant retidos na fila/mapa (guarda cross-tenant): NÃO são
     *  entregáveis nesta sessão por definição — ficam FORA de `total` (que gata
     *  logout manual e logoff remoto; retê-los travaria os dois PARA SEMPRE).
     *  Sobrevivem ao signOut normal e sincronizam na sessão da empresa dona. */
    retidasOutroTenant: number;
    indeterminado: boolean;
    /** Data (ms) do item MAIS ANTIGO entre os de `total` — o aviso de sincronização
     *  pendente (utils/avisoSync) mede a idade por aqui. null = nada datado. */
    maisAntigaEm: number | null;
  }> => {
    try {
      const [rawMapa, fila, antigaIlegivel, rawCompl] = await Promise.all([
        AsyncStorage.getItem(LOCAL_PONTOS_KEY),
        lerFilaSoLeitura(),
        filaAntigaIlegivel(),
        AsyncStorage.getItem(LOCAL_CHECKLIST_COMPL_PENDENTE_KEY),
      ]);
      const mapa: Record<string, any> = rawMapa ? JSON.parse(rawMapa) : {};
      if (typeof mapa !== "object" || mapa === null) {
        throw new Error("estado local corrompido (mapa com formato inválido)");
      }
      const complPendentes: Record<string, any> = rawCompl
        ? JSON.parse(rawCompl)
        : {};
      if (typeof complPendentes !== "object" || complPendentes === null) {
        throw new Error(
          "estado local corrompido (complementos pendentes com formato inválido)",
        );
      }

      const hashSessao = await tenantHashSessaoAtual().catch(() => "");
      const deOutroTenant = (th: any): boolean => {
        const t = String(th ?? "")
          .trim()
          .toUpperCase();
        return !!t && !!hashSessao && t !== hashSessao;
      };

      let retidasOutroTenant = 0;
      // Idade da pendência: só do que entra em `total` (retido de outro tenant não conta).
      let maisAntigaEm: number | null = null;
      const considerar = (ms: unknown) => {
        const n = Number(ms);
        if (Number.isFinite(n) && n > 0 && (maisAntigaEm == null || n < maisAntigaEm))
          maisAntigaEm = n;
      };

      let pontos = 0;
      for (const p of Object.values(mapa) as any[]) {
        if (!p || p.offline !== true) continue;
        if (deOutroTenant(p.hash)) retidasOutroTenant++;
        else {
          pontos++;
          considerar(p.timestamp);
        }
      }
      // Cópia em arquivo PENDENTE fora do mapa (a gravação no mapa falhou, ou ele foi
      // apagado pelo "Sair") também é batida esperando envio.
      const backups = await SyncService._lerBackupsPonto();
      const idsComCopia = new Set(backups.map((b) => String(b?.id)));
      for (const b of backups) {
        if (!b || b.offline !== true || mapa[b.id]) continue;
        if (deOutroTenant(b.hash)) retidasOutroTenant++;
        else {
          pontos++;
          considerar(b.timestamp);
        }
      }

      const TIPOS_RONDA = new Set([
        "RONDA_INICIAR",
        "RONDA_SALVAR_PONTO",
        "RONDA_FINALIZAR",
        "RONDA_CANCELAR",
        "CHECKLIST_SALVAR",
      ]);
      const TIPOS_LIVRO = new Set([
        "LIVRO_INICIAR",
        "LIVRO_OCORRENCIA",
        "LIVRO_FINALIZAR",
      ]);

      let assinaturas = 0;
      let rondas = 0;
      let livros = 0;
      let outras = 0;
      for (const a of fila) {
        const t = (a as any)?.type;
        if (!t) continue;
        // Batida que SÓ existe na fila (sem entrada no mapa nem cópia em arquivo): herdada
        // de versão antiga, ou o mapa a perdeu. Até 06/10/2026 nenhuma contagem a via — o
        // "Sair" liberava e o clear() a apagava. As outras PONTO já são contadas acima.
        const idBatidaSoNaFila =
          t === "PONTO"
            ? String(
                (a as any)?.data?.localId ?? (a as any)?.data?.ponto?.id ?? "",
              )
            : "";
        const batidaSoNaFila =
          t === "PONTO" &&
          (!idBatidaSoNaFila ||
            (!mapa[idBatidaSoNaFila] && !idsComCopia.has(idBatidaSoNaFila)));
        if (
          deOutroTenant(
            (a as any)?.data?.ponto?.hash ||
              (a as any)?.data?.hash ||
              (a as any)?.tenantHash,
          )
        ) {
          // PONTO retido já foi contado pelo MAPA (não duplicar) — salvo se só existe aqui.
          if (t !== "PONTO" || batidaSoNaFila) retidasOutroTenant++;
          continue;
        }
        if (batidaSoNaFila) {
          pontos++;
          considerar((a as any)?.data?.ponto?.timestamp ?? (a as any)?.timestamp);
          continue;
        }
        if (t !== "PONTO" && t !== "NOTIFICACOES_LIDAS")
          considerar((a as any)?.criadoEm ?? (a as any)?.timestamp);
        if (t === "SIGNATURE") assinaturas++;
        else if (
          t === "CHECKLIST_SALVAR" &&
          !(a as any)?.data?.id_adm_ronda_cadastro
        )
          outras++; // checklist de OS (sem ronda) — NÃO rotular como "ronda" no modal
        else if (TIPOS_RONDA.has(t)) rondas++;
        else if (TIPOS_LIVRO.has(t)) livros++;
        // PONTO já é contado pelo MAPA local (contar a ação da fila duplicaria);
        // NOTIFICACOES_LIDAS é marcador benigno (perdê-lo não perde dado do
        // colaborador) — nenhum dos dois entra em "outras".
        else if (t !== "PONTO" && t !== "NOTIFICACOES_LIDAS") outras++;
      }
      // Complemento de checklist respondido antes de a execução ter id: mora numa chave
      // própria (não na fila) e o clear() do "Sair" o apagava sem ninguém contar.
      for (const c of Object.values(complPendentes) as any[]) {
        if (!c) continue;
        if (deOutroTenant(c.hash)) retidasOutroTenant++;
        else {
          outras++;
          considerar(c.criadoEm);
        }
      }
      // Fila antiga (até a 4.0.22) ainda ilegível: são ações guardadas que ninguém conta, e o
      // clear() do "Sair" as apagaria (não dá para ler e regravar). Conta como pendência: o
      // "Sair" manual bloqueia até a recuperação (nova tentativa a cada 15 min).
      if (antigaIlegivel) outras++;

      return {
        pontos,
        assinaturas,
        rondas,
        livros,
        outras,
        total: pontos + assinaturas + rondas + livros + outras,
        retidasOutroTenant,
        // Fila antiga ainda ilegível: o que há nela não está nas contagens (só o +1 acima).
        indeterminado: antigaIlegivel,
        maisAntigaEm,
      };
    } catch (e) {
      console.warn(
        "[Sync] contarPendenciasSync: erro (estado INDETERMINADO):",
        e,
      );
      return {
        pontos: 0,
        assinaturas: 0,
        rondas: 0,
        livros: 0,
        outras: 0,
        total: 0,
        retidasOutroTenant: 0,
        indeterminado: true,
        maisAntigaEm: null,
      };
    }
  },

  /**
   * O MESMO levantamento do `contarPendenciasSync` (é ele que bloqueia o "Sair"), item a
   * item, para a tela MOSTRAR o que falta subir. WEB: painel "Sincronização" do desktop —
   * ele lia só a fila e dizia "Tudo enviado" enquanto o "Sair" seguia bloqueado por batidas
   * que moram no MAPA local ou na cópia em arquivo (fora da fila). Mantido em paridade com
   * `contarPendenciasSync`: `itens.length` === `total` dele.
   */
  detalharPendenciasSync: async (): Promise<{
    itens: {
      grupo: "ponto" | "assinatura" | "ronda" | "livro" | "outra";
      titulo: string;
      detalhe: string;
    }[];
    indeterminado: boolean;
    motivo?: string;
  }> => {
    type Item = {
      grupo: "ponto" | "assinatura" | "ronda" | "livro" | "outra";
      titulo: string;
      detalhe: string;
    };
    const itens: Item[] = [];
    const quandoPonto = (p: any) =>
      [p?.data, p?.hora].filter(Boolean).join(" às ") || "data não registrada";
    const quandoAcao = (a: any) => {
      const ts = Number(a?.timestamp);
      if (!Number.isFinite(ts) || ts <= 0) return "";
      return new Date(ts).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    };
    try {
      const [rawMapa, fila, antigaIlegivel, rawCompl] = await Promise.all([
        AsyncStorage.getItem(LOCAL_PONTOS_KEY),
        lerFilaSoLeitura(),
        filaAntigaIlegivel(),
        AsyncStorage.getItem(LOCAL_CHECKLIST_COMPL_PENDENTE_KEY),
      ]);
      const mapa: Record<string, any> = rawMapa ? JSON.parse(rawMapa) : {};
      if (typeof mapa !== "object" || mapa === null) {
        throw new Error("estado local corrompido (mapa com formato inválido)");
      }
      const complPendentes: Record<string, any> = rawCompl
        ? JSON.parse(rawCompl)
        : {};
      if (typeof complPendentes !== "object" || complPendentes === null) {
        throw new Error(
          "estado local corrompido (complementos pendentes com formato inválido)",
        );
      }
      const hashSessao = await tenantHashSessaoAtual().catch(() => "");
      const deOutroTenant = (th: any): boolean => {
        const t = String(th ?? "")
          .trim()
          .toUpperCase();
        return !!t && !!hashSessao && t !== hashSessao;
      };

      for (const p of Object.values(mapa) as any[]) {
        if (!p || p.offline !== true || deOutroTenant(p.hash)) continue;
        itens.push({
          grupo: "ponto",
          titulo: "Batida de ponto",
          detalhe: `${quandoPonto(p)} · guardada neste aparelho`,
        });
      }
      const backups = await SyncService._lerBackupsPonto();
      const idsComCopia = new Set(backups.map((b) => String(b?.id)));
      for (const b of backups) {
        if (!b || b.offline !== true || mapa[b.id] || deOutroTenant(b.hash))
          continue;
        itens.push({
          grupo: "ponto",
          titulo: "Batida de ponto",
          detalhe: `${quandoPonto(b)} · só na cópia de segurança`,
        });
      }

      const TIPOS_RONDA = new Set([
        "RONDA_INICIAR",
        "RONDA_SALVAR_PONTO",
        "RONDA_FINALIZAR",
        "RONDA_CANCELAR",
        "CHECKLIST_SALVAR",
      ]);
      const TIPOS_LIVRO = new Set([
        "LIVRO_INICIAR",
        "LIVRO_OCORRENCIA",
        "LIVRO_FINALIZAR",
      ]);
      for (const a of fila) {
        const t = (a as any)?.type;
        if (!t) continue;
        const idBatidaSoNaFila =
          t === "PONTO"
            ? String(
                (a as any)?.data?.localId ?? (a as any)?.data?.ponto?.id ?? "",
              )
            : "";
        const batidaSoNaFila =
          t === "PONTO" &&
          (!idBatidaSoNaFila ||
            (!mapa[idBatidaSoNaFila] && !idsComCopia.has(idBatidaSoNaFila)));
        if (
          deOutroTenant(
            (a as any)?.data?.ponto?.hash ||
              (a as any)?.data?.hash ||
              (a as any)?.tenantHash,
          )
        )
          continue;
        const quando = quandoAcao(a);
        const sufixo = quando ? ` · ${quando}` : "";
        if (batidaSoNaFila) {
          itens.push({
            grupo: "ponto",
            titulo: "Batida de ponto",
            detalhe: `${quandoPonto((a as any)?.data?.ponto)} · só na fila de envio`,
          });
          continue;
        }
        if (t === "SIGNATURE")
          itens.push({
            grupo: "assinatura",
            titulo: "Assinatura de documento",
            detalhe: `na fila de envio${sufixo}`,
          });
        else if (
          t === "CHECKLIST_SALVAR" &&
          !(a as any)?.data?.id_adm_ronda_cadastro
        )
          itens.push({
            grupo: "outra",
            titulo: "Checklist",
            detalhe: `na fila de envio${sufixo}`,
          });
        else if (TIPOS_RONDA.has(t))
          itens.push({
            grupo: "ronda",
            titulo: "Ronda",
            detalhe: `na fila de envio${sufixo}`,
          });
        else if (TIPOS_LIVRO.has(t))
          itens.push({
            grupo: "livro",
            titulo: "Livro de ocorrências",
            detalhe: `na fila de envio${sufixo}`,
          });
        else if (t === "TROCA_PLANTAO" || t === "TROCA_PLANTAO_RESPOSTA")
          itens.push({
            grupo: "outra",
            titulo:
              t === "TROCA_PLANTAO"
                ? "Troca de plantão"
                : "Resposta de troca de plantão",
            detalhe: `na fila de envio${sufixo}`,
          });
        else if (t !== "PONTO" && t !== "NOTIFICACOES_LIDAS")
          itens.push({
            grupo: "outra",
            titulo: "Envio pendente",
            detalhe: `${String(t).toLowerCase().replace(/_/g, " ")}${sufixo}`,
          });
      }
      for (const c of Object.values(complPendentes) as any[]) {
        if (!c || deOutroTenant(c.hash)) continue;
        itens.push({
          grupo: "outra",
          titulo: "Resposta de checklist",
          detalhe: "aguardando a execução ser criada no servidor",
        });
      }
      if (antigaIlegivel)
        itens.push({
          grupo: "outra",
          titulo: "Fila da versão anterior",
          detalhe: "ainda não pôde ser lida neste aparelho (nova tentativa a cada 15 min)",
        });
      return {
        itens,
        indeterminado: antigaIlegivel,
        motivo: antigaIlegivel
          ? "A fila da versão anterior do app ainda não pôde ser lida."
          : undefined,
      };
    } catch (e) {
      return {
        itens: [],
        indeterminado: true,
        motivo: String((e as any)?.message ?? e),
      };
    }
  },

  /**
   * LIMPA todo o estado local de sincronização (fila + mapas). Uso EXCLUSIVO do logout
   * FORÇADO por sessão de HOMOLOGAÇÃO: a fila/mapas SOBREVIVEM ao signOut normal e, se o
   * usuário relogar em OUTRO tenant, ações antigas seriam entregues à sessão nova com
   * ids do banco ERRADO (contaminação cross-tenant). Aqui, depois do best-effort de
   * drenar pro ambiente antigo, o que sobrar é DESCARTADO (dado de homologação) — loga
   * o que caiu. NÃO limpa o docs_assinados_log (é escopado por empresa, e é requisito
   * que sobreviva à troca).
   */
  /**
   * Antes do "Sair" (BottomBar): a fila fica só com o que precisa sobreviver — as ações
   * retidas de OUTRO tenant. Da sessão que sai, só sai o que é comprovadamente dispensável:
   *  - NOTIFICACOES_LIDAS (marcador benigno);
   *  - PONTO cuja batida está no mapa como JÁ ENVIADA (o mapa e a cura conduzem).
   * Qualquer outra ação da sessão aqui entrou DEPOIS da contagem que liberou o "Sair" (o
   * dreno e as telas enfileiram em segundo plano): lança, e o "Sair" não acontece. Antes,
   * ela era descartada junto.
   *
   * Por que existe (06/10/2026): a fila mora em ARQUIVO (services/FilaOffline.ts) e o
   * `AsyncStorage.clear()` do "Sair" só apaga o ponteiro dela — e ponteiro perdido com
   * versões na pasta é tratado como perda involuntária (a fila é recuperada). Então esvaziar
   * a fila no "Sair" tem de ser explícito, aqui. A gravação começa uma cadeia NOVA: as
   * versões anteriores guardam ações já entregues da sessão que sai e voltariam na próxima.
   *
   * Sem o tenant da sessão (sem token), nenhuma ação carimbada é tratada como de outro
   * tenant: todas contam como da sessão (a mesma regra da contagem). Lança se não deu para
   * ler ou gravar.
   */
  prepararFilaParaSair: async (): Promise<void> => {
    const hashSessao = await tenantHashSessaoAtual().catch(() => "");
    await comFilaLock(async () => {
      const fila = await lerFilaParaEscrita();
      const rawMapa = await AsyncStorage.getItem(LOCAL_PONTOS_KEY); // erro lança
      const mapa: Record<string, any> = rawMapa ? JSON.parse(rawMapa) : {};
      const manter: OfflineAction[] = [];
      const novas: string[] = [];
      let descartadas = 0;
      for (const a of fila as any[]) {
        const t = String(
          a?.data?.ponto?.hash || a?.data?.hash || a?.tenantHash || "",
        )
          .trim()
          .toUpperCase();
        if (t && hashSessao && t !== hashSessao) {
          manter.push(a);
          continue;
        }
        if (a?.type === "NOTIFICACOES_LIDAS") {
          descartadas++;
          continue;
        }
        if (a?.type === "PONTO") {
          const id = String(a?.data?.localId ?? a?.data?.ponto?.id ?? "");
          const lp = id ? mapa?.[id] : null;
          if (lp && lp.offline !== true) {
            descartadas++;
            continue;
          }
        }
        novas.push(String(a?.type ?? "?"));
      }
      if (novas.length > 0) {
        throw new Error(
          `[Sync] prepararFilaParaSair: ${novas.length} ação(ões) da sessão entraram na fila depois da conferência (${novas.join(", ")}) — o "Sair" não acontece.`,
        );
      }
      if (descartadas > 0) {
        console.warn(
          `[Sync] prepararFilaParaSair: ${descartadas} ação(ões) dispensável(is) da sessão descartada(s), ${manter.length} de outro tenant mantida(s).`,
        );
      }
      await gravarFila(manter, { descartarVersoesAnteriores: true });
    });
  },

  /**
   * Chaves que o "Sair" NÃO pode apagar, seja qual for o tenant: únicas cópias de dado
   * ainda não enviado que moram FORA da fila e do mapa de pontos.
   *  - complementos de checklist esperando o id da execução (e a quarentena deles): os da
   *    sessão bloqueiam o "Sair" (contarPendenciasSync); sobram os de outro tenant;
   *  - diárias do PRESTADOR (services/PrestadorDiaria.ts): outro domínio, outra sessão — o
   *    "Sair" do colaborador apagava as que ainda não tinham subido.
   * Lança se não deu para ler — e aí o "Sair" não acontece. A BottomBar regrava depois do
   * clear().
   */
  lerChavesQueSobrevivemAoSair: async (): Promise<[string, string][]> => {
    const prefixos = [
      LOCAL_CHECKLIST_COMPL_PENDENTE_KEY,
      COMPL_PENDENTE_QUARENTENA_KEY,
      "@tirvumais:prestador_diarias", // PrestadorDiaria PREFIXO_DIARIAS (`#HASH:ID`, `_corrupt`)
    ];
    const todas = await AsyncStorage.getAllKeys();
    const alvo = todas.filter((k) =>
      prefixos.some(
        (p) => k === p || k.startsWith(`${p}#`) || k.startsWith(`${p}_`),
      ),
    );
    if (alvo.length === 0) return [];
    const pares = await AsyncStorage.multiGet(alvo);
    return pares.filter((par): par is [string, string] => par[1] != null);
  },

  /**
   * Snapshot das ações da FILA e pontos do MAPA retidos de OUTRO tenant (guarda
   * cross-tenant), para SOBREVIVEREM ao wipe total do logout manual
   * (AsyncStorage.clear() no confirmLogout). Sem isto, o gate de logout — que só
   * conta pendências do tenant da sessão — liberaria a saída e o clear()
   * destruiria batidas/assinaturas retidas de outra empresa (perda permanente).
   */
  snapshotRetidasOutroTenant: async (): Promise<{
    fila: OfflineAction[];
    mapa: Record<string, any>;
    extras: Array<[string, string]>;
  } | null> => {
    try {
      const hashSessao = await tenantHashSessaoAtual().catch(() => "");
      if (!hashSessao) return null; // sem tenant da sessão não há como distinguir
      const deOutro = (th: any) => {
        const t = String(th ?? "")
          .trim()
          .toUpperCase();
        return !!t && t !== hashSessao;
      };
      const [filaRaw, rawMapa] = await Promise.all([
        lerFilaSoLeitura(),
        AsyncStorage.getItem(LOCAL_PONTOS_KEY),
      ]);
      const mapaRaw = rawMapa ? JSON.parse(rawMapa) : {};
      const fila = filaRaw.filter((a: any) =>
        deOutro(a?.data?.ponto?.hash || a?.data?.hash || a?.tenantHash),
      );
      const mapa: Record<string, any> = {};
      if (mapaRaw && typeof mapaRaw === "object") {
        for (const [id, lp] of Object.entries(mapaRaw)) {
          if (deOutro((lp as any)?.hash)) mapa[id] = lp;
        }
      }

      // 🔒 CHAVES DE SUPORTE ao replay das retidas (verificação adversarial
      // 30/07 — blocker): a fila preservada é INÚTIL sem elas, e o
      // AsyncStorage.clear() do logout as apagava:
      //  • livro_id_map / os_sobdemanda_idmap → sem o mapa localId→realId, o
      //    dreno devolve LIVRO_OCORRENCIA/FINALIZAR e OS_FOTO_SAVE a
      //    remainingActions "aguardando id-base" PARA SEMPRE (registro nunca
      //    entregue E pendência eterna travando o logout do tenant dono);
      //  • docs_assinados_log → evidência sticky contra a oscilação do servidor
      //    (o próprio limparEstadoLocalSync promete preservá-la); sem ela, doc
      //    assinado reaparece como "assinar" e o colaborador reassina.
      // Preserva TODAS as variantes por tenant (a chave é `base#HASH`).
      const extras: Array<[string, string]> = [];
      try {
        const prefixosPreservados = [
          LIVRO_ID_MAP_KEY,
          LOCAL_OS_SOBDEMANDA_IDMAP_KEY,
          "@TirvuMais:docs_assinados_log", // docAssinadoLog LOG_KEY (já é por-tenant internamente)
          "@TirvuMais:userdata_fetched_at_por_tenant", // FETCHED_AT_KEY (freshness da cura)
        ];
        const todas = await AsyncStorage.getAllKeys();
        const alvo = todas.filter((k) =>
          prefixosPreservados.some((p) => k === p || k.startsWith(`${p}#`)),
        );
        if (alvo.length > 0) {
          const pares = await AsyncStorage.multiGet(alvo);
          for (const [k, v] of pares) if (v != null) extras.push([k, v]);
        }
      } catch {}

      if (
        fila.length === 0 &&
        Object.keys(mapa).length === 0 &&
        extras.length === 0
      )
        return null;
      return { fila, mapa, extras };
    } catch (e) {
      console.warn("[Sync] snapshotRetidasOutroTenant: erro:", e);
      return null;
    }
  },

  /** Regrava o snapshot acima após um wipe total (logout). Best-effort com log. */
  restaurarRetidasOutroTenant: async (snap: {
    fila: OfflineAction[];
    mapa: Record<string, any>;
    extras?: Array<[string, string]>;
  }): Promise<void> => {
    try {
      if (snap.fila.length > 0) {
        // Sob o lock e JUNTANDO com o que houver: um enqueue de fundo entre o clear() e
        // aqui não pode ser sobrescrito. Falhar aqui não impede o resto da restauração.
        await comFilaLock(async () => {
          const atual = await lerFilaParaEscrita();
          const ids = new Set(atual.map((a) => String(a?.id)));
          await gravarFila([
            ...snap.fila.filter((a) => !ids.has(String(a?.id))),
            ...atual,
          ]);
        }).catch((e) =>
          console.error(
            "[Sync] restaurarRetidasOutroTenant: FALHA ao regravar a fila retida:",
            e,
          ),
        );
      }
      if (Object.keys(snap.mapa).length > 0) {
        await gravarMapaPontos(snap.mapa);
      }
      if (snap.extras?.length) {
        await AsyncStorage.multiSet(snap.extras);
      }
      console.warn(
        `[Sync] 🔒 Wipe de logout: ${snap.fila.length} ação(ões), ${Object.keys(snap.mapa).length} ponto(s) e ${snap.extras?.length ?? 0} chave(s) de suporte PRESERVADOS.`,
      );
    } catch (e) {
      console.error(
        "[Sync] restaurarRetidasOutroTenant: FALHA (dado retido pode ter sido perdido no wipe):",
        e,
      );
    }
  },

  limparEstadoLocalSync: async (hashSessaoWipe?: string): Promise<void> => {
    try {
      // Guarda cross-tenant do WIPE: ações/pontos RETIDOS de OUTRO tenant (ex.:
      // batidas de produção retidas enquanto a sessão era de homologação) NÃO
      // podem ser destruídos — são exatamente o dado que a retenção do dreno
      // prometeu preservar. Com o hash da sessão que está sendo limpa, o wipe
      // vira FILTRO: descarta só o que é DESTA sessão (ou sem carimbo); preserva
      // o resto. Sem hash → comportamento antigo (wipe total).
      const wipeHash = String(hashSessaoWipe ?? "")
        .trim()
        .toUpperCase();
      const tenantDe = (th: any): string =>
        String(th ?? "")
          .trim()
          .toUpperCase();
      const preservarAcao = (a: any): boolean => {
        const t = tenantDe(
          a?.data?.ponto?.hash || a?.data?.hash || a?.tenantHash,
        );
        return !!wipeHash && !!t && t !== wipeHash;
      };
      // Fila e mapas são lidos e regravados SOB os respectivos locks: um RMW trancado em
      // voo (dequeue do dreno / cura) regravaria a fila/mapa do tenant antigo DEPOIS do
      // wipe. Erro de LEITURA → não mexe: apagar sem saber o que há levaria junto o que é
      // de outra empresa (até 05/10/2026 a falha de leitura virava "vazio" e apagava tudo).
      await comFilaLock(async () => {
        let fila: OfflineAction[];
        try {
          fila = await lerFilaParaEscrita();
        } catch (e) {
          console.warn(
            "[Sync] limparEstadoLocalSync: fila ilegível — mantida como está:",
            e,
          );
          return;
        }
        const descartar = fila.filter((a) => !preservarAcao(a));
        const preservadas = fila.filter(preservarAcao);
        if (descartar.length) {
          console.warn(
            "[Sync] limparEstadoLocalSync: DESCARTANDO",
            descartar.length,
            "ação(ões) pendente(s) da fila (sessão de homologação):",
            descartar.map((a) => a?.type).join(","),
          );
        }
        if (preservadas.length) {
          console.warn(
            "[Sync] limparEstadoLocalSync: PRESERVANDO",
            preservadas.length,
            "ação(ões) de OUTRO tenant retida(s) na fila.",
          );
        }
        if (descartar.length > 0) {
          await gravarFila(preservadas).catch((e) =>
            console.warn(
              "[Sync] limparEstadoLocalSync: não regravou a fila — mantida como está:",
              e,
            ),
          );
        }
      });
      // Mapas GLOBAIS carimbados por tenant (pontos, execuções e complementos de
      // checklist): fica só o que é de OUTRA empresa. Valor que não é JSON de objeto →
      // a chave sai (como sempre). Erro de leitura ou de gravação → mantém como está.
      const lerMapaCru = (chave: string) => async () => {
        const raw = await AsyncStorage.getItem(chave); // erro de leitura lança
        if (!raw) return {};
        try {
          const v = JSON.parse(raw);
          if (v && typeof v === "object" && !Array.isArray(v)) return v;
        } catch {}
        return {};
      };
      const filtrarMapaPorTenant = (
        chave: string,
        ler: () => Promise<Record<string, any>>,
        gravar: (m: Record<string, any>) => Promise<void>,
        rotulo: string,
      ) =>
        comMapaLock(async () => {
          try {
            const mapa = await ler();
            const mantidos: Record<string, any> = {};
            for (const [id, item] of Object.entries(mapa)) {
              const t = tenantDe((item as any)?.hash);
              if (!!wipeHash && !!t && t !== wipeHash) mantidos[id] = item;
            }
            if (Object.keys(mantidos).length > 0) {
              console.warn(
                "[Sync] limparEstadoLocalSync: PRESERVANDO",
                Object.keys(mantidos).length,
                `${rotulo} de OUTRO tenant.`,
              );
              await gravar(mantidos);
            } else {
              await AsyncStorage.removeItem(chave);
            }
          } catch (e) {
            console.warn(
              `[Sync] limparEstadoLocalSync: ${rotulo} — leitura ou gravação falhou, mantido como está:`,
              e,
            );
          }
        });
      await filtrarMapaPorTenant(
        LOCAL_PONTOS_KEY,
        lerMapaPontosParaEscrita,
        gravarMapaPontos,
        "ponto(s) local(is)",
      );
      await filtrarMapaPorTenant(
        LOCAL_CHECKLIST_EXEC_KEY,
        lerMapaCru(LOCAL_CHECKLIST_EXEC_KEY),
        (m) => AsyncStorage.setItem(LOCAL_CHECKLIST_EXEC_KEY, JSON.stringify(m)),
        "execução(ões) de checklist",
      );
      await filtrarMapaPorTenant(
        LOCAL_CHECKLIST_COMPL_KEY,
        lerMapaCru(LOCAL_CHECKLIST_COMPL_KEY),
        (m) =>
          AsyncStorage.setItem(LOCAL_CHECKLIST_COMPL_KEY, JSON.stringify(m)),
        "complemento(s) de checklist",
      );
      // Espelhos são ESCOPADOS por tenant (chaveTenant): remove a versão do
      // tenant DESTA sessão E a chave crua legada (pré-namespace) — espelhos de
      // OUTROS tenants ficam intactos, coerente com a preservação acima.
      await Promise.all(
        [
          LOCAL_JUSTIFICATIVAS_KEY,
          LOCAL_DOC_STATUS_KEY,
          LOCAL_DOC_CONTESTACAO_KEY,
          LOCAL_CONTEST_VISTAS_KEY,
          LOCAL_DOC_SIGNED_KEY,
          LOCAL_DOC_URL_KEY,
          LOCAL_DOC_DELIVERED_AT_KEY,
          LOCAL_OS_STATUS_KEY,
          LOCAL_OS_FOTOS_KEY,
          LOCAL_OS_SOBDEMANDA_KEY,
          LOCAL_OS_SOBDEMANDA_IDMAP_KEY,
          LOCAL_NOTIF_LIDAS_KEY,
          LOCAL_RECUSAS_VISTAS_KEY,
          LIVRO_FINALIZADOS_KEY,
          LIVRO_LOCAL_KEY,
          LIVRO_LOCAIS_KEY,
          LIVRO_ID_MAP_KEY,
          ENQUETES_RESPONDIDAS_KEY,
        ].map(async (k) => {
          await AsyncStorage.removeItem(await chaveTenant(k)).catch(() => {});
          await AsyncStorage.removeItem(k).catch(() => {});
        }),
      );
    } catch (e) {
      console.warn("[Sync] limparEstadoLocalSync: erro:", e);
    }
  },

  /**
   * Limpa a fila offline completamente
   */
  clearOfflineQueue: async () => {
    try {
      await comFilaLock(async () => {
        await lerFilaParaEscrita(); // migra a fila antiga antes, senão ela sobreviveria
        await gravarFila([]);
      });
      console.log("[Sync] Fila offline limpa com sucesso!");
    } catch (error) {
      console.error("[Sync] Erro ao limpar fila offline:", error);
    }
  },

  /**
   * Obtém o conteúdo da fila offline
   */
  getOfflineQueue: async (): Promise<OfflineAction[]> => {
    try {
      const actions = await lerFilaSoLeitura();
      if (actions.length === 0) {
        console.log("[Sync] getOfflineQueue: Fila está vazia");
        return [];
      }
      console.log(
        "[Sync] getOfflineQueue: Retornando fila com",
        actions.length,
        "ações:",
        actions.map((a: any) => ({ id: a.id, type: a.type })),
      );
      return actions;
    } catch (error) {
      console.error("[Sync] Erro ao obter fila offline:", error);
      return [];
    }
  },

  /**
   * Sincroniza todos os dados do app (fila offline, e quaisquer outros dados que precisem ser sincronizados)
   */
  /**
   * @param opcoes.manual `true` quando a pessoa PUXOU para sincronizar. Um sync manual
   *   ignora o backoff das ações rejeitadas pelo servidor (ver `processOfflineQueue`).
   */
  syncAll: async (opcoes?: { manual?: boolean }) => {
    try {
      console.log("[Sync] syncAll: Iniciando sincronização completa...");
      // Geração de sessão NO INÍCIO do sync (ver peekApiSessionGen no Api.tsx): se um
      // signOut/trocarEmpresa acontecer no meio, o snapshot montado abaixo pertence à
      // sessão morta e NÃO pode regravar o userData.json — ressuscitaria flags como
      // forcar_logoff='S' pré-rearme ou vazaria dados da empresa anterior.
      const sessionGen = peekApiSessionGen();

      // 1. Processar a fila offline
      await SyncService.processOfflineQueue({ manual: !!opcoes?.manual });

      // 2. Atualizar dados do usuário
      console.log("[Sync] syncAll: Atualizando userData...");
      let baseData = await readJSONFile("userData.json");
      // 🔒 CROSS-TENANT (30/07): arquivo de OUTRO tenant (troca de empresa) não
      // pode virar base nem doar domínios — descarta (o /iniciar desta sessão
      // repovoa).
      {
        const hArq = String(
          (baseData as any)?.hash ?? (baseData as any)?.done?.hash ?? "",
        )
          .trim()
          .toUpperCase();
        const hSessao = await tenantHashSessaoAtual().catch(() => "");
        if (hArq && hSessao && hArq !== hSessao) {
          console.warn(
            "[Sync] 🔒 syncAll: userData.json em cache é de OUTRO tenant — descartado.",
          );
          baseData = null;
        }
      }
      // Domínios que NÃO vêm no /iniciar (carregados por endpoints dedicados do t-credentials):
      // ronda/livro/OS + as listas de clientes/equipes (filtro + OS Sob Demanda). Sem preservá-los,
      // todo syncAll zerava userData com a resposta crua do /iniciar e o bottom sheet ficava vazio.
      const prevUserData = baseData;

      let iniciarFresco = false;
      try {
        const response = await Api.start.iniciar();

        if (response.success && response.done) {
          // Carimbo de FRESCOR (por tenant): um /iniciar REAL acabou de responder. É este
          // carimbo (e só ele) que permite ao resolver confirmar revert de assinatura — a
          // hidratação de cache NÃO carimba (dado velho nunca cobra falso pendente).
          // Aguardado (antes era `void`): a conferência logo abaixo lê este carimbo.
          await gravarUserDataFetchedAt(response.done?.hash);
          iniciarFresco = true;
          baseData = response.done;
          // Preserva SÓ se o snapshot anterior é do MESMO tenant do payload fresco.
          const hFresco = String((baseData as any)?.hash ?? "")
            .trim()
            .toUpperCase();
          const hPrev = String((prevUserData as any)?.hash ?? "")
            .trim()
            .toUpperCase();
          if (prevUserData && hFresco && hPrev && hFresco !== hPrev) {
            console.warn(
              "[Sync] 🔒 syncAll: snapshot anterior é de OUTRO tenant — domínios NÃO preservados.",
            );
          } else if (prevUserData) {
            baseData = {
              ...baseData,
              ronda: prevUserData.ronda ?? baseData.ronda,
              rondaHistorico:
                prevUserData.rondaHistorico ?? baseData.rondaHistorico,
              ordemServicos:
                prevUserData.ordemServicos ?? baseData.ordemServicos,
              // lista FRESCA vence quando vem preenchida (alinha com o refreshUserData;
              // preferir sempre a antiga ressuscitava clientes do tenant anterior).
              clientes:
                Array.isArray(baseData.clientes) && baseData.clientes.length > 0
                  ? baseData.clientes
                  : (prevUserData.clientes ?? baseData.clientes),
              equipes: prevUserData.equipes ?? baseData.equipes,
              livroOcorrencia:
                prevUserData.livroOcorrencia ?? baseData.livroOcorrencia,
              // Mesmo tratamento de `clientes` acima: o `??` sozinho não basta porque o
              // domínio "livro" grava este campo com `?? []`, então ele é sempre um array e
              // nunca null/undefined — uma carga vazia ficava presa em [] para sempre,
              // esvaziando o select de "tipo do item". Ver AuthContext, mesmo trecho.
              livroOcorrenciaItensTipos:
                Array.isArray(baseData.livroOcorrenciaItensTipos) &&
                baseData.livroOcorrenciaItensTipos.length > 0
                  ? baseData.livroOcorrenciaItensTipos
                  : (prevUserData.livroOcorrenciaItensTipos ??
                    baseData.livroOcorrenciaItensTipos),
              livroOcorrenciaParametros:
                prevUserData.livroOcorrenciaParametros ??
                baseData.livroOcorrenciaParametros,
            };
          }
        }
      } catch (error) {
        console.error(
          "[Sync] syncAll: Erro ao atualizar userData via API:",
          error,
        );
      }

      // 3. Aplicar status locais (pontos, OS, documentos)
      if (baseData) {
        let merged = await SyncService.applyLocalPontos(baseData);
        merged = await SyncService.applyLocalOsSobDemanda(merged);
        merged = await SyncService.applyLocalOsStatuses(merged);
        merged = await SyncService.applyLocalDocStatus(merged);
        merged = await SyncService.applyLocalEnquetes(merged);
        // GUARD DE SESSÃO: a fila acima JÁ foi drenada (desejado até no logoff — fluxo
        // cooperativo); só a REGRAVAÇÃO do snapshot é descartada se a sessão acabou ou
        // trocou durante o sync. Nenhum caller consome o userData retornado.
        if (peekApiSessionGen() !== sessionGen) {
          console.warn(
            "[Sync] syncAll: sessão encerrada/trocada durante o sync — regravação do userData DESCARTADA.",
          );
          return { success: true, userData: null };
        }
        await writeJSONFile("userData.json", merged);
        console.log("[Sync] syncAll: userData atualizado!");
        baseData = merged;
        // Cache fresco AGORA: segunda passada do dreno se alguma batida pede conferência —
        // é o que faz a cura retroativa achar o "2 checks fantasma" sem ninguém puxar a tela.
        if (iniciarFresco) await SyncService.conferirPontosAposIniciar();
      }

      console.log("[Sync] syncAll: Sincronização completa finalizada!");
      return { success: true, userData: baseData };
    } catch (error) {
      console.error("[Sync] syncAll: Erro na sincronização completa:", error);
      return { success: false, error };
    }
  },

  // ─── ORDEM DE SERVIÇO ──────────────────────────────────────────────────────

  /**
   * Garante que o diretório de fotos de OS existe
   */
  initOsPhotosDir: async () => {
    const info = await FileSystem.getInfoAsync(OS_PHOTOS_DIR);
    if (!info.exists) {
      await FileSystem.makeDirectoryAsync(OS_PHOTOS_DIR, {
        intermediates: true,
      });
    }
  },

  /**
   * Copia uma foto temporária para o diretório persistente da OS
   */
  persistOsPhoto: async (uri: string): Promise<string | null> => {
    try {
      await SyncService.initOsPhotosDir();
      // Já persistida? Devolve a própria uri (mesma guarda do
      // persistChecklistAsset). Sem isto, CADA replay copiava o arquivo de novo
      // — com a duplicação de fila que existia, o disco enchia sozinho.
      if (String(uri ?? "").startsWith(OS_PHOTOS_DIR)) {
        try {
          const info = await FileSystem.getInfoAsync(uri);
          if (info.exists) return uri;
        } catch {}
      }
      const filename = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}.jpg`;
      const dest = `${OS_PHOTOS_DIR}${filename}`;
      await FileSystem.copyAsync({ from: uri, to: dest });
      return dest;
    } catch (error) {
      console.warn("[Sync] Falha ao persistir foto de OS:", error);
      return null;
    }
  },

  /**
   * Fotos do LIVRO no espelho local: base64 → arquivo, devolvendo só o caminho.
   *
   * ⚠️ Base64 NÃO pode entrar no espelho local (`@TirvuMais:livros_locais`). No Android o
   * AsyncStorage é SQLite: a escrita de uma linha enorme falha em silêncio (o
   * `setLivrosLocais` engole o erro) e a leitura de uma linha acima da CursorWindow
   * estoura — e o `getLivrosLocais` devolve `[]` no catch. Nos dois casos o plantão
   * recém-iniciado simplesmente não existe para a tela, e o card de "em andamento" só
   * aparece quando o SERVIDOR alcança. Cada item do livro exige ao menos uma foto, então
   * isso acontecia em praticamente todo início de plantão com itens.
   *
   * Mesmo padrão de `persistOsPhoto` / `persistChecklistAsset`. URL do servidor e caminho
   * já persistido passam direto.
   */
  persistFotosLivroLocal: async (fotos: any): Promise<string[]> => {
    const entrada = Array.isArray(fotos) ? fotos : [];
    const out: string[] = [];
    for (const f of entrada) {
      // Caminho do container de antes de um update do iOS: é o MESMO arquivo, na pasta nova
      // (sem isto ele não batia com LIVRO_PHOTOS_DIR, a cópia falhava e a foto era descartada).
      const s = caminhoNoAppAtual(String(f ?? "").trim());
      if (!s) continue;
      // URL do storage: nada a fazer, o arquivo é do servidor.
      if (SyncService._ehUrlRemota(s)) {
        out.push(s);
        continue;
      }
      // Já dentro do nosso diretório: é definitivo, não copia de novo.
      if (s.startsWith(LIVRO_PHOTOS_DIR)) {
        out.push(s);
        continue;
      }
      try {
        const info = await FileSystem.getInfoAsync(LIVRO_PHOTOS_DIR);
        if (!info.exists) {
          await FileSystem.makeDirectoryAsync(LIVRO_PHOTOS_DIR, {
            intermediates: true,
          });
        }
        // Extensão pela do próprio dado. A ASSINATURA é PNG (`data:image/png`) e, gravada
        // como .jpg, voltava rotulada image/jpeg — o storage do backend escolhe a extensão
        // pelo que recebe, então o arquivo publicado saía .jpg com conteúdo PNG.
        const ext =
          /^data:image\/png/i.test(s) || /\.png($|\?)/i.test(s) ? "png" : "jpg";
        const dest = `${LIVRO_PHOTOS_DIR}${Date.now()}_${Math.random()
          .toString(36)
          .slice(2, 8)}.${ext}`;
        if (SyncService.isBase64(s)) {
          await FileSystem.writeAsStringAsync(dest, s.split(",")[1] || s, {
            encoding: FileSystem.EncodingType.Base64,
          });
        } else {
          // URI de arquivo (o ImagePicker devolve isto quando não vem base64). O caminho
          // original mora no CACHE, que o sistema pode limpar — sem copiar, a fila
          // guardaria uma referência que some e a ação trava para sempre.
          await FileSystem.copyAsync({ from: s, to: dest });
        }
        out.push(dest);
      } catch (e) {
        console.warn("[Sync] persistFotosLivroLocal: falhou", e);
        // O fallback depende do QUE é `s`:
        //
        // • base64 → mantém. A linha do espelho fica grande (o problema que esta função
        //   existe para evitar), mas linha grande é estado DEGRADADO e soltar a foto
        //   seria perda de registro.
        //
        // • caminho de arquivo → mantê-lo só faz sentido se o arquivo AINDA EXISTE. Desde
        //   que a tela passou a guardar caminho (e não data URI), o arquivo vive no CACHE,
        //   que o Android limpa sozinho quando o disco aperta — e o formulário do plantão
        //   fica aberto por minutos. Empurrar um caminho MORTO para a fila envenena a ação
        //   inteira: `hidratarFotosLivro` lança em toda tentativa, o dreno devolve para a
        //   fila para sempre e a tela já disse "plantão iniciado". Perder UMA foto é
        //   degradação; perder o plantão é quebrar a regra de ouro.
        if (SyncService.isBase64(s)) {
          out.push(s);
        } else {
          let vivo = false;
          try {
            const fi = await FileSystem.getInfoAsync(s);
            vivo = !!fi?.exists;
          } catch {
            vivo = false;
          }
          if (vivo) out.push(s);
          else
            console.warn(
              "[Sync] persistFotosLivroLocal: arquivo sumiu do cache, foto descartada",
            );
        }
      }
    }
    return out;
  },

  /**
   * Inverso do `persistFotosLivroLocal`: a API do livro recebe a foto em base64.
   * Base64 e URL do storage passam direto.
   */
  fotoLivroParaBase64: async (uri: any): Promise<string | null> => {
    // Caminho de antes de uma atualização (iOS muda a pasta do app) → pasta atual.
    const s = caminhoNoAppAtual(String(uri ?? "").trim());
    if (!s) return null;
    if (SyncService.isBase64(s) || SyncService._ehUrlRemota(s)) return s;
    try {
      const b64 = await FileSystem.readAsStringAsync(s, {
        encoding: FileSystem.EncodingType.Base64,
      });
      // Mime pela extensão do arquivo — fixar image/jpeg fazia a assinatura (PNG) chegar
      // rotulada errado, e o storage do backend nomeia o arquivo pelo que recebe.
      const mime = /\.png($|\?)/i.test(s) ? "image/png" : "image/jpeg";
      return `data:${mime};base64,${b64}`;
    } catch (e) {
      console.warn("[Sync] fotoLivroParaBase64: falhou", e);
      return null;
    }
  },

  /**
   * Payload de livro pronto para a FILA: as imagens viram caminho de arquivo.
   *
   * ⚠️ Por que isto existe: até 05/10/2026 a fila era UMA linha do AsyncStorage com TODAS
   * as ações. Base64 de foto engordava essa linha e, no Android, acima de ~2 MB ela não
   * voltava na leitura — e a fila inteira, de todos os domínios, era zerada. Hoje a fila
   * mora em arquivo (services/FilaOffline.ts) e não tem esse teto, mas cada gravação
   * reescreve a fila inteira: base64 nela continua custando memória e tempo em todo
   * enfileirar e baixar.
   *
   * Cobre os três formatos do livro: `itens[].fotos` (iniciar), `fotos` (ocorrência) e
   * `assinatura` (finalizar). Devolve um objeto NOVO — cópia rasa não serve, porque
   * `{ ...payload }` compartilha o array `itens` com quem for enviar online.
   */
  desidratarFotosLivro: async (payload: any): Promise<any> => {
    if (!payload || typeof payload !== "object") return payload;
    const out: any = { ...payload };

    if (Array.isArray(payload.itens)) {
      out.itens = await Promise.all(
        payload.itens.map(async (it: any) => ({
          ...it,
          fotos: await SyncService.persistFotosLivroLocal(it?.fotos),
        })),
      );
    }
    if (Array.isArray(payload.fotos)) {
      out.fotos = await SyncService.persistFotosLivroLocal(payload.fotos);
    }
    if (payload.assinatura) {
      const [arquivo] = await SyncService.persistFotosLivroLocal([
        payload.assinatura,
      ]);
      // Sem arquivo, mantém o base64: linha grande é melhor que assinatura perdida.
      out.assinatura = arquivo ?? payload.assinatura;
    }
    return out;
  },

  /**
   * Inverso: payload da fila → payload da API (imagens em base64).
   *
   * Idempotente de propósito — TODO valor passa pelo `fotoLivroParaBase64`, que devolve
   * base64 e URL remota intactos. É isso que faz as ações antigas (enfileiradas em base64
   * antes desta mudança) continuarem funcionando depois da atualização do app.
   *
   * Lança se um arquivo referenciado sumiu: quem chama TEM que manter a ação na fila em
   * vez de enviar sem foto — enviar vazio e dar baixa apagaria o registro.
   */
  hidratarFotosLivro: async (payload: any): Promise<any> => {
    if (!payload || typeof payload !== "object") return payload;

    /**
     * Converte uma imagem para base64.
     *
     * Devolve `null` quando o ARQUIVO SUMIU — e aí quem chama descarta só aquela foto.
     * Lançar aqui prendia a ação na fila para sempre: um arquivo apagado do cache nunca
     * volta, então a exceção se repetia em todo ciclo do dreno enquanto a tela já tinha
     * anunciado "salvo". O plantão vale mais que a foto.
     * Erro TRANSITÓRIO de leitura continua lançando: aí vale a pena tentar de novo.
     */
    const converter = async (
      valor: any,
      onde: string,
    ): Promise<string | null> => {
      const bruto = caminhoNoAppAtual(String(valor ?? "").trim());
      if (!bruto) return null;
      const b64 = await SyncService.fotoLivroParaBase64(bruto);
      if (b64) return b64;
      // base64/URL nunca falham em `fotoLivroParaBase64`; chegar aqui significa arquivo.
      let existe = false;
      try {
        const fi = await FileSystem.getInfoAsync(bruto);
        existe = !!fi?.exists;
      } catch {
        existe = false;
      }
      if (!existe) {
        console.warn(
          `[Sync] hidratarFotosLivro: arquivo sumiu (${onde}), foto descartada — a ação SEGUE`,
        );
        return null;
      }
      throw new Error(
        `[Sync] hidratarFotosLivro: imagem indisponível (${onde}): ${bruto.slice(0, 120)}`,
      );
    };

    /** Converte a lista e joga fora o que sumiu (mantendo a ordem do que sobrou). */
    const converterLista = async (lista: any, onde: string) => {
      const arr = Array.isArray(lista) ? lista : [];
      const convertidas = await Promise.all(
        arr.map((f: any) => converter(f, onde)),
      );
      return convertidas.filter((x): x is string => !!x);
    };

    const out: any = { ...payload };
    if (Array.isArray(payload.itens)) {
      out.itens = await Promise.all(
        payload.itens.map(async (it: any) => ({
          ...it,
          fotos: await converterLista(it?.fotos, "itens[].fotos"),
        })),
      );
    }
    if (Array.isArray(payload.fotos)) {
      out.fotos = await converterLista(payload.fotos, "fotos");
    }
    if (payload.assinatura) {
      // Assinatura sumida vira string vazia (o backend aceita) — o plantão finaliza.
      out.assinatura =
        (await converter(payload.assinatura, "assinatura")) ?? "";
    }
    return out;
  },

  /**
   * Faz upload de uma foto de OS para o storage geral.
   *
   * `sufixoEstavel` (a chave da ação) torna o NOME determinístico: o retry sobrescreve o
   * mesmo objeto no storage em vez de acumular cópia nova a cada tentativa — antes cada
   * replay gerava `os_..._${Date.now()}.jpg` diferente, e as tentativas ambíguas viravam
   * N binários (e N linhas, cada uma com URL distinta do mesmo conteúdo).
   */
  uploadOsFoto: async (
    uri: string,
    idColaborador: number,
    idCronograma: number,
    sufixoEstavel?: string,
  ): Promise<string | null> => {
    try {
      const sufixo = String(sufixoEstavel ?? "").replace(/[^a-zA-Z0-9_-]/g, "");
      const safeName = `os_${idCronograma}_${sufixo || Date.now()}.jpg`;
      const caminhoPasta = `OS_Fotos/${idColaborador}/${idCronograma}`;

      const arquivo = {
        uri: uri,
        name: safeName,
        type: "image/jpeg",
      } as any;

      console.log("[Sync] Subindo foto de OS para storage:", {
        caminhoPasta,
        safeName,
      });
      const response = await Api.geral.upload(arquivo, caminhoPasta, safeName);

      if (!response.success) {
        console.warn("[Sync] Falha no upload da foto de OS:", response.erros);
        return null;
      }

      const done: any = response.done;
      const url =
        done?.urlAcesso ?? done?.url ?? done?.endereco_arquivo ?? null;

      console.log("[Sync] Foto de OS subida com sucesso:", url);
      return url ? String(url).trim() : null;
    } catch (error) {
      console.error("[Sync] Erro no upload da foto de OS:", error);
      return null;
    }
  },

  /**
   * Garante que o diretório de assets de checklist existe
   */
  initChecklistAssetsDir: async () => {
    const info = await FileSystem.getInfoAsync(CHECKLIST_ASSETS_DIR);
    if (!info.exists) {
      await FileSystem.makeDirectoryAsync(CHECKLIST_ASSETS_DIR, {
        intermediates: true,
      });
    }
  },

  /**
   * Verifica se uma string é um base64
   */
  isBase64: (str: string): boolean => {
    return str.startsWith("data:");
  },

  /**
   * Converte um base64 para arquivo e retorna o URI
   */
  base64ToFileUri: async (
    base64String: string,
    fileType: "image" | "pdf" = "image",
  ): Promise<string | null> => {
    try {
      await SyncService.initChecklistAssetsDir();
      const ext = fileType === "pdf" ? "pdf" : "jpg";
      const filename = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const dest = `${CHECKLIST_ASSETS_DIR}${filename}`;

      // Extrai o conteúdo base64 (remove o prefixo data:...)
      const base64Content = base64String.split(",")[1] || base64String;

      await FileSystem.writeAsStringAsync(dest, base64Content, {
        encoding: FileSystem.EncodingType.Base64,
      });

      return dest;
    } catch (error) {
      console.error("[Sync] Erro ao converter base64 para arquivo:", error);
      return null;
    }
  },

  /**
   * URI que já aponta para o storage (http/https) — não é arquivo local. Acontece ao
   * EDITAR uma execução: as fotos gravadas voltam da API como URL. O FileSystem não abre
   * http, então tanto o persist quanto o upload falhariam e a foto sumiria do checklist.
   */
  _ehUrlRemota: (uri: any): boolean =>
    typeof uri === "string" && /^https?:\/\//i.test(uri.trim()),

  /**
   * Persiste um asset de checklist temporário em diretório persistente
   */
  /**
   * Persiste uma lista de assets de checklist em `checklist_assets/`, devolvendo os
   * mesmos itens com `Uri` de ARQUIVO.
   *
   * ⚠️ Obrigatório antes de guardar assets em qualquer espelho do AsyncStorage: o sheet
   * devolve uri de CACHE (que o sistema limpa) e, no caso da assinatura, `data:` base64 —
   * que estoura a linha do storage. URL remota (edição) passa direto.
   */
  persistChecklistAssets: async (assets: any): Promise<any[]> => {
    const lista = Array.isArray(assets) ? assets : [];
    const out: any[] = [];
    for (const asset of lista) {
      const uri = String((asset as any)?.Uri ?? "");
      if (!uri) continue;
      if (SyncService._ehUrlRemota(uri)) {
        out.push(asset);
        continue;
      }
      const tipo = (asset as any)?.Tipo === "pdf" ? "pdf" : "image";
      let persistido = await SyncService.persistChecklistAsset(uri, tipo);
      // `persistChecklistAsset` devolve o próprio uri no catch e null quando falha a
      // conversão do base64 — nos dois casos o dado NÃO pode entrar no espelho como veio.
      if (persistido && SyncService.isBase64(persistido)) {
        persistido = await SyncService.base64ToFileUri(persistido, tipo);
      }
      if (!persistido || SyncService.isBase64(persistido)) {
        console.warn(
          "[Sync] persistChecklistAssets: asset sem caminho utilizável — mantido como veio (degradado)",
        );
        out.push(asset);
        continue;
      }
      out.push({ ...asset, Uri: persistido });
    }
    return out;
  },

  persistChecklistAsset: async (
    uriOriginal: string,
    fileType: "image" | "pdf" = "image",
  ): Promise<string | null> => {
    // Caminho de antes de uma atualização (iOS muda a pasta do app) → pasta atual.
    const uri = caminhoNoAppAtual(uriOriginal);
    try {
      console.log(
        "[Sync] persistChecklistAsset: uri:",
        String(uri).slice(0, 64),
        `(${String(uri).length} chars)`,
      );

      // Se já estiver no diretório de checklist assets, retorna a própria URI
      if (uri.startsWith(CHECKLIST_ASSETS_DIR)) {
        console.log(
          "[Sync] persistChecklistAsset: já está no diretório persistente, retornando URI original:",
          uri,
        );
        const fileInfo = await FileSystem.getInfoAsync(uri);
        if (fileInfo.exists) {
          return uri;
        }
      }

      // Se for base64, converte para arquivo primeiro
      if (SyncService.isBase64(uri)) {
        console.log(
          "[Sync] persistChecklistAsset: é base64, convertendo para arquivo...",
        );
        const fileUri = await SyncService.base64ToFileUri(uri, fileType);
        if (fileUri) {
          return fileUri;
        }
      }

      await SyncService.initChecklistAssetsDir();
      const ext = fileType === "pdf" ? "pdf" : "jpg";
      const filename = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const dest = `${CHECKLIST_ASSETS_DIR}${filename}`;
      console.log("[Sync] persistChecklistAsset: destino:", dest);

      const sourceInfo = await FileSystem.getInfoAsync(uri);
      console.log("[Sync] persistChecklistAsset: sourceInfo:", sourceInfo);

      if (!sourceInfo.exists) {
        console.error(
          "[Sync] persistChecklistAsset: Arquivo de origem não existe!",
        );
        return null;
      }

      await FileSystem.copyAsync({ from: uri, to: dest });

      const destInfo = await FileSystem.getInfoAsync(dest);
      console.log("[Sync] persistChecklistAsset: destInfo:", destInfo);

      if (destInfo.exists) {
        return dest;
      } else {
        console.warn(
          "[Sync] persistChecklistAsset: Arquivo de destino não existe, retornando URI original:",
          uri,
        );
        return uri;
      }
    } catch (error) {
      console.warn("[Sync] Falha ao persistir asset de checklist:", error);
      return uri;
    }
  },

  /**
   * Faz upload de um asset (foto, assinatura, pdf) para o storage geral de checklist
   */
  uploadChecklistAsset: async (
    uriOriginal: string,
    idColaborador: number,
    idChecklist: number | string,
    fileType: "image" | "pdf" = "image",
  ): Promise<string | null> => {
    // Caminho de antes de uma atualização (iOS muda a pasta do app) → pasta atual.
    const uri = caminhoNoAppAtual(uriOriginal);
    try {
      console.log(
        "[Sync] uploadChecklistAsset: uri:",
        String(uri).slice(0, 64),
        `(${String(uri).length} chars)`,
      );

      // Se for base64, converte para arquivo primeiro
      let fileUri = uri;
      if (SyncService.isBase64(uri)) {
        console.log(
          "[Sync] uploadChecklistAsset: é base64, convertendo para arquivo...",
        );
        const convertedUri = await SyncService.base64ToFileUri(uri, fileType);
        if (convertedUri) {
          fileUri = convertedUri;
        }
      }

      // Tenta o fileUri primeiro
      let fileInfo = await FileSystem.getInfoAsync(fileUri);
      console.log(
        "[Sync] uploadChecklistAsset: fileInfo do fileUri:",
        fileInfo,
      );

      if (!fileInfo.exists && fileUri !== uri) {
        // Se não existe, tenta a URI original
        console.log(
          "[Sync] uploadChecklistAsset: fileUri não existe, tentando URI original:",
          uri,
        );
        fileUri = uri;
        fileInfo = await FileSystem.getInfoAsync(fileUri);
        console.log(
          "[Sync] uploadChecklistAsset: fileInfo da URI original:",
          fileInfo,
        );
      }

      if (!fileInfo.exists) {
        console.error(
          "[Sync] uploadChecklistAsset: Arquivo não existe!",
          fileUri,
        );
        return null;
      }

      const ext = fileType === "pdf" ? "pdf" : "jpg";
      const mimeType = fileType === "pdf" ? "application/pdf" : "image/jpeg";
      const safeName = `checklist_${idChecklist}_${Date.now()}.${ext}`;
      const caminhoPasta = `CheckList/${new Date().getFullYear()}/${String(new Date().getMonth() + 1).padStart(2, "0")}/${idColaborador}`;

      const arquivo = {
        uri: fileUri,
        name: safeName,
        type: mimeType,
      } as any;

      console.log("[Sync] Subindo asset de checklist para storage:", {
        caminhoPasta,
        safeName,
        arquivo,
      });
      const response = await Api.geral.upload(arquivo, caminhoPasta, safeName);

      if (!response.success) {
        console.warn(
          "[Sync] Falha no upload do asset de checklist:",
          response.erros,
        );
        return null;
      }

      const done: any = response.done;
      const url =
        done?.urlAcesso ?? done?.url ?? done?.endereco_arquivo ?? null;

      console.log("[Sync] Asset de checklist subido com sucesso:", url);
      return url ? String(url).trim() : null;
    } catch (error) {
      console.error("[Sync] Erro no upload do asset de checklist:", error);
      return null;
    }
  },

  processChecklistAssets: async (
    checklist: any,
    idColaborador: number,
    idInspecaoCadastro: string | number = "ronda",
  ) => {
    if (!checklist || typeof checklist !== "object") {
      return {
        checklistProcessado: checklist,
        todosUploadsOk: true,
        urisParaRemover: [],
      };
    }

    let checklistProcessado = { ...checklist };
    let todosUploadsOk = true;
    const urisParaRemover: string[] = [];
    const checklistKeys = Object.keys(checklistProcessado);

    for (const key of checklistKeys) {
      const value = checklistProcessado[key];

      // Se for um array de assets
      if (Array.isArray(value)) {
        const novosAssets = [];

        for (const asset of value) {
          if (
            asset &&
            asset.uri &&
            (asset.uri.includes("os_fotos/") ||
              asset.uri.includes("checklist_assets/"))
          ) {
            try {
              const fileType = asset.name?.endsWith(".pdf") ? "pdf" : "image";
              const url = await SyncService.uploadChecklistAsset(
                asset.uri,
                idColaborador,
                idInspecaoCadastro,
                fileType,
              );

              if (url) {
                novosAssets.push({ ...asset, uri: url });
                urisParaRemover.push(asset.uri);
              } else {
                todosUploadsOk = false;
                break;
              }
            } catch (e) {
              console.error("[Sync] Erro no upload de asset do checklist:", e);
              todosUploadsOk = false;
              break;
            }
          } else {
            novosAssets.push(asset);
          }
        }

        if (!todosUploadsOk) break;
        checklistProcessado[key] = novosAssets;
      }
      // Se for um único asset (como assinatura)
      else if (value && typeof value === "object" && value.uri) {
        if (
          value.uri.includes("os_fotos/") ||
          value.uri.includes("checklist_assets/")
        ) {
          try {
            const fileType = value.name?.endsWith(".pdf") ? "pdf" : "image";
            const url = await SyncService.uploadChecklistAsset(
              value.uri,
              idColaborador,
              idInspecaoCadastro,
              fileType,
            );

            if (url) {
              checklistProcessado[key] = { ...value, uri: url };
              urisParaRemover.push(value.uri);
            } else {
              todosUploadsOk = false;
            }
          } catch (e) {
            console.error("[Sync] Erro no upload de asset do checklist:", e);
            todosUploadsOk = false;
          }
        }
      }
    }

    return { checklistProcessado, todosUploadsOk, urisParaRemover };
  },

  /**
   * Realiza o upload da foto e salva no banco de dados da OS.
   * Se offline, persiste a foto e enfileira.
   */
  osUploadESalvarFoto: async (
    payload: {
      id_adm_ordem_servico_cronograma: number;
      tipo_foto: "A" | "P";
      uri: string;
      idColaborador: number;
      data_log?: string;
    },
    actionId?: string,
  ) => {
    await carimbarHashTenant(payload);
    // Hora da CAPTURA (BRT). Carimba na 1ª chamada (tela da OS) e persiste na fila; no
    // replay do sync vem preenchida em payload.data_log e é reusada — assim a foto tirada
    // offline guarda a hora real da captura, não a hora do upload/sync.
    const dataLog = payload.data_log ?? nowLocalSqlDateTime();
    // Identidade estável da FOTO em todo replay: a tela passa o id do asset e o dreno o
    // action.id — os dois são estáveis. É ela que (a) fixa o NOME no storage (retry
    // sobrescreve em vez de acumular) e (b) vira a chave_cliente que o T-Consumer usa
    // na UNIQUE — a mesma foto reenviada converge em UMA linha (relato de campo: 12x).
    const idAcao =
      String(actionId ?? "") ||
      `f${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const chaveFoto =
      String((payload as any).chave_cliente ?? "") ||
      `${String((payload as any).hash ?? "").trim()}:${idAcao}`;
    // 🔒 Tenant do EVENTO: se um trocarEmpresa acontecer durante o upload (45s),
    // o enqueue do fallback ainda carimba a empresa DONA da foto.
    const tenantEvento = await tenantHashSessaoAtual().catch(() => "");
    // URL já conquistada num ciclo anterior (upload ok + resposta do salvar perdida):
    // reaproveita em vez de subir o binário de novo.
    let urlConquistada: string | null =
      String((payload as any).arquivo_url ?? "").trim() || null;
    try {
      const isConnected = await SyncService.checkInternet();

      if (isConnected) {
        console.log("[Sync] Iniciando fluxo de foto de OS (Upload -> Save)...");
        // 🔒 Guarda de sessão (upload de até 45s): trocarEmpresa no meio faria o
        // salvar sair no tenant errado — mudou a gen, cai no fluxo offline (fila
        // guardada por tenant).
        const genEnvio = peekApiSessionGen();

        // 1) Upload para obter a URL — pulado quando um ciclo anterior já a obteve.
        const urlAcesso =
          urlConquistada ??
          (await SyncService.uploadOsFoto(
            payload.uri,
            payload.idColaborador,
            payload.id_adm_ordem_servico_cronograma,
            idAcao,
          ));
        if (urlAcesso) urlConquistada = urlAcesso;

        if (urlAcesso && peekApiSessionGen() !== genEnvio) {
          console.warn(
            "[Sync] 🔒 Sessão mudou durante o envio imediato de OS_FOTO_SAVE — enfileirando.",
          );
        } else if (urlAcesso) {
          // 2) Agora salva o registro na OS usando a URL obtida enviando como JSON (conforme backend [FromBody])
          const payloadSave = {
            id_adm_ordem_servico_cronograma:
              payload.id_adm_ordem_servico_cronograma,
            tipo_foto: payload.tipo_foto,
            arquivo: urlAcesso,
            id_adm_usuario: payload.idColaborador,
            data_log: dataLog,
            chave_cliente: chaveFoto,
            // tenant dono do registro (carimbado no arg pela função de origem)
            hash: (payload as any).hash ?? "",
          };

          const saveResp = await Api.ordemServico.salvarFotos(
            payloadSave as any,
          );

          if (saveResp.success) {
            console.log(
              "[Sync] Registro de foto de OS salvo com sucesso via JSON",
            );
            // Espelho local MESMO no sucesso: 200 = enfileirado no Rabbit, e a lista de
            // OS (cache) só devolve a foto minutos depois — sem o espelho, a foto
            // "enviada" ficava invisível nos detalhes até o servidor alcançar.
            const uriEspelho = await SyncService.persistOsPhoto(payload.uri);
            await SyncService.registrarFotoLocalOs(
              payload.id_adm_ordem_servico_cronograma,
              {
                id: idAcao,
                tipo_foto: String(payload.tipo_foto ?? ""),
                uri: uriEspelho ?? undefined,
                url: urlAcesso,
                pendente: false,
                em: Date.now(),
              },
            );
            return { success: true, offline: false };
          }
          console.warn(
            "[Sync] Falha ao salvar registro de foto de OS após upload:",
            saveResp.erros,
          );
          // REJEIÇÃO do servidor (não transitória): enfileira do mesmo jeito (regra de
          // ouro — jamais perder registro), mas o retorno diz a verdade para o dreno
          // aplicar backoff em vez de re-tentar em todos os ~20 gatilhos.
          if ((saveResp as any)?.offline !== true) {
            const uriRejeicao =
              (await SyncService.persistOsPhoto(payload.uri)) || payload.uri;
            await SyncService.enqueueOfflineAction(
              "OS_FOTO_SAVE",
              {
                ...payload,
                uri: uriRejeicao,
                data_log: dataLog,
                chave_cliente: chaveFoto,
                arquivo_url: urlConquistada ?? undefined,
              },
              idAcao,
              tenantEvento,
            );
            await SyncService.registrarFotoLocalOs(
              payload.id_adm_ordem_servico_cronograma,
              {
                id: idAcao,
                tipo_foto: String(payload.tipo_foto ?? ""),
                uri: uriRejeicao,
                url: urlConquistada ?? undefined,
                pendente: true,
                em: Date.now(),
              },
            );
            return {
              success: false,
              offline: false,
              erros: (saveResp as any)?.erros,
            };
          }
        } else {
          console.warn("[Sync] Falha no upload prévio da foto de OS");
        }
      }

      // Fluxo Offline ou Falha na API
      console.log(
        "[Sync] Enfileirando foto de OS para sincronização posterior...",
      );
      const persistedUri = await SyncService.persistOsPhoto(payload.uri);

      await SyncService.enqueueOfflineAction(
        "OS_FOTO_SAVE",
        {
          ...payload,
          uri: persistedUri || payload.uri,
          data_log: dataLog,
          chave_cliente: chaveFoto,
          // A URL já obtida viaja com a ação: o próximo ciclo pula o upload.
          arquivo_url: urlConquistada ?? undefined,
        },
        idAcao,
        tenantEvento,
      );
      await SyncService.registrarFotoLocalOs(
        payload.id_adm_ordem_servico_cronograma,
        {
          id: idAcao,
          tipo_foto: String(payload.tipo_foto ?? ""),
          uri: persistedUri || payload.uri,
          url: urlConquistada ?? undefined,
          pendente: true,
          em: Date.now(),
        },
      );

      return { success: true, offline: true };
    } catch (error) {
      console.error("[Sync] Erro no fluxo de foto de OS:", error);
      const persistedUri = await SyncService.persistOsPhoto(payload.uri);
      await SyncService.enqueueOfflineAction(
        "OS_FOTO_SAVE",
        {
          ...payload,
          uri: persistedUri || payload.uri,
          data_log: dataLog,
          chave_cliente: chaveFoto,
          arquivo_url: urlConquistada ?? undefined,
        },
        idAcao,
        tenantEvento,
      );
      await SyncService.registrarFotoLocalOs(
        payload.id_adm_ordem_servico_cronograma,
        {
          id: idAcao,
          tipo_foto: String(payload.tipo_foto ?? ""),
          uri: persistedUri || payload.uri,
          url: urlConquistada ?? undefined,
          pendente: true,
          em: Date.now(),
        },
      );
      return { success: true, offline: true };
    }
  },

  /**
   * Salva fotos de uma OS: tenta online ou enfileira offline
   * @deprecated Usar osUploadESalvarFoto que trata o upload prévio
   */
  osSalvarFotos: async (payload: any, actionId?: string) => {
    // Mantido por compatibilidade se necessário, mas redireciona para o novo fluxo se possível
    return SyncService.osUploadESalvarFoto(payload, actionId);
  },

  /**
   * Exclui uma foto de uma OS: tenta online ou enfileira offline
   */
  osExcluirFoto: async (codigoFoto: string, actionId?: string) => {
    try {
      const isConnected = await SyncService.checkInternet();

      if (isConnected) {
        console.log("[Sync] Excluindo foto de OS via API...");
        const response = await Api.ordemServico.excluirFoto(codigoFoto);
        if (response.success) {
          console.log("[Sync] Foto de OS excluída com sucesso via API");
          return { success: true, offline: false };
        }
        console.warn(
          "[Sync] Falha ao excluir foto de OS via API:",
          response.erros,
        );
      }

      // Fluxo Offline ou Falha na API
      console.log(
        "[Sync] Enfileirando exclusão de foto de OS para sincronização posterior...",
      );

      await SyncService.enqueueOfflineAction(
        "OS_FOTO_DELETE",
        {
          codigoFoto,
        },
        actionId,
      );

      return { success: true, offline: true };
    } catch (error) {
      console.error("[Sync] Erro no fluxo de exclusão de foto de OS:", error);
      await SyncService.enqueueOfflineAction(
        "OS_FOTO_DELETE",
        {
          codigoFoto,
        },
        actionId,
      );
      return { success: true, offline: true };
    }
  },

  /**
   * Salva uma anotação de uma OS: tenta online ou enfileira offline
   */
  osSalvarAnotacao: async (
    payload: {
      id_adm_ordem_servico_cronograma: number;
      anotacao: string;
      id_adm_usuario: number;
      data_log: string;
    },
    actionId?: string,
  ) => {
    await carimbarHashTenant(payload);
    try {
      const isConnected = await SyncService.checkInternet();

      if (isConnected) {
        console.log("[Sync] Salvando anotação de OS via API...");
        const response = await Api.ordemServico.salvarAnotacao(payload);
        if (response.success) {
          console.log("[Sync] Anotação de OS salva com sucesso via API");
          return { success: true, offline: false };
        }
        console.warn(
          "[Sync] Falha ao salvar anotação de OS via API:",
          response.erros,
        );
      }

      // Fluxo Offline ou Falha na API
      console.log(
        "[Sync] Enfileirando anotação de OS para sincronização posterior...",
      );

      await SyncService.enqueueOfflineAction(
        "OS_ANOTACAO_SAVE",
        payload,
        actionId,
      );

      return { success: true, offline: true };
    } catch (error) {
      console.error(
        "[Sync] Erro no fluxo de salvamento de anotação de OS:",
        error,
      );
      await SyncService.enqueueOfflineAction(
        "OS_ANOTACAO_SAVE",
        payload,
        actionId,
      );
      return { success: true, offline: true };
    }
  },

  /**
   * Inicia uma OS: tenta online ou enfileira offline
   */
  osIniciar: async (
    payload: {
      id_adm_ordem_servico_cronograma: number;
      id_adm_usuario: number;
      data_log: string;
      latitude?: string | null;
      longitude?: string | null;
    },
    actionId?: string,
  ) => {
    await carimbarHashTenant(payload);
    try {
      const isConnected = await SyncService.checkInternet();

      if (isConnected) {
        console.log("[Sync] Iniciando OS via API...");
        const response = await Api.ordemServico.iniciarOs(payload);
        if (response.success) {
          console.log("[Sync] OS iniciada com sucesso via API");
          return { success: true, offline: false };
        }
        console.warn("[Sync] Falha ao iniciar OS via API:", response.erros);
      }

      // Fluxo Offline ou Falha na API
      console.log(
        "[Sync] Enfileirando início de OS para sincronização posterior...",
      );

      await SyncService.enqueueOfflineAction("OS_INICIAR", payload, actionId);

      return { success: true, offline: true };
    } catch (error) {
      console.error("[Sync] Erro no fluxo de início de OS:", error);
      await SyncService.enqueueOfflineAction("OS_INICIAR", payload, actionId);
      return { success: true, offline: true };
    }
  },

  /**
   * Conclui uma OS: tenta online ou enfileira offline
   */
  osConcluir: async (
    payload: {
      id_adm_ordem_servico_cronograma: number;
      id_adm_usuario: number;
      data_log: string;
      anotacao_final: string;
      latitude?: string | null;
      longitude?: string | null;
      /** Lista de tarefas como o colaborador deixou (JSON; ver utils/listaTarefas). */
      lista_tarefas_execucao?: string;
    },
    actionId?: string,
  ) => {
    await carimbarHashTenant(payload);
    try {
      const isConnected = await SyncService.checkInternet();

      if (isConnected) {
        console.log("[Sync] Concluindo OS via API...");
        const response = await Api.ordemServico.concluirOs(payload);
        if (response.success) {
          console.log("[Sync] OS concluída com sucesso via API");
          return { success: true, offline: false };
        }
        console.warn("[Sync] Falha ao concluir OS via API:", response.erros);
      }

      // Fluxo Offline ou Falha na API
      console.log(
        "[Sync] Enfileirando conclusão de OS para sincronização posterior...",
      );

      await SyncService.enqueueOfflineAction("OS_CONCLUIR", payload, actionId);

      return { success: true, offline: true };
    } catch (error) {
      console.error("[Sync] Erro no fluxo de conclusão de OS:", error);
      await SyncService.enqueueOfflineAction("OS_CONCLUIR", payload, actionId);
      return { success: true, offline: true };
    }
  },

  ordemServicoSobDemandaSalvar: async (
    payload: any,
    options?: {
      actionId?: string;
      clienteLabel?: string;
      equipeLabel?: string;
    },
  ) => {
    await carimbarHashTenant(payload);
    // A chave de idempotência nasce AQUI, antes da 1ª tentativa — a mesma viaja no POST
    // de foreground, na fila e em todo replay do dreno. O T-ARC tem UNIQUE nela: retry
    // com a mesma chave devolve a OS já criada em vez de criar outra (relato: 8 cópias,
    // semeadas por timeout com o INSERT já commitado). Sem prefixo __ de propósito, para
    // atravessar o _cleanOsSobDemandaPayload e chegar ao servidor.
    const actionId =
      options?.actionId ?? Math.random().toString(36).slice(2, 11);
    if (!payload.chave_cliente) {
      payload.chave_cliente = `${String(payload?.hash ?? "").trim()}:${actionId}`;
    }
    const cleaned = _cleanOsSobDemandaPayload(payload);
    try {
      const isConnected = await SyncService.checkInternet();

      if (isConnected) {
        const response = await Api.ordemServico.salvar(cleaned);
        if (response.success) {
          const id = Number((response as any)?.done?.id_adm_ordem_servico);
          return {
            success: true,
            offline: false,
            id_adm_ordem_servico: Number.isFinite(id) ? id : undefined,
          };
        }
      }

      const tempCronogramaId = -Math.floor(Date.now());
      await SyncService.setLocalOsSobDemanda({
        temp_cronograma_id: tempCronogramaId,
        payload: cleaned,
        created_at: Date.now(),
        cliente_label: options?.clienteLabel,
        equipe_label: options?.equipeLabel,
      });

      await SyncService.enqueueOfflineAction(
        "OS_SOBDEMANDA_SALVAR",
        {
          ...cleaned,
          __temp_cronograma_id: tempCronogramaId,
          __cliente_label: options?.clienteLabel,
          __equipe_label: options?.equipeLabel,
          __local: true,
        },
        actionId,
      );
      return {
        success: true,
        offline: true,
        temp_cronograma_id: tempCronogramaId,
      };
    } catch {
      const tempCronogramaId = -Math.floor(Date.now());
      await SyncService.setLocalOsSobDemanda({
        temp_cronograma_id: tempCronogramaId,
        payload: cleaned,
        created_at: Date.now(),
        cliente_label: options?.clienteLabel,
        equipe_label: options?.equipeLabel,
      });

      await SyncService.enqueueOfflineAction(
        "OS_SOBDEMANDA_SALVAR",
        {
          ...cleaned,
          __temp_cronograma_id: tempCronogramaId,
          __cliente_label: options?.clienteLabel,
          __equipe_label: options?.equipeLabel,
          __local: true,
        },
        actionId,
      );
      return {
        success: true,
        offline: true,
        temp_cronograma_id: tempCronogramaId,
      };
    }
  },

  treinamentoGravarLogAssistido: async (
    payload: {
      idTreinamento?: number;
      idColaborador?: number;
      idTreinamentoVideo?: number;
    },
    actionId?: string,
  ) => {
    await carimbarHashTenant(payload);
    try {
      const isConnected = await SyncService.checkInternet();

      if (isConnected) {
        const response = await Api.treinamento.gravarLogAssistido(payload);
        if (response.success) {
          return { success: true, offline: false };
        }
      }

      await SyncService.enqueueOfflineAction(
        "TREINAMENTO_LOG_ASSISTIDO",
        payload,
        actionId,
      );
      return { success: true, offline: true };
    } catch (error) {
      await SyncService.enqueueOfflineAction(
        "TREINAMENTO_LOG_ASSISTIDO",
        payload,
        actionId,
      );
      return { success: true, offline: true };
    }
  },

  treinamentoGravarLogAssistidoParcial: async (
    payload: {
      idTreinamento?: number;
      idColaborador?: number;
    },
    actionId?: string,
  ) => {
    await carimbarHashTenant(payload);
    try {
      const isConnected = await SyncService.checkInternet();

      if (isConnected) {
        const response =
          await Api.treinamento.gravarLogAssistidoParcial(payload);
        if (response.success) {
          return { success: true, offline: false };
        }
      }

      await SyncService.enqueueOfflineAction(
        "TREINAMENTO_LOG_ASSISTIDO_PARCIAL",
        payload,
        actionId,
      );
      return { success: true, offline: true };
    } catch (error) {
      await SyncService.enqueueOfflineAction(
        "TREINAMENTO_LOG_ASSISTIDO_PARCIAL",
        payload,
        actionId,
      );
      return { success: true, offline: true };
    }
  },

  treinamentoGravarAvaliacao: async (payload: any, actionId?: string) => {
    await carimbarHashTenant(payload);
    try {
      const isConnected = await SyncService.checkInternet();

      if (isConnected) {
        const response = await Api.treinamento.gravarAvaliacao(payload);
        if (response.success) {
          return { success: true, offline: false };
        }
      }

      await SyncService.enqueueOfflineAction(
        "TREINAMENTO_AVALIACAO",
        payload,
        actionId,
      );
      return { success: true, offline: true };
    } catch (error) {
      await SyncService.enqueueOfflineAction(
        "TREINAMENTO_AVALIACAO",
        payload,
        actionId,
      );
      return { success: true, offline: true };
    }
  },

  estoquePedidoSalvar: async (payload: any, actionId?: string) => {
    await carimbarHashTenant(payload);
    try {
      const isConnected = await SyncService.checkInternet();

      if (isConnected) {
        const response = await Api.clientesEnvioProdutos.salvar(payload);
        if (SyncService._isEnfileiradoResponse(response)) {
          return { success: true, offline: false };
        }
      }

      await SyncService.enqueueOfflineAction(
        "ESTOQUE_PEDIDO_SALVAR",
        payload,
        actionId,
      );
      return { success: true, offline: true };
    } catch {
      await SyncService.enqueueOfflineAction(
        "ESTOQUE_PEDIDO_SALVAR",
        payload,
        actionId,
      );
      return { success: true, offline: true };
    }
  },

  estoquePedidoEditar: async (payload: any, actionId?: string) => {
    await carimbarHashTenant(payload);
    try {
      const isConnected = await SyncService.checkInternet();

      if (isConnected) {
        const response = await Api.clientesEnvioProdutos.editar(payload);
        if (SyncService._isEnfileiradoResponse(response)) {
          return { success: true, offline: false };
        }
      }

      await SyncService.enqueueOfflineAction(
        "ESTOQUE_PEDIDO_EDITAR",
        payload,
        actionId,
      );
      return { success: true, offline: true };
    } catch {
      await SyncService.enqueueOfflineAction(
        "ESTOQUE_PEDIDO_EDITAR",
        payload,
        actionId,
      );
      return { success: true, offline: true };
    }
  },

  estoquePedidoExcluir: async (
    payload: {
      id?: number;
      codigoCliente?: number;
      codigoColaborador?: number;
    },
    actionId?: string,
  ) => {
    await carimbarHashTenant(payload);
    try {
      const isConnected = await SyncService.checkInternet();

      if (isConnected) {
        const response = await Api.clientesEnvioProdutos.excluir(payload);
        if (SyncService._isEnfileiradoResponse(response)) {
          return { success: true, offline: false };
        }
      }

      await SyncService.enqueueOfflineAction(
        "ESTOQUE_PEDIDO_EXCLUIR",
        payload,
        actionId,
      );
      return { success: true, offline: true };
    } catch {
      await SyncService.enqueueOfflineAction(
        "ESTOQUE_PEDIDO_EXCLUIR",
        payload,
        actionId,
      );
      return { success: true, offline: true };
    }
  },

  /**
   * Salva checklist execução: tenta online ou enfileira offline
   */
  checklistSalvar: async (
    payload: any,
    actionId?: string,
    opts?: {
      /**
       * Pula o envio online e vai DIRETO para a fila. Usado pelo checklist de RONDA
       * quando o PONTO ficou retido na fila local: enviá-lo online na frente fazia o
       * consumer receber um checklist cujo ponto não existe (morria no .dead após o
       * retry). Na fila, o dreno preserva a ordem ponto → checklist.
       */
      forcarFila?: boolean;
    },
  ) => {
    await carimbarHashTenant(payload);
    try {
      const isConnected =
        !opts?.forcarFila && (await SyncService.checkInternet());
      // __localExecId / __localComplementoId são rastro INTERNO dos mapas locais: ficam
      // na ação enfileirada (o dreno precisa deles) mas nunca vão para a API.
      const { Assets, __localExecId, __localComplementoId, ...restPayload } =
        payload;
      let processedPayload = { ...restPayload };

      // `Assets` pode não vir (chamador novo, complemento sem anexo). O spread cru
      // lançava TypeError ANTES da guarda de baixo e caía no catch geral — que enfileira:
      // ação na fila mesmo ONLINE, com a UI prometendo "envio quando houver conexão".
      const listaAssets = Array.isArray(Assets) ? Assets : [];

      // Primeiro, SEMPRE persistimos os assets, mesmo online!
      let assetsPersistidos: Array<{
        PerguntaId: number;
        Tipo: "foto" | "assinatura" | "pdf";
        Uri: string;
      }> = [...listaAssets];
      if (listaAssets.length > 0) {
        assetsPersistidos = [];
        for (const asset of listaAssets) {
          // Asset que JÁ está no storage (edição de uma execução: as fotos voltam da API
          // como https://info.tirvumais.com.br/...). Copiar/enviar de novo é impossível —
          // FileSystem não abre http — e o upload falhando fazia a foto sumir da execução
          // ao salvar a edição. Aqui ela passa direto, com a mesma URL.
          if (SyncService._ehUrlRemota((asset as any)?.Uri)) {
            assetsPersistidos.push(asset);
            continue;
          }
          const fileType = asset.Tipo === "pdf" ? "pdf" : "image";
          console.log(
            "[Sync] checklistSalvar: persistindo asset (uri",
            String((asset as any)?.Uri ?? "").length,
            "chars)",
          );
          const uriPersistido = await SyncService.persistChecklistAsset(
            asset.Uri,
            fileType,
          );
          console.log("[Sync] checklistSalvar: uriPersistido:", uriPersistido);

          if (uriPersistido) {
            assetsPersistidos.push({
              ...asset,
              Uri: uriPersistido,
            });
          } else {
            assetsPersistidos.push(asset);
          }
        }
      }

      if (isConnected) {
        console.log("[Sync] Salvando checklist via API...");

        // Se temos assets, fazemos upload primeiro (usando os assets persistidos)
        if (assetsPersistidos && assetsPersistidos.length > 0) {
          const respostasComUrls = [...restPayload.Respostas];

          for (const asset of assetsPersistidos) {
            const fileType = asset.Tipo === "pdf" ? "pdf" : "image";
            // Já é URL do storage (veio da própria execução, numa edição): reaproveita.
            const url = SyncService._ehUrlRemota(asset.Uri)
              ? String(asset.Uri).trim()
              : await SyncService.uploadChecklistAsset(
                  asset.Uri,
                  restPayload.ResponsavelId,
                  restPayload.ChecklistId,
                  fileType,
                );

            if (url) {
              if (asset.Tipo === ("lsp" as any)) {
                // LSP: junta a URL da foto no Foto da resposta EXISTENTE (a placa), sem criar outra.
                const alvo = respostasComUrls.find(
                  (r: any) => r.PerguntaId === asset.PerguntaId,
                );
                if (alvo) alvo.Foto = url;
                else
                  respostasComUrls.push({
                    PerguntaId: asset.PerguntaId,
                    Resposta: "",
                    Foto: url,
                  });
              } else {
                const respostaTexto =
                  asset.Tipo === "foto"
                    ? "Foto"
                    : asset.Tipo === "assinatura"
                      ? "Assinatura (App)"
                      : "PDF";
                respostasComUrls.push({
                  PerguntaId: asset.PerguntaId,
                  Resposta: respostaTexto,
                  Foto: url,
                });
              }
            }
          }

          processedPayload.Respostas = respostasComUrls;
        }

        // DIAGNÓSTICO LSP: confirma se a foto foi anexada às respostas ANTES de enviar.
        // Se aparecer "respostas c/ Foto: 0" com uma pergunta LSP respondida, o problema é
        // no app (asset LSP não chegou / upload falhou); se > 0, o app está enviando certo
        // e o que falta é o T-ARC/T-Consumer redeployados com a gravação da coluna `foto`.
        try {
          const comFoto = (processedPayload.Respostas || []).filter(
            (r: any) => r && r.Foto,
          );
          console.log(
            `[Sync][LSP] respostas c/ Foto: ${comFoto.length}`,
            comFoto.map((r: any) => ({
              PerguntaId: r.PerguntaId,
              Foto: String(r.Foto).slice(0, 60),
            })),
          );
        } catch {}

        const response = await Api.checklistExecucao.salvar(processedPayload);
        if (response.success) {
          console.log("[Sync] Checklist salvo com sucesso via API");
          return { success: true, offline: false };
        }
        console.warn(
          "[Sync] Falha ao salvar checklist via API:",
          response.erros,
        );
      }

      // Fluxo Offline ou Falha na API: usamos os assets já persistidos
      console.log(
        "[Sync] Enfileirando checklist para sincronização posterior...",
      );

      let payloadParaEnfileirar = {
        ...payload,
        Assets: assetsPersistidos,
      };

      await SyncService.enqueueOfflineAction(
        "CHECKLIST_SALVAR",
        payloadParaEnfileirar,
        actionId,
      );

      return { success: true, offline: true };
    } catch (error) {
      console.error("[Sync] Erro no fluxo de salvamento de checklist:", error);

      // Tenta persistir os assets mesmo em caso de erro
      let payloadParaEnfileirar = { ...payload };

      try {
        const Assets = Array.isArray(payload?.Assets) ? payload.Assets : [];
        if (Assets.length > 0) {
          const assetsPersistidos: Array<{
            PerguntaId: number;
            Tipo: "foto" | "assinatura" | "pdf";
            Uri: string;
          }> = [];

          for (const asset of Assets) {
            // URL do storage (edição): nada a persistir, a foto já está no servidor.
            if (SyncService._ehUrlRemota(asset?.Uri)) {
              assetsPersistidos.push(asset);
              continue;
            }
            const fileType = asset.Tipo === "pdf" ? "pdf" : "image";
            const uriPersistido = await SyncService.persistChecklistAsset(
              asset.Uri,
              fileType,
            );

            if (uriPersistido) {
              assetsPersistidos.push({
                ...asset,
                Uri: uriPersistido,
              });
            } else {
              assetsPersistidos.push(asset);
            }
          }

          payloadParaEnfileirar.Assets = assetsPersistidos;
        }
      } catch (persistErr) {
        console.error("[Sync] Erro ao persistir assets no catch:", persistErr);
      }

      await SyncService.enqueueOfflineAction(
        "CHECKLIST_SALVAR",
        payloadParaEnfileirar,
        actionId,
      );
      return { success: true, offline: true };
    }
  },

  _inferEnqueteRespostaFileType: (
    respostaItem: any,
    fotoValue?: string | null,
  ): "image" | "pdf" => {
    const resp = String(respostaItem?.resposta ?? respostaItem?.Resposta ?? "")
      .trim()
      .toUpperCase();
    if (resp === "PDF" || resp.includes("PDF")) return "pdf";

    const foto = String(
      fotoValue ?? respostaItem?.foto ?? respostaItem?.Foto ?? "",
    ).trim();
    const lower = foto.toLowerCase();
    if (lower.startsWith("data:application/pdf")) return "pdf";
    if (lower.includes(".pdf")) return "pdf";
    return "image";
  },

  uploadEnqueteAsset: async (
    uriOriginal: string,
    idColaborador: number,
    idEnqueteExecucao: number | string,
    fileType: "image" | "pdf" = "image",
  ): Promise<string | null> => {
    // Caminho de antes de uma atualização (iOS muda a pasta do app) → pasta atual.
    const uri = caminhoNoAppAtual(uriOriginal);
    try {
      let fileUri = uri;
      if (typeof uri === "string" && SyncService.isBase64(uri)) {
        const convertedUri = await SyncService.base64ToFileUri(uri, fileType);
        if (convertedUri) {
          fileUri = convertedUri;
        }
      }

      let fileInfo = await FileSystem.getInfoAsync(fileUri);
      if (!fileInfo.exists && fileUri !== uri) {
        fileUri = uri;
        fileInfo = await FileSystem.getInfoAsync(fileUri);
      }

      if (!fileInfo.exists) {
        return null;
      }

      const ext = fileType === "pdf" ? "pdf" : "jpg";
      const mimeType = fileType === "pdf" ? "application/pdf" : "image/jpeg";
      const safeName = `enquete_${idEnqueteExecucao}_${Date.now()}.${ext}`;
      const caminhoPasta = `Enquete/${new Date().getFullYear()}/${String(new Date().getMonth() + 1).padStart(2, "0")}/${idColaborador}`;

      const arquivo = {
        uri: fileUri,
        name: safeName,
        type: mimeType,
      } as any;

      const response = await Api.geral.upload(arquivo, caminhoPasta, safeName);
      if (!response.success) {
        return null;
      }

      const done: any = response.done;
      const url =
        done?.urlAcesso ?? done?.url ?? done?.endereco_arquivo ?? null;
      return url ? String(url).trim() : null;
    } catch {
      return null;
    }
  },

  _processEnquetePayloadUploads: async (
    payload: any,
  ): Promise<{ payload: any; urisParaRemover: string[]; changed: boolean }> => {
    if (!payload || typeof payload !== "object") {
      return { payload, urisParaRemover: [], changed: false };
    }

    const next = { ...payload };
    const respostasKey = Array.isArray((next as any).respostas)
      ? "respostas"
      : Array.isArray((next as any).Respostas)
        ? "Respostas"
        : null;
    if (!respostasKey) {
      return { payload: next, urisParaRemover: [], changed: false };
    }

    const idColaboradorRaw =
      (next as any).colaboradorId ?? (next as any).ColaboradorId;
    const idColaboradorNum = Number(idColaboradorRaw);
    const idColaborador = Number.isFinite(idColaboradorNum)
      ? idColaboradorNum
      : 0;

    const execIdRaw =
      (next as any).id ??
      (next as any).Id ??
      (next as any).idEnqueteExecucao ??
      (next as any).IdEnqueteExecucao ??
      (next as any).idExecucao ??
      (next as any).IdExecucao;
    const execId = execIdRaw ?? "enquete";

    const respostas = (next as any)[respostasKey] as any[];
    const urisParaRemover: string[] = [];
    let changed = false;

    const mapped = [];
    for (const item of respostas) {
      if (!item || typeof item !== "object") {
        mapped.push(item);
        continue;
      }

      const nextItem: any = { ...item };
      const fotoKey = Object.prototype.hasOwnProperty.call(nextItem, "foto")
        ? "foto"
        : Object.prototype.hasOwnProperty.call(nextItem, "Foto")
          ? "Foto"
          : null;

      if (!fotoKey) {
        mapped.push(nextItem);
        continue;
      }

      let fotoVal: any = nextItem[fotoKey];
      if (fotoVal && typeof fotoVal === "object" && "uri" in fotoVal) {
        fotoVal = String((fotoVal as any).uri);
        nextItem[fotoKey] = fotoVal;
        changed = true;
      }

      if (typeof fotoVal !== "string") {
        mapped.push(nextItem);
        continue;
      }

      const fotoStr = fotoVal.trim();
      if (!fotoStr) {
        mapped.push(nextItem);
        continue;
      }

      if (fotoStr.startsWith("http://") || fotoStr.startsWith("https://")) {
        mapped.push(nextItem);
        continue;
      }

      if (!idColaborador || idColaborador <= 0) {
        mapped.push(nextItem);
        continue;
      }

      const fileType = SyncService._inferEnqueteRespostaFileType(
        nextItem,
        fotoStr,
      );
      const url = await SyncService.uploadEnqueteAsset(
        fotoStr,
        idColaborador,
        execId,
        fileType,
      );
      if (url) {
        nextItem[fotoKey] = url;
        changed = true;
        if (fotoStr.startsWith(CHECKLIST_ASSETS_DIR)) {
          urisParaRemover.push(fotoStr);
        }
      }

      mapped.push(nextItem);
    }

    (next as any)[respostasKey] = mapped;
    return { payload: next, urisParaRemover, changed };
  },

  _persistEnquetePayloadAssets: async (payload: any): Promise<any> => {
    if (!payload || typeof payload !== "object") {
      return payload;
    }

    const next = { ...payload };
    const respostasKey = Array.isArray((next as any).respostas)
      ? "respostas"
      : Array.isArray((next as any).Respostas)
        ? "Respostas"
        : null;
    if (!respostasKey) return next;

    const respostas = (next as any)[respostasKey] as any[];
    const mapped = [];

    for (const item of respostas) {
      if (!item || typeof item !== "object") {
        mapped.push(item);
        continue;
      }

      const nextItem: any = { ...item };
      const fotoKey = Object.prototype.hasOwnProperty.call(nextItem, "foto")
        ? "foto"
        : Object.prototype.hasOwnProperty.call(nextItem, "Foto")
          ? "Foto"
          : null;

      if (!fotoKey) {
        mapped.push(nextItem);
        continue;
      }

      let fotoVal: any = nextItem[fotoKey];
      if (fotoVal && typeof fotoVal === "object" && "uri" in fotoVal) {
        fotoVal = String((fotoVal as any).uri);
        nextItem[fotoKey] = fotoVal;
      }

      if (typeof fotoVal !== "string") {
        mapped.push(nextItem);
        continue;
      }

      const fotoStr = fotoVal.trim();
      if (!fotoStr) {
        mapped.push(nextItem);
        continue;
      }

      if (fotoStr.startsWith("http://") || fotoStr.startsWith("https://")) {
        mapped.push(nextItem);
        continue;
      }

      const fileType = SyncService._inferEnqueteRespostaFileType(
        nextItem,
        fotoStr,
      );
      const persisted = await SyncService.persistChecklistAsset(
        fotoStr,
        fileType,
      );
      if (persisted) {
        nextItem[fotoKey] = persisted;
      }

      mapped.push(nextItem);
    }

    (next as any)[respostasKey] = mapped;
    return next;
  },

  enqueteSalvar: async (payload: any, actionId?: string) => {
    await carimbarHashTenant(payload);
    // Marca "respondida NESTE aparelho" no ponto ÚNICO por onde toda resposta
    // passa (envio imediato, fila e erro→fila) — guarda anti-reabertura do
    // pull-to-refresh (o /iniciar devolve a enquete como pendente por minutos:
    // cache + gravação assíncrona). Best-effort: falha aqui não bloqueia o envio.
    try {
      const idEnvio = Number(payload?.id);
      if (Number.isFinite(idEnvio) && idEnvio > 0) {
        await SyncService.markEnqueteRespondidaLocal(idEnvio);
      }
    } catch {}
    try {
      const isConnected = await SyncService.checkInternet();

      if (isConnected) {
        const processed =
          await SyncService._processEnquetePayloadUploads(payload);
        const response = await Api.enqueteExecucao.salvar(processed.payload);
        if (response.success) {
          for (const uri of processed.urisParaRemover) {
            try {
              await FileSystem.deleteAsync(uri, { idempotent: true });
            } catch {}
          }
          return { success: true, offline: false };
        }

        if (processed.changed) {
          for (const uri of processed.urisParaRemover) {
            try {
              await FileSystem.deleteAsync(uri, { idempotent: true });
            } catch {}
          }
        }

        await SyncService.enqueueOfflineAction(
          "ENQUETE_SALVAR",
          processed.payload,
          actionId,
        );
        return { success: true, offline: true };
      }

      const persisted = await SyncService._persistEnquetePayloadAssets(payload);
      await SyncService.enqueueOfflineAction(
        "ENQUETE_SALVAR",
        persisted,
        actionId,
      );
      return { success: true, offline: true };
    } catch {
      const persisted = await SyncService._persistEnquetePayloadAssets(payload);
      await SyncService.enqueueOfflineAction(
        "ENQUETE_SALVAR",
        persisted,
        actionId,
      );
      return { success: true, offline: true };
    }
  },

  rondaIniciar: async (
    payload: {
      codigoRonda?: number;
      codigoColaborador: number;
      id_adm_ronda_cadastro?: number;
      dataInicio?: string;
    },
    actionId?: string,
  ) => {
    await carimbarHashTenant(payload);
    try {
      // data_log_inicio = HORA REAL de início, capturada AGORA (no momento de iniciar) e gravada
      // no payload ENFILEIRADO. Assim, mesmo sincronizando depois (offline), o backend grava a
      // hora do INÍCIO, não a da inserção. nowLocalSqlDateTime() = BRT "yyyy-MM-dd HH:mm:ss".
      const payloadComInicio = {
        ...payload,
        dataInicio: payload.dataInicio ?? nowLocalSqlDateTime(),
      };
      console.log(
        "[Sync] rondaIniciar INICIADO! Payload:",
        payloadComInicio,
        "actionId:",
        actionId,
      );

      // SEMPRE enfileirar RONDA_INICIAR, mesmo com internet conectada!
      // Isso garante que cada execução terá seu próprio RONDA_INICIAR na fila
      const enqueuedId = await SyncService.enqueueOfflineAction(
        "RONDA_INICIAR",
        payloadComInicio,
        actionId,
      );
      console.log("[Sync] Ação RONDA_INICIAR ENFILEIRADA com sucesso!");

      // Se tem internet, tenta sincronizar imediatamente
      const isConnected = await SyncService.checkInternet();
      console.log("[Sync] rondaIniciar: isConnected:", isConnected);

      if (isConnected) {
        try {
          console.log("[Sync] rondaIniciar: Tentando enviar via API...");
          const response = await Api.ronda.iniciar(payloadComInicio);
          console.log("[Sync] rondaIniciar: Resposta da API:", response);

          if (response.success) {
            console.log("[Sync] rondaIniciar: Sucesso via API!");
            const done: any = response.done;
            const execucaoId =
              done?.id_adm_ronda_execucao ??
              done?.id_execucao ??
              done?.idExecucao ??
              done?.id_adm_ronda_exec;

            if (enqueuedId && execucaoId) {
              try {
                await comFilaLock(async () => {
                  const actions = await lerFilaParaEscrita();
                  const updated = actions.map((a) => {
                    if (a.id !== enqueuedId) return a;
                    return {
                      ...a,
                      data: { ...a.data, id_adm_ronda_execucao: execucaoId },
                    };
                  });
                  await gravarFila(updated);
                });
              } catch (e) {
                console.warn(
                  "[Sync] rondaIniciar: Falha ao atualizar ação enfileirada com id_adm_ronda_execucao:",
                  e,
                );
              }
            }

            if (enqueuedId) {
              await SyncService.dequeueOfflineAction(enqueuedId);
            }

            return {
              success: true,
              offline: false,
              data: { ...done, id_adm_ronda_execucao: execucaoId },
            };
          }
          console.warn(
            "[Sync] Falha ao iniciar ronda via API, mas ação já está enfileirada:",
            response.erros,
          );
        } catch (apiErr) {
          console.warn(
            "[Sync] Erro ao tentar iniciar ronda via API, mas ação já está enfileirada:",
            apiErr,
          );
        }
      }

      console.log("[Sync] rondaIniciar: Retornando offline true");
      return { success: true, offline: true };
    } catch (error) {
      console.error("[Sync] Erro no fluxo de início de ronda:", error);
      return { success: true, offline: true };
    }
  },

  rondaSalvarPonto: async (payload: any, actionId?: string) => {
    await carimbarHashTenant(payload);
    try {
      // Se tem foto local, persistir primeiro
      let persistedUri = payload.foto;

      if (payload.foto && payload.foto.includes("os_fotos/")) {
        persistedUri = await SyncService.persistOsPhoto(payload.foto);
      }

      // SEMPRE enfileirar a ação, mesmo com internet conectada!
      const payloadToEnqueue = { ...payload, foto: persistedUri };
      const enqueuedId = await SyncService.enqueueOfflineAction(
        "RONDA_SALVAR_PONTO",
        payloadToEnqueue,
        actionId,
      );
      console.log("[Sync] Ação RONDA_SALVAR_PONTO enfileirada com sucesso!");

      // Se tem internet, tenta sincronizar imediatamente
      const isConnected = await SyncService.checkInternet();
      if (isConnected) {
        try {
          console.log(
            "[Sync] Tentando enviar RONDA_SALVAR_PONTO via API (mas ação já está enfileirada)...",
          );
          // 🔒 Guarda de sessão do envio IMEDIATO: o upload abre janela de até
          // 45s — um trocarEmpresa no meio trocaria o token e o salvar sairia
          // no TENANT ERRADO (vetor do vazamento cross-tenant). Gen capturada
          // aqui e conferida antes do salvar; mudou → fica só enfileirado (o
          // dreno guarda por tenant).
          const genEnvio = peekApiSessionGen();

          // Se temos foto persistida, fazemos upload
          let fotoUrl = persistedUri;
          if (persistedUri) {
            try {
              const safeName = `ronda_${Date.now()}.jpg`;
              const caminhoPasta = "Rondas";

              const arquivo = {
                uri: persistedUri,
                name: safeName,
                type: "image/jpeg",
              } as any;

              const uploadResponse = await Api.geral.upload(
                arquivo,
                caminhoPasta,
                safeName,
              );

              if (uploadResponse?.success) {
                const done: any = uploadResponse.done;
                fotoUrl =
                  done?.urlAcesso ??
                  done?.url ??
                  done?.endereco_arquivo ??
                  fotoUrl;
              }
            } catch (uploadErr) {
              console.warn(
                "[Sync] Erro no upload de foto de ronda (mas ação já está enfileirada):",
                uploadErr,
              );
            }
          }

          if (peekApiSessionGen() !== genEnvio) {
            console.warn(
              "[Sync] 🔒 Sessão mudou durante o envio imediato de RONDA_SALVAR_PONTO — mantendo só na fila.",
            );
            return { success: true, offline: true };
          }
          const payloadToSend = {
            ...payload,
            foto: fotoUrl,
          };
          const response = await Api.ronda.salvarPontoQr(payloadToSend);
          if (response.success) {
            console.log("[Sync] Ponto de ronda salvo com sucesso via API!");
            if (enqueuedId) {
              await SyncService.dequeueOfflineAction(enqueuedId);
            }
            // Remove arquivo local após sucesso
            if (persistedUri && persistedUri.includes("os_fotos/")) {
              try {
                await FileSystem.deleteAsync(persistedUri, {
                  idempotent: true,
                });
              } catch (e) {
                console.warn(
                  "[Sync] Falha ao excluir arquivo local de ronda:",
                  e,
                );
              }
            }
            return { success: true, offline: false };
          }
          console.warn(
            "[Sync] Falha ao salvar ponto de ronda via API, mas ação já está enfileirada:",
            response.erros,
          );
        } catch (apiErr) {
          console.warn(
            "[Sync] Erro ao tentar enviar ponto via API, mas ação já está enfileirada:",
            apiErr,
          );
        }
      }

      return { success: true, offline: true };
    } catch (error) {
      console.error("[Sync] Erro no fluxo de salvar ponto de ronda:", error);
      return { success: true, offline: true };
    }
  },

  rondaFinalizar: async (payload: any[], actionId?: string) => {
    try {
      // Carimbo de tenant POR ELEMENTO (payload é ARRAY — o helper de objeto não
      // se aplica): o dreno reconstrói a lista com spread por ponto, então o
      // hash sobrevive nos dois caminhos (imediato e replay).
      {
        const hReg = await tenantHashSessaoAtual().catch(() => "");
        if (hReg && Array.isArray(payload)) {
          payload = payload.map((p: any) =>
            p && typeof p === "object" && !String(p.hash ?? "").trim()
              ? { ...p, hash: hReg }
              : p,
          );
        }
      }
      // Obter o id_adm_ronda_cadastro do primeiro ponto
      const id_adm_ronda_cadastro =
        payload.length > 0 ? payload[0].id_adm_ronda_cadastro : null;

      // Payload da ação offline com id_adm_ronda_cadastro E id_adm_ronda_execucao no
      // nível superior (2026-07-17 — bug das rondas fantasma): o agrupador do dreno
      // (extractExecucaoId) não olhava dentro de `pontos`, então um RONDA_FINALIZAR
      // órfão (INICIAR já drenado online) era tratado como "sem execução" e o
      // processador CRIAVA uma execução NOVA no servidor → ronda duplicada em rajada.
      // Com o id no topo, o replay vai para a execução ORIGINAL (idempotente lá).
      const id_adm_ronda_execucao =
        payload.length > 0 ? (payload[0].id_adm_ronda_execucao ?? null) : null;
      const offlinePayload = {
        id_adm_ronda_cadastro,
        id_adm_ronda_execucao,
        pontos: payload,
      };

      // SEMPRE enfileirar a ação, mesmo com internet conectada!
      const enqueuedId = await SyncService.enqueueOfflineAction(
        "RONDA_FINALIZAR",
        offlinePayload,
        actionId,
      );
      console.log("[Sync] Ação RONDA_FINALIZAR enfileirada com sucesso!");

      // Se tem internet, tenta sincronizar imediatamente
      const isConnected = await SyncService.checkInternet();
      // ⚠️ Sem id_adm_ronda_execucao NÃO há envio imediato: o T-ARC devolve 200
      // (= só enfileirou), o dequeue abaixo apagaria a ÚNICA cópia local e o
      // T-Consumer estoura SQL com o id nulo → finalização morre no .dead sem
      // rastro no app. Fica enfileirado: o dreno resolve a execução (payload,
      // pontos ou rondaHistorico) antes de despachar.
      if (isConnected && id_adm_ronda_execucao == null) {
        console.warn(
          "[Sync] RONDA_FINALIZAR sem id_adm_ronda_execucao - envio imediato suprimido; o dreno resolverá a execução antes de despachar.",
        );
      }
      if (isConnected && id_adm_ronda_execucao != null) {
        try {
          console.log(
            "[Sync] Tentando enviar RONDA_FINALIZAR via API (mas ação já está enfileirada)...",
          );
          // 🔒 Guarda de sessão (janela de uploads de até 45s por foto): um
          // trocarEmpresa no meio faria o finalizar sair no tenant errado.
          const genEnvio = peekApiSessionGen();

          // Processar cada ponto: upload de fotos locais e substituir por URLs
          let processedPayload = [...payload];
          const urisParaRemover: string[] = [];

          // Fazer upload de todas as fotos locais primeiro
          for (let i = 0; i < processedPayload.length; i++) {
            const ponto = processedPayload[i];
            if (ponto.foto && ponto.foto.includes("os_fotos/")) {
              try {
                const safeName = `ronda_${Date.now()}_${i}.jpg`;
                const caminhoPasta = "Rondas";

                const arquivo = {
                  uri: ponto.foto,
                  name: safeName,
                  type: "image/jpeg",
                } as any;

                const uploadResponse = await Api.geral.upload(
                  arquivo,
                  caminhoPasta,
                  safeName,
                );

                if (uploadResponse?.success) {
                  const done: any = uploadResponse.done;
                  const fotoUrl =
                    done?.urlAcesso ??
                    done?.url ??
                    done?.endereco_arquivo ??
                    ponto.foto;
                  processedPayload[i] = { ...ponto, foto: fotoUrl };
                  urisParaRemover.push(ponto.foto);
                }
              } catch (uploadErr) {
                console.warn(
                  "[Sync] Erro no upload de foto de ronda (mas ação já está enfileirada):",
                  uploadErr,
                );
              }
            }
          }

          if (peekApiSessionGen() !== genEnvio) {
            console.warn(
              "[Sync] 🔒 Sessão mudou durante o envio imediato de RONDA_FINALIZAR — mantendo só na fila.",
            );
            return { success: true, offline: true };
          }
          const response = await Api.ronda.finalizarQr(processedPayload);
          if (response.success) {
            console.log("[Sync] Ronda finalizada com sucesso via API!");
            if (enqueuedId) {
              await SyncService.dequeueOfflineAction(enqueuedId);
            }
            // Remove arquivos locais após sucesso
            for (const uri of urisParaRemover) {
              try {
                await FileSystem.deleteAsync(uri, { idempotent: true });
              } catch (e) {
                console.warn(
                  "[Sync] Falha ao excluir arquivo local de ronda:",
                  e,
                );
              }
            }
            return { success: true, offline: false };
          }
          console.warn(
            "[Sync] Falha ao finalizar ronda via API, mas ação já está enfileirada:",
            response.erros,
          );
        } catch (apiErr) {
          console.warn(
            "[Sync] Erro ao tentar finalizar ronda via API, mas ação já está enfileirada:",
            apiErr,
          );
        }
      }

      return { success: true, offline: true };
    } catch (error) {
      console.error("[Sync] Erro no fluxo de finalizar ronda:", error);
      return { success: true, offline: true };
    }
  },

  rondaCancelar: async (
    payload: {
      codigoRonda?: number;
      id_adm_ronda_execucao?: number;
      id_adm_ronda_cadastro?: number;
    },
    actionId?: string,
  ) => {
    try {
      // SEMPRE enfileirar RONDA_CANCELAR, mesmo com internet conectada!
      const enqueuedId = await SyncService.enqueueOfflineAction(
        "RONDA_CANCELAR",
        payload,
        actionId,
      );

      // Se tem internet, tenta cancelar via API imediatamente
      const isConnected = await SyncService.checkInternet();
      if (isConnected) {
        try {
          console.log("[Sync] Cancelando ronda via API...");
          const rondaId = payload.id_adm_ronda_execucao ?? payload.codigoRonda;
          const response = await Api.ronda.cancelar(rondaId);

          if (response.success) {
            console.log("[Sync] Ronda cancelada com sucesso via API");
            if (enqueuedId) {
              await SyncService.dequeueOfflineAction(enqueuedId);
            }
            return { success: true, offline: false };
          }
          console.warn(
            "[Sync] Falha ao cancelar ronda via API, mas ação já está enfileirada:",
            response.erros,
          );
        } catch (apiErr) {
          console.warn(
            "[Sync] Erro ao tentar cancelar ronda via API, mas ação já está enfileirada:",
            apiErr,
          );
        }
      }

      return { success: true, offline: true };
    } catch (error) {
      console.error("[Sync] Erro no fluxo de cancelar ronda:", error);
      return { success: true, offline: true };
    }
  },

  // ─── JUSTIFICATIVAS ─────────────────────────────────────────────────────────

  /**
   * Garante que o diretório onde guardamos os anexos das justificativas existe.
   * Os arquivos do DocumentPicker ficam no cache do app e podem sumir entre
   * sessões — copiamos para um diretório persistente antes de enfileirar.
   */
  initJustificativasDir: async () => {
    const info = await FileSystem.getInfoAsync(JUSTIFICATIVAS_DIR);
    if (!info.exists) {
      await FileSystem.makeDirectoryAsync(JUSTIFICATIVAS_DIR, {
        intermediates: true,
      });
    }
  },

  /**
   * Lê o cache local com os status das justificativas (para pintar badges
   * mesmo quando offline).
   */
  getLocalJustificativasMap: async (): Promise<LocalJustificativasMap> => {
    try {
      const raw = await AsyncStorage.getItem(
        await chaveTenant(LOCAL_JUSTIFICATIVAS_KEY),
      );
      return raw ? reescreverCaminhosAntigos(JSON.parse(raw)) : {};
    } catch (error) {
      console.warn("[Sync] Erro lendo justificativas locais:", error);
      return {};
    }
  },

  setLocalJustificativa: async (data: string, info: LocalJustificativa) => {
    try {
      await mutarEspelhoTenant(LOCAL_JUSTIFICATIVAS_KEY, (atual) => {
        const map = atual && typeof atual === "object" ? atual : {};
        map[data] = info;
        return map;
      });
    } catch (error) {
      console.warn("[Sync] Erro salvando justificativa local:", error);
    }
  },

  clearLocalJustificativa: async (data: string) => {
    try {
      await mutarEspelhoTenant(LOCAL_JUSTIFICATIVAS_KEY, (atual) => {
        if (!atual || typeof atual !== "object") return undefined;
        delete atual[data];
        return atual;
      });
    } catch (error) {
      console.warn("[Sync] Erro removendo justificativa local:", error);
    }
  },

  /**
   * Determina se a solicitação é abono (todos os ajustes vazios) ou justificativa.
   */
  inferJustificativaTipo: (
    punchesAjuste: (string | null)[],
    atestado?: AtestadoPayload,
    abono?: AbonoPayload,
  ): JustificativaTipo => {
    // Atestado e abono se declaram; não são inferidos pelas horas (não têm batidas).
    if (atestado?.tipo_atestado) return "atestado";
    if (abono?.tipo_abono) return "abono";
    const algumPreenchido = punchesAjuste.some(
      (v) => !!v && String(v).trim() !== "",
    );
    return algumPreenchido ? "ajuste" : "abono";
  },

  /**
   * Copia os anexos do DocumentPicker (cache temporário) para um diretório
   * persistente. Devolve a lista com os novos URIs prontos pra reuso offline.
   */
  persistJustificativaAttachments: async (
    files: JustificativaPayload["files"],
  ): Promise<JustificativaPayload["files"]> => {
    if (!files?.length) return [];
    await SyncService.initJustificativasDir();
    const persisted: JustificativaPayload["files"] = [];
    for (const file of files) {
      try {
        const safeName = (file.name || "anexo").replace(/[^\w.\-]/g, "_");
        const dest = `${JUSTIFICATIVAS_DIR}${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${safeName}`;
        await FileSystem.copyAsync({ from: file.uri, to: dest });
        persisted.push({ uri: dest, name: file.name, mimeType: file.mimeType });
      } catch (error) {
        console.warn(
          "[Sync] Falha ao persistir anexo de justificativa:",
          error,
        );
      }
    }
    return persisted;
  },

  /**
   * Monta o FormData esperado pelo endpoint RhPontoJustificativa/salvar.
   * O arquivo (quando existe) é enviado previamente pelo Start/upload — aqui
   * só vai a URL retornada (`enderecoArquivo`). Quando não há anexo, o campo
   * é deixado como string vazia (equivalente a null no servidor).
   */
  buildJustificativaFormData: (
    payload: JustificativaPayload,
    enderecoArquivo?: string | null,
  ): FormData => {
    const form = new FormData();

    const localId = Date.now() % 2147483647; // int32 dentro do range
    // Hora da SUBMISSÃO (BRT). Prefere a que foi carimbada no envio e persistida na
    // fila (payload.data_log) — assim uma justificativa enviada offline guarda a hora
    // real do envio, não a hora do sync. Fallback: agora (caso de envio online direto).
    const dataLog = payload.data_log || nowLocalSqlDateTime();

    const nullable = (v: string | null | undefined) =>
      v == null ? "" : String(v);

    form.append("id_rh_ponto_justificativa_app", String(localId));
    form.append("id_adm_colaborador", String(payload.id_adm_colaborador ?? 0));
    form.append("data_competencia", payload.data_competencia ?? "");
    // tenant dono do registro (carimbado na função de origem; "" = legado)
    form.append("hash", String((payload as any).hash ?? ""));

    form.append("hora1_atual", nullable(payload.punchesAtual?.[0]));
    form.append("hora1_ajuste", nullable(payload.punchesAjuste?.[0]));
    form.append("hora2_atual", nullable(payload.punchesAtual?.[1]));
    form.append("hora2_ajuste", nullable(payload.punchesAjuste?.[1]));
    form.append("hora3_atual", nullable(payload.punchesAtual?.[2]));
    form.append("hora3_ajuste", nullable(payload.punchesAjuste?.[2]));
    form.append("hora4_atual", nullable(payload.punchesAtual?.[3]));
    form.append("hora4_ajuste", nullable(payload.punchesAjuste?.[3]));
    form.append("hora5_atual", nullable(payload.punchesAtual?.[4]));
    form.append("hora5_ajuste", nullable(payload.punchesAjuste?.[4]));
    form.append("hora6_atual", nullable(payload.punchesAtual?.[5]));
    form.append("hora6_ajuste", nullable(payload.punchesAjuste?.[5]));
    form.append("hora7_atual", nullable(payload.punchesAtual?.[6]));
    form.append("hora7_ajuste", nullable(payload.punchesAjuste?.[6]));
    form.append("hora8_atual", nullable(payload.punchesAtual?.[7]));
    form.append("hora8_ajuste", nullable(payload.punchesAjuste?.[7]));

    form.append("motivo_descricao", payload.motivo ?? "");
    // Quando não há anexo, mandamos string vazia (não 'N/A'). O backend trata como null.
    form.append(
      "endereco_arquivo",
      enderecoArquivo ? String(enderecoArquivo) : "",
    );
    form.append("data_log", dataLog);

    // Atestado médico — vazio na correção comum. Sempre enviados, porque o modelo do
    // T-ARC é [FromForm] e um campo ausente vira null lá de qualquer jeito; mandar ""
    // explícito deixa o payload igual em todos os casos, o que facilita ler o log.
    const at = payload.atestado;
    const ab = payload.abono;
    form.append("tipo_atestado", at?.tipo_atestado ?? "");
    // Dias/horas são colunas compartilhadas: vêm do atestado OU do abono.
    form.append("qtd_dias", at?.qtd_dias ?? ab?.qtd_dias ?? "");
    form.append("data_fim", at?.data_fim ?? ab?.data_fim ?? "");
    form.append("cid", at?.cid ?? "");
    form.append("hora_inicio", at?.hora_inicio ?? ab?.hora_inicio ?? "");
    form.append("hora_fim", at?.hora_fim ?? ab?.hora_fim ?? "");
    // Solicitação de abono — vazio no atestado e na correção.
    form.append("tipo_abono", ab?.tipo_abono ?? "");
    form.append("periodo_abono", ab?.periodo_abono ?? "");
    form.append("descricao_abono", ab?.descricao_abono ?? "");

    // Campos preenchidos pelo administrador na validação.
    form.append("id_adm_usuario_validacao", "0"); // integer válido — admin ainda não atribuído
    form.append("aceito", "N"); // ainda não validado pelo admin
    form.append("data_log_validacao", dataLog); // placeholder — sobrescrito pelo backend ao validar
    form.append("observacao_validacao", ""); // vazio — preenchido pelo admin na validação

    return form;
  },

  /**
   * Faz upload de um único anexo para o endpoint Start/upload e retorna a URL
   * absoluta gerada pelo servidor. Retorna null se a chamada falhar.
   */
  uploadJustificativaArquivo: async (
    file: { uri: string; name: string; mimeType?: string },
    idAdmColaborador: number,
    dataCompetencia: string,
  ): Promise<string | null> => {
    try {
      const safeName = (file.name || "anexo").replace(/[^\w.\-]/g, "_");
      const stamp = Date.now();
      const nomeArquivo = `${stamp}_${safeName}`;
      // Pasta lógica no servidor — segregada por colaborador e mês
      const [, mes, ano] = (dataCompetencia || "").split("/");
      const subPasta = mes && ano ? `${mes}_${ano}` : "geral";
      const caminhoPasta = `Justificativas/${idAdmColaborador}/${subPasta}`;

      const arquivo = {
        uri: file.uri,
        name: nomeArquivo,
        type: file.mimeType || "application/octet-stream",
      } as any;

      console.log("[Sync] Subindo anexo da justificativa:", {
        caminhoPasta,
        nomeArquivo,
      });
      const response = await Api.geral.upload(
        arquivo,
        caminhoPasta,
        nomeArquivo,
      );

      if (!response.success) {
        console.warn(
          "[Sync] Falha no upload do anexo:",
          response.erros,
          response.done,
        );
        return null;
      }

      // A API retorna { sucesso, urlAcesso, ... }
      const done: any = response.done;
      const url: string | null =
        typeof done === "string"
          ? done
          : (done?.urlAcesso ??
            done?.url ??
            done?.endereco_arquivo ??
            done?.path ??
            done?.link ??
            null);

      if (!url) {
        console.warn(
          "[Sync] Upload retornou sem URL identificável. Resposta bruta:",
          JSON.stringify(done)?.slice(0, 400),
        );
        return null;
      }

      console.log("[Sync] Upload concluído:", url);
      return String(url).trim();
    } catch (error) {
      console.error("[Sync] Erro no upload do anexo:", error);
      return null;
    }
  },

  /**
   * Submete uma justificativa: tenta enviar online imediatamente; se sem
   * internet (ou falhar), enfileira pra reenvio posterior. Em qualquer caso
   * o status local fica como PENDENTE até o admin validar.
   */
  submitJustificativa: async (
    payload: JustificativaPayload,
  ): Promise<{
    success: boolean;
    offline: boolean;
    tipo: JustificativaTipo;
  }> => {
    await carimbarHashTenant(payload);
    const tipo = SyncService.inferJustificativaTipo(
      payload.punchesAjuste ?? [],
      payload.atestado,
      payload.abono,
    );
    const dataKey = payload.data_competencia;

    // Carimba a hora da SUBMISSÃO AGORA (BRT) e a persiste em payload.data_log. Se cair
    // offline, vai pra fila com esta hora; o sync reenvia com a hora real do envio (não
    // a hora do sync). Se já vier preenchida (replay), preserva.
    const payloadComLog: JustificativaPayload = {
      ...payload,
      data_log: payload.data_log || nowLocalSqlDateTime(),
    };
    payload = payloadComLog;

    // O espelho local carrega o MESMO instante do `data_log` que foi para a fila — não
    // um `Date.now()` alguns ms depois. É o que a tela mostra como "Enviado em", e o
    // servidor vai devolver o `data_log` verbatim: os dois têm que ser iguais ao
    // minuto, senão o card muda de hora (e, à meia-noite, de DIA) ao sincronizar.
    const msDoDataLog = msDeDataLog(payload.data_log);

    // Cópia PERSISTENTE do anexo, antes de qualquer envio (12/09/2026). Três razões:
    //   1. o espelho local mostra a foto NA HORA — a URI do picker/ImageManipulator é
    //      CACHE do sistema e some quando ele quiser; e a URL do servidor só chega
    //      quando o /iniciar (com cache) devolver a linha. Sem isto o card ficava com
    //      o ícone "sem foto" logo depois de enviar, com 1 e com 2 checks;
    //   2. a fila offline e o retry sobem daqui (era só o caminho offline que copiava);
    //   3. offline, é a única foto que existe — a URL do servidor não carrega.
    // Fica no aparelho depois do envio: é a foto do card. Falhou a cópia → segue com a
    // URI original (envio não pode travar por causa disto).
    const persistidos = await SyncService.persistJustificativaAttachments(
      payload.files ?? [],
    );
    if (persistidos.length > 0) payload = { ...payload, files: persistidos };
    const fotoLocal = payload.files?.[0]?.uri ?? "";

    const markPendingLocal = async (offline: boolean) => {
      await SyncService.setLocalJustificativa(dataKey, {
        data: dataKey,
        status: "PENDENTE",
        tipo,
        offline,
        timestamp: msDoDataLog,
        motivo: payload.motivo,
        foto: fotoLocal || undefined,
        horasAtual: payload.punchesAtual,
        horasAjuste: payload.punchesAjuste,
        origem: "local",
        atestado: payload.atestado,
        abono: payload.abono,
      });
    };

    try {
      const isConnected = await SyncService.checkInternet();

      if (isConnected) {
        console.log("[Sync] Enviando justificativa online...");

        // 1) Faz upload do arquivo original diretamente via Api.geral.upload
        let enderecoArquivo: string | null = null;
        const fileOriginal = payload.files?.[0];
        if (fileOriginal?.uri) {
          const safeName = (fileOriginal.name || "anexo").replace(
            /[^\w.\-]/g,
            "_",
          );
          const nomeArquivo = `${Date.now()}_${safeName}`;
          const [, mes, ano] = (payload.data_competencia || "").split("/");
          const subPasta = mes && ano ? `${mes}_${ano}` : "geral";
          const caminhoPasta = `Justificativas/${payload.id_adm_colaborador}/${subPasta}`;

          const arquivo = {
            uri: fileOriginal.uri,
            name: nomeArquivo,
            type: fileOriginal.mimeType || "application/octet-stream",
          } as any;

          console.log("[Sync] Chamando Api.geral.upload:", {
            caminhoPasta,
            nomeArquivo,
            uri: fileOriginal.uri,
          });
          const uploadResp = await Api.geral.upload(
            arquivo,
            caminhoPasta,
            nomeArquivo,
          );
          console.log(
            "[Sync] Resposta Api.geral.upload:",
            JSON.stringify(uploadResp),
          );

          if (!uploadResp.success) {
            console.warn(
              "[Sync] Upload falhou:",
              uploadResp.erros,
              uploadResp.done,
            );
            // Anexo já persistido lá em cima: enfileira como está para o retry.
            await SyncService.enqueueOfflineAction("JUSTIFICATIVA", payload);
            await markPendingLocal(true);
            return { success: true, offline: true, tipo };
          }

          // Extrai urlAcesso da resposta
          const done: any = uploadResp.done;
          enderecoArquivo =
            done?.urlAcesso ?? done?.url ?? done?.endereco_arquivo ?? null;
          console.log("[Sync] urlAcesso obtida:", enderecoArquivo);
        }

        // 2) Salva a justificativa com a URL do arquivo
        const formData = SyncService.buildJustificativaFormData(
          payload,
          enderecoArquivo,
        );
        const saveResp = await Api.rhPontoJustificativa.salvar(formData);
        if (saveResp.success) {
          console.log("[Sync] Justificativa salva com sucesso");
          await markPendingLocal(false);
          return { success: true, offline: false, tipo };
        }
        console.warn(
          "[Sync] Falha ao salvar justificativa:",
          saveResp.erros,
          JSON.stringify(saveResp.done),
        );
        await SyncService.enqueueOfflineAction("JUSTIFICATIVA", payload);
        await markPendingLocal(true);
        return { success: true, offline: true, tipo };
      }

      // Sem internet: enfileira (anexo já persistido)
      console.log("[Sync] Sem internet, enfileirando justificativa...");
      await SyncService.enqueueOfflineAction("JUSTIFICATIVA", payload);
      await markPendingLocal(true);
      return { success: true, offline: true, tipo };
    } catch (error) {
      console.error("[Sync] Erro no fluxo de justificativa:", error);
      await SyncService.enqueueOfflineAction("JUSTIFICATIVA", payload);
      await markPendingLocal(true);
      return { success: true, offline: true, tipo };
    }
  },

  /**
   * Cancela uma solicitação PENDENTE (atestado ou correção) a pedido do colaborador.
   *
   * É uma ESCRITA, então vai pelo caminho de todas: tenta online e, se não der, fila
   * offline — o dreno reenvia. Nunca falha para quem está cancelando.
   *
   * Três passos, nesta ordem:
   *   1. Se a solicitação ainda está na FILA (nunca chegou ao T-ARC), sai da fila e o
   *      anexo persistido é apagado. O cancelamento é enviado MESMO ASSIM: um 200
   *      perdido no envio original pode ter deixado a linha pendente no servidor, e o
   *      UPDATE (aceito 'P' → 'C') é idempotente — não achar nada custa nada.
   *   2. O espelho local vira CANCELADO com `offline:false`. NÃO `true`: Home e
   *      Justificativas tratam `offline:true` como "pendência esperando sync" e
   *      voltariam a mostrar a solicitação. Quem espera sync é a AÇÃO na fila.
   *   3. Envia (ou enfileira) o cancelamento com o `data_log` da solicitação: o consumer
   *      só cancela a linha que casar — reprocessar depois de uma NOVA solicitação do
   *      mesmo dia não derruba a nova. Sem `data_log`, cancela a pendente do dia/tipo.
   */
  cancelarJustificativa: async (
    payload: JustificativaCancelarPayload,
  ): Promise<{ success: boolean; offline: boolean }> => {
    await carimbarHashTenant(payload);
    const dataKey = payload.data_competencia;
    const p: JustificativaCancelarPayload = {
      ...payload,
      data_cancelamento: payload.data_cancelamento || nowLocalSqlDateTime(),
    };
    // Eixo da solicitação: atestado × abono × correção — o cancelamento só encosta no
    // mesmo eixo (é o escopo do consumer; o espelho e a fila seguem a mesma regra).
    const eixo = p.tipo_atestado
      ? "atestado"
      : p.tipo_abono
        ? "abono"
        : "ajuste";
    const eixoDe = (x: {
      atestado?: unknown;
      abono?: unknown;
      tipo?: string;
    }) =>
      x.atestado
        ? "atestado"
        : x.abono
          ? "abono"
          : x.tipo === "atestado"
            ? "atestado"
            : "ajuste";

    // 1) ainda na fila? tira (nunca foi enviada) e apaga o anexo persistido
    try {
      const fila = await lerFilaSoLeitura();
      const naFila = fila.filter((a) => {
        if (a?.type !== "JUSTIFICATIVA") return false;
        const d = a.data as JustificativaPayload;
        if (d?.data_competencia !== dataKey) return false;
        if (eixoDe(d ?? {}) !== eixo) return false;
        return !p.data_log || !d?.data_log || d.data_log === p.data_log;
      });
      for (const a of naFila) {
        await SyncService.dequeueOfflineAction(a.id);
        for (const f of (a.data as JustificativaPayload)?.files ?? []) {
          if (String(f?.uri ?? "").startsWith(JUSTIFICATIVAS_DIR))
            FileSystem.deleteAsync(f.uri, { idempotent: true }).catch(() => {});
        }
        console.log(
          `[Sync] Solicitação ${dataKey} ainda estava na fila — removida antes de chegar ao servidor.`,
        );
      }
    } catch (e) {
      console.warn("[Sync] cancelarJustificativa: não leu a fila:", e);
    }

    // 2) espelho local — só mexe se o espelho do dia é do mesmo eixo (atestado, abono
    //    e correção dividem a chave por data; não apagar a pendência do outro)
    const mapa = await SyncService.getLocalJustificativasMap();
    const atual = mapa[dataKey];
    if (!atual || eixoDe(atual) === eixo) {
      await SyncService.setLocalJustificativa(dataKey, {
        // Sem espelho (solicitação feita em outro aparelho): cria um só para a tela
        // esconder a linha do servidor. `timestamp` 0 quando não se conhece o data_log —
        // "sem como comparar" faz a tela tratar como a mesma solicitação.
        ...(atual ?? {
          data: dataKey,
          tipo: eixo,
          timestamp: p.data_log ? msDeDataLog(p.data_log) : 0,
        }),
        status: "CANCELADO",
        offline: false,
        origem: "local",
      });
    }

    // 3) envia agora; sem rede (ou recusado) vai para a fila
    try {
      if (await SyncService.checkInternet()) {
        const r = await Api.rhPontoJustificativa.cancelar(p);
        if (r.success) {
          console.log(`[Sync] Solicitação ${dataKey} cancelada no servidor.`);
          return { success: true, offline: false };
        }
        console.warn(
          "[Sync] Cancelamento recusado — vai para a fila:",
          r.erros,
        );
      }
    } catch (e) {
      console.warn("[Sync] Erro ao cancelar online — vai para a fila:", e);
    }
    await SyncService.enqueueOfflineAction("JUSTIFICATIVA_CANCELAR", p);
    return { success: true, offline: true };
  },

  /**
   * Força o despacho das justificativas pendentes — chamado ao ENTRAR na tela de
   * Histórico/Justificativas. "Pendente" = ação JUSTIFICATIVA ainda na fila offline
   * (o envio falhou ou estava sem internet na hora da solicitação). Roda a fila e,
   * se ainda sobrar justificativa (ex.: a rodada foi pulada pela guarda
   * isProcessingQueue por colidir com outro sync), espera e tenta de novo UMA vez.
   * Garante que nenhuma solicitação fique presa esperando o sync periódico — toda
   * solicitação tem que ser enviada. Offline: mantém na fila (não há como enviar agora).
   *
   * @returns quantas justificativas estavam pendentes na fila ao entrar.
   */
  flushPendingJustificativas: async (): Promise<number> => {
    const contarJustNaFila = async (): Promise<number> => {
      try {
        const acoes = await lerFilaSoLeitura();
        return acoes.filter(
          (a) =>
            a?.type === "JUSTIFICATIVA" || a?.type === "JUSTIFICATIVA_CANCELAR",
        ).length;
      } catch {
        return 0;
      }
    };

    const pendentesAntes = await contarJustNaFila();
    if (pendentesAntes > 0) {
      console.log(
        `[Sync] ${pendentesAntes} justificativa(s) pendente(s) local — forçando despacho ao abrir a tela.`,
      );
    }

    // Despacha a fila (justificativas + demais ações). A checagem de internet e a
    // guarda isProcessingQueue ficam dentro do processOfflineQueue.
    await SyncService.processOfflineQueue();

    // Se ainda sobrou justificativa na fila, a 1ª rodada pode ter sido PULADA pela
    // guarda (outro sync em voo). Espera o outro terminar e tenta forçar de novo.
    if (pendentesAntes > 0) {
      const pendentesDepois = await contarJustNaFila();
      if (pendentesDepois > 0) {
        await new Promise((r) => setTimeout(r, 800));
        await SyncService.processOfflineQueue();
      }
    }

    return pendentesAntes;
  },

  /**
   * Sincroniza justificativas confirmadas pelo servidor (vindas do Start/iniciar)
   * para o cache local. Entradas ACEITO/RECUSADO do servidor sobrepõem PENDENTE local;
   * datas não presentes no servidor mantêm o estado local intacto.
   */
  applyServerJustificativas: async (
    serverList: Array<{
      data?: string;
      data_competencia?: string;
      status: JustificativaStatus;
      tipo?: JustificativaTipo;
      observacao_validacao?: string;
      nome_validador?: string;
    }>,
  ): Promise<void> => {
    if (!serverList?.length) return;
    try {
      const kJust = await chaveTenant(LOCAL_JUSTIFICATIVAS_KEY);
      const map = await SyncService.getLocalJustificativasMap();
      for (const item of serverList) {
        // Aceita tanto `data` quanto `data_competencia` como campo de data
        const dataKey = _normDate(
          String(item.data_competencia ?? item.data ?? ""),
        );
        if (!dataKey) continue;
        const current = map[dataKey];
        // Servidor ACEITO/RECUSADO sempre ganha; servidor PENDENTE só preenche se não há local
        if (!current || item.status !== "PENDENTE") {
          map[dataKey] = {
            data: dataKey,
            status: item.status,
            tipo: item.tipo ?? current?.tipo ?? "ajuste",
            offline: false,
            timestamp: Date.now(),
            motivo: current?.motivo,
            observacao_validacao:
              item.observacao_validacao || current?.observacao_validacao,
            nome_validador: item.nome_validador ?? current?.nome_validador,
          };
        }
      }
      await setItemEscopado(
        LOCAL_JUSTIFICATIVAS_KEY,
        JSON.stringify(map),
        kJust,
      );
    } catch (error) {
      console.warn("[Sync] Erro aplicando justificativas do servidor:", error);
    }
  },

  /**
   * Loga no console todas as chaves de array presentes em userData para facilitar
   * a identificação do nome do campo que contém as justificativas no servidor.
   */
  debugUserDataArrays: (userData: any): void => {
    if (!userData) {
      console.log("[Sync] debugUserDataArrays: userData é null/undefined");
      return;
    }
    for (const key of Object.keys(userData)) {
      const val = userData[key];
      if (Array.isArray(val) && val.length > 0) {
        const sample = val[0];
      }
    }
  },

  /**
   * Converte um item de justificativa vindo do servidor em LocalJustificativa.
   * Suporta dois formatos:
   *   - Formato bruto da API  → campos `aceito`, `hora1_ajuste`, `hora1_atual`, etc.
   *   - Formato pré-processado → campos `status` ("ACEITO"/"RECUSADO"/"PENDENTE") e `tipo`
   * Retorna null se não for possível extrair uma data válida.
   */
  serverJustificativaToLocal: (item: any): LocalJustificativa | null => {
    if (!item) return null;
    const rawData = item.data_competencia ?? item.data ?? "";
    const data = _normDate(String(rawData));
    if (!data) return null;

    // ── Status ────────────────────────────────────────────────────────────────
    // Formato pré-processado: já tem `status` como string "ACEITO"/"RECUSADO"/"PENDENTE"
    // Formato bruto: usa campo `aceito` ('S'/'N') + id do validador para RECUSADO
    let status: JustificativaStatus;
    if (
      item.status === "ACEITO" ||
      item.status === "RECUSADO" ||
      item.status === "PENDENTE"
    ) {
      status = item.status as JustificativaStatus;
    } else if (item.aceito === "S") {
      status = "ACEITO";
    } else if (
      item.aceito === "N" &&
      item.id_adm_usuario_validacao &&
      Number(item.id_adm_usuario_validacao) > 0
    ) {
      status = "RECUSADO";
    } else {
      status = "PENDENTE";
    }

    // ── Tipo ──────────────────────────────────────────────────────────────────
    // Formato pré-processado: já tem `tipo` como string "ajuste"/"abono"
    // Formato bruto: infere a partir dos campos hora_X_ajuste
    const toHour = (v: any) =>
      v && String(v).trim() && String(v).trim() !== "null"
        ? String(v).trim()
        : null;

    let tipo: JustificativaTipo;
    if (
      item.tipo === "ajuste" ||
      item.tipo === "abono" ||
      item.tipo === "atestado"
    ) {
      // "atestado" (12/09/2026): antes caía na inferência por horas e virava "abono" —
      // o pop-up de recusa da Home o descrevia como abono e a dedup por
      // `data|status|tipo` o fundia com uma correção do mesmo dia.
      tipo = item.tipo;
    } else {
      const inferredAjuste = [
        toHour(item.hora1_ajuste),
        toHour(item.hora2_ajuste),
        toHour(item.hora3_ajuste),
        toHour(item.hora4_ajuste),
      ];
      tipo = inferredAjuste.some((h) => !!h) ? "ajuste" : "abono";
    }

    // ── Horas ─────────────────────────────────────────────────────────────────
    // Só existem no formato bruto da API (hora1_ajuste, hora1_atual, etc.)
    const hasRawHours =
      item.hora1_ajuste != null ||
      item.hora2_ajuste != null ||
      item.hora1_atual != null ||
      item.hora2_atual != null;

    const horasAjuste = hasRawHours
      ? [
          toHour(item.hora1_ajuste),
          toHour(item.hora2_ajuste),
          toHour(item.hora3_ajuste),
          toHour(item.hora4_ajuste),
          toHour(item.hora5_ajuste),
          toHour(item.hora6_ajuste),
          toHour(item.hora7_ajuste),
          toHour(item.hora8_ajuste),
        ]
      : undefined;

    const horasAtual = hasRawHours
      ? [
          toHour(item.hora1_atual),
          toHour(item.hora2_atual),
          toHour(item.hora3_atual),
          toHour(item.hora4_atual),
          toHour(item.hora5_atual),
          toHour(item.hora6_atual),
          toHour(item.hora7_atual),
          toHour(item.hora8_atual),
        ]
      : undefined;

    return {
      data,
      status,
      tipo,
      offline: false,
      timestamp: Date.now(),
      motivo: item.motivo_descricao ?? item.motivo ?? undefined,
      // String vazia também vira undefined para não poluir a UI
      observacao_validacao: item.observacao_validacao || undefined,
      nome_validador:
        item.nome_validador ?? item.nome_usuario_validacao ?? undefined,
      data_validacao:
        item.data_log_validacao && String(item.data_log_validacao).trim()
          ? String(item.data_log_validacao).trim()
          : undefined,
      foto:
        (item.endereco_arquivo &&
        String(item.endereco_arquivo).trim() &&
        String(item.endereco_arquivo).trim() !== "N/A"
          ? String(item.endereco_arquivo).trim()
          : undefined) ??
        (item.foto && String(item.foto).trim()
          ? String(item.foto).trim()
          : undefined),
      horasAtual,
      horasAjuste,
      visto: !!(item.data_log_visto && String(item.data_log_visto).trim()),
      origem: "servidor",
      // Detalhe do atestado, como o statement 7 devolve (data_fim já em DD/MM/YYYY).
      atestado:
        tipo === "atestado"
          ? {
              tipo_atestado:
                String(item.tipo_atestado ?? "").toUpperCase() === "C"
                  ? "C"
                  : "I",
              qtd_dias: String(item.qtd_dias ?? ""),
              data_fim: String(item.data_fim ?? ""),
              cid: String(item.cid ?? ""),
              hora_inicio: String(item.hora_inicio ?? ""),
              hora_fim: String(item.hora_fim ?? ""),
            }
          : undefined,
      // Detalhe do abono: só quando a linha veio da tela nova (`tipo_abono` preenchido).
      // O abono LEGADO (aba extinta) segue sem detalhe — é assim que as telas o distinguem.
      abono:
        tipo === "abono" && String(item.tipo_abono ?? "").trim()
          ? {
              tipo_abono: String(item.tipo_abono).toUpperCase() as TipoAbono,
              periodo_abono:
                String(item.periodo_abono ?? "").toUpperCase() === "P"
                  ? "P"
                  : "I",
              descricao_abono: String(item.descricao_abono ?? ""),
              qtd_dias: String(item.qtd_dias ?? ""),
              data_fim: String(item.data_fim ?? ""),
              hora_inicio: String(item.hora_inicio ?? ""),
              hora_fim: String(item.hora_fim ?? ""),
            }
          : undefined,
    };
  },

  /**
   * Varre o objeto `userData` em busca do array de justificativas do servidor,
   * testando nomes candidatos e, como fallback, buscando por heurística de campos.
   * Suporta tanto o formato bruto (campo `aceito`) quanto o pré-processado (campo `status`).
   */
  extractServerJustificativas: (userData: any): LocalJustificativa[] => {
    if (!userData) return [];

    const allItemsMap: Record<string, LocalJustificativa> = {};

    const CANDIDATE_KEYS = [
      "justificativasAceitas",
      "justificativasAceita",
      "justificativas",
      "justificativasPontos",
      "rhPontoJustificativas",
      "pontoJustificativas",
      "historicoPonto",
      "ajustes",
    ];

    // Detecta se um item de amostra é uma justificativa — exige campo de data + campo específico
    const isJustificativaSample = (sample: any): boolean => {
      if (!sample || typeof sample !== "object") return false;
      const hasDate = "data_competencia" in sample || "data" in sample;
      if (!hasDate) return false;
      return (
        "aceito" in sample ||
        "motivo_descricao" in sample ||
        "hora1_ajuste" in sample ||
        "hora1_atual" in sample
      );
    };

    const processArray = (arr: any[]) => {
      if (!Array.isArray(arr) || arr.length === 0) return;
      if (!isJustificativaSample(arr[0])) return;

      const converted = arr
        .map((item: any) => SyncService.serverJustificativaToLocal(item))
        .filter((j): j is LocalJustificativa => j !== null);

      for (const item of converted) {
        // Chave por data + status + tipo: um MESMO dia pode ter uma justificativa
        // resolvida (ACEITO/RECUSADO) E uma nova PENDENTE — são distintas e ambas
        // devem ser contadas. Antes a chave era só `data`, então a PENDENTE era
        // engolida pela resolvida do mesmo dia (contava 1 em vez de 2).
        const mapKey = `${item.data}|${item.status}|${item.tipo}`;
        const current = allItemsMap[mapKey];
        // Se não existe ou o novo status é mais relevante que PENDENTE, substitui mas mescla campos
        if (!current || item.status !== "PENDENTE") {
          allItemsMap[mapKey] = {
            ...item,
            horasAtual: item.horasAtual || current?.horasAtual,
            horasAjuste: item.horasAjuste || current?.horasAjuste,
            motivo: item.motivo || current?.motivo,
          };
        } else {
          // Se o novo é PENDENTE e já temos um registro, tenta apenas enriquecer com horas se faltarem
          allItemsMap[mapKey] = {
            ...current,
            horasAtual: current.horasAtual || item.horasAtual,
            horasAjuste: current.horasAjuste || item.horasAjuste,
          };
        }
      }
    };

    // 1. Tenta chaves conhecidas
    for (const key of CANDIDATE_KEYS) {
      processArray(userData[key]);
    }

    // 2. Fallback heurístico para outras chaves de array
    for (const key of Object.keys(userData)) {
      if (CANDIDATE_KEYS.includes(key)) continue;
      processArray(userData[key]);
    }

    return Object.values(allItemsMap);
  },

  /**
   * Chaves de justificativas recusadas que o usuário já viu no aviso da Home
   * (para mostrar o modal de recusa apenas uma vez por solicitação).
   */
  getRecusasVistas: async (): Promise<string[]> => {
    try {
      const raw = await AsyncStorage.getItem(
        await chaveTenant(LOCAL_RECUSAS_VISTAS_KEY),
      );
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.map(String) : [];
    } catch (error) {
      console.warn("[Sync] Erro lendo recusas vistas:", error);
      return [];
    }
  },

  markRecusasVistas: async (keys: string[]): Promise<void> => {
    try {
      if (!keys?.length) return;
      await mutarEspelhoTenant(LOCAL_RECUSAS_VISTAS_KEY, (atualRaw) => {
        const atuais = Array.isArray(atualRaw) ? atualRaw.map(String) : [];
        return Array.from(new Set([...atuais, ...keys.map(String)]));
      });
    } catch (error) {
      console.warn("[Sync] Erro salvando recusas vistas:", error);
    }
  },

  /**
   * Recebe uma lista de itens brutos retornados pela API (acompanhamento),
   * converte para LocalJustificativa, mescla com o cache local e persiste tudo.
   * Retorna o mapa final já mesclado (pronto para setar no estado).
   */
  applyRawServerJustificativas: async (
    rawItems: any[],
  ): Promise<LocalJustificativasMap> => {
    const serverItems = rawItems
      .map((item: any) => SyncService.serverJustificativaToLocal(item))
      .filter((j): j is LocalJustificativa => j !== null);

    const kJustMerge = await chaveTenant(LOCAL_JUSTIFICATIVAS_KEY);
    const localMap = await SyncService.getLocalJustificativasMap();
    if (!serverItems.length) return localMap;

    const merged = SyncService.mergeJustificativas(localMap, serverItems);
    try {
      await setItemEscopado(
        LOCAL_JUSTIFICATIVAS_KEY,
        JSON.stringify(merged),
        kJustMerge,
      );
    } catch (e) {
      console.warn(
        "[Sync] Erro ao persistir mapa mesclado de justificativas:",
        e,
      );
    }
    return merged;
  },

  /**
   * Mescla o mapa local de justificativas com itens vindos do servidor.
   * Entradas ACEITO/RECUSADO do servidor sempre sobrepõem entradas locais PENDENTE.
   * Datas não presentes no servidor mantêm o estado local intacto.
   */
  mergeJustificativas: (
    local: LocalJustificativasMap,
    serverItems: LocalJustificativa[],
  ): LocalJustificativasMap => {
    const merged: LocalJustificativasMap = { ...local };
    for (const item of serverItems) {
      const current = merged[item.data];
      // Servidor ACEITO/RECUSADO ganha sobre local; servidor PENDENTE só preenche se não há local
      if (!current || item.status !== "PENDENTE") {
        // Preserva campos que podem estar ausentes no item do servidor (como horas)
        // se eles já existirem no mapa local.
        merged[item.data] = {
          ...item,
          horasAtual: item.horasAtual || current?.horasAtual,
          horasAjuste: item.horasAjuste || current?.horasAjuste,
          motivo: item.motivo || current?.motivo,
        };
      }
    }
    return merged;
  },

  /**
   * Remove do cache local todas as justificativas de um período específico que
   * não estejam offline (pendentes de sync). Chamado quando o servidor confirma
   * que não há justificativas para o período, limpando entradas obsoletas.
   */
  clearServerJustificativasForPeriod: async (
    year: number,
    month: number,
  ): Promise<LocalJustificativasMap> => {
    try {
      const kJust = await chaveTenant(LOCAL_JUSTIFICATIVAS_KEY);
      const map = await SyncService.getLocalJustificativasMap();
      const m = String(month).padStart(2, "0");
      const suffix = `/${m}/${year}`;
      const cleaned: LocalJustificativasMap = {};
      for (const [key, val] of Object.entries(map)) {
        if (!key.endsWith(suffix) || val.offline) {
          cleaned[key] = val;
        }
      }
      await setItemEscopado(
        LOCAL_JUSTIFICATIVAS_KEY,
        JSON.stringify(cleaned),
        kJust,
      );
      return cleaned;
    } catch (e) {
      console.warn("[Sync] Erro ao limpar justificativas do período:", e);
      return SyncService.getLocalJustificativasMap();
    }
  },

  _buildContatoBody: (
    contato: DadosColaboradorPayload["contato"],
  ): FormData => {
    const form = new FormData();
    form.append("whatsapp", contato.celular ?? "");
    form.append("email", contato.email ?? "");
    return form;
  },

  _buildSalvarColaboradorBody: (payload: DadosColaboradorPayload): FormData => {
    const form = new FormData();
    form.append("whatsapp", payload.contato?.celular ?? "");
    form.append("email", payload.contato?.email ?? "");
    // tenant dono do registro (PIX/conta bancária no tenant errado é o pior
    // caso de vazamento — carimbo habilita a validação server-side)
    form.append("hash", String((payload as any).hash ?? ""));

    const db = payload.dadosBancarios;
    const PIX_TYPE_MAP: Record<string, string> = {
      CPF: "CPF",
      Email: "EMAIL",
      Telefone: "CELULAR",
      Aleatória: "ALEATORIA",
      Aleatoria: "ALEATORIA",
      ALEATORIA: "ALEATORIA",
      CELULAR: "CELULAR",
      EMAIL: "EMAIL",
    };

    if (db?.hasPix) {
      const tipoMapped =
        PIX_TYPE_MAP[db.tipo_chave_pix ?? ""] ?? db.tipo_chave_pix ?? "";
      form.append("tipo_chave_pix", tipoMapped);
      form.append("chave_pix", db.chave_pix ?? "");

      form.append("id_adm_cadastro_banco", "");
      form.append("descricao_banco", "");
      form.append("agencia", "");
      form.append("conta", "");
      form.append("tipo_conta", "");
    } else {
      if (db?.bancoId != null && String(db.bancoId) !== "") {
        form.append("id_adm_cadastro_banco", String(db.bancoId));
      } else {
        form.append("id_adm_cadastro_banco", "");
      }
      form.append("descricao_banco", db?.banco ?? "");
      form.append("agencia", db?.agencia ?? "");
      form.append("conta", db?.conta ?? "");
      form.append("tipo_conta", "");

      form.append("tipo_chave_pix", "");
      form.append("chave_pix", "");
    }

    return form;
  },

  _buildDadosBancariosBody: (
    db: DadosColaboradorPayload["dadosBancarios"],
  ): FormData => {
    const form = new FormData();
    const PIX_TYPE_MAP: Record<string, string> = {
      CPF: "CPF",
      Email: "EMAIL",
      Telefone: "CELULAR",
      Aleatória: "ALEATORIA",
    };
    if (db.hasPix) {
      const tipoMapped =
        PIX_TYPE_MAP[db.tipo_chave_pix ?? ""] ?? db.tipo_chave_pix ?? "";
      form.append("tipo_chave_pix", tipoMapped);
      form.append("chave_pix", db.chave_pix ?? "");
    } else {
      if (db.bancoId != null && String(db.bancoId) !== "") {
        form.append("id_adm_cadastro_banco", String(db.bancoId));
      }
      form.append("agencia", db.agencia ?? "");
      form.append("conta", db.conta ?? "");
      form.append("tipo_conta", "");
    }
    return form;
  },

  /**
   * Salva contato e dados bancários do colaborador.
   * Sem internet → enfileira para sync posterior.
   * Com internet mas API falha → retorna os erros reais (não enfileira).
   */
  salvarDadosColaborador: async (
    payload: DadosColaboradorPayload,
  ): Promise<SalvarDadosResult> => {
    await carimbarHashTenant(payload);
    try {
      const isConnected = await SyncService.checkInternet();

      if (!isConnected) {
        console.log(
          "[Sync] Sem internet, enfileirando dados do colaborador...",
        );
        await SyncService.enqueueOfflineAction("DADOS_COLABORADOR", payload);
        return { success: true, offline: true };
      }

      console.log("[Sync] Enviando dados do colaborador online...");
      console.log(
        "[Sync] codigoColaborador:",
        payload.codigoColaborador,
        "| tipoColaborador:",
        payload.tipoColaborador,
      );

      const res = await Api.colaboradores.salvar(
        payload.codigoColaborador,
        payload.tipoColaborador,
        SyncService._buildSalvarColaboradorBody(payload),
      );

      if (res.success) {
        console.log("[Sync] Dados do colaborador enviados com sucesso");
        return { success: true, offline: false };
      }

      // API retornou erro — expõe para o usuário, não enfileira como offline
      const erros = res.erros ?? [];
      console.warn(
        "[Sync] Falha na API ao salvar dados do colaborador:",
        erros,
      );
      return {
        success: false,
        offline: false,
        erros: Array.isArray(erros) ? erros : [String(erros)],
      };
    } catch (error: any) {
      // Exceção de rede — considera offline e enfileira
      console.error("[Sync] Exceção ao salvar dados do colaborador:", error);
      await SyncService.enqueueOfflineAction("DADOS_COLABORADOR", payload);
      return { success: true, offline: true };
    }
  },

  // ─── PONTOS ──────────────────────────────────────────────────────────────

  /** Garante que o diretório persistente de fotos de ponto existe. */
  initPontoPhotosDir: async () => {
    const info = await FileSystem.getInfoAsync(PONTO_PHOTOS_DIR);
    if (!info.exists) {
      await FileSystem.makeDirectoryAsync(PONTO_PHOTOS_DIR, {
        intermediates: true,
      });
    }
  },

  /** Copia a foto temporária da câmera pro diretório persistente. */
  persistPontoPhoto: async (uri: string): Promise<string | null> => {
    try {
      await SyncService.initPontoPhotosDir();
      const filename = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}.jpg`;
      const dest = `${PONTO_PHOTOS_DIR}${filename}`;
      await FileSystem.copyAsync({ from: uri, to: dest });
      return dest;
    } catch (error) {
      console.warn("[Sync] Falha ao persistir foto de ponto:", error);
      return null;
    }
  },

  /**
   * Expõe a chave de storage ESCOPADA por tenant para TELAS que guardam espelho
   * de domínio fora do Sync (ex.: ronda em andamento no Ronda.tsx). Mesmo
   * contrato do chaveTenant interno: `base#HASH`, migração única do legado,
   * fail-open sem hash de sessão.
   */
  chaveEscopadaTenant: async (base: string): Promise<string> =>
    chaveTenant(base),

  getLocalPontosMap: async (): Promise<LocalPontosMap> => {
    try {
      const raw = await AsyncStorage.getItem(LOCAL_PONTOS_KEY);
      return raw ? reescreverCaminhosAntigos(JSON.parse(raw)) : {};
    } catch {
      return {};
    }
  },

  /**
   * Pontos locais DA SESSÃO atual (filtro por tenant) — é o que as TELAS devem
   * usar para exibir (Registro/Justificativas). O mapa cru é GLOBAL de propósito
   * (retenção cross-tenant), mas batida carimbada de OUTRA empresa não pode
   * pintar selo/grid/histórico desta sessão (ids/dias colidem entre bancos).
   * Sem carimbo (legado) = fail-open (aparece, como sempre; a adoção carimba).
   */
  /**
   * Avisa quando o mapa de pontos local muda — batida nova, enviada, curada, confirmada ou
   * removida —, inclusive pelo envio em segundo plano. Devolve a função que cancela o aviso.
   * Quem desenha a partir do mapa (getLocalPontosDaSessao) relê quando é avisado.
   */
  observarPontosLocais: (ouvinte: () => void): (() => void) => {
    ouvintesPontosLocais.add(ouvinte);
    return () => {
      ouvintesPontosLocais.delete(ouvinte);
    };
  },

  getLocalPontosDaSessao: async (): Promise<LocalPonto[]> => {
    const map = await SyncService.getLocalPontosMap();
    const hashSessao = await tenantHashSessaoAtual().catch(() => "");
    return Object.values(map || {}).filter((lp: any) => {
      const th = String(lp?.hash ?? "")
        .trim()
        .toUpperCase();
      return !th || !hashSessao || th === hashSessao;
    });
  },

  // ── Último posto do ponto (rastro permanente, por tenant) ───────────────────

  /** Sobrescrito a CADA batida deste aparelho (chamado pelo submitPonto). */
  registrarUltimoPostoPonto: async (
    idCliente: number,
    postoNome: string,
    dataLog: string,
  ) => {
    try {
      const id = Number(idCliente) || 0;
      if (!id) return;
      await AsyncStorage.setItem(
        await chaveTenant(ULTIMO_POSTO_PONTO_KEY),
        JSON.stringify({
          id_adm_cliente: id,
          posto_nome: String(postoNome ?? ""),
          data_log: String(dataLog ?? ""),
        }),
      );
    } catch (e) {
      console.warn("[Sync] registrarUltimoPostoPonto: erro", e);
    }
  },

  /**
   * O posto (id_adm_cliente) do ÚLTIMO ponto registrado NESTE aparelho, no tenant da
   * sessão — 100% local, sem backend (offline-first): o valor certo existe aqui desde o
   * instante da batida, mesmo com a batida ainda na fila.
   *
   * Fontes, da mais confiável para a mais fraca:
   *  1. o rastro permanente (gravado em toda batida);
   *  2. o espelho de pontos pendentes — cobre quem atualizou o app com batida na fila
   *     feita ANTES de o rastro existir;
   *  3. null — quem chama decide o fallback (ex.: lotação fixa do userData).
   *
   * ⚠️ Regra das 24h: batida mais velha que isso NÃO conta como "onde ele está" — um
   * colaborador de folga ontem no posto A pode abrir o app hoje vinculado ao posto B.
   * Fora da janela devolve null e o chamador cai na lotação do DataContent.
   */
  resolverPostoDoUltimoPonto: async (): Promise<{
    id_adm_cliente: number;
    posto_nome: string;
  } | null> => {
    const JANELA_24H_MS = 24 * 60 * 60 * 1000;
    // "YYYY-MM-DD HH:mm:ss" (formato dos dois rastros) → epoch; ilegível → 0.
    const instante = (dl: string): number => {
      const t = new Date(String(dl ?? "").replace(" ", "T")).getTime();
      return Number.isFinite(t) ? t : 0;
    };
    try {
      let melhor: {
        id_adm_cliente: number;
        posto_nome: string;
        dl: string;
      } | null = null;
      try {
        const raw = await AsyncStorage.getItem(
          await chaveTenant(ULTIMO_POSTO_PONTO_KEY),
        );
        const dado = raw ? JSON.parse(raw) : null;
        const id = Number(dado?.id_adm_cliente) || 0;
        if (id > 0) {
          melhor = {
            id_adm_cliente: id,
            posto_nome: String(dado?.posto_nome ?? ""),
            dl: String(dado?.data_log ?? ""),
          };
        }
      } catch {}

      const pendentes = await SyncService.getLocalPontosDaSessao();
      for (const lp of pendentes) {
        const id = Number((lp as any)?.id_adm_cliente) || 0;
        if (!id) continue;
        const dl = String((lp as any)?.data_log ?? "");
        // data_log local é "YYYY-MM-DD HH:mm:ss" — comparação lexicográfica basta.
        if (!melhor || dl > melhor.dl) {
          melhor = {
            id_adm_cliente: id,
            posto_nome: String((lp as any)?.postoNome ?? ""),
            dl,
          };
        }
      }

      // Batida fora da janela de 24h não representa "onde ele está agora".
      if (melhor && Date.now() - instante(melhor.dl) > JANELA_24H_MS) {
        melhor = null;
      }
      return melhor
        ? {
            id_adm_cliente: melhor.id_adm_cliente,
            posto_nome: melhor.posto_nome,
          }
        : null;
    } catch {
      return null;
    }
  },

  /** Grava a batida no mapa. `false` = NÃO gravou (quem chama precisa de outro lastro). */
  setLocalPonto: async (ponto: LocalPonto): Promise<boolean> => {
    try {
      await comMapaLock(async () => {
        const map = await lerMapaPontosParaEscrita();
        map[ponto.id] = ponto;
        await gravarMapaPontos(map);
      });
      return true;
    } catch (e) {
      console.warn("[Sync] Erro salvando ponto local:", e);
      return false;
    }
  },

  removeLocalPonto: async (id: string) => {
    try {
      const fotoUri = await comMapaLock(async () => {
        const map = await lerMapaPontosParaEscrita();
        if (!map[id]) return null;
        const foto = map[id].fotoUri;
        delete map[id];
        await gravarMapaPontos(map);
        return foto ?? null;
      });
      // A cópia em arquivo sai junto: sem a entrada no mapa, ela seria "restaurada" como
      // pendente pelo reconciliador e reenviaria uma batida já gravada.
      await SyncService._removerBackupPonto(id);
      // Remove foto persistida (já foi enviada)
      if (fotoUri && fotoUri.includes("ponto_fotos/")) {
        try {
          await FileSystem.deleteAsync(fotoUri, { idempotent: true });
        } catch {}
      }
    } catch (e) {
      console.warn("[Sync] Erro removendo ponto local:", e);
    }
  },

  // ── Cópia de segurança das batidas em ARQUIVO (`ponto_pendentes/<id>.json`) ─────────
  //
  // O mapa e a fila vivem no AsyncStorage. No Android ele tem teto (6 MB por padrão) e
  // falha com o celular sem espaço — e a gravação falhava CALADA: a pessoa via "Ponto
  // registrado!" e a batida não existia em lugar nenhum. O arquivo fica FORA desse banco.
  // Ciclo de vida:
  //  - nasce no submitPonto (pendente);
  //  - acompanha o envio (markLocalPontoSynced regrava como enviada);
  //  - sai quando o servidor CONFIRMA a batida (linha genuína no /iniciar), quando a
  //    batida sai do mapa (removeLocalPonto) ou, enviada e nunca confirmada, após 15 dias;
  //  - se a batida sumiu do mapa ainda pendente, o reconciliador a devolve ao mapa — e,
  //    se nem isso der (storage quebrado), o dreno envia direto daqui.
  // Pendente nunca é apagada por idade.

  _gravarBackupPonto: async (lp: LocalPonto): Promise<boolean> => {
    try {
      const info = await FileSystem.getInfoAsync(PONTO_BACKUP_DIR);
      if (!info.exists) {
        await FileSystem.makeDirectoryAsync(PONTO_BACKUP_DIR, {
          intermediates: true,
        });
      }
      await FileSystem.writeAsStringAsync(
        `${PONTO_BACKUP_DIR}${lp.id}.json`,
        JSON.stringify(lp),
      );
      return true;
    } catch (e) {
      console.warn("[Sync] Falha ao gravar a cópia em arquivo da batida:", e);
      return false;
    }
  },

  /** Regrava a cópia SÓ se ela já existe — batida antiga (de antes da cópia) não ganha uma. */
  _atualizarBackupPontoSeExistir: async (lp: LocalPonto): Promise<void> => {
    try {
      const info = await FileSystem.getInfoAsync(
        `${PONTO_BACKUP_DIR}${lp.id}.json`,
      );
      if (info.exists) await SyncService._gravarBackupPonto(lp);
    } catch {}
  },

  _removerBackupPonto: async (id: string): Promise<void> => {
    try {
      await FileSystem.deleteAsync(`${PONTO_BACKUP_DIR}${id}.json`, {
        idempotent: true,
      });
    } catch {}
  },

  /**
   * Batidas PENDENTES sem cópia em arquivo ganham uma — uma vez por abertura do app.
   *
   * Por que existe (06/10/2026): a cópia em `ponto_pendentes/` nasceu em 30/09; a batida
   * feita offline numa versão anterior (até a 4.0.22) só existe no mapa e/ou na fila, que
   * moram no AsyncStorage. Ela era justamente a que sumia quando, depois de atualizar o app,
   * a fila não voltava na leitura e o "Sair" liberava. Com a cópia, ela volta pelo
   * reconciliador mesmo se o mapa e a fila se perderem. A cópia não envia nada sozinha: com
   * a batida no mapa, é o mapa que conduz (ver o passo das CÓPIAS EM ARQUIVO).
   *
   * Falha de leitura → tenta no próximo dreno (nada é gravado pela metade).
   */
  _garantirCopiasDasPendentes: async (): Promise<void> => {
    if (copiasDasPendentesConferidas) return;
    try {
      const info = await FileSystem.getInfoAsync(PONTO_BACKUP_DIR);
      const comCopia = new Set(
        info.exists
          ? (await FileSystem.readDirectoryAsync(PONTO_BACKUP_DIR))
              .filter((n) => n.endsWith(".json"))
              .map((n) => n.slice(0, -5))
          : [],
      );
      const raw = await AsyncStorage.getItem(LOCAL_PONTOS_KEY); // erro lança
      const mapa: LocalPontosMap = raw
        ? reescreverCaminhosAntigos(JSON.parse(raw))
        : {};
      const fila = await lerFilaSoLeitura(); // erro lança
      const semCopia: LocalPonto[] = [];
      for (const lp of Object.values(mapa ?? {})) {
        if (lp?.id && lp.offline === true && !comCopia.has(String(lp.id))) {
          semCopia.push(lp);
          comCopia.add(String(lp.id));
        }
      }
      for (const a of fila) {
        if (a?.type !== "PONTO") continue;
        const lp = (a.data as any)?.ponto as LocalPonto | undefined;
        if (!lp?.id || lp.offline === false) continue;
        if (mapa?.[lp.id] || comCopia.has(String(lp.id))) continue;
        semCopia.push({ ...lp, offline: true });
        comCopia.add(String(lp.id));
      }
      let gravadas = 0;
      for (const lp of semCopia) {
        // Confere no lock do mapa: o dreno pode ter enviado a batida desde a leitura acima —
        // cópia "pendente" de batida já enviada voltaria a ser enviada se o mapa se perdesse.
        const ok = await comMapaLock(async () => {
          const rawAgora = await AsyncStorage.getItem(LOCAL_PONTOS_KEY);
          const noMapa = rawAgora ? JSON.parse(rawAgora)?.[lp.id] : null;
          if (noMapa && noMapa.offline !== true) return true; // já enviada: dispensa
          return SyncService._gravarBackupPonto(
            noMapa ? reescreverCaminhosAntigos(noMapa) : lp,
          );
        });
        if (ok) gravadas++;
      }
      if (gravadas > 0) {
        console.warn(
          `[Sync] ${gravadas} batida(s) pendente(s) de versão anterior conferida(s) para a cópia em arquivo.`,
        );
      }
      // Fila antiga ainda ilegível: as batidas que só existem nela não foram vistas — confere
      // de novo no próximo dreno.
      if (gravadas === semCopia.length && !(await filaAntigaIlegivel()))
        copiasDasPendentesConferidas = true;
    } catch (e) {
      console.warn("[Sync] Cópia das batidas pendentes adiada:", e);
    }
  },

  /** Todas as cópias legíveis. Arquivo ilegível fica onde está (forense) e é ignorado. */
  _lerBackupsPonto: async (): Promise<LocalPonto[]> => {
    try {
      const info = await FileSystem.getInfoAsync(PONTO_BACKUP_DIR);
      if (!info.exists) return [];
      const nomes = await FileSystem.readDirectoryAsync(PONTO_BACKUP_DIR);
      const out: LocalPonto[] = [];
      for (const nome of nomes.slice(0, 500)) {
        if (!nome.endsWith(".json")) continue;
        try {
          const lp = JSON.parse(
            await FileSystem.readAsStringAsync(`${PONTO_BACKUP_DIR}${nome}`),
          );
          if (
            lp &&
            typeof lp === "object" &&
            lp.id &&
            lp.data &&
            Number(lp.id_rh_ponto_tipo) > 0
          ) {
            out.push(reescreverCaminhosAntigos(lp));
          }
        } catch {}
      }
      return out;
    } catch {
      return [];
    }
  },

  // ── Execuções de checklist avulsas (gatilho livre / ponto) ──────────────────
  // Mesma mecânica do mapa de pontos: a tela lê daqui para mostrar o que foi
  // executado NESTE aparelho antes de o servidor confirmar, e o selo sai do campo
  // `offline`.

  getLocalChecklistExecucoesMap:
    async (): Promise<LocalChecklistExecucoesMap> => {
      try {
        const raw = await AsyncStorage.getItem(LOCAL_CHECKLIST_EXEC_KEY);
        return raw ? JSON.parse(raw) : {};
      } catch {
        return {};
      }
    },

  /**
   * Execuções locais DA SESSÃO (filtro por tenant) — é o que as telas devem usar.
   * Execução carimbada de outra empresa não pode aparecer no histórico desta.
   * Sem carimbo (legado) = fail-open. Mais recente primeiro.
   */
  getLocalChecklistExecucoesDaSessao: async (): Promise<
    LocalChecklistExecucao[]
  > => {
    const map = await SyncService.getLocalChecklistExecucoesMap();
    const hashSessao = await tenantHashSessaoAtual().catch(() => "");
    return Object.values(map || {})
      .filter((e: any) => {
        const th = String(e?.hash ?? "")
          .trim()
          .toUpperCase();
        return !th || !hashSessao || th === hashSessao;
      })
      .sort((a, b) => (b?.timestamp ?? 0) - (a?.timestamp ?? 0));
  },

  setLocalChecklistExecucao: async (exec: LocalChecklistExecucao) => {
    try {
      await comMapaLock(async () => {
        const map = await SyncService.getLocalChecklistExecucoesMap();
        map[exec.id] = exec;
        await AsyncStorage.setItem(
          LOCAL_CHECKLIST_EXEC_KEY,
          JSON.stringify(map),
        );
      });
    } catch (e) {
      console.warn("[Sync] Erro salvando execução de checklist local:", e);
    }
  },

  /**
   * Complemento de uma execução que AINDA NÃO SAIU deste aparelho.
   *
   * ⚠️ Aqui o complemento NÃO é uma segunda gravação: enquanto a execução está na fila,
   * ela e o complemento são o MESMO registro. Por isso as respostas são fundidas no
   * payload enfileirado (e no espelho), e o servidor recebe uma execução COMPLETA quando
   * a fila drenar. Sem isto o complemento dependia do `id_inspecao` do servidor — que só
   * existe depois do sync (o `Salvar` devolve 200 = enfileirado, sem id) —, e o card
   * ficava travado em "Complementar após sincronizar", furando o offline-first.
   *
   * Devolve `false` quando não há mais ação na fila para esta execução (ela já foi
   * despachada): aí o complemento precisa mesmo do id do servidor.
   */
  mesclarComplementoNaExecucaoLocal: async (
    localId: string,
    respostas: any[],
    assets: any[],
  ): Promise<"FILA" | "PENDENTE"> => {
    const chaveResp = (r: any) =>
      String(r?.PerguntaId ?? r?.perguntaId ?? "").trim();

    // 0) Assets ARQUIVADOS antes de qualquer gravação: o sheet devolve uri de cache e,
    //    na assinatura, base64 — nenhum dos dois pode entrar em espelho/fila.
    const assetsPersistidos = await SyncService.persistChecklistAssets(assets);

    // 1) Espelho local: a tela mostra as respostas na hora (inclusive no "ver").
    try {
      await comMapaLock(async () => {
        const map = await SyncService.getLocalChecklistExecucoesMap();
        const cur = map[localId];
        if (!cur) return;
        const novas = (Array.isArray(respostas) ? respostas : []).map(
          (r: any) => ({
            perguntaId: Number(r?.PerguntaId) || 0,
            resposta: r?.Resposta == null ? null : String(r.Resposta),
            foto: r?.Foto ?? null,
          }),
        );
        const idsNovos = new Set(novas.map((r) => String(r.perguntaId)));
        map[localId] = {
          ...cur,
          respostas: [
            ...(cur.respostas ?? []).filter(
              (r: any) => !idsNovos.has(String(r?.perguntaId ?? "")),
            ),
            ...novas,
          ],
        };
        await AsyncStorage.setItem(
          LOCAL_CHECKLIST_EXEC_KEY,
          JSON.stringify(map),
        );
      });
    } catch (e) {
      console.warn("[Sync] Erro fundindo complemento no espelho local:", e);
    }

    // 2) Ação enfileirada: funde no MESMO payload, para o servidor receber a execução
    //    inteira de uma vez (nada de segunda gravação com Id).
    try {
      const fila = await lerFilaSoLeitura();
      const alvo = fila.find(
        (a) =>
          a.type === "CHECKLIST_SALVAR" &&
          String((a.data as any)?.__localExecId ?? "") === String(localId),
      );
      if (!alvo) return "PENDENTE";

      const atuais = Array.isArray((alvo.data as any)?.Respostas)
        ? (alvo.data as any).Respostas
        : [];
      const idsNovos = new Set(
        (Array.isArray(respostas) ? respostas : []).map(chaveResp),
      );
      const fundidas = [
        ...atuais.filter((r: any) => !idsNovos.has(chaveResp(r))),
        ...(Array.isArray(respostas) ? respostas : []),
      ];
      const assetsAtuais = Array.isArray((alvo.data as any)?.Assets)
        ? (alvo.data as any).Assets
        : [];
      await SyncService.updateOfflineActionData(alvo.id, {
        ...(alvo.data as any),
        Respostas: fundidas,
        Assets: [...assetsAtuais, ...assetsPersistidos],
      });
      return "FILA";
    } catch (e) {
      console.warn("[Sync] Erro fundindo complemento na fila:", e);
      return "PENDENTE";
    }
  },

  /** 1 check → 2 checks cinza: o T-ARC aceitou o Salvar (ainda não confirmado na carga). */
  markLocalChecklistExecucaoEnviada: async (id: string) => {
    if (!id) return;
    try {
      await comMapaLock(async () => {
        const map = await SyncService.getLocalChecklistExecucoesMap();
        const cur = map[id];
        if (!cur) return;
        map[id] = { ...cur, offline: false, syncedEm: Date.now() };
        await AsyncStorage.setItem(
          LOCAL_CHECKLIST_EXEC_KEY,
          JSON.stringify(map),
        );
      });
    } catch (e) {
      console.warn("[Sync] Erro marcando execução de checklist enviada:", e);
    }
  },

  removeLocalChecklistExecucao: async (id: string) => {
    try {
      await comMapaLock(async () => {
        const map = await SyncService.getLocalChecklistExecucoesMap();
        if (!map[id]) return;
        delete map[id];
        await AsyncStorage.setItem(
          LOCAL_CHECKLIST_EXEC_KEY,
          JSON.stringify(map),
        );
      });
    } catch (e) {
      console.warn("[Sync] Erro removendo execução de checklist local:", e);
    }
  },

  /**
   * Reconcilia o mapa local com o histórico do servidor: execução já confirmada lá sai
   * daqui e passa a ser renderizada pela lista do servidor (2 checks AZUL).
   *
   * O servidor não devolve o id local, então casamos por (checklistId + tempo_inicio ao
   * SEGUNDO) — TempoInicio é gerado no app e gravado literal pelo T-Consumer, o que faz
   * dele uma chave natural e estável mesmo entre reenvios da fila.
   */
  reconciliarChecklistExecucoes: async (execucoesServidor: any[]) => {
    try {
      const servidor = Array.isArray(execucoesServidor)
        ? execucoesServidor
        : [];
      if (servidor.length === 0) return;

      // MAPA (não Set): é daqui que sai o id REAL da execução — o `Salvar` nunca o
      // devolve, e o complemento pendente precisa dele para ter alvo.
      const confirmadas = new Map<string, any>();
      for (const e of servidor) {
        const k = chaveNaturalExecucao(
          e?.checklistId ?? e?.id_inspecao_cadastro,
          e?.tempoInicio ?? e?.tempo_inicio,
        );
        if (!k) continue;
        // Reenvio da fila pode ter gerado DUAS linhas com a mesma chave natural: vence a
        // de maior id (a mais recente). Sem desempate explícito, o alvo do complemento
        // ficaria à sorte da ordem do JSON.
        const atual = confirmadas.get(k);
        if (!atual || (Number(e?.id) || 0) > (Number(atual?.id) || 0)) {
          confirmadas.set(k, e);
        }
      }
      if (confirmadas.size === 0) return;

      // Pendentes de vínculo: enquanto existir um para a execução, ela NÃO pode sair do
      // espelho sem id utilizável — apagar aqui deixaria o complemento órfão para sempre.
      const pendentes = await SyncService.getLocalChecklistComplPendentesMap();

      await comMapaLock(async () => {
        const map = await SyncService.getLocalChecklistExecucoesMap();
        let mudou = false;
        for (const [id, exec] of Object.entries(map || {})) {
          const k = chaveNaturalExecucao(
            (exec as any)?.checklistId,
            (exec as any)?.tempoInicio,
          );
          if (!k || !confirmadas.has(k)) continue;
          const idServidor = Number(confirmadas.get(k)?.id) || 0;
          if (idServidor <= 0 && pendentes[id]) continue; // fail-closed
          delete map[id];
          mudou = true;
        }
        if (mudou) {
          await AsyncStorage.setItem(
            LOCAL_CHECKLIST_EXEC_KEY,
            JSON.stringify(map),
          );
        }
      });
    } catch (e) {
      console.warn("[Sync] Erro reconciliando execuções de checklist:", e);
    }
  },

  /**
   * Envia os complementos que estavam esperando o id da execução.
   *
   * Chamado logo APÓS as reconciliações, com a lista fresca do servidor — é dela que sai
   * o id real. Fora de qualquer lock: `submitChecklistComplemento` toma o `comMapaLock`
   * (que não é reentrante) e enfileira, e a ordem mapa→fila é proibida neste arquivo.
   */
  vincularComplementosPendentes: async (
    execucoesServidor: any[],
  ): Promise<void> => {
    if (vinculandoComplementos) return; // duas passadas enviariam duas vezes
    vinculandoComplementos = true;
    try {
      const servidor = Array.isArray(execucoesServidor)
        ? execucoesServidor
        : [];
      if (servidor.length === 0) return;

      const porChave = new Map<string, any>();
      for (const e of servidor) {
        const k = chaveNaturalExecucao(
          e?.checklistId ?? e?.id_inspecao_cadastro,
          e?.tempoInicio ?? e?.tempo_inicio,
        );
        if (!k) continue;
        const atual = porChave.get(k);
        if (!atual || (Number(e?.id) || 0) > (Number(atual?.id) || 0)) {
          porChave.set(k, e);
        }
      }
      if (porChave.size === 0) return;

      const pendentes =
        await SyncService.getLocalChecklistComplPendentesDaSessao();
      if (Object.keys(pendentes).length === 0) return;

      const fila = await lerFilaSoLeitura();
      const rastros = await SyncService.getLocalChecklistComplementosMap();

      // Reserva sob lock (só leitura/escrita de mapa); o envio vem depois.
      const paraEnviar: {
        pend: LocalChecklistComplPendente;
        exec: any;
      }[] = [];
      await comMapaLock(async () => {
        const map = await SyncService.getLocalChecklistComplPendentesMap();
        let mudou = false;
        for (const [localId, pend] of Object.entries(pendentes)) {
          const exec = porChave.get(String(pend?.chaveNatural ?? ""));
          const idExec = Number(exec?.id) || 0;
          if (!exec || idExec <= 0) continue;

          // ADIA (não descarta) enquanto houver EDIÇÃO em voo para esta execução: o
          // `Salvar` com Id INATIVA tudo e regrava — os dois se atropelariam.
          const rastro: any = rastros[String(idExec)];
          if (rastro?.tipo === "EDICAO") continue;
          const edicaoNaFila = fila.some(
            (a) =>
              a.type === "CHECKLIST_SALVAR" &&
              Number((a.data as any)?.Id ?? 0) === idExec,
          );
          if (edicaoNaFila) continue;

          // Lease: entrada reservada há pouco fica de fora (outra passada cuida dela).
          const desde = Number((map[localId] as any)?.enviandoDesde ?? 0);
          if (desde > 0 && Date.now() - desde < LEASE_VINCULO_MS) continue;

          map[localId] = {
            ...(map[localId] as any),
            enviandoDesde: Date.now(),
            tentativas: Number((map[localId] as any)?.tentativas ?? 0) + 1,
          };
          mudou = true;
          paraEnviar.push({ pend, exec });
        }
        if (mudou) {
          await AsyncStorage.setItem(
            LOCAL_CHECKLIST_COMPL_PENDENTE_KEY,
            JSON.stringify(map),
          );
        }
      });

      for (const { pend, exec } of paraEnviar) {
        const idExec = Number(exec?.id) || 0;
        const respostasServidor = Array.isArray(exec?.respostas)
          ? exec.respostas
          : [];
        // Fail-closed: sem as respostas originais em mãos, enviar com `Id` APAGARIA a
        // execução (o backend inativa tudo e regrava só o que vier). Espera a próxima.
        if (respostasServidor.length === 0) {
          await SyncService.liberarLeaseComplementoPendente(pend.localExecId);
          continue;
        }

        // União: originais do SERVIDOR (com as URLs reais) + as do complemento. A
        // original só sai quando é de TEXTO — linha de mídia da mesma pergunta fica.
        const idsCompl = new Set(
          (pend.respostas ?? []).map((r) => String(r.PerguntaId)),
        );
        const originais = respostasServidor
          .filter(
            (r: any) =>
              !idsCompl.has(String(r?.perguntaId ?? r?.PerguntaId ?? "")) ||
              ehLinhaDeMidia(r),
          )
          .map((r: any) => ({
            PerguntaId: Number(r?.perguntaId ?? r?.PerguntaId) || 0,
            Resposta: String(r?.resposta ?? r?.Resposta ?? ""),
            Foto: r?.foto ?? r?.Foto ?? undefined,
          }))
          .filter((r: any) => Number.isFinite(r.PerguntaId));

        const r = await SyncService.submitChecklistComplemento(
          {
            Id: idExec,
            ChecklistId: pend.checklistId,
            ResponsavelId:
              pend.responsavelId || Number(exec?.responsavelId) || 0,
            Respostas: [...originais, ...(pend.respostas ?? [])],
            Assets: pend.assets ?? [],
            Latitude: pend.latitude ?? "",
            Longitude: pend.longitude ?? "",
          },
          { perguntaIds: pend.perguntaIds ?? [], tipo: "COMPLEMENTO" },
        );

        if (r?.success) {
          // Enfileirado ou aceito: o rastro de complemento assume daqui (é ele que
          // segura o selo na tela). O pendente cumpriu o papel.
          await SyncService.removerComplementoPendente(pend.localExecId);
        } else {
          await SyncService.liberarLeaseComplementoPendente(pend.localExecId);
        }
      }
    } catch (e) {
      console.warn("[Sync] Erro vinculando complementos pendentes:", e);
    } finally {
      vinculandoComplementos = false;
    }
  },

  /** Devolve o pendente à fila de tentativas (some a reserva, some o registro não). */
  liberarLeaseComplementoPendente: async (localExecId: string) => {
    try {
      await comMapaLock(async () => {
        const map = await SyncService.getLocalChecklistComplPendentesMap();
        const cur: any = map[localExecId];
        if (!cur) return;
        delete cur.enviandoDesde;
        map[localExecId] = cur;
        await AsyncStorage.setItem(
          LOCAL_CHECKLIST_COMPL_PENDENTE_KEY,
          JSON.stringify(map),
        );
      });
    } catch {}
  },

  /**
   * Teto do pendente. Estourou (execução nunca voltou do servidor): NÃO some — vai para
   * uma chave forense, para o suporte recuperar. Regra de ouro: jamais perder registro.
   */
  /**
   * Varre `livro_fotos/` e apaga arquivo ÓRFÃO e VELHO.
   *
   * Por que existe: `persistFotosLivroLocal` copia toda foto de item/ocorrência/assinatura
   * para cá e NADA apagava — o diretório crescia para sempre. Disco cheio faz aquele mesmo
   * `persistFotosLivroLocal` cair no ramo degradado e devolver base64, que engorda a linha
   * do AsyncStorage e é exatamente o que ele existe para evitar. Ou seja: sem faxina, o
   * acúmulo ressuscita a falha original.
   *
   * ⚠️ Duas guardas, porque apagar arquivo em uso é perda de registro:
   * 1. Só apaga o que NÃO está referenciado — nem pela fila offline (a linha bruta é
   *    varrida como TEXTO de propósito: pega o caminho esteja ele onde estiver no payload,
   *    sem depender do formato de cada tipo de ação) nem pelo espelho de livros locais.
   * 2. Só apaga o que tem mais de 7 dias. É cinto e suspensório: se alguma referência
   *    escapar da varredura por texto, a foto ainda sobrevive à janela em que a fila
   *    realisticamente drena.
   */
  varrerFotosLivroOrfas: async (): Promise<void> => {
    try {
      const info = await FileSystem.getInfoAsync(LIVRO_PHOTOS_DIR);
      if (!info.exists) return;

      const nomes = await FileSystem.readDirectoryAsync(LIVRO_PHOTOS_DIR);
      if (nomes.length === 0) return;

      // Referências: fila offline + espelho de livros locais de TODOS OS TENANTS.
      //
      // ⚠️ O diretório `livro_fotos/` NÃO é escopado por tenant (o nome do arquivo é só
      // timestamp+random), mas as chaves do AsyncStorage SÃO (`base#hash`). Olhar apenas
      // o tenant da sessão apagaria a foto pendente de OUTRA empresa do mesmo aparelho —
      // quem trabalha em duas empresas tem os dois conjuntos aqui dentro. Por isso a
      // varredura junta todas as chaves que começam pelas duas bases.
      const todasChaves = await AsyncStorage.getAllKeys();
      const chavesRef = todasChaves.filter(
        (k) => k === LIVRO_LOCAIS_KEY || k.startsWith(`${LIVRO_LOCAIS_KEY}#`),
      );
      // FALHA FECHADA em todo insumo duvidoso. Uma referência que não foi lida vira
      // "arquivo órfão" e o arquivo é apagado — com a fila intacta apontando para ele.
      // Cenário real: o `AsyncStorage.clear()` do logout (BottomBar) tem uma janela em
      // que NENHUMA dessas chaves existe, e o dreno dispara de ~20 lugares do app.
      // A fila mora em arquivo (services/FilaOffline.ts): erro de leitura lança e cai no
      // catch abaixo; fila antiga ainda não lida = referências desconhecidas.
      if (await filaAntigaIlegivel()) return;
      const fila = await lerFilaSoLeitura();
      if (chavesRef.length === 0 && !(await filaRegistrada())) return; // diretório com arquivos e zero referência = suspeito
      const pares =
        chavesRef.length > 0 ? await AsyncStorage.multiGet(chavesRef) : [];
      if (pares.some(([, v]) => v == null)) return; // chave sumiu entre listar e ler
      const referencias = [
        JSON.stringify(fila),
        ...pares.map(([, v]) => v ?? ""),
      ].join("\n");

      const LIMITE_MS = 7 * 24 * 60 * 60 * 1000;
      const agora = Date.now();
      let apagadas = 0;

      for (const nome of nomes) {
        if (referencias.includes(nome)) continue; // em uso por fila ou espelho
        // Idade pelo NOME, não pelo `modificationTime`. `persistFotosLivroLocal` grava
        // `${Date.now()}_${random}.ext`, então o epoch está ali e é confiável. O
        // modificationTime volta 0 quando o sistema de arquivos não sabe informar (iOS
        // com attributesOfItem falhando, Android com lastModified em erro de I/O) — e um
        // 0 fazia a guarda de idade sumir e o arquivo ser apagado NA HORA. Nome ilegível
        // = não apaga: sem idade comprovada, não se mexe.
        const epoch = Number(String(nome).split("_")[0]);
        if (!Number.isFinite(epoch) || epoch <= 0) continue;
        if (agora - epoch < LIMITE_MS) continue;
        try {
          await FileSystem.deleteAsync(`${LIVRO_PHOTOS_DIR}${nome}`, {
            idempotent: true,
          });
          apagadas += 1;
        } catch {
          // Travado ou já removido: a próxima varredura tenta de novo.
        }
      }
      if (apagadas > 0)
        console.log(`[Sync] varrerFotosLivroOrfas: ${apagadas} arquivo(s)`);
    } catch (e) {
      console.warn("[Sync] varrerFotosLivroOrfas: erro", e);
    }
  },

  varrerComplementosPendentesVencidos: async (): Promise<void> => {
    try {
      const map = await SyncService.getLocalChecklistComplPendentesMap();
      const vencidos = Object.entries(map).filter(
        ([, p]) =>
          Date.now() - (Number((p as any)?.criadoEm) || 0) >
          TETO_COMPL_PENDENTE_MS,
      );
      if (vencidos.length === 0) return;
      console.warn(
        "[Sync] Complementos pendentes vencidos (quarentena):",
        vencidos.map(([k]) => k),
      );
      const raw = await AsyncStorage.getItem(COMPL_PENDENTE_QUARENTENA_KEY);
      const anterior = raw ? JSON.parse(raw) : [];
      await AsyncStorage.setItem(
        COMPL_PENDENTE_QUARENTENA_KEY,
        JSON.stringify(
          [
            ...(Array.isArray(anterior) ? anterior : []),
            ...vencidos.map(([, p]) => p),
          ].slice(-50),
        ),
      );
      await comMapaLock(async () => {
        const atual = await SyncService.getLocalChecklistComplPendentesMap();
        for (const [k] of vencidos) delete atual[k];
        await AsyncStorage.setItem(
          LOCAL_CHECKLIST_COMPL_PENDENTE_KEY,
          JSON.stringify(atual),
        );
      });
    } catch (e) {
      console.warn("[Sync] Erro varrendo complementos pendentes:", e);
    }
  },

  // ── Complementos PENDENTES DE VÍNCULO ───────────────────────────────────────
  // (respondidos antes de o servidor devolver o id da execução — ver a constante)

  getLocalChecklistComplPendentesMap: async (): Promise<
    Record<string, LocalChecklistComplPendente>
  > => {
    try {
      const raw = await AsyncStorage.getItem(
        LOCAL_CHECKLIST_COMPL_PENDENTE_KEY,
      );
      const obj = raw ? JSON.parse(raw) : {};
      return obj && typeof obj === "object" ? obj : {};
    } catch {
      return {};
    }
  },

  /** Só os do tenant da SESSÃO — ids de execução colidem entre bancos. */
  getLocalChecklistComplPendentesDaSessao: async (): Promise<
    Record<string, LocalChecklistComplPendente>
  > => {
    const map = await SyncService.getLocalChecklistComplPendentesMap();
    const hashSessao = await tenantHashSessaoAtual().catch(() => "");
    const out: Record<string, LocalChecklistComplPendente> = {};
    for (const [id, c] of Object.entries(map || {})) {
      const th = String((c as any)?.hash ?? "")
        .trim()
        .toUpperCase();
      if (!th || !hashSessao || th === hashSessao) out[id] = c;
    }
    return out;
  },

  /**
   * Guarda o complemento que ainda não tem alvo. Os assets são PERSISTIDOS antes —
   * uri de cache (e o base64 da assinatura) não sobrevivem até o vínculo.
   */
  guardarComplementoPendente: async (
    registro: Omit<
      LocalChecklistComplPendente,
      "hash" | "criadoEm" | "assets"
    > & {
      assets: any[];
    },
  ): Promise<void> => {
    try {
      const hash = await tenantHashSessaoAtual().catch(() => "");
      const assets = await SyncService.persistChecklistAssets(registro.assets);
      await comMapaLock(async () => {
        const map = await SyncService.getLocalChecklistComplPendentesMap();
        map[registro.localExecId] = {
          ...registro,
          assets,
          hash: hash || undefined,
          criadoEm: Date.now(),
        };
        await AsyncStorage.setItem(
          LOCAL_CHECKLIST_COMPL_PENDENTE_KEY,
          JSON.stringify(map),
        );
      });
    } catch (e) {
      console.warn("[Sync] Erro guardando complemento pendente:", e);
    }
  },

  removerComplementoPendente: async (localExecId: string): Promise<void> => {
    try {
      await comMapaLock(async () => {
        const map = await SyncService.getLocalChecklistComplPendentesMap();
        if (!map[localExecId]) return;
        delete map[localExecId];
        await AsyncStorage.setItem(
          LOCAL_CHECKLIST_COMPL_PENDENTE_KEY,
          JSON.stringify(map),
        );
      });
    } catch (e) {
      console.warn("[Sync] Erro removendo complemento pendente:", e);
    }
  },

  // ── Complemento (seção de preenchimento posterior) ──────────────────────────

  getLocalChecklistComplementosMap:
    async (): Promise<LocalChecklistComplementosMap> => {
      try {
        const raw = await AsyncStorage.getItem(LOCAL_CHECKLIST_COMPL_KEY);
        return raw ? JSON.parse(raw) : {};
      } catch {
        return {};
      }
    },

  /** Complementos desta sessão (filtro por tenant), indexados por id da execução. */
  getLocalChecklistComplementosDaSessao: async (): Promise<
    Record<string, LocalChecklistComplemento>
  > => {
    const map = await SyncService.getLocalChecklistComplementosMap();
    const hashSessao = await tenantHashSessaoAtual().catch(() => "");
    const out: Record<string, LocalChecklistComplemento> = {};
    for (const [id, c] of Object.entries(map || {})) {
      const th = String((c as any)?.hash ?? "")
        .trim()
        .toUpperCase();
      if (!th || !hashSessao || th === hashSessao) out[id] = c;
    }
    return out;
  },

  markLocalChecklistComplementoEnviado: async (idExecucao: string | number) => {
    const chave = String(idExecucao || "");
    if (!chave) return;
    try {
      await comMapaLock(async () => {
        const map = await SyncService.getLocalChecklistComplementosMap();
        const cur = map[chave];
        if (!cur) return;
        // Vale para os dois tipos: aceito pelo T-ARC = ENFILEIRADO. O rastro continua no
        // mapa (é ele que segura o valor novo na tela) e só sai quando a execução do
        // servidor vier com a resposta editada — ver reconciliarChecklistComplementos.
        map[chave] = { ...cur, offline: false, syncedEm: Date.now() };
        await AsyncStorage.setItem(
          LOCAL_CHECKLIST_COMPL_KEY,
          JSON.stringify(map),
        );
      });
    } catch (e) {
      console.warn("[Sync] Erro marcando complemento enviado:", e);
    }
  },

  /**
   * Complemento confirmado sai do mapa: reconhecemos pela presença, na execução do
   * servidor, de resposta para alguma das perguntas que ESTE complemento enviou.
   * (Não dá para casar por tempo — o complemento não altera o tempo_inicio.)
   */
  reconciliarChecklistComplementos: async (execucoesServidor: any[]) => {
    try {
      const servidor = Array.isArray(execucoesServidor)
        ? execucoesServidor
        : [];
      if (servidor.length === 0) return;

      const normalizarTexto = (v: any) =>
        String(v ?? "")
          .trim()
          .toUpperCase();

      const respondidasPorExecucao = new Map<string, Set<number>>();
      // pid → textos que o servidor tem para aquela pergunta (uma pergunta pode ter mais
      // de uma linha). É por aqui que a EDIÇÃO é reconhecida: o servidor passar a
      // devolver o texto novo é a única prova de que o T-Consumer gravou.
      const textosPorExecucao = new Map<string, Map<string, Set<string>>>();
      for (const e of servidor) {
        const id = String(e?.id ?? "");
        if (!id) continue;
        const ids = new Set<number>();
        const textos = new Map<string, Set<string>>();
        for (const r of Array.isArray(e?.respostas) ? e.respostas : []) {
          const pid = Number(r?.perguntaId);
          if (Number.isFinite(pid)) ids.add(pid);
          const chavePid = String(r?.perguntaId ?? "");
          if (!chavePid) continue;
          const atual = textos.get(chavePid) ?? new Set<string>();
          atual.add(normalizarTexto(r?.resposta));
          textos.set(chavePid, atual);
        }
        respondidasPorExecucao.set(id, ids);
        textosPorExecucao.set(id, textos);
      }

      await comMapaLock(async () => {
        const map = await SyncService.getLocalChecklistComplementosMap();
        let mudou = false;
        for (const [chave, compl] of Object.entries(map || {})) {
          // EDIÇÃO não se reconhece por pergunta respondida — o servidor JÁ tinha resposta
          // para todas elas ANTES da edição, e essa regra daria positivo na hora, jogando
          // fora o valor novo que a tela está segurando. A prova aqui é o CONTEÚDO: o
          // servidor devolver o texto editado.
          if ((compl as any)?.tipo === "EDICAO") {
            const idade = Date.now() - (Number((compl as any)?.timestamp) || 0);
            // Teto de idade: ação descartada não pode deixar valor local preso na tela.
            if (idade > TETO_EDICAO_PENDENTE_MS) {
              delete map[chave];
              mudou = true;
              continue;
            }
            const textos = textosPorExecucao.get(chave);
            if (!textos) continue; // execução fora da janela do histórico
            const editadas = ((compl as any)?.respostas ?? []).filter(
              (r: any) => !r?.Foto && normalizarTexto(r?.Resposta) !== "",
            );
            // Edição só de foto não tem texto a conferir: some quando sai da fila, já que
            // não há valor local para segurar na tela.
            if (editadas.length === 0) {
              if (!(compl as any)?.offline) {
                delete map[chave];
                mudou = true;
              }
              continue;
            }
            const refletiu = editadas.every((r: any) =>
              textos
                .get(String(Number(r?.PerguntaId) || 0))
                ?.has(normalizarTexto(r?.Resposta)),
            );
            if (refletiu) {
              delete map[chave];
              mudou = true;
            }
            continue;
          }
          const respondidas = respondidasPorExecucao.get(chave);
          if (!respondidas) continue; // execução fora da janela do histórico
          const chegou = ((compl as any)?.perguntaIds ?? []).some((p: number) =>
            respondidas.has(Number(p)),
          );
          if (chegou) {
            delete map[chave];
            mudou = true;
          }
        }
        if (mudou) {
          await AsyncStorage.setItem(
            LOCAL_CHECKLIST_COMPL_KEY,
            JSON.stringify(map),
          );
        }
      });
    } catch (e) {
      console.warn("[Sync] Erro reconciliando complementos de checklist:", e);
    }
  },

  /**
   * Envia o complemento de uma execução existente.
   *
   * ATENÇÃO ao contrato do T-Consumer: com `Id` preenchido ele INATIVA todas as respostas
   * da execução e regrava só o que vier no payload. Por isso `Respostas` tem que trazer as
   * respostas ORIGINAIS somadas às do complemento — mandar só o complemento apagaria a
   * execução inteira. Quem monta essa união é o chamador; aqui só registramos o rastro.
   */
  submitChecklistComplemento: async (
    payload: any,
    meta: { perguntaIds: number[]; tipo?: "COMPLEMENTO" | "EDICAO" },
  ): Promise<{ success: boolean; offline: boolean }> => {
    const idExecucao = Number(payload?.Id) || 0;
    if (!idExecucao) {
      throw new Error(
        "submitChecklistComplemento: Id da execução é obrigatório (sem ele o backend criaria uma execução nova).",
      );
    }
    const hashTenant = await tenantHashSessaoAtual().catch(() => "");

    await comMapaLock(async () => {
      const map = await SyncService.getLocalChecklistComplementosMap();
      map[String(idExecucao)] = {
        idExecucao,
        hash: hashTenant || undefined,
        perguntaIds: meta.perguntaIds,
        tipo: meta.tipo ?? "COMPLEMENTO",
        respostas: (Array.isArray(payload?.Respostas)
          ? payload.Respostas
          : []
        ).map((r: any) => ({
          PerguntaId: Number(r?.PerguntaId) || 0,
          Resposta: r?.Resposta == null ? "" : String(r.Resposta),
          Foto: r?.Foto ? String(r.Foto) : undefined,
        })),
        offline: true,
        timestamp: Date.now(),
      };
      await AsyncStorage.setItem(
        LOCAL_CHECKLIST_COMPL_KEY,
        JSON.stringify(map),
      );
    });

    const resultado = await SyncService.checklistSalvar({
      ...payload,
      __localComplementoId: String(idExecucao),
    });

    if (resultado?.success && !resultado?.offline) {
      await SyncService.markLocalChecklistComplementoEnviado(idExecucao);
    }

    return {
      success: !!resultado?.success,
      offline: !!resultado?.offline,
    };
  },

  /**
   * Envia uma execução de checklist AVULSA (gatilho livre / ponto eletrônico) já
   * registrando-a no mapa local — offline-first: a tela mostra a execução na hora, com
   * 1 check, e o selo evolui sozinho conforme o Sync drena.
   *
   * Reaproveita o checklistSalvar (upload de assets, fila, replay) e só acrescenta o
   * rastro local, que Ronda e O.S. não têm porque já se apoiam nos próprios espelhos.
   */
  submitChecklistExecucao: async (
    payload: any,
    meta: {
      checklistNome: string;
      origem: "LIVRE" | "PONTO";
      tipoBatida?: string | null;
    },
  ): Promise<{ success: boolean; offline: boolean; localId: string }> => {
    const localId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const hashTenant = await tenantHashSessaoAtual().catch(() => "");

    // POSTO do executor, carimbado AQUI no instante da execução (persiste na fila; o
    // valor não muda se a sincronização demorar). Regra:
    //  1. ponto registrado nas últimas 24h → o posto dele (rastro local, offline-first);
    //  2. sem ponto na janela → a LOTAÇÃO do colaborador no tenant (id_adm_cliente do
    //     DataContent — quem está de folga/sem bater conta pelo vínculo);
    //  3. nada → não manda o campo (o consumer grava 0 = "sem posto"; coluna INT).
    if (!payload.IdAdmCliente) {
      const posto = await SyncService.resolverPostoDoUltimoPonto();
      if (posto) {
        payload.IdAdmCliente = posto.id_adm_cliente;
      } else {
        try {
          const ud: any = await readJSONFile("userData.json");
          // ⚠️ No DataContent o campo é `idCliente` (startRepository.js:593 —
          // `result.idCliente = toInt(colab.id_adm_cliente)`), NÃO `id_adm_cliente`.
          // Ler o nome da COLUNA em vez do nome do CAMPO devolvia undefined e o posto
          // nunca era carimbado (gravava 0 no banco).
          const lotacao = Number(
            ud?.idCliente ??
              ud?.done?.idCliente ??
              ud?.id_adm_cliente ??
              ud?.done?.id_adm_cliente,
          );
          if (Number.isFinite(lotacao) && lotacao > 0) {
            payload.IdAdmCliente = lotacao;
          }
        } catch {}
      }
    }

    const localExec: LocalChecklistExecucao = {
      id: localId,
      hash: hashTenant || undefined,
      checklistId: Number(payload?.ChecklistId) || 0,
      checklistNome: meta.checklistNome,
      colaboradorId: Number(payload?.ResponsavelId) || 0,
      tempoInicio: String(payload?.TempoInicio ?? ""),
      origem: meta.origem,
      tipoBatida: meta.tipoBatida ?? null,
      latitude: payload?.Latitude ?? null,
      longitude: payload?.Longitude ?? null,
      respostas: (Array.isArray(payload?.Respostas)
        ? payload.Respostas
        : []
      ).map((r: any) => ({
        perguntaId: Number(r?.PerguntaId) || 0,
        resposta: r?.Resposta == null ? null : String(r.Resposta),
        foto: r?.Foto ?? null,
      })),
      offline: true,
      timestamp: Date.now(),
    };

    // Otimista ANTES do envio: se o app morrer no meio, a execução já está registrada
    // e a fila reenvia — o inverso perderia o rastro.
    await SyncService.setLocalChecklistExecucao(localExec);

    // __localExecId viaja junto para o dreno da fila conseguir marcar ESTA entrada como
    // enviada quando a ação for despachada mais tarde. É removido antes de ir à API.
    const resultado = await SyncService.checklistSalvar({
      ...payload,
      __localExecId: localId,
    });

    if (resultado?.success && !resultado?.offline) {
      await SyncService.markLocalChecklistExecucaoEnviada(localId);
    }

    return {
      success: !!resultado?.success,
      offline: !!resultado?.offline,
      localId,
    };
  },

  getLocalOsStatusMap: async (): Promise<LocalOsStatusMap> => {
    try {
      const raw = await AsyncStorage.getItem(
        await chaveTenant(LOCAL_OS_STATUS_KEY),
      );
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  },

  setLocalOsStatus: async (
    idCronograma: string,
    status: LocalOsStatus,
    data_log: string,
  ) => {
    try {
      await mutarEspelhoTenant(LOCAL_OS_STATUS_KEY, (atual) => {
        const map = atual && typeof atual === "object" ? atual : {};
        map[idCronograma] = { status, data_log, offline: true };
        return map;
      });
    } catch (e) {
      console.warn("[Sync] Erro salvando status local da OS:", e);
    }
  },

  markLocalOsStatusSynced: async (idCronograma: string) => {
    try {
      await mutarEspelhoTenant(LOCAL_OS_STATUS_KEY, (atual) => {
        const map = atual && typeof atual === "object" ? atual : {};
        const cur = map[idCronograma];
        if (!cur) return;
        map[idCronograma] = { ...cur, offline: false };
        return map;
      });
    } catch (e) {
      console.warn("[Sync] Erro marcando status da OS como sincronizado:", e);
    }
  },

  clearLocalOsStatus: async (idCronograma: string) => {
    try {
      await mutarEspelhoTenant(LOCAL_OS_STATUS_KEY, (atual) => {
        const map = atual && typeof atual === "object" ? atual : {};
        delete map[idCronograma];
        return map;
      });
    } catch (e) {
      console.warn("[Sync] Erro removendo status local da OS:", e);
    }
  },

  // ── Espelho de FOTOS de OS (ver LOCAL_OS_FOTOS_KEY) ─────────────────────────

  getLocalOsFotosMap: async (): Promise<LocalOsFotosMap> => {
    try {
      const raw = await AsyncStorage.getItem(
        await chaveTenant(LOCAL_OS_FOTOS_KEY),
      );
      const obj = raw ? reescreverCaminhosAntigos(JSON.parse(raw)) : {};
      return obj && typeof obj === "object" ? obj : {};
    } catch {
      return {};
    }
  },

  /** Upsert por id do asset — replay atualiza a mesma entrada, nunca duplica. */
  registrarFotoLocalOs: async (
    idCronograma: string | number,
    foto: LocalOsFoto,
  ) => {
    try {
      const chave = String(idCronograma ?? "");
      if (!chave || !foto?.id) return;
      await mutarEspelhoTenant(LOCAL_OS_FOTOS_KEY, (atual) => {
        const map: LocalOsFotosMap =
          atual && typeof atual === "object" ? atual : {};
        const lista = Array.isArray(map[chave]) ? map[chave] : [];
        const idx = lista.findIndex((f) => String(f?.id) === String(foto.id));
        if (idx >= 0) lista[idx] = { ...lista[idx], ...foto };
        else lista.push(foto);
        map[chave] = lista;
        return map;
      });
    } catch (e) {
      console.warn("[Sync] Erro registrando foto local de OS:", e);
    }
  },

  /**
   * Remove do espelho as fotos que o SERVIDOR já devolve (casadas por URL — o nome no
   * storage é determinístico pela chave do asset, então a URL é estável). Só aí o
   * arquivo local morre: antes disso ele é a única miniatura que a tela tem.
   */
  reconciliarFotosLocaisOs: async (
    idCronograma: string | number,
    urlsServidor: string[],
  ) => {
    try {
      const chave = String(idCronograma ?? "");
      if (!chave || !Array.isArray(urlsServidor) || urlsServidor.length === 0)
        return;
      const setServidor = new Set(
        urlsServidor.map((u) => String(u ?? "").trim()).filter(Boolean),
      );
      const removidas: LocalOsFoto[] = [];
      await mutarEspelhoTenant(LOCAL_OS_FOTOS_KEY, (atual) => {
        const map: LocalOsFotosMap =
          atual && typeof atual === "object" ? atual : {};
        const lista = Array.isArray(map[chave]) ? map[chave] : [];
        const restantes = lista.filter((f) => {
          const refletida = !!f?.url && setServidor.has(String(f.url).trim());
          if (refletida) removidas.push(f);
          return !refletida;
        });
        if (restantes.length === lista.length) return;
        if (restantes.length === 0) delete map[chave];
        else map[chave] = restantes;
        return map;
      });
      for (const f of removidas) {
        if (f?.uri && String(f.uri).startsWith(OS_PHOTOS_DIR)) {
          try {
            await FileSystem.deleteAsync(f.uri, { idempotent: true });
          } catch {}
        }
      }
    } catch (e) {
      console.warn("[Sync] Erro reconciliando fotos locais de OS:", e);
    }
  },

  /** Migra as fotos do id temporário para o real (mesmo papel do remap de status). */
  remapLocalOsFotosKey: async (
    fromId: string | number,
    toId: string | number,
  ) => {
    try {
      await mutarEspelhoTenant(LOCAL_OS_FOTOS_KEY, (atual) => {
        const map: LocalOsFotosMap =
          atual && typeof atual === "object" ? atual : {};
        const fromKey = String(fromId);
        const toKey = String(toId);
        const cur = map[fromKey];
        if (!cur) return;
        map[toKey] = [...(map[toKey] ?? []), ...cur];
        delete map[fromKey];
        return map;
      });
    } catch (e) {
      console.warn("[Sync] Erro ao remapear fotos locais da OS:", e);
    }
  },

  getLocalOsSobDemandaMap: async (): Promise<LocalOsSobDemandaMap> => {
    try {
      const raw = await AsyncStorage.getItem(
        await chaveTenant(LOCAL_OS_SOBDEMANDA_KEY),
      );
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  },

  setLocalOsSobDemanda: async (item: LocalOsSobDemandaItem) => {
    try {
      await mutarEspelhoTenant(LOCAL_OS_SOBDEMANDA_KEY, (atual) => {
        const map = atual && typeof atual === "object" ? atual : {};
        map[String(item.temp_cronograma_id)] = item;
        return map;
      });
    } catch (e) {
      console.warn("[Sync] Erro salvando O.S. sob demanda local:", e);
    }
  },

  removeLocalOsSobDemanda: async (tempCronogramaId: string | number) => {
    try {
      await mutarEspelhoTenant(LOCAL_OS_SOBDEMANDA_KEY, (atual) => {
        const map = atual && typeof atual === "object" ? atual : {};
        const key = String(tempCronogramaId);
        if (!map[key]) return;
        delete map[key];
        return map;
      });
    } catch (e) {
      console.warn("[Sync] Erro removendo O.S. sob demanda local:", e);
    }
  },

  getLocalOsSobDemandaIdMap: async (): Promise<LocalOsSobDemandaIdMap> => {
    try {
      const raw = await AsyncStorage.getItem(
        await chaveTenant(LOCAL_OS_SOBDEMANDA_IDMAP_KEY),
      );
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  },

  setLocalOsSobDemandaIdMap: async (next: LocalOsSobDemandaIdMap) => {
    try {
      await setItemEscopado(
        LOCAL_OS_SOBDEMANDA_IDMAP_KEY,
        JSON.stringify(next),
      );
    } catch (e) {
      console.warn("[Sync] Erro salvando mapa de IDs da O.S. sob demanda:", e);
    }
  },

  remapLocalOsStatusKey: async (
    fromId: string | number,
    toId: string | number,
  ) => {
    try {
      await mutarEspelhoTenant(LOCAL_OS_STATUS_KEY, (atual) => {
        const map = atual && typeof atual === "object" ? atual : {};
        const fromKey = String(fromId);
        const toKey = String(toId);
        const cur = map[fromKey];
        if (!cur) return;
        map[toKey] = cur;
        delete map[fromKey];
        return map;
      });
    } catch (e) {
      console.warn("[Sync] Erro ao remapear status local da OS:", e);
    }
  },

  /**
   * Marca a batida como enviada. `confirmado` = o próprio servidor mostrou a linha
   * genuína dela (dedup do reconciliador) — aí ela também está no MySQL, não só no Rabbit.
   */
  markLocalPontoSynced: async (
    id: string,
    fotoUrl?: string | null,
    confirmado?: boolean,
  ) => {
    try {
      const atualizado = await comMapaLock(async () => {
        const map = await lerMapaPontosParaEscrita();
        const cur = map[id];
        if (!cur) return null;
        const agora = Date.now();
        map[id] = {
          ...cur,
          offline: false,
          // Carimbo p/ a CURA RETROATIVA: dá o grace de propagação (Rabbit → Consumer →
          // MySQL → cache do /iniciar) antes de julgar que o ponto NÃO está no servidor.
          syncedEm: agora,
          fotoUrl: fotoUrl ?? cur.fotoUrl,
          ...(confirmado ? { confirmadoEm: agora } : {}),
        };
        await gravarMapaPontos(map);
        return map[id];
      });
      if (confirmado) await SyncService._removerBackupPonto(id);
      else if (atualizado)
        await SyncService._atualizarBackupPontoSeExistir(atualizado);
    } catch (e) {
      console.warn("[Sync] Erro marcando ponto como sincronizado:", e);
    }
  },

  /**
   * Faz upload da foto + chama Api.ponto.registrarPonto.
   * Devolve a URL da foto no servidor (ou null se sem foto / falhou).
   */
  _enviarPontoOnline: async (
    ponto: LocalPonto,
    fotoUri: string | null,
    extras: { id_adm_cliente: number; id_adm_empresa: number },
  ): Promise<{
    ok: boolean;
    fotoUrl: string | null;
    erros?: string[];
    offline?: boolean; // API inalcançável (rede/timeout/5xx) → enfileirar e tentar depois
  }> => {
    // Sem a hora do TOQUE a batida não sai: o servidor gravaria a hora do ENVIO (NOW()).
    // Fica na fila como recusada (espera e nova tentativa) — nunca sobe com hora inventada.
    if (!dataLogDaBatida(ponto)) {
      console.warn("[Sync] Batida sem hora legível — retida no aparelho:", ponto?.id);
      return {
        ok: false,
        fotoUrl: null,
        erros: ["Batida sem hora legível"],
        offline: false,
      };
    }
    // Foto que JÁ subiu numa tentativa anterior (o registrarPonto falhou depois do upload,
    // ou é a cura reenviando uma batida "enviada"): reaproveita a URL — sem novo upload e
    // sem cópia órfã no storage. Mesmo critério do envio pelos dados móveis.
    const fotoJaEnviada =
      ponto.fotoUrl && /^https?:\/\//i.test(String(ponto.fotoUrl))
        ? String(ponto.fotoUrl)
        : null;
    let fotoUrl: string | null = fotoJaEnviada;
    // true = a foto subiu NESTA chamada: se o registro falhar, a URL é guardada para a
    // próxima tentativa (ver _guardarFotoUrlDaBatida).
    let fotoSubiuAgora = false;
    const guardarFotoSeSubiuAgora = async () => {
      if (fotoSubiuAgora && fotoUrl) {
        await SyncService._guardarFotoUrlDaBatida(ponto, fotoUrl);
      }
    };

    try {
      const normalizeFileUri = (u: string) => {
        // Foto de antes de uma atualização (iOS muda a pasta do app a cada update): sem
        // isto a batida subia SEM foto, com o arquivo intacto na pasta nova.
        const trimmed = caminhoNoAppAtual(String(u || "").trim());
        if (!trimmed) return "";
        if (trimmed.startsWith("file://") || trimmed.startsWith("content://"))
          return trimmed;
        return `file://${trimmed}`;
      };

      console.log("[Sync] _enviarPontoOnline: iniciado:", {
        localId: ponto.id,
        hasFoto: !!fotoUri,
        id_adm_colaborador: ponto.id_adm_colaborador,
        id_rh_ponto_tipo: ponto.id_rh_ponto_tipo,
        data: ponto.data,
      });

      let fotoToUpload: string | null =
        !fotoJaEnviada && fotoUri ? normalizeFileUri(fotoUri) : null;
      if (fotoJaEnviada) {
        console.log(
          "[Sync] _enviarPontoOnline: reaproveitando a foto já enviada (sem novo upload).",
          { localId: ponto.id },
        );
      }
      if (fotoToUpload) {
        try {
          const info = await FileSystem.getInfoAsync(fotoToUpload);
          if (!info.exists) {
            console.warn(
              "[Sync] _enviarPontoOnline: foto não existe no dispositivo, enviando sem foto:",
              { fotoUri: fotoToUpload },
            );
            fotoToUpload = null;
          }
        } catch (e) {
          console.warn(
            "[Sync] _enviarPontoOnline: falha ao verificar foto, enviando sem foto:",
            e,
          );
          fotoToUpload = null;
        }
      }

      if (fotoToUpload) {
        const safeName = `ponto_${ponto.id_adm_colaborador}_${ponto.id_rh_ponto_tipo}_${Date.now()}.jpg`;
        const [, mes, ano] = (ponto.data || "").split("/");
        const subPasta = mes && ano ? `${mes}_${ano}` : "geral";
        const caminhoPasta = `Pontos/${ponto.id_adm_colaborador}/${subPasta}`;
        const arquivo = {
          uri: fotoToUpload,
          name: safeName,
          type: "image/jpeg",
        } as any;

        const upResp = await Api.geral.upload(arquivo, caminhoPasta, safeName);
        if (!upResp.success) {
          console.warn("[Sync] _enviarPontoOnline: falha no upload:", {
            erros: upResp.erros,
            done: upResp.done,
          });
          return {
            ok: false,
            fotoUrl: null,
            erros: upResp.erros ?? ["Falha no upload da foto"],
            offline: (upResp as any).offline === true,
          };
        }
        const done: any = upResp.done;
        fotoUrl =
          done?.urlAcesso ??
          done?.url ??
          done?.endereco_arquivo ??
          done?.path ??
          null;
        if (!fotoUrl || !/^https?:\/\//i.test(String(fotoUrl))) {
          // Sem URL de verdade: quem respondeu 200 não foi o nosso upload (portal de
          // wi-fi, proxy, página de bloqueio) — trata como rede, não como sucesso.
          return {
            ok: false,
            fotoUrl: null,
            erros: ["Servidor não retornou URL da foto"],
            offline: true,
          };
        }
        fotoSubiuAgora = true;
      }

      const payload = SyncService._montarPayloadPonto(
        ponto,
        extras,
        fotoUrl ?? "",
      );

      console.log(
        "[Sync] _enviarPontoOnline: payload:",
        JSON.stringify(payload),
      );

      const temFoto = !!fotoUrl;
      const acao = temFoto ? "registrarPonto" : "registrarPontoSemFoto";
      const resp = temFoto
        ? await Api.ponto.registrarPonto(payload)
        : await Api.ponto.registrarPontoSemFoto(payload);

      if (!resp.success) {
        await guardarFotoSeSubiuAgora();
        return {
          ok: false,
          fotoUrl,
          erros: resp.erros ?? [`Servidor recusou ${acao}`],
          offline: (resp as any).offline === true,
        };
      }
      if (!pontoFoiEnfileirado(resp.done)) {
        // 2xx SEM o recibo do T-ARC (`{ enfileirado: true }`): a resposta não veio da
        // nossa API. Marcar "enviado" aqui era o 2 checks fantasma de rede — fica na fila.
        console.warn(
          "[Sync] _enviarPontoOnline: 2xx sem 'enfileirado' — tratado como falha de rede.",
        );
        await guardarFotoSeSubiuAgora();
        return {
          ok: false,
          fotoUrl,
          erros: ["Resposta inesperada ao registrar o ponto"],
          offline: true,
        };
      }
      return { ok: true, fotoUrl };
    } catch (e: any) {
      console.warn("[Sync] _enviarPontoOnline: erro inesperado:", e);
      await guardarFotoSeSubiuAgora().catch(() => {});
      // Exceção inesperada (rede/parse) → trata como API offline: enfileira e tenta depois.
      return {
        ok: false,
        fotoUrl,
        erros: [String(e?.message ?? e)],
        offline: true,
      };
    }
  },

  /**
   * A foto da batida JÁ subiu, mas o envio não terminou (o registrarPonto falhou ou caiu
   * depois do upload). Guarda a URL onde a batida estiver — mapa e cópia em arquivo; a
   * ação da fila só quando o mapa não tem a batida — para a próxima tentativa, e a cura,
   * reaproveitarem a foto. Cada upload é uma ida ao storage: sem isto, cada nova
   * tentativa subia a mesma foto de novo e deixava uma cópia órfã (análise de carga de
   * 30/09/2026). Nunca lança.
   */
  _guardarFotoUrlDaBatida: async (
    ponto: LocalPonto,
    fotoUrl: string,
  ): Promise<void> => {
    try {
      const doMapa = await comMapaLock(async () => {
        const mapa = await lerMapaPontosParaEscrita();
        const cur = mapa[ponto.id];
        if (!cur) return null;
        if (cur.fotoUrl !== fotoUrl) {
          mapa[ponto.id] = { ...cur, fotoUrl };
          await gravarMapaPontos(mapa);
        }
        return mapa[ponto.id];
      }).catch(() => null);
      await SyncService._atualizarBackupPontoSeExistir({
        ...(doMapa ?? ponto),
        fotoUrl,
      });
      if (doMapa) return;
      // Fora do mapa (a gravação falhou): quem leva a batida é o snapshot da ação na fila.
      await comFilaLock(async () => {
        const fila = await lerFilaParaEscrita();
        let mudou = false;
        for (const a of fila) {
          if (a?.type !== "PONTO") continue;
          if (String((a as any)?.data?.localId ?? "") !== String(ponto.id)) continue;
          const snap = (a as any)?.data?.ponto;
          if (!snap || snap.fotoUrl === fotoUrl) continue;
          (a as any).data = { ...(a as any).data, ponto: { ...snap, fotoUrl } };
          mudou = true;
        }
        if (mudou) {
          await gravarFila(fila);
        }
      });
    } catch (e) {
      console.warn("[Sync] Não foi possível guardar a URL da foto já enviada:", e);
    }
  },

  /**
   * Corpo do `Ponto/registrarPonto[SemFoto]` — o MESMO para o envio normal e para o
   * envio pelos dados móveis. `foto` = URL já enviada, base64 (o T-ARC sobe para o
   * storage) ou "".
   */
  _montarPayloadPonto: (
    ponto: LocalPonto,
    extras: { id_adm_cliente: number; id_adm_empresa: number },
    foto: string,
  ) => {
    // Hora e competência DA BATIDA (as do toque), nunca as do envio — ver dataLogDaBatida.
    const dataLog = dataLogDaBatida(ponto);
    const dataCompetenciaSql = competenciaSqlDaBatida(ponto, dataLog);
    return {
        id_adm_colaborador: ponto.id_adm_colaborador,
        id_adm_cliente: extras.id_adm_cliente,
        id_adm_empresa: extras.id_adm_empresa,
        id_rh_ponto_tipo: ponto.id_rh_ponto_tipo,
        data_competencia: dataCompetenciaSql,
        data_log: dataLog,
        latitude: ponto.latitude ?? "",
        longitude: ponto.longitude ?? "",
        latitude_ponto: ponto.latitude_ponto ?? ponto.latitude ?? "",
        longitude_ponto: ponto.longitude_ponto ?? ponto.longitude ?? "",
        latitude_lotacao: ponto.latitude_lotacao ?? "",
        longitude_lotacao: ponto.longitude_lotacao ?? "",
        distancia: ponto.distancia ?? "",
        foto,
        // Carimbo do TENANT dono da batida: o T-ARC valida contra o claim c_hash
        // do token e REJEITA (409) se divergir — trava server-side contra o
        // vazamento cross-tenant (batida de uma empresa enviada na sessão de
        // outra). "" = legado, o servidor faz fail-open.
        hash: String(ponto.hash ?? "")
          .trim()
          .toUpperCase(),
        tipo: "C",
        origem: "M",
        expressao: ponto.expressao ?? "", // expressão facial classificada da foto
        registra_almoco: ponto.registra_almoco,

        // ─── AUDITORIA DO RELÓGIO (observação — o `data_log` acima NÃO depende disto) ───
        // O app carimba a hora do APARELHO e manda junto o que o relógio confiável dizia,
        // para o backend guardar e o administrativo montar relatório. Quem decide o que
        // fazer com a divergência é a folha, não o app — e mudar esse critério passa a
        // não depender de release de loja.
        //
        // ⚠️ O T-ARC precisa aceitar e o T-Consumer precisa GRAVAR estes campos; até lá
        // eles são ignorados no servidor. Ver o INSERT em
        // T-Consumer/Repositories/PontoRepository.cs.
        // ⚠️ TUDO STRING, inclusive os numéricos. O `RhPontosData` do T-Consumer é todo
        // `string?`, e o System.Text.Json ESTOURA ao ler um número JSON num `string?` —
        // a batida iria inteira para o parking-lot `t-arc.ponto.dead`. Campo ausente vai
        // como "" e o SQL converte com NULLIF.
        hora_confiavel: ponto.horaConfiavel ?? "",
        fonte_hora: ponto.fonteHora ?? "",
        divergencia_seg:
          ponto.divergenciaSeg == null ? "" : String(ponto.divergenciaSeg),
        tipo_divergencia: ponto.tipoDivergencia ?? "",
        utc_offset_min:
          ponto.utcOffsetMin == null ? "" : String(ponto.utcOffsetMin),
        coerencia_fuso: ponto.coerenciaFuso ?? "",
        // Quanto tempo a batida ficou na FILA, medido pelo relógio monotônico —
        // calculado AGORA, no envio, não na batida. É o que permite ao servidor
        // reconstruir a hora real de uma batida offline (`agora_servidor − idade`) sem
        // depender de nada que o aparelho afirme. "" quando não dá para afirmar
        // (sem módulo nativo, ou houve reboot entre a batida e o envio).
        idade_ms: (() => {
          const i = RelogioConfiavel.idadeMs(ponto.uptimeMs);
          return i == null ? "" : String(Math.round(i));
        })(),
      };
  },

  /**
   * RECUPERAÇÃO + DEDUP de pontos pendentes, a partir do MAPA local
   * (@TirvuMais:local_pontos — a fonte da verdade do que o colaborador registrou). Roda no
   * INÍCIO do processOfflineQueue (sob a guarda isProcessingQueue), ANTES do dreno, e faz:
   *
   *  (1) DEDUP vs SERVIDOR — se um ponto pendente (offline:true) já consta em `ultimosPontos`
   *      (cache do /iniciar, que INCLUI pontos batidos em OUTRO aparelho), casando por
   *      DIA-de-`data_competencia` + `id_rh_ponto_tipo`, ele é marcado como sincronizado e
   *      REMOVIDO da fila — NÃO é reenviado (evita duplicar no servidor). NÃO casamos por HORA
   *      de propósito: o MESMO ponto lógico tem hora de captura diferente em cada aparelho, então
   *      casar por hora DERROTARIA o dedup. Um ACERTO no cache é confiável (o servidor confirmou
   *      que tem aquele competência+tipo); um MISS é inconclusivo → nesse caso ENVIAMOS (nunca
   *      perdemos batida — perder ponto é pior que duplicar).
   *  (2) ÓRFÃOS — todo pendente que NÃO está no servidor e NÃO tem mais ação na fila (sumiu por
   *      um write não-atômico da fila) é REENFILEIRADO, e o dreno abaixo o envia. É isso que
   *      recupera o backlog de vários dias parado (visível em Justificativas mas nunca enviado).
   *
   * Best-effort: o cache pode estar defasado (outro aparelho acabou de bater e este ainda não
   * refez o /iniciar) → a idempotência DEFINITIVA (colaborador + data_competencia + tipo) TEM que
   * ser garantida no BACKEND. Idempotente e sob a trava do dreno → sem envio dobrado.
   * Premissa: 1 ponto por (competência + tipo) — mesma convenção do applyLocalPontos.
   */
  _reconciliarPontosPendentes: async (): Promise<{
    reenfileirados: number;
    jaNoServidor: number;
    curados: number;
    /** Batidas que só existem na cópia em arquivo e NÃO voltaram ao mapa nem à fila
     *  (armazenamento quebrado) — o dreno as envia direto do arquivo. */
    paraDespachoDireto: LocalPonto[];
  }> => {
    try {
      const map = await SyncService.getLocalPontosMap();
      // Cópias em arquivo entram mesmo com o mapa VAZIO: é justamente o caso em que a
      // gravação no mapa falhou (ou ele foi apagado) e só o arquivo guardou a batida.
      const backups = await SyncService._lerBackupsPonto();
      if (Object.keys(map).length === 0 && backups.length === 0)
        return {
          reenfileirados: 0,
          jaNoServidor: 0,
          curados: 0,
          paraDespachoDireto: [],
        };

      // Datas e "o que o servidor tem" vêm do módulo — fonte ÚNICA, a mesma da conferência
      // local pós-/iniciar (ver lerBatidasGenuinasDoCache: só batida GENUÍNA conta).
      const diaDe = diaDaBatida;
      const ordDe = ordemDoDia;
      // MISS continua inconclusivo → ENVIA (perder ponto é pior que duplicar; o INSERT
      // do T-Consumer é idempotente por ODKU — reenvio apenas converge). HIT só vale para
      // ESTA batida (dia + tipo + hora próxima) — ver servidorTemEstaBatida.
      const cacheSrv = await lerBatidasGenuinasDoCache();
      const colabAtual = cacheSrv.colab; // colaborador da SESSÃO (guarda cross-tenant da cura)
      const empresaAtual = cacheSrv.empresa;
      const clienteAtual = cacheSrv.cliente;

      // (0) CURA RETROATIVA dos "2 checks fantasma": ponto marcado sincronizado
      // (offline:false) SEM batida genuína correspondente no servidor = o "sucesso" foi
      // um 200 do T-ARC (enfileirado) cuja mensagem o T-Consumer descartou, ou um dedup
      // antigo contra linha de edição/injeção. Reverte para pendente — os passos (1)/(2)
      // abaixo decidem (reenvio é seguro: ODKU no consumer converge, nunca duplica).
      // Guardas anti-falso-positivo/anti-loop: ver CURA_* e ehCandidataACura (topo do
      // módulo) — cache fresco, janela do LIMIT 50, grace de 10 min, 3 curas com 6 h entre
      // elas, tenant carimbado, e nunca batida já CONFIRMADA.
      // Hash do tenant da SESSÃO atual (claim do token — não defasa no trocarEmpresa) —
      // guarda cross-tenant da cura E dos passos (1)/(2): ids numéricos colidem entre
      // bancos; só o hash identifica a empresa dona da batida.
      const hashSessao = await tenantHashSessaoAtual().catch(() => "");

      // ADOÇÃO DE LEGADO (uma vez por ponto): batidas gravadas por versões ANTERIORES do
      // app não têm o carimbo `hash` — e a cura é FAIL-CLOSED (sem hash, nunca cura), o
      // que deixaria os fantasmas PRÉ-EXISTENTES ("2 checks" sem linha no servidor)
      // presos para sempre. Adota o legado para o tenant da sessão quando os TRÊS ids
      // batem (colaborador + empresa + cliente) — colisão TRIPLA entre bancos distintos
      // é improvável (sequências auto-increment independentes). A adoção só habilita
      // cura/dedup; o ENVIO de legado sem hash já era permitido antes (fail-open), então
      // isto não amplia o risco cross-tenant.
      if (hashSessao && colabAtual > 0 && empresaAtual > 0) {
        try {
          const idsAdotar = Object.values(map)
            .filter(
              (lp) =>
                lp &&
                !lp.hash &&
                Number(lp.id_adm_colaborador) === colabAtual &&
                Number(lp.id_adm_empresa) === empresaAtual &&
                (clienteAtual <= 0 ||
                  Number(lp.id_adm_cliente) === clienteAtual),
            )
            .map((lp) => String(lp.id));
          if (idsAdotar.length > 0) {
            await comMapaLock(async () => {
              const fresco = await lerMapaPontosParaEscrita();
              let mudou = 0;
              for (const id of idsAdotar) {
                const cur = fresco[id];
                if (!cur || cur.hash) continue;
                fresco[id] = { ...cur, hash: hashSessao };
                mudou++;
              }
              if (mudou > 0) {
                await gravarMapaPontos(fresco);
                // warn (não log): o babel de produção remove console.log — este é
                // log FORENSE de campo (diagnóstico dos fantasmas legados).
                console.warn(
                  `[Sync] Adoção de legado: ${mudou} ponto(s) sem hash carimbados para o tenant atual.`,
                );
              }
            });
            // Reflete no snapshot em memória: a cura/pendentes DESTE ciclo já veem o hash.
            for (const id of idsAdotar) {
              if (map[id] && !map[id].hash)
                map[id] = { ...map[id], hash: hashSessao };
            }
          }
        } catch (e) {
          console.warn("[Sync] Adoção de legado: erro (ignorado):", e);
        }
      }

      let curados = 0;
      let mapAtual = map; // pós-cura, os PENDENTES são decididos sobre o estado FRESCO
      try {
        const agora = Date.now();
        // Guardas (cache fresco, janela, grace, tetos, tenant, já confirmada) em
        // curaPodeAgir/ehCandidataACura — o MESMO critério que decide, logo após o
        // /iniciar, se vale gastar rede com um dreno (_temCandidatasACura).
        const fetchedAtMs = await lerUserDataFetchedAt();
        if (curaPodeAgir(cacheSrv, hashSessao, fetchedAtMs, agora)) {
          // 1ª passada: só COLETA os ids a curar (o snapshot `map` é velho — entre a
          // leitura lá em cima e aqui há awaits longos, ex.: parse do userData.json).
          const idsACurar: string[] = [];
          for (const lp of Object.values(map)) {
            if (!ehCandidataACura(lp, cacheSrv, hashSessao, agora)) continue;
            idsACurar.push(String(lp.id));
            if (idsACurar.length >= CURA_MAX_POR_CICLO) break; // rajada não — espalha nos próximos drenos
          }
          // 2ª passada: aplica as mutações sobre o mapa FRESCO, sob o lock do mapa —
          // persistir o snapshot inteiro apagaria uma batida gravada em paralelo pelo
          // submitPonto (last-write-wins), e batida perdida do mapa é irrecuperável.
          if (idsACurar.length > 0) {
            mapAtual = await comMapaLock(async () => {
              const fresco = await lerMapaPontosParaEscrita();
              for (const id of idsACurar) {
                const cur = fresco[id];
                if (!cur || cur.offline !== false) continue; // mudou nesse meio-tempo → não mexe
                fresco[id] = {
                  ...cur,
                  offline: true,
                  curas: (cur.curas ?? 0) + 1,
                  ultimaCuraEm: agora,
                };
                curados++;
              }
              if (curados > 0) {
                await gravarMapaPontos(fresco);
              }
              return fresco;
            });
            if (curados > 0) {
              console.warn(
                `[Sync] CURA RETROATIVA: ${curados} ponto(s) marcados sincronizados SEM registro genuíno no servidor — revertidos para pendente (serão reenviados).`,
              );
            }
          }
        }
      } catch (e) {
        console.warn("[Sync] Cura retroativa: erro (ignorado):", e);
      }

      // CONFIRMAÇÃO: batida enviada (offline:false) cuja linha GENUÍNA voltou no /iniciar
      // está no MySQL, não só no Rabbit. Carimba `confirmadoEm` — sai das candidatas a
      // cura (ver ehCandidataACura) e a cópia em arquivo pode ir embora. Mesmo critério da
      // conferência local pós-/iniciar (_confirmarPontosPeloCache).
      try {
        const idsConfirmar = Object.values(mapAtual)
          .filter((lp) => podeConfirmarBatida(lp, cacheSrv, hashSessao))
          .map((lp) => String(lp.id));
        if (idsConfirmar.length > 0) {
          const agoraConf = Date.now();
          mapAtual = await comMapaLock(async () => {
            const fresco = await lerMapaPontosParaEscrita();
            let mudou = 0;
            for (const id of idsConfirmar) {
              const cur = fresco[id];
              if (!cur || cur.offline !== false || cur.confirmadoEm) continue;
              fresco[id] = { ...cur, confirmadoEm: agoraConf };
              mudou++;
            }
            if (mudou > 0) {
              await gravarMapaPontos(fresco);
            }
            return fresco;
          });
          for (const id of idsConfirmar)
            await SyncService._removerBackupPonto(id);
        }
      } catch (e) {
        console.warn("[Sync] Confirmação de batidas: erro (ignorado):", e);
      }

      // CÓPIAS EM ARQUIVO (ver _gravarBackupPonto): decide o destino de cada uma.
      //  - está no mapa → o mapa conduz; a cópia sai quando a batida for confirmada (ou,
      //    enviada e nunca confirmada, depois de 15 dias — o mapa segue como registro);
      //  - fora do mapa e o servidor já tem → cumpriu o papel, sai;
      //  - fora do mapa, enviada direto do arquivo → espera a confirmação;
      //  - fora do mapa e PENDENTE → volta ao mapa (o passo 2 reenfileira); se nem o mapa
      //    aceitar escrita, vai para o envio direto do arquivo.
      // Cópia de OUTRA empresa fica intocada (ids colidem entre bancos — ver hash).
      let paraDespachoDireto: LocalPonto[] = [];
      if (backups.length > 0) {
        try {
          const agoraB = Date.now();
          const QUINZE_DIAS_MS = 15 * 24 * 60 * 60 * 1000;
          const remover: string[] = [];
          const restaurar: LocalPonto[] = [];
          for (const b of backups) {
            const th = String(b.hash ?? "")
              .trim()
              .toUpperCase();
            if (th && hashSessao && th !== hashSessao) continue;
            const noMapa = mapAtual[b.id];
            if (noMapa) {
              if (
                noMapa.confirmadoEm ||
                (noMapa.offline === false &&
                  noMapa.syncedEm != null &&
                  agoraB - noMapa.syncedEm > QUINZE_DIAS_MS)
              ) {
                remover.push(String(b.id));
              }
              continue;
            }
            const noServidor =
              servidorTemEstaBatida(b, cacheSrv) &&
              (colabAtual <= 0 ||
                Number(b.id_adm_colaborador) === colabAtual);
            if (noServidor) {
              remover.push(String(b.id));
              continue;
            }
            if (b.offline === false) {
              if (b.syncedEm != null && agoraB - b.syncedEm > QUINZE_DIAS_MS)
                remover.push(String(b.id));
              continue;
            }
            restaurar.push(b);
          }
          if (restaurar.length > 0) {
            try {
              mapAtual = await comMapaLock(async () => {
                const fresco = await lerMapaPontosParaEscrita();
                for (const b of restaurar) {
                  if (!fresco[b.id]) fresco[b.id] = { ...b, offline: true };
                }
                await gravarMapaPontos(fresco);
                return fresco;
              });
              console.warn(
                `[Sync] ${restaurar.length} batida(s) recuperada(s) da cópia em arquivo para o mapa.`,
              );
            } catch (e) {
              console.warn(
                "[Sync] Cópias em arquivo não voltaram ao mapa — serão enviadas direto do arquivo:",
                e,
              );
              paraDespachoDireto = restaurar;
            }
          }
          for (const id of remover) await SyncService._removerBackupPonto(id);
        } catch (e) {
          console.warn("[Sync] Cópias em arquivo: erro (ignorado):", e);
        }
      }
      // Envio direto só do que também NÃO está na fila: a ação da fila envia pelo
      // snapshot dela — mandar pelos dois lados dobraria o envio.
      if (paraDespachoDireto.length > 0) {
        try {
          const fila = await lerFilaSoLeitura();
          const naFila = new Set(
            fila
              .filter((a: any) => a?.type === "PONTO")
              .map((a: any) => String(a?.data?.localId || "")),
          );
          paraDespachoDireto = paraDespachoDireto.filter(
            (b) => !naFila.has(String(b.id)),
          );
        } catch {}
      }

      const pendentes = Object.values(mapAtual).filter(
        (lp) => lp && lp.offline === true,
      );
      if (pendentes.length === 0)
        return {
          reenfileirados: 0,
          jaNoServidor: 0,
          curados,
          paraDespachoDireto,
        };

      // Reescrita da fila sob o lock: sem ele, um enqueue concorrente (batida nova
      // durante a reconciliação) era apagado pelo setItem do snapshot (last-write-wins).
      const resultado = await comFilaLock(async () => {
        let actions = await lerFilaParaEscrita();

        // (1) DEDUP: pendentes que o servidor JÁ tem (batida GENUÍNA) → marca synced e
        // tira da fila (não reenvia — evita duplicar no servidor).
        const jaNoServidorIds = new Set<string>();
        for (const lp of pendentes) {
          // Ponto carimbado com hash de OUTRO tenant: o ultimosPontos da sessão atual
          // não diz nada sobre ele — nem confirma (dedup) nem envia (passo 2).
          if (lp.hash && hashSessao && hashNormalizado(lp.hash) !== hashSessao) continue;
          // ESTA batida (dia + tipo + hora próxima) — não basta outra com a mesma chave:
          // ver servidorTemEstaBatida.
          if (servidorTemEstaBatida(lp, cacheSrv)) {
            // Linha genuína no servidor = enviada E confirmada no MySQL.
            await SyncService.markLocalPontoSynced(lp.id, undefined, true);
            jaNoServidorIds.add(String(lp.id));
          }
        }
        if (jaNoServidorIds.size > 0) {
          actions = actions.filter(
            (a) =>
              !(
                a?.type === "PONTO" &&
                jaNoServidorIds.has(String(a?.data?.localId || ""))
              ),
          );
        }

        // (2) ÓRFÃOS: reenfileira os pendentes restantes que não têm ação na fila.
        const localIdsNaFila = new Set(
          actions
            .filter((a) => a?.type === "PONTO")
            .map((a) => String(a?.data?.localId || ""))
            .filter(Boolean),
        );
        let reenfileirados = 0;
        for (const lp of pendentes) {
          if (jaNoServidorIds.has(String(lp.id))) continue; // resolvido no passo (1)
          if (localIdsNaFila.has(String(lp.id))) continue; // já tem ação na fila
          // NUNCA reenfileirar batida carimbada com hash de OUTRO tenant: o envio sairia
          // com o Bearer da sessão atual → INSERT no banco errado (cross-tenant). Ela
          // volta a ser elegível quando o usuário logar de novo na empresa dona.
          if (lp.hash && hashSessao && hashNormalizado(lp.hash) !== hashSessao) continue;
          actions.push({
            id: Math.random().toString(36).substr(2, 9),
            type: "PONTO",
            data: {
              localId: lp.id,
              ponto: lp,
              id_adm_cliente: lp.id_adm_cliente,
              id_adm_empresa: lp.id_adm_empresa,
            },
            timestamp: lp.timestamp ?? Date.now(),
          });
          reenfileirados++;
        }

        if (reenfileirados > 0 || jaNoServidorIds.size > 0) {
          await gravarFila(actions);
          console.log(
            `[Sync] Reconciliação de pontos: ${reenfileirados} órfão(s) reenfileirado(s), ${jaNoServidorIds.size} já no servidor (marcado synced) — de ${pendentes.length} pendente(s).`,
          );
        }
        return { reenfileirados, jaNoServidor: jaNoServidorIds.size };
      });

      // GC do MAPA: ponto SINCRONIZADO (offline:false) com competência antiga (>60 dias)
      // sai do mapa — e a foto persistida é liberada pelo removeLocalPonto. Motivos:
      //  (a) o mapa não pode crescer sem limite (4-8 batidas/dia p/ sempre);
      //  (b) ponto fora da janela do /iniciar nunca mais "casa" com nada — sem GC ele
      //      seria REAVALIADO eternamente em todo ciclo (cura/dedup mais caros à toa);
      //  (c) entrada removida = zero chance de algum dia virar reenvio de dado já gravado.
      // PENDENTE (offline:true) JAMAIS é coletado — é a fonte da verdade do que falta subir.
      try {
        const agora = Date.now();
        const GC_IDADE_DIAS = 60;
        const GC_MAX_POR_CICLO = 40;
        const corte = new Date(agora - GC_IDADE_DIAS * 24 * 60 * 60 * 1000);
        const corteOrd = Number(
          `${corte.getFullYear()}${String(corte.getMonth() + 1).padStart(2, "0")}${String(corte.getDate()).padStart(2, "0")}`,
        );
        const idsGC: string[] = [];
        for (const lp of Object.values(mapAtual)) {
          if (!lp || lp.offline !== false) continue;
          const ord = ordDe(diaDe(lp.data));
          if (ord > 0 && ord < corteOrd) {
            idsGC.push(String(lp.id));
            if (idsGC.length >= GC_MAX_POR_CICLO) break;
          }
        }
        for (const id of idsGC) await SyncService.removeLocalPonto(id);
        if (idsGC.length > 0) {
          console.log(
            `[Sync] GC do mapa de pontos: ${idsGC.length} sincronizado(s) com mais de ${GC_IDADE_DIAS} dias removido(s).`,
          );
        }
      } catch (e) {
        console.warn("[Sync] GC do mapa de pontos: erro (ignorado):", e);
      }

      return { ...resultado, curados, paraDespachoDireto };
    } catch (e) {
      console.warn("[Sync] _reconciliarPontosPendentes: erro:", e);
      return {
        reenfileirados: 0,
        jaNoServidor: 0,
        curados: 0,
        paraDespachoDireto: [],
      };
    }
  },

  /**
   * Submete um ponto: tenta online (foto via upload + Api.ponto.registrarPonto).
   * Sempre persiste localmente (otimista) — se offline ou falhar, fica em fila.
   * Devolve `localPonto` pra UI atualizar imediatamente.
   */
  submitPonto: async (
    payload: PontoPayload,
    opts?: { aguardarEnvio?: boolean },
  ): Promise<{
    success: boolean;
    offline: boolean;
    /** `false` = a batida NÃO ficou guardada em lugar nenhum (mapa, arquivo e fila
     *  falharam) e não chegou ao servidor — a tela TEM de dizer que falhou. */
    salvo: boolean;
    localPonto: LocalPonto;
    erros?: string[];
  }> => {
    // Persiste a foto antes de tudo (caso vá offline)
    const persistedFotoUri = payload.fotoUri
      ? await SyncService.persistPontoPhoto(payload.fotoUri)
      : null;

    // HORA DA BATIDA = RELÓGIO DO APARELHO. Comportamento histórico, e é o que a folha
    // lê. NÃO troque pelo relógio confiável.
    //
    // Já foi tentado o contrário: o `data_log` passou a sair do RelogioConfiavel e um
    // bug na validação de reboot fez batidas serem gravadas com HORAS de atraso em
    // produção. A lição não é "a âncora é ruim" — é que o app não pode ser dono dessa
    // decisão. Aqui ele só OBSERVA: carimba a hora do aparelho, como sempre, e anexa
    // os dados do relógio confiável em campos SEPARADOS. Quem decide o que fazer com a
    // divergência é o backend/RH, com relatório e critério — e mudar esse critério não
    // depende de release de loja.
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    const horaStr = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
    const dataLog = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

    // Leitura do relógio confiável — SÓ para auditoria. Não influencia nada acima.
    const relogio = RelogioConfiavel.estado();
    const dConf = new Date(relogio.ms);
    const horaConfiavel = `${dConf.getFullYear()}-${pad(dConf.getMonth() + 1)}-${pad(dConf.getDate())} ${pad(dConf.getHours())}:${pad(dConf.getMinutes())}:${pad(dConf.getSeconds())}`;

    // Carimbo de TENANT: os ids numéricos (colaborador/empresa/cliente) são auto-increment
    // POR BANCO e COLIDEM entre empresas — só o hash identifica o tenant dono da batida.
    // Fonte = claim do TOKEN (nunca defasa no trocarEmpresa; o cache do /iniciar defasava).
    // É a guarda cross-tenant do DESPACHO, da cura retroativa e do dedup ('' se indisponível).
    const hashTenant = await tenantHashSessaoAtual().catch(() => "");

    const localPonto: LocalPonto = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      id_adm_colaborador: payload.id_adm_colaborador,
      id_rh_ponto_tipo: payload.id_rh_ponto_tipo,
      hash: hashTenant || undefined,
      data: payload.data_competencia,
      hora: horaStr,
      data_log: dataLog,
      id_adm_cliente: payload.id_adm_cliente,
      id_adm_empresa: payload.id_adm_empresa,
      origem: "M",
      fotoUri: persistedFotoUri ?? payload.fotoUri,
      fotoUrl: null,
      latitude: payload.latitude,
      longitude: payload.longitude,
      postoNome: payload.postoNome,
      latitude_ponto: payload.latitude_ponto,
      longitude_ponto: payload.longitude_ponto,
      latitude_lotacao: payload.latitude_lotacao,
      longitude_lotacao: payload.longitude_lotacao,
      distancia: payload.distancia,
      offline: true,
      // Mesma escala do `data_log` (relógio do aparelho) — este timestamp ordena as
      // batidas, e misturar as duas escalas embaralharia a ordem.
      timestamp: now.getTime(),
      // ─── AUDITORIA: só observação, nada aqui altera a hora gravada acima ───
      horaConfiavel,
      uptimeMs: relogio.uptimeMs,
      fonteHora: relogio.fonte,
      horaDispositivo: Date.now(),
      divergenciaSeg: relogio.divergenciaSeg,
      tipoDivergencia: relogio.tipoDivergencia,
      utcOffsetMin: relogio.utcOffsetMin,
      // Testemunha independente do fuso: o GPS da batida. Prioriza a posição REAL do
      // aparelho no momento; cai no ponto/lotação quando a batida não trouxe GPS próprio.
      coerenciaFuso: RelogioConfiavel.coerenciaFusoPosicao(
        payload.longitude ??
          payload.longitude_ponto ??
          payload.longitude_lotacao,
        relogio.utcOffsetMin,
      ),
      expressao: payload.expressao ?? "", // expressão facial classificada da foto
      registra_almoco: payload.registra_almoco ?? "",
    };

    // Salva otimista — UI já mostra. São DOIS lastros independentes: o mapa (AsyncStorage)
    // e a cópia em arquivo. O AsyncStorage do Android tem teto e falha com o celular sem
    // espaço — e essa falha era engolida: a pessoa via "Ponto registrado!" e a batida não
    // existia em lugar nenhum (relato de 30/09/2026).
    const noMapa = await SyncService.setLocalPonto(localPonto);
    const emArquivo = await SyncService._gravarBackupPonto(localPonto);

    // Rastro PERMANENTE do último posto batido (por tenant): o espelho acima é podado
    // quando a batida confirma no servidor, mas quem consome "onde foi o último ponto?"
    // (checklist avulso) precisa da resposta muito depois disso — e offline-first, sem
    // depender de backend. Uma chave pequena, sobrescrita a cada batida.
    await SyncService.registrarUltimoPostoPonto(
      payload.id_adm_cliente,
      payload.postoNome ?? "",
      dataLog,
    );

    // Enfileira e diz se a batida ficou guardada em ALGUM lugar (mapa, arquivo ou fila).
    // Nenhum dos três = a batida não existe: anunciar "registrado" aí é a pior mentira
    // possível num app offline-first — a tela precisa pedir para tentar de novo.
    const enfileirar = async (): Promise<boolean> => {
      const enqueuedId = await SyncService.enqueueOfflineAction("PONTO", {
        localId: localPonto.id,
        ponto: localPonto,
        id_adm_cliente: payload.id_adm_cliente,
        id_adm_empresa: payload.id_adm_empresa,
      });
      const guardada = noMapa || emArquivo || !!enqueuedId;
      if (!guardada) {
        console.error(
          "[Sync] submitPonto: batida NÃO ficou guardada — mapa, arquivo e fila falharam.",
        );
      }
      return guardada;
    };

    // Modo "não esperar o servidor" (ex.: ponto SEM FOTO): apenas otimista — enfileira e
    // retorna NA HORA. O envio online fica a cargo do syncAll/processOfflineQueue em
    // background, então a tela pode redirecionar sem aguardar a confirmação do servidor.
    if (opts?.aguardarEnvio === false) {
      const salvo = await enfileirar();
      return salvo
        ? { success: true, offline: true, salvo, localPonto }
        : {
            success: false,
            offline: true,
            salvo,
            localPonto,
            erros: [ERRO_PONTO_NAO_SALVO],
          };
    }

    try {
      // A NOSSA API, não "qualquer internet": em rede que bloqueia os nossos hosts a
      // tentativa só gastava tempo antes de cair na fila.
      const isConnected = await SyncService.apiAlcancavel();
      if (!isConnected) {
        const salvo = await enfileirar();
        return salvo
          ? { success: true, offline: true, salvo, localPonto }
          : {
              success: false,
              offline: true,
              salvo,
              localPonto,
              erros: [ERRO_PONTO_NAO_SALVO],
            };
      }

      const result = await SyncService._enviarPontoOnline(
        localPonto,
        persistedFotoUri ?? payload.fotoUri,
        {
          id_adm_cliente: payload.id_adm_cliente,
          id_adm_empresa: payload.id_adm_empresa,
        },
      );

      if (!result.ok) {
        // App é OFFLINE-FIRST: QUALQUER falha no envio online (API fora, timeout,
        // erro de servidor) → enfileira e sincroniza depois. Trata como "offline"
        // para a UI NÃO mostrar erro de "falha ao registrar o ponto".
        // A foto pode ter subido antes da falha: o snapshot da fila leva a URL (o mapa e
        // a cópia em arquivo já a receberam em _guardarFotoUrlDaBatida).
        if (result.fotoUrl) localPonto.fotoUrl = result.fotoUrl;
        const salvo = await enfileirar();
        return {
          success: false,
          offline: true,
          salvo,
          localPonto,
          erros: salvo ? result.erros : [ERRO_PONTO_NAO_SALVO],
        };
      }

      // Sucesso online — marca local como sincronizado
      await SyncService.markLocalPontoSynced(localPonto.id, result.fotoUrl);
      if (!noMapa) {
        // O mapa não aceitou a batida: a cópia em arquivo é o único registro local e
        // precisa saber que já foi enviada (senão volta como pendente e reenvia).
        await SyncService._atualizarBackupPontoSeExistir({
          ...localPonto,
          offline: false,
          syncedEm: Date.now(),
          fotoUrl: result.fotoUrl,
        });
      }
      return {
        success: true,
        offline: false,
        salvo: true,
        localPonto: { ...localPonto, offline: false, fotoUrl: result.fotoUrl },
      };
    } catch (error: any) {
      console.error("[Sync] Erro inesperado em submitPonto:", error);
      const salvo = await enfileirar();
      return {
        success: false,
        offline: true,
        salvo,
        localPonto,
        erros: salvo
          ? [String(error?.message ?? error)]
          : [ERRO_PONTO_NAO_SALVO],
      };
    }
  },

  /**
   * Mescla pontos locais (offline + recém sincronizados ainda não confirmados pelo
   * servidor) dentro de userData.ultimosPontos. Chamado pelo AuthContext.refreshUserData.
   * Pontos locais já confirmados pelo servidor (mesmo dia/tipo presente na resposta) são removidos.
   */
  applyLocalPontos: async (userData: any): Promise<any> => {
    if (!userData) return userData;
    // serverPontos pode já conter injeções locais de um merge ANTERIOR (quando baseData vem
    // do CACHE já mesclado — ex.: /iniciar falhou e usamos userData.json). Removemos essas
    // injeções (marcadas com `_local: boolean`) para tornar applyLocalPontos IDEMPOTENTE —
    // senão a label "{n} pendente(s)" do Home ACUMULA a cada refresh feito sobre o cache.
    const serverPontosAll: any[] = Array.isArray(userData.ultimosPontos)
      ? userData.ultimosPontos
      : [];
    const serverPontos = serverPontosAll.filter(
      (p: any) => typeof p?._local !== "boolean",
    );
    const limpo =
      serverPontos.length !== serverPontosAll.length
        ? { ...userData, ultimosPontos: serverPontos }
        : userData;

    const map = await SyncService.getLocalPontosMap();
    const ids = Object.keys(map);
    if (ids.length === 0) return limpo;

    const queue = await SyncService.getOfflineQueue();
    const localIdsEmFila = new Set(
      (queue || [])
        .filter((a: any) => a?.type === "PONTO")
        .map((a: any) => String(a?.data?.localId || ""))
        .filter((v: string) => !!v),
    );

    // Normaliza qualquer data para o DIA "DD/MM/YYYY" (aceita "DD/MM/YYYY", "YYYY-MM-DD" e
    // variantes com hora). Reconciliação local x servidor é por DIA, não comparação de string.
    const diaDe = (v: any): string => {
      const s = String(v ?? "").trim();
      const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
      const br = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
      return br ? `${br[1]}/${br[2]}/${br[3]}` : "";
    };
    // Para JULGAR se o servidor confirmou uma batida, só valem batidas GENUÍNAS (linha
    // real de tab_rh_pontos): linhas de EDIÇÃO do gestor (tab_rh_pontos_edicao → o
    // /iniciar as injeta com editado=1 e id=0) NÃO são batida — casar com elas removia
    // do mapa (com a FOTO) uma batida real que nunca subiu.
    const ehEdicaoSrv = (sp: any) =>
      sp?.editado === true ||
      Number(sp?.editado) === 1 ||
      sp?.editado === "1" ||
      sp?.editado === "S";
    const serverReais = serverPontos.filter(
      (sp: any) => !ehEdicaoSrv(sp) && Number(sp?.id ?? sp?.id_rh_ponto) > 0,
    );
    // Hash do tenant atual (claim do token — não defasa no trocarEmpresa): sobra de
    // OUTRA empresa nunca é removida por match contra o ultimosPontos desta sessão
    // (ids numéricos colidem entre bancos).
    const hashSessao = await tenantHashSessaoAtual().catch(() => "");

    // Identifica quais locais já foram confirmados pelo servidor (mesmo DIA-de-competência + tipo)
    const idsParaRemover: string[] = [];
    for (const id of ids) {
      if (localIdsEmFila.has(String(id))) continue; // offline genuíno (ainda na fila) NÃO sai
      const lp = map[id];
      // PENDENTE (offline:true) NUNCA sai do mapa por aqui: pendente só vira removível
      // depois de sincronizado (markLocalPontoSynced → offline:false, decidido pelo
      // _reconciliarPontosPendentes com batida GENUÍNA). Remover pendente por match de
      // dia+tipo apagava batida real (e a foto) num falso match — perda permanente.
      if (lp?.offline === true) continue;
      // Guarda cross-tenant FAIL-CLOSED (igual à cura): só remove por match quando o
      // carimbo do ponto CASA com a sessão. Ponto sem hash (legado não-adotado) ou
      // sessão sem hash → NUNCA remove por coincidência de dia+tipo: o ultimosPontos
      // desta sessão não diz nada sobre batida de outro banco (ids/dias colidem).
      // A adoção de legado (ids triplos) carimba o legado legítimo — que aí dedupa.
      if (!lp?.hash || !hashSessao || hashNormalizado(lp.hash) !== hashSessao) continue;
      const lpDia = diaDe(lp.data); // competência local
      // ATENÇÃO: em ultimosPontos o campo `data` é a DATA DO data_log (dia da BATIDA) — em jornada
      // NOTURNA difere da competência (ex.: batida 03:17 de 19/06 cuja competência é 18/06). Por
      // isso casamos a competência local contra `data_competencia` (dia de trabalho), usando
      // `data` APENAS como fallback quando a linha não traz data_competencia — como OR
      // indiscriminado, o `data` casava a competência local com o DIA DA BATIDA de OUTRA
      // competência (turno noturno) e removia o ponto errado.
      // E é ESTA batida (hora próxima), não outra com o mesmo dia+tipo — ver
      // servidorTemEstaBatida: remover a cópia local por coincidência de chave apagava a
      // ÚNICA cópia de uma batida que o servidor nunca gravou (descartada pela UNIQUE).
      // Mesmo dia e mesmo SEGUNDO em qualquer tipo também é esta batida (o gestor reordenou
      // os tipos) — mesma regra de servidorTemEstaBatida.
      const lpTs = pontoParaTimestamp({ data_log: lp.data_log });
      const matched = serverReais.some((sp: any) => {
        const spDia = diaDe(sp?.data_competencia) || diaDe(sp?.data);
        if (spDia !== lpDia) return false;
        if (
          Number.isFinite(lpTs) &&
          pontoParaTimestamp({ data_log: sp?.data_log }) === lpTs
        )
          return true;
        if (Number(sp?.id_rh_ponto_tipo) !== Number(lp.id_rh_ponto_tipo))
          return false;
        const spTs = pontoParaTimestamp(sp);
        if (!Number.isFinite(lpTs) || !Number.isFinite(spTs)) return true;
        return Math.abs(spTs - lpTs) <= MESMA_BATIDA_TOLERANCIA_MS;
      });
      if (matched) idsParaRemover.push(id);
    }

    // Remove os já confirmados (em background)
    if (idsParaRemover.length) {
      void Promise.all(
        idsParaRemover.map((id) => SyncService.removeLocalPonto(id)),
      ).catch(() => {});
    }

    const pendentes = ids
      .filter((id) => !idsParaRemover.includes(id))
      .map((id) => map[id])
      // 🔒 EXIBIÇÃO por tenant (caso real: batida offline da empresa A aparecia
      // no HISTÓRICO da empresa B após trocar de empresa): ponto carimbado de
      // OUTRO tenant NÃO entra no ultimosPontos desta sessão — segue no mapa e
      // reaparece quando a sessão da empresa dona voltar. Sem carimbo (legado)
      // = fail-open (mostra, como sempre; a adoção de legado carimba em seguida).
      .filter((lp) => {
        const th = String(lp?.hash ?? "")
          .trim()
          .toUpperCase();
        return !th || !hashSessao || th === hashSessao;
      })
      .map((lp) => ({
        // shape compatível com o que Registro.tsx/Home.tsx esperam
        id: lp.id,
        data: lp.data,
        data_log: lp.data_log,
        hora: lp.hora,
        id_rh_ponto_tipo: lp.id_rh_ponto_tipo,
        origem: lp.origem,
        offline: lp.offline,
        foto: lp.fotoUrl ?? lp.fotoUri ?? "",
        latitude_ponto: lp.latitude ?? "",
        longitude_ponto: lp.longitude ?? "",
        posto_nome: lp.postoNome ?? "",
        // A UI marca "pendente" via (offline || _local). Um ponto JÁ sincronizado (offline:false)
        // que ainda não saiu do mapa (janela antes da remoção) NÃO deve mostrar "pendente".
        _local: lp.offline !== false,
      }));

    if (!pendentes.length) return limpo;

    return {
      ...userData,
      ultimosPontos: [...pendentes, ...serverPontos],
    };
  },

  applyLocalEnquetes: async (userData: any): Promise<any> => {
    if (!userData) return userData;
    const queue = await SyncService.getOfflineQueue();
    const idsEmFila = new Set(
      (queue || [])
        .filter((a: any) => a?.type === "ENQUETE_SALVAR")
        .map((a: any) => a?.data?.id)
        .filter(
          (v: any) => typeof v === "number" && Number.isFinite(v) && v > 0,
        ),
    );

    if (idsEmFila.size === 0) return userData;

    const next = { ...userData };

    if (Array.isArray(next.enquetesPendentes)) {
      next.enquetesPendentes = next.enquetesPendentes.filter(
        (e: any) =>
          !idsEmFila.has(
            Number(e?.idEnqueteExecucao ?? e?.idExecucao ?? e?.id),
          ),
      );
    }

    if (Array.isArray(next.enquete)) {
      next.enquete = next.enquete.filter(
        (e: any) => !idsEmFila.has(Number(e?.idExecucao ?? e?.id)),
      );
    }

    return next;
  },

  applyLocalOsSobDemanda: async (userData: any): Promise<any> => {
    if (!userData) return userData;
    const map = await SyncService.getLocalOsSobDemandaMap();
    const tempIds = Object.keys(map);
    if (tempIds.length === 0) return userData;

    const ordemServicos: any[] = Array.isArray(userData.ordemServicos)
      ? userData.ordemServicos
      : [];
    const existingIds = new Set(
      ordemServicos.map((o: any) => String(o?.id_adm_ordem_servico_cronograma)),
    );

    // Coordenadas por posto (userData.clientes = lista de postos do tenant). A O.S. sob
    // demanda nasce OFFLINE e nunca passa pela SELECT de OS, então o local dela vem daqui —
    // sem isto, justamente a O.S. de quem não tem posto fixo ficaria sem o botão de rota.
    const coordsPorCliente = new Map<number, { lat: string; lng: string }>();
    for (const c of Array.isArray(userData?.clientes)
      ? userData.clientes
      : []) {
      const idCliente = Number(
        (c as any)?.id ?? (c as any)?.id_adm_cliente ?? 0,
      );
      if (idCliente > 0) {
        coordsPorCliente.set(idCliente, {
          lat: String((c as any)?.latitude ?? ""),
          lng: String((c as any)?.longitude ?? ""),
        });
      }
    }

    const stubItems: any[] = [];
    for (const key of tempIds) {
      if (existingIds.has(key)) continue;
      const item = map[key];
      const iso = String(item?.payload?.data_execucao ?? "").trim();
      const clienteLabel = item?.cliente_label;
      const equipeLabel = item?.equipe_label;
      const exigeFoto = String(item?.payload?.exige_foto ?? "N").trim() || "N";

      stubItems.push({
        id_adm_ordem_servico:
          item.id_adm_ordem_servico ?? item.temp_cronograma_id,
        id_adm_ordem_servico_cronograma:
          item.id_adm_ordem_servico_cronograma ?? item.temp_cronograma_id,
        id_adm_ordem_servico_cadastro:
          item?.payload?.id_adm_ordem_servico_cadastro ?? 999999999,
        id_adm_ordem_servico_equipe:
          item?.payload?.id_adm_ordem_servico_equipe ?? 0,
        id_adm_cliente: item?.payload?.id_adm_cliente ?? 0,
        data_execucao: item?.payload?.data_execucao ?? null,
        data_execucao2: item?.payload?.data_execucao2 ?? null,
        periodicidade: item?.payload?.periodicidade ?? 0,
        tipo_periodicidade: item?.payload?.tipo_periodicidade ?? "S",
        tipo_servico: item?.payload?.tipo_servico ?? "S",
        tipo_servico_descricao:
          item?.payload?.tipo_servico_descricao ?? "SOB DEMANDA",
        valor: item?.payload?.valor ?? "0",
        observacao: item?.payload?.observacao ?? "",
        id_adm_usuario: item?.payload?.id_adm_usuario ?? 0,
        data_log: new Date(item.created_at || Date.now()).toLocaleString(
          "pt-BR",
        ),
        descricao_servico: "SOB DEMANDA",
        descricao_equipe: equipeLabel ?? "SOB DEMANDA",
        nome_razao: clienteLabel ?? "SOB DEMANDA",
        nome_apelido: clienteLabel ?? "SOB DEMANDA",
        nome_usuario: null,
        ativo_inativo: "A",
        status_label: "PENDENTE (OFFLINE)",
        css_status_label: null,
        tipo_solicitacao: item?.payload?.tipo_solicitacao ?? "S",
        tipo_solicitacao_descricao:
          item?.payload?.tipo_solicitacao_descricao ?? "SOB DEMANDA",
        qtde_dias_previsto: item?.payload?.qtde_dias_previsto ?? 0,
        hora_preferencial: item?.payload?.hora_preferencial ?? "00:00:00",
        exige_foto: exigeFoto,
        data_previsto: iso || null,
        data_inicio_real: "",
        data_fim_real: "",
        tempo_execucao: "",
        observacao_execucao: null,
        endereco: "",
        latitude:
          coordsPorCliente.get(Number(item?.payload?.id_adm_cliente ?? 0))
            ?.lat ?? "",
        longitude:
          coordsPorCliente.get(Number(item?.payload?.id_adm_cliente ?? 0))
            ?.lng ?? "",
        possui_checklist: 0,
        id_inspecao_cadastro: null,
        descricao_checklist: "CHECKLIST DE O.S",
        checklist_respondido: false,
        // Lista de tarefas criada junto com a O.S. (29/10/2026): offline ela já aparece para executar.
        lista_tarefas: item?.payload?.lista_tarefas ?? null,
        _local: true,
      });
    }

    if (!stubItems.length) return userData;

    return {
      ...userData,
      ordemServicos: [...stubItems, ...ordemServicos],
    };
  },

  applyLocalOsStatuses: async (userData: any): Promise<any> => {
    if (!userData) return userData;
    const map = await SyncService.getLocalOsStatusMap();
    const ids = Object.keys(map);
    if (ids.length === 0) return userData;

    const ordemServicos: any[] = Array.isArray(userData.ordemServicos)
      ? userData.ordemServicos
      : [];

    // Identifica quais locais já foram confirmados pelo servidor
    const idsParaRemover: string[] = [];
    for (const idCronograma of ids) {
      const localStatus = map[idCronograma];
      const serverOs = ordemServicos.find(
        (o: any) => String(o.id_adm_ordem_servico_cronograma) === idCronograma,
      );

      // Se o servidor já tem data_inicio_real para OS iniciada
      // ou data_fim_real para OS concluída, remove o status local.
      // ⚠️ `_pendenteSync` marca uma INJEÇÃO LOCAL de uma passada ANTERIOR: o
      // userData mesclado é persistido no userData.json, então numa próxima
      // chamada (ex.: offline, onde baseData = cache) a O.S. já vem com
      // data_inicio_real preenchida PELO PRÓPRIO PATCH. Sem esta guarda, isso
      // era lido como "o servidor confirmou", o status local era apagado e o
      // selo virava 2 CHECKS mesmo sem nunca ter subido — exatamente o que se
      // via ao iniciar uma O.S. com 4G/Wi-Fi desligados. (Mesmo cuidado que o
      // applyLocalPontos já tinha ao filtrar as injeções `_local`.)
      if (serverOs && serverOs._pendenteSync !== true) {
        if (
          (localStatus.status === "iniciada" && serverOs.data_inicio_real) ||
          (localStatus.status === "concluida" && serverOs.data_fim_real)
        ) {
          idsParaRemover.push(idCronograma);
        }
      }
    }

    // Remove os já confirmados (em background)
    if (idsParaRemover.length) {
      void Promise.all(
        idsParaRemover.map((id) => SyncService.clearLocalOsStatus(id)),
      ).catch(() => {});
    }

    // Aplica os status locais que ainda não foram confirmados pelo servidor
    const ordensAtualizadas = ordemServicos.map((os: any) => {
      const idCronograma = String(os.id_adm_ordem_servico_cronograma);
      const localStatus = map[idCronograma];

      // Sem status local (ou já confirmado pelo servidor): LIMPA o marcador de
      // uma injeção anterior que tenha vindo grudado no cache — senão a O.S.
      // ficaria com 1 check para sempre, mesmo depois de sincronizada.
      if (!localStatus || idsParaRemover.includes(idCronograma)) {
        if (os?._pendenteSync) {
          const { _pendenteSync, ...semMarcador } = os;
          return semMarcador;
        }
        return os;
      }

      // Aplica o status local otimista. `_pendenteSync` sinaliza que o
      // início/conclusão ainda NÃO foi confirmado pelo servidor — é o que a tela
      // usa para o selo de 1 check (só no aparelho) × 2 checks (sincronizado),
      // no mesmo padrão de ponto/justificativa/livro/ronda.
      const osAtualizada = { ...os, _pendenteSync: true };
      if (localStatus.status === "iniciada") {
        osAtualizada.data_inicio_real = localStatus.data_log;
      } else if (localStatus.status === "concluida") {
        osAtualizada.data_fim_real = localStatus.data_log;
      }
      return osAtualizada;
    });

    return {
      ...userData,
      ordemServicos: ordensAtualizadas,
    };
  },

  /**
   * Processa a fila de ações offline (assinaturas, pontos, etc.)
   */
  /**
   * @param opcoes.manual sync PUXADO pela pessoa: as ações em backoff (rejeitadas pelo
   *   servidor) são tentadas mesmo assim. O backoff existe para o sync AUTOMÁTICO não
   *   martelar um erro que se repetiria igual; um gesto humano é o "tenta agora" — e,
   *   depois de um deploy que corrige o servidor, é a única forma de subir sem esperar
   *   a janela (que chega a 6 h). Ficou visível em 12/09/2026: T-ARC corrigido, a
   *   pessoa puxando para baixo, e o dreno pulando a ação em silêncio.
   */
  /**
   * Envia DIRETO da cópia em arquivo as batidas que não couberam no mapa nem na fila
   * (armazenamento cheio/ilegível). Em sucesso a cópia é regravada como enviada e fica
   * até o servidor confirmar — é o único registro local delas.
   */
  _despacharBackupsDireto: async (lista: LocalPonto[]): Promise<void> => {
    if (!lista || lista.length === 0) return;
    const hashSessao = await tenantHashSessaoAtual().catch(() => "");
    for (const b of lista) {
      const th = String(b.hash ?? "")
        .trim()
        .toUpperCase();
      if (th && hashSessao && th !== hashSessao) continue; // outra empresa: espera a sessão dela
      try {
        const r = await SyncService._enviarPontoOnline(b, b.fotoUri ?? null, {
          id_adm_cliente: Number(b.id_adm_cliente) || 0,
          id_adm_empresa: Number(b.id_adm_empresa) || 0,
        });
        if (!r.ok) continue;
        await SyncService._gravarBackupPonto({
          ...b,
          offline: false,
          syncedEm: Date.now(),
          fotoUrl: r.fotoUrl ?? b.fotoUrl,
        });
        console.warn(
          "[Sync] Batida enviada direto da cópia em arquivo (mapa/fila sem escrita):",
          b.id,
        );
      } catch (e) {
        console.warn("[Sync] Envio direto da cópia em arquivo falhou:", e);
      }
    }
  },

  /**
   * Uma batida pelos DADOS MÓVEIS: POST único com a foto em base64 — o T-ARC sobe a foto
   * para o storage (`SubirFotoPonto`), então o multipart do upload não é necessário.
   * `semRede` = nem pelos dados móveis deu (para o laço; não insiste batida a batida).
   */
  _enviarPontoPorDadosMoveis: async (
    lp: LocalPonto,
    extras: { id_adm_cliente: number; id_adm_empresa: number },
  ): Promise<{
    ok: boolean;
    fotoUrl: string | null;
    semRede?: boolean;
    erros?: string[] | null;
  }> => {
    // Mesma regra do _enviarPontoOnline: sem a hora do toque, não sai.
    if (!dataLogDaBatida(lp))
      return { ok: false, fotoUrl: null, erros: ["Batida sem hora legível"] };
    let foto = "";
    if (lp.fotoUrl && /^https?:\/\//i.test(String(lp.fotoUrl))) {
      foto = String(lp.fotoUrl); // já subiu numa tentativa anterior
    } else if (lp.fotoUri) {
      try {
        const bruto = caminhoNoAppAtual(String(lp.fotoUri).trim());
        const uri =
          bruto.startsWith("file://") || bruto.startsWith("content://")
            ? bruto
            : `file://${bruto}`;
        const info = await FileSystem.getInfoAsync(uri);
        if (info.exists) {
          foto = await FileSystem.readAsStringAsync(uri, {
            encoding: FileSystem.EncodingType.Base64,
          });
        }
      } catch {}
    }
    const payload = SyncService._montarPayloadPonto(lp, extras, foto);
    const resp = await Api.ponto.registrarPorDadosMoveis(
      foto ? "Ponto/registrarPonto" : "Ponto/registrarPontoSemFoto",
      payload,
    );
    if (!resp.success) {
      return {
        ok: false,
        fotoUrl: null,
        semRede: resp.offline === true,
        erros: resp.erros as string[] | null,
      };
    }
    if (!pontoFoiEnfileirado(resp.done)) {
      return { ok: false, fotoUrl: null, semRede: true };
    }
    return { ok: true, fotoUrl: /^https?:\/\//i.test(foto) ? foto : null };
  },

  /**
   * ENVIO PELOS DADOS MÓVEIS (Android) quando a rede atual não alcança a nossa API.
   *
   * Caso real (30/09/2026): Wi-Fi de órgão público com o Google liberado e os nossos
   * hosts bloqueados. O Android fica nesse Wi-Fi "porque tem internet" e a batida não
   * saía até a pessoa abrir o app em outra rede. Só as BATIDAS vão por aqui — pequenas e
   * críticas; o resto da fila espera a rede normal. Sem o módulo nativo (iOS, build
   * antigo) ou já fora do Wi-Fi, não faz nada.
   */
  _drenarPontosPorDadosMoveis: async (opcoes?: {
    manual?: boolean;
  }): Promise<number> => {
    try {
      if (!(await DadosMoveis.redeAtualEhWifi())) return 0;
      // Reconciliação é local (sem rede): devolve órfãos do mapa/arquivo à fila.
      const rec = await SyncService._reconciliarPontosPendentes();
      // Batida RECUSADA pelo servidor espera a vez aqui também (puxar a tela ignora a
      // espera, como no dreno normal) — ver registrarRecusaDaAcao.
      const esperas = await lerEsperasDeAcoes();
      const agoraEsp = Date.now();
      const acoes = (await SyncService.getOfflineQueue()).filter(
        (a) =>
          a?.type === "PONTO" &&
          (opcoes?.manual || !acaoEmEspera(esperas, a.id, agoraEsp)),
      );
      if (acoes.length === 0 && rec.paraDespachoDireto.length === 0) return 0;
      // A nossa API responde pelos dados móveis? Sem isso nem tenta as batidas.
      if (!(await Api.ponto.alcancavelPorDadosMoveis())) return 0;

      const hashSessao = await tenantHashSessaoAtual().catch(() => "");
      const deOutroTenant = (h: any) => {
        const t = String(h ?? "")
          .trim()
          .toUpperCase();
        return !!t && !!hashSessao && t !== hashSessao;
      };
      const map = await SyncService.getLocalPontosMap();
      let enviados = 0;
      for (const action of acoes) {
        const { localId, id_adm_cliente, id_adm_empresa, ponto } =
          action.data ?? {};
        const lp: LocalPonto | null =
          localId && map?.[localId] ? map[localId] : (ponto ?? null);
        if (!lp) continue;
        if (deOutroTenant(lp.hash) || deOutroTenant(action.tenantHash))
          continue;
        const r = await SyncService._enviarPontoPorDadosMoveis(lp, {
          id_adm_cliente: Number(id_adm_cliente) || 0,
          id_adm_empresa: Number(id_adm_empresa) || 0,
        });
        if (!r.ok) {
          if (r.semRede) break;
          // O servidor RESPONDEU e recusou: espera a vez antes da próxima tentativa.
          try {
            await registrarRecusaDaAcao(
              action,
              r.erros?.[0],
              ESPERAS_PONTO_RECUSADO_MS,
            );
          } catch {}
          continue;
        }
        await SyncService.markLocalPontoSynced(lp.id, r.fotoUrl);
        if (!(localId && map?.[localId])) {
          await SyncService._atualizarBackupPontoSeExistir({
            ...lp,
            offline: false,
            syncedEm: Date.now(),
          });
        }
        await SyncService.dequeueOfflineAction(action.id);
        enviados++;
      }
      for (const b of rec.paraDespachoDireto) {
        if (deOutroTenant(b.hash)) continue;
        const r = await SyncService._enviarPontoPorDadosMoveis(b, {
          id_adm_cliente: Number(b.id_adm_cliente) || 0,
          id_adm_empresa: Number(b.id_adm_empresa) || 0,
        });
        if (!r.ok) {
          if (r.semRede) break;
          continue;
        }
        await SyncService._gravarBackupPonto({
          ...b,
          offline: false,
          syncedEm: Date.now(),
        });
        enviados++;
      }
      if (enviados > 0) {
        console.warn(
          `[Sync] ${enviados} batida(s) enviada(s) pelos DADOS MÓVEIS — a rede atual não alcança a API.`,
        );
      }
      return enviados;
    } catch (e) {
      console.warn("[Sync] Envio pelos dados móveis: erro (ignorado):", e);
      return 0;
    }
  },

  /**
   * CONFIRMAÇÃO LOCAL, sem rede: carimba `confirmadoEm` nas batidas enviadas cuja linha
   * GENUÍNA voltou no cache do /iniciar — mesmo critério do reconciliador
   * (podeConfirmarBatida). Logo após um /iniciar REAL, é só disso que a maioria das
   * batidas precisa; antes, cada batida custava um dreno inteiro (com a sondagem das duas
   * APIs) só para ganhar o carimbo (análise de carga de 30/09/2026).
   */
  _confirmarPontosPeloCache: async (): Promise<number> => {
    try {
      const cache = await lerBatidasGenuinasDoCache();
      if (cache.chaves.size === 0) return 0;
      const hashSessao = await tenantHashSessaoAtual().catch(() => "");
      const map = await SyncService.getLocalPontosMap();
      const ids = Object.values(map)
        .filter((lp) => podeConfirmarBatida(lp, cache, hashSessao))
        .map((lp) => String(lp.id));
      if (ids.length === 0) return 0;
      const agora = Date.now();
      const confirmados = await comMapaLock(async () => {
        const fresco = await lerMapaPontosParaEscrita();
        const feitos: string[] = [];
        for (const id of ids) {
          const cur = fresco[id];
          if (!cur || cur.offline !== false || cur.confirmadoEm) continue;
          fresco[id] = { ...cur, confirmadoEm: agora };
          feitos.push(id);
        }
        if (feitos.length > 0) {
          await gravarMapaPontos(fresco);
        }
        return feitos;
      });
      for (const id of confirmados) await SyncService._removerBackupPonto(id);
      return confirmados.length;
    } catch (e) {
      console.warn("[Sync] Confirmação local das batidas: erro (ignorado):", e);
      return 0;
    }
  },

  /**
   * Alguma batida "enviada" que a cura retroativa reverteria AGORA? Mesmo critério do
   * reconciliador (curaPodeAgir + ehCandidataACura): cache fresco, dentro da janela do
   * LIMIT 50, passado o grace, abaixo dos tetos, do tenant da sessão e nunca confirmada.
   * Pode ser um "2 checks fantasma" (o T-ARC aceitou e o T-Consumer não gravou). Só
   * leitura local.
   */
  _temCandidatasACura: async (): Promise<boolean> => {
    try {
      const hashSessao = await tenantHashSessaoAtual().catch(() => "");
      const agora = Date.now();
      const cache = await lerBatidasGenuinasDoCache();
      if (!curaPodeAgir(cache, hashSessao, await lerUserDataFetchedAt(), agora))
        return false;
      const map = await SyncService.getLocalPontosMap();
      return Object.values(map).some((lp) =>
        ehCandidataACura(lp, cache, hashSessao, agora),
      );
    } catch {
      return false;
    }
  },

  /**
   * Chamar logo após um /iniciar REAL. Com o cache fresco:
   *  1. confirma LOCALMENTE as batidas que voltaram do servidor (sem rede);
   *  2. só drena se houver o que enviar agora (temEnvioPendente) ou batida que a cura
   *     reverteria — e aí a cura age sozinha. Antes ela dependia de a pessoa puxar a tela
   *     de Justificativas (o único gesto que atualizava o cache LOGO DEPOIS de um dreno) —
   *     era o "só sobe quando puxa".
   */
  conferirPontosAposIniciar: async (): Promise<void> => {
    try {
      await SyncService._confirmarPontosPeloCache();
      if (
        !(await SyncService.temEnvioPendente()) &&
        !(await SyncService._temCandidatasACura())
      )
        return;
      await SyncService.processOfflineQueue();
    } catch (e) {
      console.warn("[Sync] conferirPontosAposIniciar: erro (ignorado):", e);
    }
  },

  /**
   * Há algo desta sessão que pode sair AGORA? Só leitura local (barato) — é o que o
   * agendador, a tarefa em segundo plano e a conferência pós-/iniciar consultam antes de
   * gastar rede. Fica de fora:
   *  - o que é de OUTRA empresa (retido até a sessão dela);
   *  - o que o servidor RECUSOU e está esperando a vez (ver registrarRecusaDaAcao): o
   *    dreno não manda mesmo, e acordar a rede por isso era a sondagem das duas APIs a
   *    cada ciclo, 24 h por dia (análise de carga de 30/09/2026);
   *  - o marcador de notificação lida (perdê-lo não perde dado do colaborador).
   * Na dúvida (estado ilegível), diz que sim: um dreno a mais é inofensivo, um a menos
   * deixa batida parada.
   */
  temEnvioPendente: async (): Promise<boolean> => {
    try {
      const [rawMapa, fila] = await Promise.all([
        AsyncStorage.getItem(LOCAL_PONTOS_KEY),
        lerFilaSoLeitura(),
      ]);
      const mapa: Record<string, any> = rawMapa ? JSON.parse(rawMapa) : {};
      if (!mapa || typeof mapa !== "object") return true;
      const esperas = await lerEsperasDeAcoes();
      const agora = Date.now();
      const hashSessao = await tenantHashSessaoAtual().catch(() => "");
      const deOutroTenant = (th: any): boolean => {
        const t = String(th ?? "")
          .trim()
          .toUpperCase();
        return !!t && !!hashSessao && t !== hashSessao;
      };

      // Ação desta sessão fora de espera = pode sair. As BATIDAS em espera ficam anotadas:
      // a pendente delas no mapa/arquivo também espera.
      const batidasEmEspera = new Set<string>();
      for (const a of fila) {
        const t = (a as any)?.type;
        if (!t || t === "NOTIFICACOES_LIDAS") continue;
        if (
          deOutroTenant(
            (a as any)?.data?.ponto?.hash ||
              (a as any)?.data?.hash ||
              (a as any)?.tenantHash,
          )
        )
          continue;
        if (acaoEmEspera(esperas, a.id, agora)) {
          const localId = (a as any)?.data?.localId;
          if (t === "PONTO" && localId != null)
            batidasEmEspera.add(String(localId));
          continue;
        }
        return true;
      }
      // Batida pendente sem ação na fila (órfã — o reconciliador reenfileira) ou que só
      // sobreviveu na cópia em arquivo.
      for (const p of Object.values(mapa) as any[]) {
        if (!p || p.offline !== true || deOutroTenant(p.hash)) continue;
        if (batidasEmEspera.has(String(p.id))) continue;
        return true;
      }
      for (const b of await SyncService._lerBackupsPonto()) {
        if (!b || b.offline !== true || mapa[b.id] || deOutroTenant(b.hash))
          continue;
        if (batidasEmEspera.has(String(b.id))) continue;
        return true;
      }
      return false;
    } catch {
      return true;
    }
  },

  processOfflineQueue: async (opcoes?: { manual?: boolean }) => {
    if (isProcessingQueue) {
      console.log("[Sync] Já está processando a fila, abortando nova chamada.");
      return;
    }

    try {
      isProcessingQueue = true;
      console.log("[Sync] Iniciando processamento da fila offline...");
      // A fila antiga (até a 4.0.22, no AsyncStorage) vem para o arquivo JÁ, com ou sem
      // rede: até lá, as contagens do "Sair" e do logoff remoto não a enxergam. Barato
      // depois de feita. Falhou → fica para o próximo acesso; nada é gravado.
      await comFilaLock(() => migrarFilaAntiga()).catch((e) =>
        console.warn("[Sync] Migração da fila antiga adiada:", e),
      );
      // Batidas pendentes herdadas de versão anterior ganham a cópia em arquivo (uma vez).
      await SyncService._garantirCopiasDasPendentes();
      // Portão = a NOSSA API responde. Com "qualquer internet" (Google), a rede de órgão
      // público que bloqueia os nossos hosts passava por online: cada ação esperava o
      // próprio timeout para falhar, e nada distinguia "sem rede" de "rede que nos barra".
      const isConnected = await SyncService.apiAlcancavel();
      console.log("[Sync] Verificando conexão:", isConnected);

      if (!isConnected) {
        // Android: a rede atual (normalmente o Wi-Fi) não alcança a nossa API — as
        // BATIDAS tentam pelos dados móveis antes de desistir deste ciclo.
        await SyncService._drenarPontosPorDadosMoveis(opcoes);
        console.log("[Sync] Sem conexão, abortando.");
        return;
      }

      // RECUPERAÇÃO + DEDUP: antes de ler a fila, reconcilia os pontos pendentes a partir do
      // MAPA local — (1) marca synced e tira da fila o que o servidor já tem (competência+tipo,
      // inclui batidas em outro aparelho), e (2) reenfileira os órfãos (sumiram da fila mas
      // seguem no mapa, visíveis em "Histórico e justificativas") para o dreno abaixo enviar.
      // Vem ANTES do early-return de "fila vazia" de propósito: o caso órfão é exatamente fila
      // vazia + mapa com pendências.
      const reconciliacao = await SyncService._reconciliarPontosPendentes();
      // Batidas que só sobreviveram na cópia em arquivo (mapa e fila sem escrita): saem
      // direto daqui, antes da fila — que pode nem ser legível nesse cenário.
      await SyncService._despacharBackupsDireto(
        reconciliacao.paraDespachoDireto,
      );

      // Complementos que ficaram sem alvo (respondidos antes de o servidor devolver o id
      // da execução). O dreno também tenta: a tela de Checklists não pode ser o único
      // gatilho — quem responde e não volta lá deixaria o complemento parado.
      await SyncService.varrerComplementosPendentesVencidos();
      // Faxina do diretório de fotos do livro (ver varrerFotosLivroOrfas).
      await SyncService.varrerFotosLivroOrfas();
      try {
        const pend =
          await SyncService.getLocalChecklistComplPendentesDaSessao();
        if (Object.keys(pend).length > 0) {
          const r = await Api.geral.obterChecklistExecucoes();
          if (r?.success && Array.isArray(r.done)) {
            await SyncService.vincularComplementosPendentes(r.done);
          }
        }
      } catch (e) {
        console.warn("[Sync] Vínculo de complementos no dreno falhou:", e);
      }

      // Leitura com QUARENTENA: fila corrompida era fatal aqui (o parse lançava, o catch
      // externo só logava e o dreno nunca mais rodava — backlog eterno). Conteúdo que não é
      // JSON de array vai pra quarentena, a fila reseta e os pontos órfãos voltam pelo
      // reconciliador. ERRO DE LEITURA, não: lança, este ciclo para no catch externo e
      // nada é sobrescrito (services/FilaOffline.ts). Sob o lock: a migração da fila
      // antiga e o reparo gravam, e não podem entrelaçar com um enqueue concorrente.
      const lidas: OfflineAction[] = await comFilaLock(() =>
        lerFilaParaEscrita(),
      );
      // Já entregues cuja baixa falhou (ver baixasPendentes): só tenta a baixa de novo.
      const actions: OfflineAction[] = [];
      for (const a of lidas) {
        if (!baixasPendentes.has(a.id)) actions.push(a);
        else await SyncService.dequeueOfflineAction(a.id);
      }
      // NÃO logar a fila crua: ações SIGNATURE carregam assinatura+selfie em BASE64 (MBs).
      console.log("[Sync] Fila offline:", `${actions.length} ação(ões)`);

      if (actions.length === 0) {
        console.log("[Sync] Nenhuma ação para processar.");
        return;
      }

      console.log(
        "[Sync] =============== INÍCIO DA SINCRONIZAÇÃO ===============",
      );
      console.log(`[Sync] Total de ações na fila: ${actions.length}`);
      // Resumo SEM data: payloads de SIGNATURE/checklist carregam base64 (MBs) — serializar
      // a fila inteira no log custava mais que o próprio processamento.
      console.log(
        `[Sync] Processando ${actions.length} ações offline:`,
        actions.map((a) => `${a.id}:${a.type}`).join(", "),
      );

      const remainingActions: OfflineAction[] = [];
      // Pares (execução|cadastro_ponto) cujo RONDA_SALVAR_PONTO FALHOU nesta passada.
      // O CHECKLIST_SALVAR do MESMO par não despacha neste ciclo: chegaria ao consumer
      // antes do ponto existir (foi assim que checklists reais morreram no .dead).
      // O sort por timestamp garante que o ponto é avaliado antes do checklist.
      const paresPontoFalhou = new Set<string>();
      const parRonda = (d: any) =>
        `${Number(d?.id_adm_ronda_execucao ?? 0)}|${Number(d?.id_adm_ronda_cadastro_ponto ?? 0)}`;

      /**
       * Destino de uma ação que FALHOU no despacho. A ação SEMPRE volta para a
       * fila — NUNCA descarta (regra de ouro: jamais perder registro do
       * colaborador). O que muda é a CADÊNCIA:
       *  • TRANSITÓRIO (`response.offline` — rede/timeout/5xx): retenta no
       *    próximo dreno, sem penalidade;
       *  • REJEIÇÃO do servidor: reenviar de imediato dá o MESMO erro e vira
       *    spam (o dreno dispara em ~20 pontos do app — conectividade, foco de
       *    tela, etc.). Aplica BACKOFF (ESPERAS_ACAO_RECUSADA_MS: 5min → 30min →
       *    6h; batida: ESPERAS_PONTO_RECUSADO_MS, 2min → 10min → 30min → 2h): a
       *    ação continua na fila e é reavaliada, só que espaçada.
       *
       * ⚠️ Aqui JÁ EXISTIU um teto que quarentenava e desenfileirava. Foi
       * REMOVIDO: `offline` só é true para 502/503/504 e queda de rede, então
       * 401 (token expirado), 429 e 500 caíam como "rejeição permanente" e a
       * fila INTEIRA seria descartada em poucos minutos; e a cópia forense
       * morria no AsyncStorage.clear() do logout. Não reintroduzir sem que o
       * ApiReturn exponha o status HTTP e sem uma quarentena que sobreviva ao
       * wipe e seja visível ao suporte.
       */
      const tratarFalhaDespacho = async (
        action: OfflineAction,
        response: any,
        contexto: string,
        esperas: number[] = ESPERAS_ACAO_RECUSADA_MS,
      ): Promise<boolean> => {
        remainingActions.push(action); // SEMPRE permanece na fila
        if (response?.offline === true) return false; // transitório: sem penalidade
        try {
          const { n, espera } = await registrarRecusaDaAcao(
            action,
            response?.erros?.[0],
            esperas,
          );
          console.warn(
            `[Sync] ${contexto}: rejeitada pelo servidor (${n}ª vez) — permanece na fila, próxima tentativa em ${Math.round(espera / 60000)}min:`,
            response?.erros,
          );
        } catch {}
        return false;
      };
      const processedIds = new Set<string>();

      // 🔒 GUARDA CROSS-TENANT (incidente 21-23/07: batida da AGIL despachada na
      // sessão do GRUPO MASTROS — colaborador com vínculo nas duas empresas
      // trocou de empresa com a fila pendente; ids numéricos colidem entre
      // bancos e o registro/foto caíram em OUTRA pessoa). O dreno SÓ despacha
      // ação cujo tenant (carimbo do enqueue, ou o hash do próprio ponto) é o
      // MESMO da sessão atual (claim c_hash do token — a fonte que o T-ARC usa
      // para rotear). Divergiu → a ação fica RETIDA na fila (nunca perde, nunca
      // vaza) e sincroniza quando o colaborador voltar à empresa dona.
      // Sem carimbo (ação legada) = fail-open (envia como sempre).
      const hashSessaoDreno = await tenantHashSessaoAtual().catch(() => "");
      // Tenant da ação: ponto (carimbo da batida) → hash carimbado no PAYLOAD
      // pela função de origem (carimbarHashTenant, capturado no EVENTO) →
      // envelope. A ordem importa: entre a origem e o enqueue há awaits
      // (checkInternet, tentativa online, persistência de foto) e um
      // trocarEmpresa nessa janela carimbaria o envelope com a empresa NOVA.
      const tenantDaAcao = (a: OfflineAction): string =>
        String(a?.data?.ponto?.hash || a?.data?.hash || a?.tenantHash || "")
          .trim()
          .toUpperCase();
      const retidasOutroTenant: OfflineAction[] = [];
      const acoesDaSessao: OfflineAction[] = [];
      for (const a of actions) {
        const th = tenantDaAcao(a);
        if (th && hashSessaoDreno && th !== hashSessaoDreno) {
          retidasOutroTenant.push(a);
        } else {
          acoesDaSessao.push(a);
        }
      }
      if (retidasOutroTenant.length > 0) {
        // warn (não log): forense de campo — o babel de produção remove console.log.
        console.warn(
          `[Sync] 🔒 ${retidasOutroTenant.length} ação(ões) de OUTRO tenant retida(s) na fila (sessão=${hashSessaoDreno.slice(0, 8)}…):`,
          retidasOutroTenant.map((a) => ({
            id: a.id,
            type: a.type,
            tenant: tenantDaAcao(a).slice(0, 8) + "…",
          })),
        );
        retidasOutroTenant.forEach((a) => remainingActions.push(a));
      }

      // Agrupar ações por ronda (usando id_adm_ronda_cadastro)
      const rondaActionsMap: Record<string, OfflineAction[]> = {};
      const otherActions: OfflineAction[] = [];

      for (const action of acoesDaSessao) {
        // Ação já em voo pelo envio de primeiro plano (ver `comDespachoDireto`): MANTÉM
        // na fila e segue. Ela só sai daqui em sucesso, então postar junto criaria um
        // segundo registro do mesmo plantão. Não conta como tentativa: quem está
        // despachando ou tem sucesso (e dá baixa) ou deixa a linha para o próximo ciclo.
        if (acoesEmDespachoDireto.has(action.id)) {
          remainingActions.push(action);
          continue;
        }

        const isRondaAction =
          action.type === "RONDA_INICIAR" ||
          action.type === "RONDA_SALVAR_PONTO" ||
          action.type === "CHECKLIST_SALVAR" ||
          action.type === "RONDA_FINALIZAR" ||
          action.type === "RONDA_CANCELAR";

        if (isRondaAction) {
          let rondaId: string | null = null;

          if (action.type === "CHECKLIST_SALVAR") {
            rondaId = action.data?.id_adm_ronda_cadastro ?? null;
            if (!rondaId) {
              // ✅ Checklist SEM ronda = checklist de OS (OrdemServico.tsx manda só
              // IdReferenciaVinculo do cronograma). Ia para remainingActions "para
              // tentar depois" — ETERNAMENTE, sem nenhum caminho de despacho (caso
              // real: "32 rondas" travando o logout numa empresa que SÓ usa OS; o
              // contador rotulava CHECKLIST_SALVAR como ronda). O despachante
              // correto JÁ EXISTE no loop de outras ações (resolveOsCronogramaId +
              // upload de assets + checklistExecucao.salvar) — roteia para lá.
              console.log(
                `[Sync] Ação ${action.id} é CHECKLIST sem ronda (OS) → outras ações`,
              );
              otherActions.push(action);
              continue;
            }
          } else if (action.type === "RONDA_FINALIZAR") {
            rondaId = action.data?.id_adm_ronda_cadastro ?? null;
            if (
              !rondaId &&
              Array.isArray(action.data) &&
              action.data.length > 0
            ) {
              rondaId = action.data[0]?.id_adm_ronda_cadastro ?? null;
            }
          } else {
            rondaId =
              action.data?.id_adm_ronda_cadastro ??
              action.data?.codigoRonda ??
              null;
          }

          if (!rondaId) {
            console.warn(
              "[Sync] Ação de ronda sem id_adm_ronda_cadastro/codigoRonda - mantendo na fila sem processar:",
              { id: action.id, type: action.type },
            );
            remainingActions.push(action);
            continue;
          }

          rondaId = String(rondaId);

          console.log(
            `[Sync] Ação ${action.id} é de ronda, id: ${rondaId} (tipo: ${action.type})`,
          );

          if (!rondaActionsMap[rondaId]) {
            rondaActionsMap[rondaId] = [];
          }
          rondaActionsMap[rondaId].push(action);
        } else {
          console.log(`[Sync] Ação ${action.id} é OUTRA ação:`, action.type);
          otherActions.push(action);
        }
      }

      console.log(
        `[Sync] Ações por ronda: ${Object.keys(rondaActionsMap).length} ronda(s); outras: ${otherActions.length}`,
      );

      // Processar cada ronda separadamente
      for (const rondaIdKey of Object.keys(rondaActionsMap)) {
        console.log(
          `\n\n\n[Sync] =============== PROCESSANDO RONDA ${rondaIdKey} ===============`,
        );
        const allRondaActions = [...rondaActionsMap[rondaIdKey]].sort(
          (a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0),
        );
        console.log(
          "[Sync] Todas as ações dessa ronda:",
          allRondaActions.map((a) => a.id),
        );

        // Dividir as ações em grupos separados por RONDA_INICIAR e finalizar após RONDA_FINALIZAR/RONDA_CANCELAR (cada grupo é uma execução separada)
        const actionGroups: OfflineAction[][] = [];
        let currentGroup: OfflineAction[] = [];

        for (const action of allRondaActions) {
          if (action.type === "RONDA_INICIAR") {
            if (currentGroup.length > 0) {
              actionGroups.push(currentGroup);
            }
            currentGroup = [action];
          } else if (
            action.type === "RONDA_FINALIZAR" ||
            action.type === "RONDA_CANCELAR"
          ) {
            currentGroup.push(action);
            actionGroups.push(currentGroup);
            currentGroup = [];
          } else {
            currentGroup.push(action);
          }
        }

        if (currentGroup.length > 0) {
          actionGroups.push(currentGroup);
        }

        console.log(
          `[Sync] Divididas em ${actionGroups.length} grupos (execuções separadas)`,
        );

        // Processar cada grupo separadamente
        for (
          let groupIndex = 0;
          groupIndex < actionGroups.length;
          groupIndex++
        ) {
          const rondaActions = actionGroups[groupIndex];
          console.log(
            `\n[Sync] Processando GRUPO ${groupIndex + 1} (${rondaActions.length} ações):`,
            rondaActions.map((a) => a.id),
          );

          let id_adm_ronda_execucao: number | null = null;
          let rondaIdNumber: number | null = null;
          const extractExecucaoId = (value: any): number | null => {
            if (value == null) return null;
            if (typeof value === "number" && Number.isFinite(value))
              return value;
            if (
              typeof value === "string" &&
              value.trim() !== "" &&
              !Number.isNaN(Number(value))
            )
              return Number(value);
            if (Array.isArray(value)) return extractExecucaoId(value[0]);
            if (typeof value === "object") {
              return (
                extractExecucaoId(value.id_adm_ronda_execucao) ??
                extractExecucaoId(value.id_adm_ronda_exec) ??
                extractExecucaoId(value.id_execucao) ??
                extractExecucaoId(value.idExecucao) ??
                extractExecucaoId(value.id) ??
                extractExecucaoId(value.codigoRonda) ??
                extractExecucaoId(value.ronda?.id_adm_ronda_execucao) ??
                extractExecucaoId(value.execucao?.id_adm_ronda_execucao) ??
                // 2026-07-17 (rondas fantasma): o exec id também mora DENTRO de cada
                // ponto do RONDA_FINALIZAR — sem esta recursão, um finalizar órfão
                // (INICIAR já drenado online) parecia "sem execução" e o dreno criava
                // uma execução NOVA no servidor. Cobre ações antigas já enfileiradas
                // (o payload novo também leva o id no top-level).
                (Array.isArray(value.pontos)
                  ? extractExecucaoId(
                      value.pontos.find(
                        (p: any) => p?.id_adm_ronda_execucao != null,
                      )?.id_adm_ronda_execucao,
                    )
                  : null)
              );
            }
            return null;
          };

          const extractColaboradorId = (data: any): number | null => {
            if (!data || typeof data !== "object") return null;
            if (Array.isArray(data?.pontos) && data.pontos.length > 0) {
              const fromPonto = extractColaboradorId(data.pontos[0]);
              if (fromPonto) return fromPonto;
            }
            if (Array.isArray(data) && data.length > 0) {
              const fromArr = extractColaboradorId(data[0]);
              if (fromArr) return fromArr;
            }
            const raw =
              data.codigoColaborador ??
              data.id_adm_colaborador ??
              data.ResponsavelId ??
              data.responsavelId ??
              data.idColaborador;
            if (typeof raw === "number" && Number.isFinite(raw)) return raw;
            if (
              typeof raw === "string" &&
              raw.trim() !== "" &&
              !Number.isNaN(Number(raw))
            )
              return Number(raw);
            return null;
          };

          try {
            rondaIdNumber = parseInt(rondaIdKey);
            console.log("[Sync] Ronda id number:", rondaIdNumber);

            if (isNaN(rondaIdNumber)) {
              console.log(
                "[Sync] Ronda id não é número, adicionando todas as ações à remainingActions",
              );
              rondaActions.forEach((a) => remainingActions.push(a));
              continue;
            }
          } catch (err) {
            console.error("[Sync] Erro ao parsear ronda id:", err);
            rondaActions.forEach((a) => remainingActions.push(a));
            continue;
          }

          // 1. Primeiro, verificar se QUALQUER ação no grupo já tem id_adm_ronda_execucao
          for (const action of rondaActions) {
            const existingExecId = extractExecucaoId(action.data);
            if (existingExecId) {
              console.log(
                "[Sync] Encontrei ação no grupo com id_adm_ronda_execucao! Usando para todo o grupo:",
                existingExecId,
              );
              id_adm_ronda_execucao = existingExecId;
              break;
            }
          }

          // 2. Processar RONDA_INICIAR primeiro
          const rondaIniciarIndex = rondaActions.findIndex(
            (a) => a.type === "RONDA_INICIAR",
          );
          console.log(
            "[Sync] Índice de RONDA_INICIAR:",
            rondaIniciarIndex,
            "id_adm_ronda_execucao já encontrado:",
            id_adm_ronda_execucao,
          );

          if (rondaIniciarIndex === -1) {
            if (id_adm_ronda_execucao) {
              console.log(
                "[Sync] Grupo sem RONDA_INICIAR, mas com id_adm_ronda_execucao - processando com o id encontrado:",
                id_adm_ronda_execucao,
              );
            } else {
              const hasCancelar = rondaActions.some(
                (a) => a.type === "RONDA_CANCELAR",
              );
              // Resolvedor REAL da execução: POST /api/ronda/carregar do t-credentials
              // (Api.tcredentials.rondaCarregar) → { ronda, rondaHistorico }.
              // ⚠️ NÃO usar Api.start.iniciar(): o domínio ronda SAIU do /start (o
              // startRepository devolve rondaHistorico: [] SEMPRE) — ler de lá
              // transformava "endpoint respondeu" num falso "não existe execução".
              // (Api.ronda.continuar também era no-op: passava id de CADASTRO onde o
              // T-ARC filtra por id de EXECUÇÃO, e o modelo de resposta nem tem o
              // campo.) Estratégia: entre as execuções deste cadastro (e do
              // colaborador do grupo, quando conhecido), escolhe a de
              // data_log_inicio mais PRÓXIMA da 1ª ação do grupo (teto 7 dias),
              // aberta OU fechada — proximidade pura, sem prioridade de abertas
              // (uma aberta de outro turno a horas de distância NÃO pode vencer a
              // fechada correta a minutos). FinalizarQr no T-Consumer é cura
              // retroativa idempotente (anti-join): replay em execução fechada
              // CONVERGE (caso real 17212).
              let historicoConsultado = false; // resposta REAL (campo presente)
              let cadastroSemExecucao = false; // nenhum registro do cadastro veio
              if (rondaIdNumber != null) {
                try {
                  // Memo com guarda de sessão (padrão session-generation-guard):
                  // trocarEmpresa durante o await → resposta descartada (helper
                  // devolve null; ids de cadastro COLIDEM entre bancos).
                  const doneRonda = await lerRondaCarregarComMemo();
                  if (doneRonda) {
                    historicoConsultado = true;
                    const colaboradorDoGrupo = extractColaboradorId(
                      rondaActions[0]?.data,
                    );
                    const doCadastro = (doneRonda.rondaHistorico || []).filter(
                      (item: any) =>
                        Number(item.id_adm_ronda_cadastro) === rondaIdNumber &&
                        item.ativo_inativo === "A" &&
                        (!colaboradorDoGrupo ||
                          Number(item.id_adm_colaborador_inicio) ===
                            Number(colaboradorDoGrupo)),
                    );
                    cadastroSemExecucao = doCadastro.length === 0;
                    const tsGrupo = Math.min(
                      ...rondaActions.map(
                        (a) => Number(a.timestamp) || Infinity,
                      ),
                    );
                    const tsRef = Number.isFinite(tsGrupo)
                      ? tsGrupo
                      : Date.now();
                    const candidata = doCadastro
                      .map((item: any) => ({
                        item,
                        ms: _parseBrDateTimeToMs(item.data_log_inicio),
                      }))
                      .filter((x: any) => x.ms != null)
                      .map((x: any) => ({ ...x, dist: Math.abs(tsRef - x.ms) }))
                      .filter((x: any) => x.dist < 7 * 24 * 60 * 60 * 1000)
                      .sort((a: any, b: any) => a.dist - b.dist)[0]?.item;
                    if (candidata?.id_adm_ronda_execucao) {
                      id_adm_ronda_execucao = candidata.id_adm_ronda_execucao;
                      console.log(
                        "[Sync] id_adm_ronda_execucao via ronda/carregar (grupo sem iniciar):",
                        id_adm_ronda_execucao,
                        "aberta:",
                        !candidata.data_log_fim ||
                          String(candidata.data_log_fim).trim() === "",
                      );
                    }
                  }
                } catch (e) {
                  console.warn(
                    "[Sync] Falha ao consultar ronda/carregar (grupo sem iniciar):",
                    e,
                  );
                }
              }

              // ⛔ 2026-07-17 (bug das RONDAS FANTASMA): o dreno JAMAIS cria execução
              // nova para grupo sem RONDA_INICIAR. Grupo sem INICIAR significa que o
              // iniciar JÁ foi processado (a execução EXISTE no servidor) — o antigo
              // "cria RONDA_INICIAR para despachar o grupo" fabricava uma execução
              // duplicada e regravava todos os pontos em rajada (caso real: execuções
              // 17219/17220, 21 pontos no mesmo segundo). Caminhos legítimos para
              // resolver o id: top-level do payload, dentro de `pontos` (recursão do
              // extractExecucaoId) e o rondaHistorico acima — todos já tentados.

              if (!id_adm_ronda_execucao) {
                if (hasCancelar) {
                  console.warn(
                    "[Sync] Grupo com RONDA_CANCELAR sem id_adm_ronda_execucao resolvível - removendo ações do grupo para evitar pendência órfã:",
                    rondaActions.map((a) => ({ id: a.id, type: a.type })),
                  );
                  for (const a of rondaActions) {
                    await SyncService.dequeueOfflineAction(a.id);
                    processedIds.add(a.id);
                  }
                  continue;
                }
                if (!historicoConsultado) {
                  // Falha TRANSITÓRIA do resolvedor (rede/500/timeout/troca de
                  // sessão) — nada de quarentena: retenta no próximo dreno.
                  console.log(
                    "[Sync] Resolvedor de execução indisponível (ronda/carregar falhou) - grupo volta para remainingActions para tentar novamente",
                  );
                  rondaActions.forEach((a) => remainingActions.push(a));
                  continue;
                }
                if (!cadastroSemExecucao) {
                  // Existem execuções do cadastro, mas nenhuma casou (sem data
                  // parseável ou fora da janela de 7 dias) — AMBÍGUO: retry, não
                  // quarentena.
                  console.log(
                    "[Sync] Histórico tem execuções do cadastro mas nenhuma casou com o grupo - retry no próximo dreno",
                  );
                  rondaActions.forEach((a) => remainingActions.push(a));
                  continue;
                }
                // Histórico REAL veio e NÃO contém o cadastro. Mas "vazio" pode ser
                // erro engolido pelo backend best-effort (carregarRonda devolve []
                // com HTTP 200 em falha de banco) — só é terminal após 3 drenos
                // CONSECUTIVOS confirmando a ausência.
                const chaveContador = "@TirvuMais:ronda_orfao_vazias";
                const chaveGrupo = String(rondaActions[0]?.id ?? rondaIdKey);
                let vaziasConsecutivas = 1;
                try {
                  const brutoC = await AsyncStorage.getItem(chaveContador);
                  const parsedC = brutoC ? JSON.parse(brutoC) : {};
                  const mapaC =
                    parsedC && typeof parsedC === "object" ? parsedC : {};
                  vaziasConsecutivas = (Number(mapaC[chaveGrupo]) || 0) + 1;
                  mapaC[chaveGrupo] = vaziasConsecutivas;
                  const chavesC = Object.keys(mapaC);
                  if (chavesC.length > 50) {
                    for (const k of chavesC.slice(0, chavesC.length - 50)) {
                      delete mapaC[k];
                    }
                  }
                  await AsyncStorage.setItem(
                    chaveContador,
                    JSON.stringify(mapaC),
                  );
                } catch {}
                if (vaziasConsecutivas < 3) {
                  console.log(
                    `[Sync] Cadastro ausente do histórico (${vaziasConsecutivas}/3 confirmações) - retry antes de quarentenar`,
                  );
                  rondaActions.forEach((a) => remainingActions.push(a));
                  continue;
                }
                {
                  // 3+ confirmações: nenhuma execução deste cadastro existe para o
                  // colaborador. Reenviar sem id é impossível e inventar execução é
                  // o bug. QUARENTENA forense local + dequeue (qualquer composição
                  // de grupo — misto sem execução também não tem para onde ir) — e
                  // o dequeue SÓ acontece se a cópia forense confirmou (senão volta
                  // para a fila e tenta de novo).
                  console.warn(
                    "[Sync] Grupo de ronda órfão sem execução resolvível (3x confirmado pelo servidor) — movendo para quarentena forense (NUNCA criar execução nova):",
                    rondaActions.map((a) => ({ id: a.id, type: a.type })),
                  );
                  let quarentenaOk = false;
                  try {
                    await gravarQuarentenaRonda(
                      rondaActions,
                      "execução inexistente (3x confirmado pelo servidor)",
                    );
                    quarentenaOk = true;
                  } catch (e) {
                    console.error(
                      "[Sync] Falha ao gravar quarentena forense de ronda - ações permanecem na fila:",
                      e,
                    );
                  }
                  if (!quarentenaOk) {
                    rondaActions.forEach((a) => remainingActions.push(a));
                    continue;
                  }
                  for (const a of rondaActions) {
                    await SyncService.dequeueOfflineAction(a.id);
                    processedIds.add(a.id);
                  }
                  // limpa o contador do grupo (best-effort)
                  try {
                    const brutoC = await AsyncStorage.getItem(chaveContador);
                    const mapaC = brutoC ? JSON.parse(brutoC) : {};
                    if (mapaC && typeof mapaC === "object") {
                      delete mapaC[chaveGrupo];
                      await AsyncStorage.setItem(
                        chaveContador,
                        JSON.stringify(mapaC),
                      );
                    }
                  } catch {}
                  continue;
                }
              }
            }
          }

          if (rondaIniciarIndex !== -1) {
            const action = rondaActions[rondaIniciarIndex];
            console.log(
              "[Sync] Ação RONDA_INICIAR:",
              JSON.stringify(action, null, 2),
            );

            const actionExecId = extractExecucaoId(action.data);
            if (actionExecId) {
              console.log(
                "[Sync] Já temos id_adm_ronda_execucao na ação! Usando diretamente:",
                actionExecId,
              );
              id_adm_ronda_execucao = actionExecId;
              processedIds.add(action.id);
              await SyncService.dequeueOfflineAction(action.id);
            } else if (id_adm_ronda_execucao) {
              console.log(
                "[Sync] id_adm_ronda_execucao já encontrado no grupo - marcando RONDA_INICIAR como processada:",
                id_adm_ronda_execucao,
              );
              processedIds.add(action.id);
              await SyncService.dequeueOfflineAction(action.id);
            } else {
              try {
                console.log(
                  "[Sync] Sincronizando início de ronda (primeiro)...",
                );
                const payload = {
                  ...action.data,
                  id_adm_ronda_cadastro:
                    action.data.id_adm_ronda_cadastro ??
                    action.data.codigoRonda,
                };
                console.log("[Sync] Payload para iniciar ronda:", payload);
                const response = await Api.ronda.iniciar(payload);
                console.log(
                  "[Sync] Resposta da API para iniciar ronda:",
                  response,
                );

                if (response.success) {
                  console.log("[Sync] Ronda iniciada com sucesso via API!");
                  console.log(
                    "[Sync] Resposta completa da API de iniciar ronda:",
                    JSON.stringify(response, null, 2),
                  );
                  id_adm_ronda_execucao =
                    extractExecucaoId(response.done) ??
                    extractExecucaoId(response);
                  console.log(
                    "[Sync] id_adm_ronda_execucao da resposta:",
                    id_adm_ronda_execucao,
                  );

                  if (!id_adm_ronda_execucao && rondaIdNumber != null) {
                    try {
                      const contResp = await Api.ronda.continuar(rondaIdNumber);
                      if (contResp?.success) {
                        id_adm_ronda_execucao =
                          extractExecucaoId(contResp.done) ??
                          extractExecucaoId(contResp);
                        console.log(
                          "[Sync] id_adm_ronda_execucao via Ronda/continuar:",
                          id_adm_ronda_execucao,
                        );
                      }
                    } catch (e) {
                      console.warn(
                        "[Sync] Falha ao consultar Ronda/continuar após iniciar:",
                        e,
                      );
                    }
                  }

                  if (!id_adm_ronda_execucao) {
                    console.log(
                      "[Sync] id_adm_ronda_execucao não veio na resposta - atualizando userData e tentando obter...",
                    );
                    const refreshResponse = await Api.start.iniciar();
                    if (refreshResponse.success && refreshResponse.done) {
                      const userData = refreshResponse.done;
                      console.log(
                        "[Sync] UserData atualizado:",
                        JSON.stringify(userData, null, 2),
                      );

                      const historicoFiltrado = (
                        userData?.rondaHistorico || []
                      ).filter(
                        (item: any) =>
                          item.id_adm_ronda_cadastro === rondaIdNumber &&
                          item.ativo_inativo === "A" &&
                          (!item.data_log_fim ||
                            item.data_log_fim.trim() === ""),
                      );

                      const historicoOrdenado = [...historicoFiltrado].sort(
                        (a: any, b: any) =>
                          (_parseBrDateTimeToMs(b.data_log_inicio) ?? 0) -
                          (_parseBrDateTimeToMs(a.data_log_inicio) ?? 0),
                      );

                      const execucaoRecente = historicoOrdenado[0];
                      console.log(
                        "[Sync] Execução mais recente encontrada:",
                        execucaoRecente,
                      );

                      if (execucaoRecente) {
                        const dataInicioExecucao = _parseBrDateTimeToMs(
                          execucaoRecente.data_log_inicio,
                        );
                        const agora = Date.now();
                        const diferencaSegundos =
                          dataInicioExecucao != null
                            ? (agora - dataInicioExecucao) / 1000
                            : null;

                        console.log(
                          "[Sync] Diferença de tempo entre agora e início da execução:",
                          diferencaSegundos,
                          "segundos",
                        );

                        if (
                          diferencaSegundos == null ||
                          !Number.isFinite(diferencaSegundos) ||
                          diferencaSegundos < 120
                        ) {
                          id_adm_ronda_execucao =
                            execucaoRecente.id_adm_ronda_execucao;
                          console.log(
                            "[Sync] Usando id_adm_ronda_execucao da execução recente:",
                            id_adm_ronda_execucao,
                          );
                        } else {
                          console.log(
                            "[Sync] Execução encontrada é muito antiga - não usaremos!",
                          );
                        }
                      }
                    }

                    if (!id_adm_ronda_execucao) {
                      console.log(
                        "[Sync] Ainda não temos id_adm_ronda_execucao - mantendo TODAS as ações do grupo (incluindo RONDA_INICIAR) para tentar novamente",
                      );
                      rondaActions.forEach((a) => remainingActions.push(a));
                      continue;
                    }
                  }

                  processedIds.add(action.id);
                  await SyncService.dequeueOfflineAction(action.id);
                } else {
                  // 🛡️ Marcador do t-credentials: o CADASTRO não existe neste
                  // tenant (validação TENANT-WIDE, autoritativa — caso real: 32
                  // ações de ronda HERDADAS de outra empresa num aparelho cuja
                  // fila sobreviveu ao logoff remoto; empresa atual nem usa o
                  // módulo de rondas). Retry seria eterno e o gate de logout
                  // ficaria travado para sempre → QUARENTENA forense + dequeue.
                  const errosTxt = (response.erros ?? [])
                    .map((e: any) => String(e))
                    .join(" | ");
                  if (errosTxt.includes("RONDA_CADASTRO_INEXISTENTE")) {
                    console.warn(
                      `[Sync] 🛡️ Cadastro ${rondaIdNumber} INEXISTENTE no tenant (confirmado pelo servidor) — grupo vai para quarentena forense:`,
                      rondaActions.map((a) => ({ id: a.id, type: a.type })),
                    );
                    let quarentenaCadOk = false;
                    try {
                      await gravarQuarentenaRonda(
                        rondaActions,
                        `cadastro ${rondaIdNumber} inexistente no tenant (servidor)`,
                      );
                      quarentenaCadOk = true;
                    } catch (e) {
                      console.error(
                        "[Sync] Falha ao gravar quarentena (cadastro inexistente) - grupo permanece na fila:",
                        e,
                      );
                    }
                    if (quarentenaCadOk) {
                      for (const a of rondaActions) {
                        await SyncService.dequeueOfflineAction(a.id);
                        processedIds.add(a.id);
                      }
                      continue;
                    }
                  }
                  console.log(
                    "[Sync] Falha ao iniciar ronda via API:",
                    response.erros,
                  );
                  rondaActions.forEach((a) => remainingActions.push(a));
                  continue;
                }
              } catch (err) {
                console.error(
                  "[Sync] Erro ao sincronizar início de ronda:",
                  err,
                );
                rondaActions.forEach((a) => remainingActions.push(a));
                continue;
              }
            }
          }

          console.log(
            `[Sync] RONDA ${rondaIdKey} - Grupo ${groupIndex + 1}: usando id_adm_ronda_execucao =`,
            id_adm_ronda_execucao,
          );

          // 2. Processar as outras ações da ronda
          for (let i = 0; i < rondaActions.length; i++) {
            const action = rondaActions[i];
            if (processedIds.has(action.id)) continue;

            // ⚠️ RONDA_FINALIZAR LEGADO tem action.data como ARRAY puro de pontos:
            // spread de array vira objeto {0:...,1:...} — a extração de pontos
            // falharia (finalizarQr([]) + 200 do T-ARC = registro legado APAGADO) e
            // um updateOfflineActionData gravaria a ação corrompida para sempre.
            // Normaliza para o formato novo { pontos: [...] } preservando os dados.
            let updatedData: any = Array.isArray(action.data)
              ? { pontos: [...action.data] }
              : { ...action.data };

            try {
              // Atualizar a ação com o id de execução, se temos
              let listaPontos: any[] = [];

              // Primeiro, verificar se a ação já tem id_adm_ronda_execucao!
              let actionExecutionId = id_adm_ronda_execucao;
              if (!actionExecutionId) {
                const fromAction = extractExecucaoId(action.data);
                if (fromAction) {
                  console.log(
                    "[Sync] Ação já tem id_adm_ronda_execucao! Usando:",
                    fromAction,
                  );
                  actionExecutionId = fromAction;
                }
              }

              if (action.type === "RONDA_FINALIZAR") {
                // Se for RONDA_FINALIZAR, extrair os pontos (formato novo ou legado
                // já normalizado acima)
                if (updatedData.pontos && Array.isArray(updatedData.pontos)) {
                  listaPontos = updatedData.pontos;
                }
                if (listaPontos.length === 0) {
                  // Finalizar sem nenhum ponto extraível: NUNCA despachar
                  // finalizarQr([]) (o 200 do T-ARC apagaria a ação com zero
                  // entrega). Mantém na fila para inspeção.
                  console.warn(
                    "[Sync] RONDA_FINALIZAR sem pontos extraíveis - mantendo na fila (não despachar vazio):",
                    action.id,
                  );
                  remainingActions.push(action);
                  continue;
                }
              }

              if (actionExecutionId) {
                updatedData.id_adm_ronda_execucao = actionExecutionId;

                // Para CHECKLIST_SALVAR, também atualiza o IdReferenciaVinculo
                if (action.type === "CHECKLIST_SALVAR") {
                  updatedData.IdReferenciaVinculo = actionExecutionId;
                }
              } else {
                // Se não temos nenhum id_adm_ronda_execucao (nem do grupo nem da própria ação), não podemos processar!
                console.log(
                  "[Sync] Não temos id_adm_ronda_execucao para essa ação - mantendo na fila",
                );
                remainingActions.push(action);
                continue;
              }

              if (action.type === "RONDA_SALVAR_PONTO") {
                console.log("[Sync] Sincronizando ponto de ronda...");
                console.log(
                  "[Sync] Dados originais da ação:",
                  JSON.stringify(action.data, null, 2),
                );
                const pontoPayload = updatedData;
                console.log(
                  "[Sync] Ponto payload com id_adm_ronda_execucao:",
                  JSON.stringify(pontoPayload, null, 2),
                );

                let fotoUrl = pontoPayload.foto;
                if (fotoUrl && fotoUrl.includes("os_fotos/")) {
                  try {
                    const safeName = `ronda_${Date.now()}.jpg`;
                    const caminhoPasta = "Rondas";

                    const arquivo = {
                      uri: fotoUrl,
                      name: safeName,
                      type: "image/jpeg",
                    } as any;

                    const uploadResponse = await Api.geral.upload(
                      arquivo,
                      caminhoPasta,
                      safeName,
                    );

                    if (uploadResponse?.success) {
                      const done: any = uploadResponse.done;
                      fotoUrl =
                        done?.urlAcesso ??
                        done?.url ??
                        done?.endereco_arquivo ??
                        fotoUrl;

                      try {
                        await FileSystem.deleteAsync(fotoUrl, {
                          idempotent: true,
                        });
                      } catch (e) {
                        console.warn(
                          "[Sync] Falha ao excluir arquivo local de ronda:",
                          e,
                        );
                      }
                    }
                  } catch (uploadErr) {
                    console.error(
                      "[Sync] Erro no upload de foto de ronda:",
                      uploadErr,
                    );
                  }
                }

                const payloadToSend = {
                  ...pontoPayload,
                  foto: fotoUrl,
                };
                console.log(
                  "[Sync] Payload final para salvar ponto:",
                  JSON.stringify(payloadToSend, null, 2),
                );

                const response = await Api.ronda.salvarPontoQr(payloadToSend);
                console.log(
                  "[Sync] Resposta da API para salvar ponto:",
                  JSON.stringify(response, null, 2),
                );

                if (!response.success) {
                  paresPontoFalhou.add(parRonda(updatedData));
                  await SyncService.updateOfflineActionData(
                    action.id,
                    updatedData,
                  );
                  remainingActions.push({ ...action, data: updatedData });
                } else {
                  processedIds.add(action.id);
                  await SyncService.dequeueOfflineAction(action.id);
                }
              } else if (action.type === "CHECKLIST_SALVAR") {
                // O ponto deste par falhou agora há pouco: segura o checklist na fila
                // (sem POST) — ele vai na próxima passada, DEPOIS do ponto.
                if (
                  Number(updatedData?.id_adm_ronda_execucao ?? 0) > 0 &&
                  paresPontoFalhou.has(parRonda(updatedData))
                ) {
                  console.warn(
                    "[Sync] Checklist de ronda retido: o ponto do par falhou nesta passada",
                  );
                  remainingActions.push({ ...action, data: updatedData });
                  continue;
                }
                console.log("[Sync] Sincronizando checklist de ronda...");
                // __localExecId / __localComplementoId não vão para a API — são as
                // âncoras nos mapas locais, usadas logo abaixo para o selo virar 2 checks.
                const {
                  Assets,
                  __localExecId,
                  __localComplementoId,
                  ...restData
                } = updatedData;
                let payloadProcessado = { ...restData };

                const urisParaRemover: string[] = [];
                let uploadFalhouComArquivo = false;
                if (Assets && Assets.length > 0) {
                  const respostasComUrls = [...restData.Respostas];

                  for (const asset of Assets) {
                    const fileType = asset.Tipo === "pdf" ? "pdf" : "image";
                    // Foto que já está no storage (edição de execução): reaproveita a URL.
                    // Sem isso o upload falharia e a foto seria descartada como "arquivo
                    // inexistente no dispositivo".
                    const jaRemoto = SyncService._ehUrlRemota(asset.Uri);
                    const url = jaRemoto
                      ? String(asset.Uri).trim()
                      : await SyncService.uploadChecklistAsset(
                          asset.Uri,
                          restData.ResponsavelId,
                          restData.ChecklistId,
                          fileType,
                        );

                    if (url) {
                      if (asset.Tipo === ("lsp" as any)) {
                        // LSP: junta a URL da foto no Foto da resposta EXISTENTE (a placa).
                        const alvo = respostasComUrls.find(
                          (r: any) => r.PerguntaId === asset.PerguntaId,
                        );
                        if (alvo) alvo.Foto = url;
                        else
                          respostasComUrls.push({
                            PerguntaId: asset.PerguntaId,
                            Resposta: "",
                            Foto: url,
                          });
                      } else {
                        const respostaTexto =
                          asset.Tipo === "foto"
                            ? "Foto"
                            : asset.Tipo === "assinatura"
                              ? "Assinatura (App)"
                              : "PDF";
                        respostasComUrls.push({
                          PerguntaId: asset.PerguntaId,
                          Resposta: respostaTexto,
                          Foto: url,
                        });
                      }
                      if (!jaRemoto) urisParaRemover.push(asset.Uri);
                    } else {
                      // Arquivo SUMIU (cache limpo/app reinstalado) → seguir sem
                      // ele (o dado do checklist vale mais que a foto perdida).
                      // Upload FALHOU com o arquivo presente → transitório:
                      // gravar o checklist agora o deixaria PARA SEMPRE sem a
                      // foto (o backend não é idempotente para reenviar depois).
                      let arquivoExiste = false;
                      try {
                        const info = await FileSystem.getInfoAsync(
                          caminhoNoAppAtual(asset.Uri),
                        );
                        arquivoExiste = !!info.exists;
                      } catch {}
                      if (arquivoExiste) uploadFalhouComArquivo = true;
                      console.warn(
                        arquivoExiste
                          ? "[Sync] Upload de asset FALHOU (arquivo existe) - checklist adiado p/ não gravar sem a foto:"
                          : "[Sync] Asset inexistente no dispositivo - seguindo sem ele:",
                        asset?.Uri,
                      );
                    }
                  }

                  payloadProcessado.Respostas = respostasComUrls;
                }
                if (uploadFalhouComArquivo) {
                  remainingActions.push(action);
                  continue;
                }

                // ⚠️ Os arquivos locais dos assets NÃO são apagados aqui: se o
                // salvar falhar, a re-tentativa precisa deles (apagar antes
                // gravava o checklist SEM as fotos). A ação é atualizada com as
                // URLs já obtidas para o retry não re-subir tudo.
                try {
                  await SyncService.updateOfflineActionData(action.id, {
                    ...action.data,
                    Respostas: payloadProcessado.Respostas,
                    Assets: [],
                  });
                } catch {}

                const response =
                  await Api.checklistExecucao.salvar(payloadProcessado);
                if (response.success) {
                  for (const uri of urisParaRemover) {
                    if (uri && uri.includes("checklist_assets/")) {
                      try {
                        await FileSystem.deleteAsync(uri, { idempotent: true });
                      } catch {}
                    }
                  }
                }
                if (!response.success) {
                  await SyncService.updateOfflineActionData(
                    action.id,
                    updatedData,
                  );
                  remainingActions.push({ ...action, data: updatedData });
                } else {
                  // Execução avulsa (livre/ponto): sai da fila → 1 check vira 2 cinza.
                  // Ronda/O.S. não carregam __localExecId e caem no no-op.
                  if (__localExecId) {
                    await SyncService.markLocalChecklistExecucaoEnviada(
                      String(__localExecId),
                    );
                  }
                  if (__localComplementoId) {
                    await SyncService.markLocalChecklistComplementoEnviado(
                      String(__localComplementoId),
                    );
                  }
                  processedIds.add(action.id);
                  await SyncService.dequeueOfflineAction(action.id);
                }
              } else if (action.type === "RONDA_FINALIZAR") {
                console.log("[Sync] Sincronizando finalização de ronda...");
                console.log(
                  "[Sync] Dados originais da ação RONDA_FINALIZAR:",
                  JSON.stringify(action.data, null, 2),
                );

                // Garantir que temos a lista de pontos
                let pontosParaProcessar = listaPontos;
                console.log(
                  "[Sync] Lista de pontos para processar:",
                  JSON.stringify(pontosParaProcessar, null, 2),
                );

                if (id_adm_ronda_execucao) {
                  pontosParaProcessar = pontosParaProcessar.map(
                    (ponto: any) => ({
                      ...ponto,
                      id_adm_ronda_execucao: id_adm_ronda_execucao,
                    }),
                  );
                  console.log(
                    "[Sync] Pontos com id_adm_ronda_execucao adicionado:",
                    JSON.stringify(pontosParaProcessar, null, 2),
                  );
                }

                let processedListaPontos = [...pontosParaProcessar];
                const urisParaRemover: string[] = [];

                for (let j = 0; j < processedListaPontos.length; j++) {
                  const ponto = processedListaPontos[j];
                  if (ponto.foto && ponto.foto.includes("os_fotos/")) {
                    try {
                      const safeName = `ronda_${Date.now()}_${j}.jpg`;
                      const caminhoPasta = "Rondas";

                      const arquivo = {
                        uri: ponto.foto,
                        name: safeName,
                        type: "image/jpeg",
                      } as any;

                      const uploadResponse = await Api.geral.upload(
                        arquivo,
                        caminhoPasta,
                        safeName,
                      );

                      if (uploadResponse?.success) {
                        const done: any = uploadResponse.done;
                        const fotoUrl =
                          done?.urlAcesso ??
                          done?.url ??
                          done?.endereco_arquivo ??
                          ponto.foto;
                        processedListaPontos[j] = { ...ponto, foto: fotoUrl };
                        urisParaRemover.push(ponto.foto);
                      }
                    } catch (uploadErr) {
                      console.error(
                        "[Sync] Erro no upload de foto de ronda:",
                        uploadErr,
                      );
                    }
                  }
                }

                console.log(
                  "[Sync] Payload final para finalizar ronda:",
                  JSON.stringify(processedListaPontos, null, 2),
                );
                const response =
                  await Api.ronda.finalizarQr(processedListaPontos);
                console.log(
                  "[Sync] Resposta da API para finalizar ronda:",
                  JSON.stringify(response, null, 2),
                );
                if (!response.success) {
                  await SyncService.updateOfflineActionData(
                    action.id,
                    updatedData,
                  );
                  remainingActions.push({ ...action, data: updatedData });
                } else {
                  processedIds.add(action.id);
                  await SyncService.dequeueOfflineAction(action.id);
                  for (const uri of urisParaRemover) {
                    try {
                      await FileSystem.deleteAsync(uri, { idempotent: true });
                    } catch (e) {
                      console.warn(
                        "[Sync] Falha ao excluir arquivo local de ronda:",
                        e,
                      );
                    }
                  }
                }
              } else if (action.type === "RONDA_CANCELAR") {
                console.log("[Sync] Sincronizando cancelamento de ronda...");
                const payload = updatedData;
                const rondaIdCancel =
                  id_adm_ronda_execucao ||
                  payload.id_adm_ronda_execucao ||
                  payload.codigoRonda;
                const response = await Api.ronda.cancelar(rondaIdCancel);
                if (!response.success) {
                  await SyncService.updateOfflineActionData(
                    action.id,
                    updatedData,
                  );
                  remainingActions.push({ ...action, data: updatedData });
                } else {
                  processedIds.add(action.id);
                  await SyncService.dequeueOfflineAction(action.id);
                }
              }
            } catch (err) {
              console.error("[Sync] Erro ao sincronizar ação de ronda:", err);
              await SyncService.updateOfflineActionData(action.id, updatedData);
              remainingActions.push({ ...action, data: updatedData });
            }
          }
        }
      }

      let osSobIdMap = await SyncService.getLocalOsSobDemandaIdMap();
      const resolveOsCronogramaId = (raw: any): number | null => {
        const n = typeof raw === "number" ? raw : Number(raw);
        if (!Number.isFinite(n)) return null;
        if (!_isTempCronogramaId(n)) return n;
        const mapped = osSobIdMap[String(n)];
        const real = mapped?.id_adm_ordem_servico_cronograma;
        return typeof real === "number" && Number.isFinite(real) && real > 0
          ? real
          : null;
      };

      const osSobCreateActions = otherActions.filter(
        (a) =>
          a.type === "OS_SOBDEMANDA_SALVAR" &&
          a?.data &&
          a.data.__temp_cronograma_id != null,
      );
      if (osSobCreateActions.length > 0) {
        for (const action of osSobCreateActions) {
          if (processedIds.has(action.id)) continue;
          const tempId = Number(action.data.__temp_cronograma_id);
          if (!Number.isFinite(tempId) || !_isTempCronogramaId(tempId)) {
            continue;
          }

          const already = osSobIdMap[String(tempId)];
          // "Já criada" = ter QUALQUER id do servidor. O POST /salvar é síncrono: vir o
          // id_adm_ordem_servico significa OS COMMITADA. A guarda antiga exigia o
          // id do CRONOGRAMA — que só aparece num /iniciar posterior (cache ~3min) — e
          // re-POSTava uma OS já criada a cada dreno (uma das portas do relato de 8x).
          // O cronograma continua sendo completado pelo resolvedor logo abaixo.
          if (
            (Number(already?.id_adm_ordem_servico_cronograma) || 0) > 0 ||
            (Number(already?.id_adm_ordem_servico) || 0) > 0
          ) {
            await SyncService.dequeueOfflineAction(action.id);
            processedIds.add(action.id);
            continue;
          }

          try {
            const response = await Api.ordemServico.salvar(
              _cleanOsSobDemandaPayload(action.data),
            );
            if (!response.success) {
              await tratarFalhaDespacho(action, response, action.type);
              continue;
            }

            const osId = Number((response as any)?.done?.id_adm_ordem_servico);
            if (Number.isFinite(osId) && osId > 0) {
              osSobIdMap[String(tempId)] = {
                ...(osSobIdMap[String(tempId)] ?? {}),
                id_adm_ordem_servico: osId,
              };
              // Write-ahead: o mapa vai ao disco ANTES do dequeue. Morrendo entre o 200
              // e o dequeue, o próximo dreno encontra o id e dá baixa sem re-criar.
              await SyncService.setLocalOsSobDemandaIdMap(osSobIdMap);
              const local = await SyncService.getLocalOsSobDemandaMap();
              const cur = local[String(tempId)];
              if (cur) {
                await SyncService.setLocalOsSobDemanda({
                  ...cur,
                  id_adm_ordem_servico: osId,
                });
              }
            }

            await SyncService.dequeueOfflineAction(action.id);
            processedIds.add(action.id);
          } catch {
            remainingActions.push(action);
          }
        }

        await SyncService.setLocalOsSobDemandaIdMap(osSobIdMap);
      }

      // 🔗 RESOLUÇÃO temp→cronograma: roda em TODO dreno, NÃO só quando ainda há
      // OS_SOBDEMANDA_SALVAR na fila. A OS-base sai da fila assim que o POST
      // responde 200, mas 200 do T-ARC = "enfileirado no Rabbit": o
      // id_adm_ordem_servico_cronograma só aparece num /iniciar posterior. Preso
      // dentro do `if`, o mapa nunca era completado depois desse ciclo e TODAS as
      // ações dependentes (checklist, foto, iniciar, concluir) ficavam órfãs para
      // sempre — causa direta de "OS com checklist pendente que nunca sincroniza".
      {
        const osSobIdMapPend = await SyncService.getLocalOsSobDemandaIdMap();
        const needCrono = Object.entries(osSobIdMapPend).filter(
          ([k, v]: [string, any]) =>
            _isTempCronogramaId(Number(k)) &&
            v?.id_adm_ordem_servico &&
            !v?.id_adm_ordem_servico_cronograma,
        );
        if (needCrono.length > 0) {
          try {
            const startResp = await Api.start.iniciar();
            const ordemServicos: any[] = Array.isArray(
              (startResp as any)?.done?.ordemServicos,
            )
              ? (startResp as any).done.ordemServicos
              : [];

            for (const [tempKey, v] of needCrono) {
              const osId = Number(v?.id_adm_ordem_servico);
              if (!Number.isFinite(osId) || osId <= 0) continue;
              const serverOs = ordemServicos.find(
                (o: any) => Number(o?.id_adm_ordem_servico) === osId,
              );
              const cronId = Number(serverOs?.id_adm_ordem_servico_cronograma);
              if (Number.isFinite(cronId) && cronId > 0) {
                osSobIdMapPend[tempKey] = {
                  ...(osSobIdMapPend[tempKey] ?? {}),
                  id_adm_ordem_servico_cronograma: cronId,
                };
                await SyncService.remapLocalOsStatusKey(tempKey, cronId);
                // Fotos registradas sob o id temporário seguem o cronograma real —
                // senão os detalhes (que abrem pelo id real) não as encontrariam.
                await SyncService.remapLocalOsFotosKey(tempKey, cronId);
                await SyncService.removeLocalOsSobDemanda(tempKey);
                console.warn(
                  "[Sync] 🔗 Cronograma REAL resolvido para OS sob demanda:",
                  { tempKey, cronId },
                );
              }
            }
            await SyncService.setLocalOsSobDemandaIdMap(osSobIdMapPend);
          } catch {}
        }
        // o resolvedor do dreno passa a enxergar o mapa recém-atualizado
        osSobIdMap = osSobIdMapPend;
      }

      // Processar as outras ações (não de ronda)
      console.log(
        "[LIVRO-DBG] processando otherActions:",
        otherActions.length,
        otherActions.map((a) => a.type),
      );
      // Mapa de backoff lido UMA vez por dreno (ações rejeitadas pelo servidor
      // esperam antes de tentar de novo — ver tratarFalhaDespacho).
      const backoffMapa = await lerEsperasDeAcoes();

      for (const action of otherActions) {
        if (processedIds.has(action.id)) continue;

        // Em BACKOFF: permanece na fila SEM tentar (evita spam contra um erro
        // que se repetiria igual). Nunca descarta — só espaça. Sync MANUAL (a
        // pessoa puxou) tenta mesmo assim — ver o doc de processOfflineQueue.
        const espera = Number(backoffMapa[action.id]?.proximaEm) || 0;
        if (espera > Date.now() && !opcoes?.manual) {
          remainingActions.push(action);
          continue;
        }
        if (espera > Date.now()) {
          console.log(
            `[Sync] ${action.type} ${action.id}: em backoff, mas o sync é manual — tentando agora.`,
          );
        }

        try {
          if (action.type === "SIGNATURE") {
            console.log(`[Sync] Sincronizando assinatura do documento...`);
            const response = await Api.arquivo.assinar(action.data);
            if (!response.success) {
              // VISÍVEL: SIGNATURE rejeitada fica na fila re-tentando (1 check "eterno" e o
              // _Ass.pdf nunca nasce). Logar o motivo é o diagnóstico do "1 check preso".
              console.warn(
                "[Sync] SIGNATURE rejeitada pelo servidor (fica na fila p/ retry):",
                action.data?.id_adm_contra_cheque_colaborador,
                JSON.stringify((response as any)?.erros ?? response),
              );
              remainingActions.push(action);
            } else {
              // 'O' (aguardando sync), NÃO 'S': enviar ao /Arquivo/assinar só ENFILEIRA
              // no Rabbit — não persiste. O applyLocalDocStatus limpa quando o servidor
              // realmente retornar assinado==='S' (consumer reescreveu o arquivo).
              const docId = action.data?.id_adm_contra_cheque_colaborador;
              if (docId != null) {
                await SyncService.setLocalDocStatus(docId, "O");
                // ENTREGUE: carimba o momento p/ o grace do _Ass.pdf (detecção de reversão).
                await SyncService.setLocalDocDeliveredAt(docId, Date.now());
                // Log persistente: a janela do "revert por confirmação fresca" conta a
                // partir DESTA entrega (não da assinatura offline, que pode ser bem antes).
                await marcarDocEntregueLog(docId);
              }
              await SyncService.dequeueOfflineAction(action.id);
              processedIds.add(action.id);
            }
          } else if (action.type === "DOCUMENTO_CONTESTAR") {
            console.log("[Sync] Sincronizando contestação de documento...");
            const response = await Api.arquivo.contestar(action.data);
            if (!response.success) {
              await tratarFalhaDespacho(action, response, action.type);
            } else {
              await SyncService.dequeueOfflineAction(action.id);
              processedIds.add(action.id);
            }
          } else if (action.type === "CONTESTACAO_VISTA") {
            console.log("[Sync] Sincronizando contestação vista...");
            const response = await Api.arquivo.contestacaoVista(action.data);
            if (!response.success) {
              await tratarFalhaDespacho(action, response, action.type);
            } else {
              await SyncService.dequeueOfflineAction(action.id);
              processedIds.add(action.id);
            }
          } else if (action.type === "NOTIFICACOES_LIDAS") {
            console.log("[Sync] Sincronizando notificações lidas...");
            const response = await Api.colaboradores.marcarNotificacoesLidas();
            if (!response.success) {
              await tratarFalhaDespacho(action, response, action.type);
            } else {
              await SyncService.dequeueOfflineAction(action.id);
              processedIds.add(action.id);
            }
          } else if (action.type === "JUSTIFICATIVA") {
            console.log("[Sync] Sincronizando justificativa pendente...");
            const payload = action.data as JustificativaPayload;

            let enderecoArquivo: string | null = null;
            const fileToUpload = payload.files?.[0];
            if (fileToUpload?.uri) {
              enderecoArquivo = await SyncService.uploadJustificativaArquivo(
                fileToUpload,
                payload.id_adm_colaborador,
                payload.data_competencia,
              );
              if (!enderecoArquivo) {
                remainingActions.push(action);
                continue;
              }
            }

            const formData = SyncService.buildJustificativaFormData(
              payload,
              enderecoArquivo,
            );
            const response = await Api.rhPontoJustificativa.salvar(formData);
            if (!response.success) {
              await tratarFalhaDespacho(action, response, action.type);
            } else {
              const map = await SyncService.getLocalJustificativasMap();
              const current = map[payload.data_competencia];
              if (current) {
                await SyncService.setLocalJustificativa(
                  payload.data_competencia,
                  {
                    ...current,
                    offline: false,
                  },
                );
              }
              await SyncService.dequeueOfflineAction(action.id);
              processedIds.add(action.id);
            }
          } else if (action.type === "JUSTIFICATIVA_CANCELAR") {
            // Mesma passada (em ordem de fila) que o JUSTIFICATIVA: um cancelamento
            // enfileirado ANTES de uma nova solicitação do mesmo dia sai antes dela.
            console.log(
              "[Sync] Sincronizando cancelamento de justificativa...",
            );
            const response = await Api.rhPontoJustificativa.cancelar(
              action.data as JustificativaCancelarPayload,
            );
            if (!response.success) {
              await tratarFalhaDespacho(action, response, action.type);
            } else {
              await SyncService.dequeueOfflineAction(action.id);
              processedIds.add(action.id);
            }
          } else if (action.type === "DADOS_COLABORADOR") {
            console.log(
              "[Sync] Sincronizando dados do colaborador pendente...",
            );
            const p = action.data as DadosColaboradorPayload;
            const res = await Api.colaboradores.salvar(
              p.codigoColaborador,
              p.tipoColaborador,
              SyncService._buildSalvarColaboradorBody(p),
            );
            if (!res.success) {
              await tratarFalhaDespacho(action, res, action.type);
            } else {
              await SyncService.dequeueOfflineAction(action.id);
              processedIds.add(action.id);
            }
          } else if (action.type === "PONTO") {
            console.log("[Sync] Sincronizando ponto pendente...");
            const { localId, id_adm_cliente, id_adm_empresa, ponto } =
              action.data ?? {};
            const map = await SyncService.getLocalPontosMap();
            // Batida que o mapa já tem como ENVIADA (ação velha que voltou com uma versão
            // recuperada da fila, ou o dequeue falhou depois do envio): não reenvia. Se ela
            // não aparecer no servidor, a cura do mapa a devolve a pendente e reenfileira.
            if (localId && map?.[localId] && map[localId].offline === false) {
              console.warn(
                "[Sync] PONTO: batida já enviada segundo o mapa — ação removida sem reenviar:",
                { actionId: action.id, localId },
              );
              await SyncService.dequeueOfflineAction(action.id);
              processedIds.add(action.id);
              continue;
            }
            const lp: LocalPonto | null =
              localId && map?.[localId] ? map[localId] : (ponto ?? null);
            console.log(
              "[Sync] PONTO: total locais no mapa:",
              Object.keys(map || {}).length,
            );
            console.log(
              "[Sync] PONTO: localId:",
              localId,
              "existe:",
              !!lp,
              "via:",
              lp ? (localId && map?.[localId] ? "map" : "snapshot") : "none",
            );

            if (!lp) {
              console.warn(
                "[Sync] PONTO: não encontrei ponto local (map/snapshot). Mantendo na fila:",
                { actionId: action.id, localId },
              );
              remainingActions.push(action);
              continue;
            }

            // 🔒 Cinto extra cross-tenant: o carimbo AUTORITATIVO da batida é o
            // hash do LocalPonto do MAPA (o envelope pode ser legado/sem stamp).
            // Batida de outro tenant NUNCA sai pela sessão atual.
            const lpHash = String(lp.hash ?? "")
              .trim()
              .toUpperCase();
            if (lpHash && hashSessaoDreno && lpHash !== hashSessaoDreno) {
              console.warn(
                "[Sync] 🔒 PONTO de OUTRO tenant retido na fila (envia quando a sessão da empresa dona voltar):",
                { actionId: action.id, localId: lp.id },
              );
              remainingActions.push(action);
              continue;
            }

            try {
              console.log("[Sync] PONTO: chamando _enviarPontoOnline...", {
                actionId: action.id,
                localId: lp.id,
              });
              const result = await SyncService._enviarPontoOnline(
                lp,
                lp.fotoUri ?? null,
                {
                  id_adm_cliente: Number(id_adm_cliente) || 0,
                  id_adm_empresa: Number(id_adm_empresa) || 0,
                },
              );
              console.log("[Sync] PONTO: resultado enviar online:", {
                ok: result.ok,
                erros: result.erros,
              });
              if (!result.ok) {
                // Fica na fila; se foi RECUSA do servidor (não rede), espera a vez antes
                // de repetir — com o agendador e a tarefa em 2º plano, sem a espera uma
                // recusa virava uma tentativa a cada poucos minutos, o dia inteiro.
                await tratarFalhaDespacho(
                  action,
                  result,
                  "PONTO",
                  ESPERAS_PONTO_RECUSADO_MS,
                );
              } else {
                await SyncService.markLocalPontoSynced(lp.id, result.fotoUrl);
                // Veio do SNAPSHOT da fila (o mapa não tinha a batida): o markLocalPontoSynced
                // não alcança a cópia em arquivo — sem isto ela seria "restaurada" como
                // pendente e reenviada.
                if (!(localId && map?.[localId])) {
                  await SyncService._atualizarBackupPontoSeExistir({
                    ...lp,
                    offline: false,
                    syncedEm: Date.now(),
                    fotoUrl: result.fotoUrl ?? lp.fotoUrl,
                  });
                }
                await SyncService.dequeueOfflineAction(action.id);
                processedIds.add(action.id);
              }
            } catch (e) {
              console.warn(
                "[Sync] PONTO: erro ao enviar ponto online, mantendo na fila:",
                e,
              );
              remainingActions.push(action);
            }
          } else if (action.type === "OS_FOTO_SAVE") {
            console.log(
              "[Sync] Sincronizando salvamento de foto de OS (Upload + Save)...",
            );
            const {
              id_adm_ordem_servico_cronograma,
              tipo_foto,
              uri,
              idColaborador,
              data_log,
            } = action.data;

            const resolvedCronogramaId = resolveOsCronogramaId(
              id_adm_ordem_servico_cronograma,
            );
            if (!resolvedCronogramaId) {
              remainingActions.push(action);
              continue;
            }

            const response = await SyncService.osUploadESalvarFoto(
              {
                id_adm_ordem_servico_cronograma: resolvedCronogramaId,
                tipo_foto,
                uri,
                idColaborador,
                data_log, // hora da captura persistida — reusada no reenvio (não a hora do sync)
                // Carimbo de tenant PRESERVADO: esta era a única rota de replay que
                // remontava o payload por whitelist e perdia o hash — a origem então
                // re-carimbava com a sessão do DRENO (tenant errado no replay).
                hash: (action.data as any)?.hash ?? "",
                // Identidade da foto atravessa o replay: a chave mantém a UNIQUE do
                // T-Consumer convergindo na mesma linha, e a URL já conquistada evita
                // subir o binário de novo (antes cada ciclo gerava URL nova, derrotando
                // qualquer dedup).
                chave_cliente: (action.data as any)?.chave_cliente,
                arquivo_url: (action.data as any)?.arquivo_url,
                // actionId: sem ele, uma falha do salvarFotos APÓS o upload cai no
                // fluxo offline e enfileira uma CÓPIA nova (id aleatório) enquanto o
                // dreno mantém a original → duplicação a cada ciclo. Com o mesmo id,
                // o re-enqueue substitui a própria ação.
              } as any,
              action.id,
            );

            if (!response.success && !(response as any).offline) {
              // Rejeição do servidor: backoff (5min→30min→6h) em vez de re-tentar em
              // todos os ~20 gatilhos de dreno — cada tentativa ambígua era candidata a
              // virar mais uma cópia no banco.
              await tratarFalhaDespacho(action, response, action.type);
            } else if (!response.success || response.offline) {
              remainingActions.push(action);
            } else {
              // O arquivo local NÃO morre aqui: 200 = enfileirado no Rabbit, e até a
              // lista de OS devolver a foto o espelho local (que aponta para este
              // arquivo) é a única miniatura que os detalhes têm. Quem apaga é a
              // reconciliação (reconciliarFotosLocaisOs), quando o servidor reflete.
              await SyncService.dequeueOfflineAction(action.id);
              processedIds.add(action.id);
            }
          } else if (action.type === "OS_FOTO_DELETE") {
            console.log("[Sync] Sincronizando exclusão de foto de OS...");
            const response = await Api.ordemServico.excluirFoto(
              action.data?.codigoFoto,
            );
            if (!response.success) {
              await tratarFalhaDespacho(action, response, action.type);
            } else {
              await SyncService.dequeueOfflineAction(action.id);
              processedIds.add(action.id);
            }
          } else if (action.type === "OS_ANOTACAO_SAVE") {
            console.log("[Sync] Sincronizando anotação de OS...");
            const resolvedCronogramaId = resolveOsCronogramaId(
              action.data?.id_adm_ordem_servico_cronograma,
            );
            if (!resolvedCronogramaId) {
              remainingActions.push(action);
              continue;
            }
            const response = await Api.ordemServico.salvarAnotacao({
              ...action.data,
              id_adm_ordem_servico_cronograma: resolvedCronogramaId,
            });
            if (!response.success) {
              await tratarFalhaDespacho(action, response, action.type);
            } else {
              await SyncService.dequeueOfflineAction(action.id);
              processedIds.add(action.id);
            }
          } else if (action.type === "OS_INICIAR") {
            console.log("[Sync] Sincronizando início de OS...");
            const resolvedCronogramaId = resolveOsCronogramaId(
              action.data?.id_adm_ordem_servico_cronograma,
            );
            if (!resolvedCronogramaId) {
              remainingActions.push(action);
              continue;
            }
            const response = await Api.ordemServico.iniciarOs({
              ...action.data,
              id_adm_ordem_servico_cronograma: resolvedCronogramaId,
            });
            if (!response.success) {
              await tratarFalhaDespacho(action, response, action.type);
            } else {
              await SyncService.markLocalOsStatusSynced(
                String(resolvedCronogramaId),
              );
              await SyncService.dequeueOfflineAction(action.id);
              processedIds.add(action.id);
            }
          } else if (action.type === "OS_CONCLUIR") {
            console.log("[Sync] Sincronizando conclusão de OS...");
            const resolvedCronogramaId = resolveOsCronogramaId(
              action.data?.id_adm_ordem_servico_cronograma,
            );
            if (!resolvedCronogramaId) {
              remainingActions.push(action);
              continue;
            }
            const anotacaoFinal =
              action.data?.anotacao_final ??
              action.data?.anotacaoFinal ??
              action.data?.anotacao ??
              action.data?.observacao ??
              "";
            const response = await Api.ordemServico.concluirOs({
              ...action.data,
              id_adm_ordem_servico_cronograma: resolvedCronogramaId,
              anotacao_final: anotacaoFinal,
            });
            if (!response.success) {
              remainingActions.push(action);
            } else {
              await SyncService.markLocalOsStatusSynced(
                String(resolvedCronogramaId),
              );
              await SyncService.dequeueOfflineAction(action.id);
              processedIds.add(action.id);
            }
          } else if (action.type === "OS_SOBDEMANDA_SALVAR") {
            console.log("[Sync] Sincronizando O.S. sob demanda (criar)...");
            if (action?.data?.__temp_cronograma_id != null) {
              remainingActions.push(action);
              continue;
            }
            const response = await Api.ordemServico.salvar(
              _cleanOsSobDemandaPayload(action.data),
            );
            if (!response.success) {
              await tratarFalhaDespacho(action, response, action.type);
            } else {
              await SyncService.dequeueOfflineAction(action.id);
              processedIds.add(action.id);
            }
          } else if (action.type === "CHECKLIST_SALVAR") {
            console.log("[Sync] Sincronizando checklist...");
            const { Assets, ...restData } = action.data;
            let payloadProcessado = { ...restData };

            const vinculoRaw = payloadProcessado.IdReferenciaVinculo;
            const resolvedVinculo = resolveOsCronogramaId(vinculoRaw);
            if (_isTempCronogramaId(vinculoRaw) && !resolvedVinculo) {
              // Vínculo TEMPORÁRIO (OS sob demanda criada offline) que não
              // resolve: enquanto a OS-base estiver na fila, esperar é o certo.
              // Mas se ela NÃO está mais lá (falhou de vez, foi quarentenada, ou
              // o id-map morreu no wipe do logout), esperar é esperar para
              // sempre — caso real de checklist de OS que nunca sincroniza.
              // ESPERA (nunca descarta). O 200 do T-ARC na criação da OS-base
              // significa "enfileirado no Rabbit", NÃO "gravado": o cronograma
              // real só aparece num /iniciar posterior (com cache de réplica de
              // ~3min), e nesse intervalo a OS-base já saiu da fila. Tratar
              // "OS-base ausente da fila" como erro descartaria justamente o
              // checklist que ia resolver minutos depois. O log dá visibilidade
              // ao suporte sem custo para o colaborador.
              console.warn(
                "[Sync] CHECKLIST de OS aguardando o cronograma REAL da OS sob demanda (vínculo temporário) — permanece na fila:",
                { id: action.id, vinculo: vinculoRaw },
              );
              remainingActions.push(action);
              continue;
            }
            if (resolvedVinculo) {
              payloadProcessado.IdReferenciaVinculo = resolvedVinculo;
            }

            const urisParaRemover: string[] = [];
            let uploadFalhouComArquivo = false;
            if (Assets && Assets.length > 0) {
              const respostasComUrls = [...restData.Respostas];

              for (const asset of Assets) {
                const fileType = asset.Tipo === "pdf" ? "pdf" : "image";
                const url = await SyncService.uploadChecklistAsset(
                  asset.Uri,
                  restData.ResponsavelId,
                  restData.ChecklistId,
                  fileType,
                );

                if (url) {
                  if (asset.Tipo === ("lsp" as any)) {
                    // LSP: junta a URL da foto no Foto da resposta EXISTENTE (a placa).
                    const alvo = respostasComUrls.find(
                      (r: any) => r.PerguntaId === asset.PerguntaId,
                    );
                    if (alvo) alvo.Foto = url;
                    else
                      respostasComUrls.push({
                        PerguntaId: asset.PerguntaId,
                        Resposta: "",
                        Foto: url,
                      });
                  } else {
                    const respostaTexto =
                      asset.Tipo === "foto"
                        ? "Foto"
                        : asset.Tipo === "assinatura"
                          ? "Assinatura (App)"
                          : "PDF";
                    respostasComUrls.push({
                      PerguntaId: asset.PerguntaId,
                      Resposta: respostaTexto,
                      Foto: url,
                    });
                  }
                  urisParaRemover.push(asset.Uri);
                } else {
                  // Ver comentário gêmeo no ramo de ronda: arquivo sumido segue
                  // sem a foto; upload falho com arquivo presente ADIA o envio.
                  let arquivoExiste = false;
                  try {
                    const info = await FileSystem.getInfoAsync(
                      caminhoNoAppAtual(asset.Uri),
                    );
                    arquivoExiste = !!info.exists;
                  } catch {}
                  if (arquivoExiste) uploadFalhouComArquivo = true;
                  console.warn(
                    arquivoExiste
                      ? "[Sync] Upload de asset FALHOU (arquivo existe) - checklist adiado p/ não gravar sem a foto:"
                      : "[Sync] Asset inexistente no dispositivo - seguindo sem ele:",
                    asset?.Uri,
                  );
                }
              }

              payloadProcessado.Respostas = respostasComUrls;
            }
            if (uploadFalhouComArquivo) {
              remainingActions.push(action);
              continue;
            }

            // ⚠️ Assets locais só são apagados APÓS o sucesso (ver comentário
            // gêmeo no ramo de ronda): apagar antes fazia a re-tentativa gravar
            // o checklist sem as fotos. Persistimos as URLs já obtidas na ação
            // para o retry não refazer os uploads.
            try {
              await SyncService.updateOfflineActionData(action.id, {
                ...action.data,
                Respostas: payloadProcessado.Respostas,
                Assets: [],
              });
            } catch {}

            const response =
              await Api.checklistExecucao.salvar(payloadProcessado);
            if (response.success) {
              for (const uri of urisParaRemover) {
                if (uri && uri.includes("checklist_assets/")) {
                  try {
                    await FileSystem.deleteAsync(uri, { idempotent: true });
                  } catch {}
                }
              }
            }
            if (!response.success) {
              await tratarFalhaDespacho(action, response, action.type);
            } else {
              await SyncService.dequeueOfflineAction(action.id);
              processedIds.add(action.id);
            }
          } else if (action.type === "TREINAMENTO_LOG_ASSISTIDO") {
            console.log("[Sync] Sincronizando treinamento (log assistido)...");
            const response = await Api.treinamento.gravarLogAssistido(
              action.data,
            );
            if (!response.success) {
              await tratarFalhaDespacho(action, response, action.type);
            } else {
              await SyncService.dequeueOfflineAction(action.id);
              processedIds.add(action.id);
            }
          } else if (action.type === "TREINAMENTO_LOG_ASSISTIDO_PARCIAL") {
            console.log(
              "[Sync] Sincronizando treinamento (log assistido parcial)...",
            );
            const response = await Api.treinamento.gravarLogAssistidoParcial(
              action.data,
            );
            if (!response.success) {
              await tratarFalhaDespacho(action, response, action.type);
            } else {
              await SyncService.dequeueOfflineAction(action.id);
              processedIds.add(action.id);
            }
          } else if (action.type === "TREINAMENTO_AVALIACAO") {
            console.log("[Sync] Sincronizando treinamento (avaliação)...");
            const response = await Api.treinamento.gravarAvaliacao(action.data);
            if (!response.success) {
              await tratarFalhaDespacho(action, response, action.type);
            } else {
              await SyncService.dequeueOfflineAction(action.id);
              processedIds.add(action.id);
            }
          } else if (action.type === "ESTOQUE_PEDIDO_SALVAR") {
            console.log("[Sync] Sincronizando estoque (salvar pedido)...");
            const response = await Api.clientesEnvioProdutos.salvar(
              action.data,
            );
            if (!SyncService._isEnfileiradoResponse(response)) {
              remainingActions.push(action);
            } else {
              await SyncService.dequeueOfflineAction(action.id);
              processedIds.add(action.id);
            }
          } else if (action.type === "ESTOQUE_PEDIDO_EDITAR") {
            console.log("[Sync] Sincronizando estoque (editar pedido)...");
            const response = await Api.clientesEnvioProdutos.editar(
              action.data,
            );
            if (!SyncService._isEnfileiradoResponse(response)) {
              remainingActions.push(action);
            } else {
              await SyncService.dequeueOfflineAction(action.id);
              processedIds.add(action.id);
            }
          } else if (action.type === "ESTOQUE_PEDIDO_EXCLUIR") {
            console.log("[Sync] Sincronizando estoque (excluir pedido)...");
            const response = await Api.clientesEnvioProdutos.excluir(
              action.data,
            );
            if (!SyncService._isEnfileiradoResponse(response)) {
              remainingActions.push(action);
            } else {
              await SyncService.dequeueOfflineAction(action.id);
              processedIds.add(action.id);
            }
          } else if (action.type === "ENQUETE_SALVAR") {
            console.log("[Sync] Sincronizando enquete (salvar respostas)...");
            const processed = await SyncService._processEnquetePayloadUploads(
              action.data,
            );
            if (processed.changed) {
              await SyncService.updateOfflineActionData(
                action.id,
                processed.payload,
              );
              for (const uri of processed.urisParaRemover) {
                try {
                  await FileSystem.deleteAsync(uri, { idempotent: true });
                } catch {}
              }
            }

            const response = await Api.enqueteExecucao.salvar(
              processed.payload,
            );
            if (!response.success) {
              await tratarFalhaDespacho(action, response, action.type);
            } else {
              await SyncService.dequeueOfflineAction(action.id);
              processedIds.add(action.id);
            }
          } else if (action.type === "LIVRO_INICIAR") {
            console.log(
              "[Sync] Sincronizando livro de ocorrência (iniciar)...",
            );
            const localId = Number(action.data?._localId ?? 0);
            // Caminho de arquivo → base64, SÓ em memória: a linha da fila continua
            // enxuta. Lança se a imagem sumiu, e aí a ação fica na fila (o catch de
            // baixo) em vez de subir sem foto e dar baixa.
            const enviar: any = await SyncService.hidratarFotosLivro(
              action.data,
            );
            delete enviar._localId;
            if (Number(enviar.id_adm_cliente_livro_ocorrencia) > 0) {
              enviar.id_adm_cliente_livro_ocorrencia =
                await SyncService.resolveLivroId(
                  enviar.id_adm_cliente_livro_ocorrencia,
                );
            }
            const inicioIsLocal = SyncService._isLocalLivroId(
              Number(enviar.id_adm_cliente_livro_ocorrencia),
            );
            console.log(
              "[LIVRO-DBG] INICIAR localId=",
              localId,
              "idLivroEnviar=",
              enviar.id_adm_cliente_livro_ocorrencia,
              "isLocal=",
              inicioIsLocal,
              "id_adm_cliente=",
              enviar.id_adm_cliente,
              "data_competencia=",
              enviar.data_competencia,
            );
            // Edição cujo livro-base ainda não sincronizou → aguarda o próximo ciclo
            if (inicioIsLocal) {
              console.log(
                "[LIVRO-DBG] INICIAR mantido na fila (id ainda local)",
              );
              remainingActions.push(action);
            } else {
              console.log("[LIVRO-DBG] INICIAR chamando salvarInicioLivro...");
              const response =
                await Api.livroOcorrencia.salvarInicioLivro(enviar);
              console.log(
                "[LIVRO-DBG] INICIAR resposta success=",
                response?.success,
                "offline=",
                (response as any)?.offline,
                "erros=",
                response?.erros,
                "done=",
                response?.done,
              );
              if (!response?.success) {
                console.log("[LIVRO-DBG] INICIAR FALHOU → mantido na fila");
                remainingActions.push(action);
              } else {
                const realId = SyncService._extractLivroId(response);
                console.log("[LIVRO-DBG] INICIAR realId extraído=", realId);
                if (realId > 0 && localId > 0)
                  await SyncService.mapLivroIdLocalParaReal(localId, realId);
                await SyncService.dequeueOfflineAction(action.id);
                processedIds.add(action.id);
                console.log("[LIVRO-DBG] INICIAR dequeued OK (sincronizado)");
              }
            }
          } else if (action.type === "LIVRO_OCORRENCIA") {
            console.log(
              "[Sync] Sincronizando livro de ocorrência (ocorrência)...",
            );
            const resolvedId = await SyncService.resolveLivroId(
              action.data?.id_adm_cliente_livro_ocorrencia,
            );
            if (SyncService._isLocalLivroId(resolvedId)) {
              remainingActions.push(action); // livro-base ainda não sincronizado
            } else {
              // Fotos: caminho → base64, só em memória (ver hidratarFotosLivro).
              const dados = await SyncService.hidratarFotosLivro(action.data);
              const response = await Api.livroOcorrencia.salvarOcorrencia({
                ...dados,
                id_adm_cliente_livro_ocorrencia: resolvedId,
              });
              if (!response?.success) {
                remainingActions.push(action);
              } else {
                await SyncService.dequeueOfflineAction(action.id);
                processedIds.add(action.id);
              }
            }
          } else if (action.type === "LIVRO_FINALIZAR") {
            console.log(
              "[Sync] Sincronizando livro de ocorrência (finalizar)...",
            );
            const localId = Number(
              action.data?.id_adm_cliente_livro_ocorrencia ?? 0,
            );
            const resolvedId = await SyncService.resolveLivroId(localId);
            const finFilaIsLocal = SyncService._isLocalLivroId(resolvedId);
            console.log(
              "[LIVRO-DBG] FILA FINALIZAR localId=",
              localId,
              "resolvedId=",
              resolvedId,
              "isLocal=",
              finFilaIsLocal,
            );
            if (finFilaIsLocal) {
              console.log(
                "[LIVRO-DBG] FILA FINALIZAR mantido (id ainda local)",
              );
              remainingActions.push(action); // livro-base ainda não sincronizado
            } else {
              // Assinatura: caminho → base64, só em memória (ver hidratarFotosLivro).
              const dados = await SyncService.hidratarFotosLivro(action.data);
              const response = await Api.livroOcorrencia.finalizarLivro({
                ...dados,
                id_adm_cliente_livro_ocorrencia: resolvedId,
              });
              console.log(
                "[LIVRO-DBG] FILA FINALIZAR resposta success=",
                response?.success,
                "erros=",
                response?.erros,
              );
              if (!response?.success) {
                console.log(
                  "[LIVRO-DBG] FILA FINALIZAR FALHOU → mantido na fila",
                );
                remainingActions.push(action);
              } else {
                if (resolvedId !== localId)
                  await SyncService.markLivroFinalizadoLocal(resolvedId);
                await SyncService.dequeueOfflineAction(action.id);
                processedIds.add(action.id);
                console.log("[LIVRO-DBG] FILA FINALIZAR dequeued OK");
              }
            }
          } else if (action.type === "TROCA_PLANTAO") {
            console.log("[Sync] Sincronizando troca de plantão...");
            const enviar = await SyncService._trocaPlantaoParaEnvio(
              action.data as TrocaPlantaoPayload,
            );
            if (!enviar) {
              // Assinatura ilegível agora: sem ela a troca não vale — segue na fila.
              remainingActions.push(action);
              continue;
            }
            const response = await Api.trocaPlantao.solicitar(enviar);
            console.log(
              "[TROCA-DBG] FILA solicitar resposta success=",
              response?.success,
              "erros=",
              response?.erros,
              "done=",
              response?.done,
            );
            if (!response?.success) {
              await tratarFalhaDespacho(action, response, action.type);
            } else {
              await SyncService.marcarTrocaPlantaoEnviada(
                String(action.data?.data_troca ?? ""),
              );
              await SyncService.dequeueOfflineAction(action.id);
              processedIds.add(action.id);
            }
          } else if (action.type === "TROCA_PLANTAO_RESPOSTA") {
            console.log("[Sync] Sincronizando resposta de troca de plantão...");
            const dados = action.data as TrocaPlantaoRespostaPayload;
            const enviar = await SyncService._trocaRespostaParaEnvio(dados);
            if (!enviar) {
              // Aceite sem assinatura legível agora: não vale — segue na fila.
              remainingActions.push(action);
              continue;
            }
            const response = await Api.trocaPlantao.responder(enviar);
            console.log(
              "[TROCA-DBG] FILA responder resposta success=",
              response?.success,
              "erros=",
              response?.erros,
              "done=",
              response?.done,
            );
            if (!response?.success) {
              await tratarFalhaDespacho(action, response, action.type);
            } else {
              await SyncService._marcarTrocaRespondida(
                Number(dados?.id_rh_troca_plantao_app ?? 0),
                String(dados?.aceito ?? ""),
                false,
              );
              await SyncService.dequeueOfflineAction(action.id);
              processedIds.add(action.id);
            }
          } else {
            remainingActions.push(action);
          }
        } catch (err) {
          console.error("[Sync] Erro ao sincronizar ação:", err);
          remainingActions.push(action);
        }
      }

      // Colapsa assinaturas duplicadas que tenham sobrado na fila (mesmo documento +
      // página). Reenviar a MESMA assinatura gera linhas duplicadas no banco quando o
      // backend não é idempotente. Lê a fila MAIS RECENTE (não o snapshot inicial) para
      // não descartar ações enfileiradas durante o processamento.
      try {
        // Sob o lock da fila: a janela entre o getItem e o setItem abaixo podia
        // engolir um enqueue concorrente (last-write-wins).
        await comFilaLock(async () => {
          const fresh: OfflineAction[] = await lerFilaParaEscrita();
          // ⚠️ Chave com TENANT (ids de contracheque colidem entre bancos): uma
          // SIGNATURE retida de outra empresa nunca é "duplicata" de uma daqui.
          const sigKey = (a: any) =>
            `${String(a?.tenantHash ?? "")
              .trim()
              .toUpperCase()}|${a?.data?.id_adm_contra_cheque_colaborador}:${a?.data?.pagina ?? ""}`;
          const seenSig = new Set<string>();
          const deduped: OfflineAction[] = [];
          // Itera de trás pra frente para manter a ÚLTIMA ocorrência de cada assinatura.
          for (let i = fresh.length - 1; i >= 0; i--) {
            const a = fresh[i];
            if (a.type === "SIGNATURE") {
              const k = sigKey(a);
              if (seenSig.has(k)) continue;
              seenSig.add(k);
            }
            deduped.unshift(a);
          }
          if (deduped.length !== fresh.length) {
            await gravarFila(deduped);
            console.log(
              `[Sync] Fila deduplicada: ${fresh.length} -> ${deduped.length} (assinaturas duplicadas removidas).`,
            );
          }
        });
      } catch (e) {
        console.warn("[Sync] Falha ao deduplicar a fila offline:", e);
      }

      console.log(
        "[Sync] Actions processadas (IDs):",
        Array.from(processedIds),
      );
      console.log(
        "[Sync] Ações a manter na fila (remainingActions):",
        remainingActions.map((a) => ({ id: a.id, type: a.type })),
      );
      const latestActions = await lerFilaSoLeitura().catch(() => null);
      console.log(
        "[Sync] Fila atual após processamento:",
        latestActions ? latestActions.length : "ilegível",
      );
    } catch (error) {
      console.error("[Sync] ERRO CRÍTICO NO PROCESSAMENTO DA FILA:", error);
      console.error("[Sync] Stack trace:", (error as Error).stack);
    } finally {
      isProcessingQueue = false;
      console.log("[Sync] Lock liberado.");
    }
  },
};
